import * as THREE from './three/three.module.js';

import { TextGeometry } from './three/examples/jsm/geometries/TextGeometry.js';
import { FontLoader } from './three/examples/jsm/loaders/FontLoader.js';
import { OBJLoader } from './three/examples/jsm/loaders/OBJLoader.js';
import { MTLLoader } from './three/examples/jsm/loaders/MTLLoader.js';
import { MTLLoaderP } from './three/examples/jsm/loaders/MTLLoaderP.js';
import { GLTFLoader } from './three/examples/jsm/loaders/GLTFLoader.js';
import { DRACOLoader } from './three/examples/jsm/loaders/DRACOLoader.js';
import { generateTangents } from './three/examples/jsm/libs/mikktspace.module.js';
import { Transformation3} from './transform.js';
import { ParticleEngine, Tween, Type, loadParticleTexture } from './three/ParticleEngine.js';


import { mergeGeometries } from "../rt/BufferGeometryUtils.js";
import { computeBoundsTree, disposeBoundsTree, acceleratedRaycast } from './three/bvh/index.module.js';
import {Character} from '../cc.js'


export const RT_SKYBOX = -3;
export const RT_SPOT_LIGHT = -2;
export const RT_POINT_LIGHT = -1;
export const RT_LIGHT = 0;
export const RT_DIFF = 1;
export const RT_REFR = 2;
export const RT_SPEC = 3;
export const RT_COAT = 4;
export const RT_CARCOAT = 5;
export const RT_TRANSLUCENT = 6;
export const RT_SPECSUB = 7;
export const RT_CHECK = 8;
export const RT_WATER = 9;
export const RT_PBR_MATERIAL = 10;
export const RT_WOOD = 11;
export const RT_SEAFLOOR = 12;
export const RT_TERRAIN = 13;
export const RT_CLOTH = 14;
export const RT_LIGHTWOOD = 15;
export const RT_DARKWOOD = 16;
export const RT_PAINTING = 17;
export const RT_METALCOAT = 18;

const tmpV31 = new THREE.Vector3();
const tmpV32 = new THREE.Vector3();

const rtContactSphere = {  
    object: {
        position: new THREE.Vector3(),
    },
    mat:{
        color : { r:0, g: 0, b: 0},
    },
    sphere: {
        radius: 0.1,
        roughness: 0.1,
        emission : { r:0, g: 0, b: 0},
        type: RT_COAT
    }
}

const carColors = [0xFF0000, 0x00FF00, 0x0000FF, 0xFFFF00];

export const impact_params =
{
    name:'impact ',
    positionStyle: Type.SPHERE,
    positionRadius : 2,
    positionSpread: new THREE.Vector3(0.1, 0.1, 0.1),
    velocityStyle: Type.CUBE,

    speedBase      : 5,
    speedSpread    : 2,
    angleVelocityBase: 40,
    angleVelocitySpread: 40,

    velocityBase: new THREE.Vector3(0.0, 10.0, 0.0),
    velocitySpread: new THREE.Vector3(0.2, -0.2, 0.2),
    accelerationBase: new THREE.Vector3(-0.01, -1.4, -0.01),

    particleTextureUrl:  '/assets/textures/12608.jpg',
    angleBase: 0,
    angleSpread: 90,
    sizeSpread: 0.2,
    sizeBase: 0.4,
    blendStyle: THREE.AdditiveBlending,

    colorBase: new THREE.Vector3(0.0, 1.0, 1.0),
    colorSpread: new THREE.Vector3(0.0, 0.2, 0.0),

    sizeTween: new Tween([0.0, 0.6, 1.2], [0.6, 0.3, 0.1]),
    opacityTween: new Tween([0.0, 0.6, 1.2], [0.2, 0.1, 0.0]),
    colorTween: new Tween([0.0, 0.8, 1.2], [new THREE.Vector3(1.0, 0.0, 0.0), new THREE.Vector3(0.0, 1.0, 1.0), new THREE.Vector3(0.0, 0.1, 0.1)]),
    particlesPerSecond: 25,
    particleDeathAge: 1.2,
    emitterDeathAge: 0.6
};


export const drift_params =
{
    name:'drift ',
    positionStyle: Type.CUBE,
    positionSpread: new THREE.Vector3(0.2, 0.1, 0.2),
    velocityStyle: Type.CUBE,
    velocityBase: new THREE.Vector3(0, 0.4, -2.2),
    velocitySpread: new THREE.Vector3(0.8, 0.5, -0.6),
    accelerationBase: new THREE.Vector3(0, -0.2, 0.1),

    particleTextureUrl: '/assets/textures/smokeparticle.png',
    angleBase: 0,
    angleSpread: 720,
    angleVelocityBase: 0,
    angleVelocitySpread: 720,
    sizeSpread: 0.5,

    colorBase: new THREE.Vector3(0, 0.6, 0.6),
    colorSpread: new THREE.Vector3(0.2, 0.2, 0.2),

    sizeTween: new Tween([0, 2.0], [0.4, 0.8]),
    opacityTween: new Tween([0.0, 1.0, 4.0], [0.8, 0.25, 0]),
    colorTween: new Tween([0.1, 0.8, 2.0], [new THREE.Vector3(0, 0.6, 0.6 ), new THREE.Vector3(0.8, 0.3, 0.3), new THREE.Vector3(0.6, 0.6, 0.6)]),
    particlesPerSecond: 100,
    particleDeathAge: 0.5,
    emitterDeathAge: 600
};

export const smoke_params =
{
    name:'smoke ',
    positionStyle: Type.CUBE,
    positionSpread: new THREE.Vector3(0.2, 0.1, 0.2),
    velocityStyle: Type.CUBE,
    velocityBase: new THREE.Vector3(0, 0.4, -2.2),
    velocitySpread: new THREE.Vector3(0.8, 0.5, -0.6),
    accelerationBase: new THREE.Vector3(0, -0.2, 0.1),

    particleTextureUrl:'/assets/textures/smokeparticle.png',
    angleBase: 0,
    angleSpread: 720,
    angleVelocityBase: 0,
    angleVelocitySpread: 720,
    sizeSpread: 0.5,

    colorBase: new THREE.Vector3(0.2, 0.2, 0.2),
    colorSpread: new THREE.Vector3(0.2, 0.2, 0.2),

    sizeTween: new Tween([0, 1.4], [0.1, 1.0]),
    opacityTween: new Tween([0.1, 1.4], [0.8, 0]),
    colorTween: new Tween([0.1, 0.3], [new THREE.Vector3(0, 0, 0.0), new THREE.Vector3(0.3, 0.3, 0.3)]),
    particlesPerSecond: 200,
    particleDeathAge: 1.2,
    emitterDeathAge: 600
};

export const dronefire_params =
{
    name:'dronefire',
    positionStyle: Type.SPHERE,
    positionRadius: 0.2,
    //positionSpread: new THREE.Vector3(1.8, 1.8, 1.8),
    velocityStyle: Type.CUBE,
    velocityBase: new THREE.Vector3(0, 1.0, 0.0),
    velocitySpread: new THREE.Vector3(1.1, 1.1, 1.1),
    accelerationBase: new THREE.Vector3(0, 0.4, 0.0),
    particleTextureUrl:'/assets/textures/fireparticle.png',
    angleBase: 0,
    angleSpread: 360,
    angleVelocityBase: 60,
    angleVelocitySpread: 15,
    sizeSpread: 0.0,
    blendStyle: THREE.AdditiveBlending,
    colorBase: new THREE.Vector3(0.8, 0.2, 0.0),
    colorSpread: new THREE.Vector3(0.0, 0.2, 0.0),

    sizeTween: new Tween([0, 2.0], [0.4, 0.01]),
    opacityTween: new Tween([0.0, 2.0], [1.0, 0]),
    colorTween: new Tween([0.0, 2.0], [new THREE.Vector3(0.8, 0.2, 0.0), new THREE.Vector3(0.3, 0.3, 0.3)]),
    particlesPerSecond: 300,
    particleDeathAge: 2.0,
    emitterDeathAge: 2
};

export const bullet_params =
{
    name:'bullet',
    positionStyle: Type.SPHERE,
    positionRadius: 0.2,
    //positionSpread: new THREE.Vector3(1.8, 1.8, 1.8),
    velocityStyle: Type.CUBE,
    velocityBase: new THREE.Vector3(0, 1.0, 0.0),
    velocitySpread: new THREE.Vector3(0.1, -1.1, 0.1),
    accelerationBase: new THREE.Vector3(0, -4.4, 0.0),
    particleTextureUrl:'/assets/textures/bulletparticle.png',
    angleBase: 0,
    angleSpread: 360,
    angleVelocityBase: 60,
    angleVelocitySpread: 15,
    sizeSpread: 0.0,
    blendStyle: THREE.AdditiveBlending,
    colorBase: new THREE.Vector3(0.8, 0.2, 0.0),
    colorSpread: new THREE.Vector3(0.0, 0.2, 0.0),

    sizeTween: new Tween([0, 1.0], [0.2, 0.01]),
    opacityTween: new Tween([0.0, 1.0], [1.0, 0]),
    colorTween: new Tween([0.0, 1.0], [new THREE.Vector3(0.6, 0.7, 0.6), new THREE.Vector3(0.3, 0.3, 0.3)]),
    particlesPerSecond: 40,
    particleDeathAge: 1.0,
    emitterDeathAge: 2
};

export const spark_params =
{
    name:'spark',
    positionStyle: Type.SPHERE,
    positionRadius: 0.1,
    angleBase: 0,
    angleSpread: 0.2,
    angleVelocityBase: 0,
    angleVelocitySpread: 180,
    
    positionSpread: new THREE.Vector3(0.2, 0.2, 0.2),
    accelerationBase: new THREE.Vector3(0, -5.4, 0.0),

    velocityStyle: Type.SPHERE,
    speedBase: 8.8,
    speedSpread: 1.4,
    velocityBase: new THREE.Vector3(0, 1.0, 0.0),
    velocitySpread: new THREE.Vector3(0.4, 1.1, 0.4),
    particleTextureUrl:'/assets/textures/sparkparticule.png',
    sizeSpread: 0.2,
    blendStyle: THREE.AdditiveBlending,
    colorBase: new THREE.Vector3(0.2, 0.8, 0.8),
    colorSpread: new THREE.Vector3(0.0, 0.4, 0.0),

    sizeTween: new Tween([0, 1.0], [0.1, 0.05]),
    opacityTween: new Tween([0.0, 1.0], [1.0, 0]),
    colorTween: new Tween([0.0, 1.0], [new THREE.Vector3(0.2, 0.8, 0.8), new THREE.Vector3(0.3, 0.4, 0.4)]),
    particlesPerSecond: 30,
    particleDeathAge: 1.0,
    emitterDeathAge: 2000
};


export const bomb_params =
{
    name:'bomb ',
    positionStyle: Type.SPHERE,
    positionRadius: 0.5,

    velocityStyle: Type.SPHERE,
    velocityBase: new THREE.Vector3(0, 8, 0),
    velocitySpread: new THREE.Vector3(14.20, 4 , 14.20),

    speedBase      : 4,
    speedSpread    : 2,

    angleBase : 0,
    angleSpread : 120,

    accelerationBase: new THREE.Vector3(-0.01, 0.2, -0.01),
    accelerationSpread: new THREE.Vector3(0.005, 0.4, 0.005),

    particleTextureUrl:'/assets/textures/smokeparticle.png',
    colorBase: new THREE.Vector3(1.0, 0.1, 0.0),
    colorSpread: new THREE.Vector3(0.0, 0.1, 0.0),
    
    sizeBase: 0.2,
    sizeSpread: 0.1,
    sizeTween: new Tween([0, 0.6, 1.5], [0.4, 0.8, 2.4]),
    opacityTween: new Tween([0.0, 0.4, 0.6, 1.5], [1, 0.6, 0.2, 0.05]),
    colorTween: new Tween([0.0, 0.4, 0.6, 1.5], [new THREE.Vector3(1, 0, 0),new THREE.Vector3(1, 0.8, 0.3),new THREE.Vector3(0.2, 0.2, 0.2), new THREE.Vector3(0.0, 0.0, 0.0)]),
    blendStyle: THREE.NormalBlending,

    particlesPerSecond: 280,
    particleDeathAge: 1.5,
    emitterDeathAge: 0.3
}

export const missile_params =
{
    name:'missile ',
    positionStyle: Type.SPHERE,
    positionRadius: 0.5,

    velocityStyle: Type.CUBE,
    velocityBase: new THREE.Vector3(0, 8, 0),
    velocitySpread: new THREE.Vector3(14.20, 4 , 14.20),

    speedBase      : 40,
    speedSpread    : 10,

    accelerationBase: new THREE.Vector3(-0.01, 0.2, -0.01),
    accelerationSpread: new THREE.Vector3(0.005, 0.4, 0.005),

    particleTextureUrl:'/assets/textures/smokeparticle.png',
    colorBase: new THREE.Vector3(1.0, 0.1, 0.0),
    colorSpread: new THREE.Vector3(0.0, 0.1, 0.0),
    
    sizeBase: 0.2,
    sizeSpread: 0.1,
    sizeTween: new Tween([0, 0.6, 1.5], [0.2, 0.4, 0.8]),
    opacityTween: new Tween([0.0, 0.4, 0.6, 1.5], [1, 0.6, 0.4, 0.2]),
    colorTween: new Tween([0.0, 0.4, 0.6, 1.5], [new THREE.Vector3(0.4, 0, 0),new THREE.Vector3(0.2, 0.2, 0.2),new THREE.Vector3(0.1, 0.1, 0.1), new THREE.Vector3(0.0, 0.0, 0.0)]),
    blendStyle: THREE.NormalBlending,

    particlesPerSecond: 80,
    particleDeathAge: 1.5,
    emitterDeathAge: 8.0
}

export const candle_params =
{
    name:'candle ',
    positionStyle: Type.SPHERE,
    positionRadius: 3,

    velocityStyle: Type.CUBE,
    velocityBase: new THREE.Vector3(0, 8, 0),
    velocitySpread: new THREE.Vector3(0.20, 0, 0.20),

    particleTextureUrl:'/assets/textures/smokeparticle.png',
    colorBase: new THREE.Vector3(1.0, 0.0, 0.0),
    colorSpread: new THREE.Vector3(0.0, 0.0, 0.0),

    sizeBase: 0.1,
    sizeSpread: 0,
    sizeTween: new Tween([0, 0.3, 2.0, 2.5], [0.1, 0.6, 0.3, 0.1]),
    opacityTween: new Tween([0.9, 2.5], [1, 0.2]),
    colorTween: new Tween([0.0, 1.0, 1.5, 2.5], [new THREE.Vector3(1, 0, 0),new THREE.Vector3(1, 0.6, 0.3),new THREE.Vector3(0.5, 0.5, 0.3), new THREE.Vector3(0.3, 0.3, 0.3)]),
    blendStyle: THREE.AdditiveBlending,

    particlesPerSecond: 80,
    particleDeathAge: 3.5,
    emitterDeathAge: 600
}


export const shot_params =
{
    name:'shot ',
    positionStyle: Type.SPHERE,
    positionRadius: 0.1,

    velocityStyle: Type.SPHERE,
    velocityBase: new THREE.Vector3(0, 0, 0),
    velocitySpread: new THREE.Vector3(0.80, 0.80, 0.80),
    accelerationBase: new THREE.Vector3(0.0, 0.2, 0.0),
    accelerationSpread: new THREE.Vector3(0.0, 0.2, 0.0),

    angleBase: 0,
    angleSpread: 90,
    angleVelocityBase: 45,
    angleVelocitySpread: 22.5,

    speedBase: 1,
    speedSpread: 0.3,

    particleTextureUrl:'/assets/textures/smokeparticle.png',
    colorBase: new THREE.Vector3(1.0, 1.0, 0.2),
    colorSpread: new THREE.Vector3(0.2, 0.2, 0.2),

    sizeBase: 0.1,
    sizeSpread: 0,
    sizeTween: new Tween([0, 1.0], [0.05, 0.1]),
    opacityTween: new Tween([0.0, 1.0], [1, 0.6]),
    colorTween: new Tween([0.0, 0.25, 0.5], [new THREE.Vector3(1.0, 1.0, 0.2), new THREE.Vector3(1.0, 0.8, 0.1), new THREE.Vector3(1.0, 0.0, 0.1)]),
    blendStyle: THREE.NormalBlending,

    particlesPerSecond: 100,
    particleDeathAge: 0.5,
    emitterDeathAge: 0.5
}

export const cyber_params =
{
    name:'cyber ',
    positionStyle: Type.SPHERE,
    positionRadius: 3,

    velocityStyle: Type.CUBE,
    velocityBase: new THREE.Vector3(0, 12, 0),
    velocitySpread: new THREE.Vector3(0.20, 0, 0.20),

    particleTextureUrl:'/assets/textures/smokeparticle.png',
    colorBase: new THREE.Vector3(0.0, 1.0, 1.0),
    colorSpread: new THREE.Vector3(0.0, 0.0, 0.0),

    sizeBase: 1,
    sizeSpread: 3,
    sizeTween: new Tween([0, 1.3, 3.5], [0.1, 0.2, 0.8]),
    opacityTween: new Tween([0.9, 3.5], [1, 0.0]),
    colorTween: new Tween([0.0, 1.5, 3.5], [new THREE.Vector3(0, 1, 1),new THREE.Vector3(1, 1, 0), new THREE.Vector3(0, 0, 1)]),
    blendStyle: THREE.NormalBlending,

    particlesPerSecond: 80,
    particleDeathAge: 3.5,
    emitterDeathAge: 600
}

export const sand_params =
{
    name:'sand ',
    positionStyle: Type.SPHERE,
    
    positionRadius: 3,
    
    angleBase: 0,
    angleSpread: 90,
    angleVelocityBase: 45,
    angleVelocitySpread: 22.5,

    accelerationBase: new THREE.Vector3(0.1, -0.2, 0.1),
    accelerationSpread: new THREE.Vector3(0.1, -0.2, 0.1),

    velocityStyle: Type.CUBE,
    velocityBase: new THREE.Vector3(0, 12, 0),
    velocitySpread: new THREE.Vector3(10, 0, 10),

    particleTextureUrl:'/assets/textures/sandpart.png',
    colorBase: new THREE.Vector3(1.0, 1.0, 1.0),
    colorSpread: new THREE.Vector3(0.0, 0.0, 0.0),

    sizeBase: 0.4,
    sizeSpread: 0.0,
    sizeTween: new Tween([0, 0.3, 1.5], [0.8, 1.2, 2.0]),
    opacityTween: new Tween([0.0, 1.5], [1, 0.0]),
    colorTween: new Tween([0.0, 0.5, 1.5], [new THREE.Vector3(1, 1, 0),new THREE.Vector3(0.8, 0.5, 0), new THREE.Vector3(0.8, 0.6, 0)]),
    blendStyle: THREE.NormalBlending,

    particlesPerSecond: 80,
    particleDeathAge: 1.2,
    emitterDeathAge: 600
}


export const firework_params  =
{
    name:'firework ',
    positionStyle  : Type.SPHERE,
    
    positionRadius : 10,
    
    velocityStyle  : Type.SPHERE,
    speedBase      : 40,
    speedSpread    : 10,
    colorBase: new THREE.Vector3(0.5, 0.5, 0.5),
    colorSpread: new THREE.Vector3(0.5, 0.5, 0.5),

    accelerationBase : new THREE.Vector3( 0, -10, 0 ),
    
    particleTextureUrl: '/assets/textures/petal.png' ,
    
    sizeTween    : new Tween( [0.5, 0.7, 1.3], [0.1, 0.2, 0.3] ),
    opacityTween : new Tween( [0.2, 0.7, 2.5], [0.75, 1, 0] ),
    colorTween   : new Tween( [0.4, 0.8, 1.0], [ new THREE.Vector3(0,1,1), new THREE.Vector3(0,1,0.6), new THREE.Vector3(0.8, 1, 0.6) ] ),
    blendStyle   : THREE.AdditiveBlending,  
    
    particlesPerSecond : 300,
    particleDeathAge   : 1.5,		
    emitterDeathAge    : 360.2
};



export const rain_params =
{
    name:'rain ',
    positionStyle: Type.CUBE,
    
    positionSpread: new THREE.Vector3(250, 10, 250),
    
    accelerationBase: new THREE.Vector3(0.0, -2, 0.0),
    accelerationSpread: new THREE.Vector3(0.1, -2, 0.1),

    velocityStyle: Type.CUBE,
    velocityBase: new THREE.Vector3(0, -50, 0),
    velocitySpread: new THREE.Vector3(1, 0, 1),

    particleTextureUrl:'/assets/textures/rain.png',
    colorBase: new THREE.Vector3(1.0, 1.0, 1.0),
    colorSpread: new THREE.Vector3(0.0, 0.0, 0.0),

    sizeTween    : new Tween( [0.0], [4] ),

    sizeBase: 0.1,
    sizeSpread: 0.0,
    blendStyle: THREE.NormalBlending,

    particlesPerSecond: 100,
    particleDeathAge: 1.2,
    emitterDeathAge: 600
}

const particles = [rain_params,firework_params,sand_params,cyber_params,shot_params,candle_params,missile_params,bomb_params,smoke_params,drift_params,impact_params, dronefire_params,bullet_params, spark_params]

/*const AudioContext = window.AudioContext || window.webkitAudioContext;*/

var SoundCfg = {
    musicVolume: 50,
    sfxVolume: 50,
    engineVolume: 50
};


const edgeTable = [
    0x0  , 0x109, 0x203, 0x30a, 0x406, 0x50f, 0x605, 0x70c,
    0x80c, 0x905, 0xa0f, 0xb06, 0xc0a, 0xd03, 0xe09, 0xf00,
    0x190, 0x99 , 0x393, 0x29a, 0x596, 0x49f, 0x795, 0x69c,
    0x99c, 0x895, 0xb9f, 0xa96, 0xd9a, 0xc93, 0xf99, 0xe90,
    0x230, 0x339, 0x33 , 0x13a, 0x636, 0x73f, 0x435, 0x53c,
    0xa3c, 0xb35, 0x83f, 0x936, 0xe3a, 0xf33, 0xc39, 0xd30,
    0x3a0, 0x2a9, 0x1a3, 0xaa , 0x7a6, 0x6af, 0x5a5, 0x4ac,
    0xbac, 0xaa5, 0x9af, 0x8a6, 0xfaa, 0xea3, 0xda9, 0xca0,
    0x460, 0x569, 0x663, 0x76a, 0x66 , 0x16f, 0x265, 0x36c,
    0xc6c, 0xd65, 0xe6f, 0xf66, 0x86a, 0x963, 0xa69, 0xb60,
    0x5f0, 0x4f9, 0x7f3, 0x6fa, 0x1f6, 0xff , 0x3f5, 0x2fc,
    0xdfc, 0xcf5, 0xfff, 0xef6, 0x9fa, 0x8f3, 0xbf9, 0xaf0,
    0x650, 0x759, 0x453, 0x55a, 0x256, 0x35f, 0x55 , 0x15c,
    0xe5c, 0xf55, 0xc5f, 0xd56, 0xa5a, 0xb53, 0x859, 0x950,
    0x7c0, 0x6c9, 0x5c3, 0x4ca, 0x3c6, 0x2cf, 0x1c5, 0xcc ,
    0xfcc, 0xec5, 0xdcf, 0xcc6, 0xbca, 0xac3, 0x9c9, 0x8c0,
    0x8c0, 0x9c9, 0xac3, 0xbca, 0xcc6, 0xdcf, 0xec5, 0xfcc,
    0xcc , 0x1c5, 0x2cf, 0x3c6, 0x4ca, 0x5c3, 0x6c9, 0x7c0,
    0x950, 0x859, 0xb53, 0xa5a, 0xd56, 0xc5f, 0xf55, 0xe5c,
    0x15c, 0x55 , 0x35f, 0x256, 0x55a, 0x453, 0x759, 0x650,
    0xaf0, 0xbf9, 0x8f3, 0x9fa, 0xef6, 0xfff, 0xcf5, 0xdfc,
    0x2fc, 0x3f5, 0xff , 0x1f6, 0x6fa, 0x7f3, 0x4f9, 0x5f0,
    0xb60, 0xa69, 0x963, 0x86a, 0xf66, 0xe6f, 0xd65, 0xc6c,
    0x36c, 0x265, 0x16f, 0x66 , 0x76a, 0x663, 0x569, 0x460,
    0xca0, 0xda9, 0xea3, 0xfaa, 0x8a6, 0x9af, 0xaa5, 0xbac,
    0x4ac, 0x5a5, 0x6af, 0x7a6, 0xaa , 0x1a3, 0x2a9, 0x3a0,
    0xd30, 0xc39, 0xf33, 0xe3a, 0x936, 0x83f, 0xb35, 0xa3c,
    0x53c, 0x435, 0x73f, 0x636, 0x13a, 0x33 , 0x339, 0x230,
    0xe90, 0xf99, 0xc93, 0xd9a, 0xa96, 0xb9f, 0x895, 0x99c,
    0x69c, 0x795, 0x49f, 0x596, 0x29a, 0x393, 0x99 , 0x190,
    0xf00, 0xe09, 0xd03, 0xc0a, 0xb06, 0xa0f, 0x905, 0x80c,
    0x70c, 0x605, 0x50f, 0x406, 0x30a, 0x203, 0x109, 0x0 ];
    
    const triTable=
    [[-1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1],
    [0, 8, 3, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1],
    [0, 1, 9, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1],
    [1, 8, 3, 9, 8, 1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1],
    [1, 2, 10, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1],
    [0, 8, 3, 1, 2, 10, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1],
    [9, 2, 10, 0, 2, 9, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1],
    [2, 8, 3, 2, 10, 8, 10, 9, 8, -1, -1, -1, -1, -1, -1, -1],
    [3, 11, 2, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1],
    [0, 11, 2, 8, 11, 0, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1],
    [1, 9, 0, 2, 3, 11, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1],
    [1, 11, 2, 1, 9, 11, 9, 8, 11, -1, -1, -1, -1, -1, -1, -1],
    [3, 10, 1, 11, 10, 3, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1],
    [0, 10, 1, 0, 8, 10, 8, 11, 10, -1, -1, -1, -1, -1, -1, -1],
    [3, 9, 0, 3, 11, 9, 11, 10, 9, -1, -1, -1, -1, -1, -1, -1],
    [9, 8, 10, 10, 8, 11, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1],
    [4, 7, 8, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1],
    [4, 3, 0, 7, 3, 4, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1],
    [0, 1, 9, 8, 4, 7, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1],
    [4, 1, 9, 4, 7, 1, 7, 3, 1, -1, -1, -1, -1, -1, -1, -1],
    [1, 2, 10, 8, 4, 7, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1],
    [3, 4, 7, 3, 0, 4, 1, 2, 10, -1, -1, -1, -1, -1, -1, -1],
    [9, 2, 10, 9, 0, 2, 8, 4, 7, -1, -1, -1, -1, -1, -1, -1],
    [2, 10, 9, 2, 9, 7, 2, 7, 3, 7, 9, 4, -1, -1, -1, -1],
    [8, 4, 7, 3, 11, 2, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1],
    [11, 4, 7, 11, 2, 4, 2, 0, 4, -1, -1, -1, -1, -1, -1, -1],
    [9, 0, 1, 8, 4, 7, 2, 3, 11, -1, -1, -1, -1, -1, -1, -1],
    [4, 7, 11, 9, 4, 11, 9, 11, 2, 9, 2, 1, -1, -1, -1, -1],
    [3, 10, 1, 3, 11, 10, 7, 8, 4, -1, -1, -1, -1, -1, -1, -1],
    [1, 11, 10, 1, 4, 11, 1, 0, 4, 7, 11, 4, -1, -1, -1, -1],
    [4, 7, 8, 9, 0, 11, 9, 11, 10, 11, 0, 3, -1, -1, -1, -1],
    [4, 7, 11, 4, 11, 9, 9, 11, 10, -1, -1, -1, -1, -1, -1, -1],
    [9, 5, 4, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1],
    [9, 5, 4, 0, 8, 3, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1],
    [0, 5, 4, 1, 5, 0, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1],
    [8, 5, 4, 8, 3, 5, 3, 1, 5, -1, -1, -1, -1, -1, -1, -1],
    [1, 2, 10, 9, 5, 4, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1],
    [3, 0, 8, 1, 2, 10, 4, 9, 5, -1, -1, -1, -1, -1, -1, -1],
    [5, 2, 10, 5, 4, 2, 4, 0, 2, -1, -1, -1, -1, -1, -1, -1],
    [2, 10, 5, 3, 2, 5, 3, 5, 4, 3, 4, 8, -1, -1, -1, -1],
    [9, 5, 4, 2, 3, 11, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1],
    [0, 11, 2, 0, 8, 11, 4, 9, 5, -1, -1, -1, -1, -1, -1, -1],
    [0, 5, 4, 0, 1, 5, 2, 3, 11, -1, -1, -1, -1, -1, -1, -1],
    [2, 1, 5, 2, 5, 8, 2, 8, 11, 4, 8, 5, -1, -1, -1, -1],
    [10, 3, 11, 10, 1, 3, 9, 5, 4, -1, -1, -1, -1, -1, -1, -1],
    [4, 9, 5, 0, 8, 1, 8, 10, 1, 8, 11, 10, -1, -1, -1, -1],
    [5, 4, 0, 5, 0, 11, 5, 11, 10, 11, 0, 3, -1, -1, -1, -1],
    [5, 4, 8, 5, 8, 10, 10, 8, 11, -1, -1, -1, -1, -1, -1, -1],
    [9, 7, 8, 5, 7, 9, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1],
    [9, 3, 0, 9, 5, 3, 5, 7, 3, -1, -1, -1, -1, -1, -1, -1],
    [0, 7, 8, 0, 1, 7, 1, 5, 7, -1, -1, -1, -1, -1, -1, -1],
    [1, 5, 3, 3, 5, 7, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1],
    [9, 7, 8, 9, 5, 7, 10, 1, 2, -1, -1, -1, -1, -1, -1, -1],
    [10, 1, 2, 9, 5, 0, 5, 3, 0, 5, 7, 3, -1, -1, -1, -1],
    [8, 0, 2, 8, 2, 5, 8, 5, 7, 10, 5, 2, -1, -1, -1, -1],
    [2, 10, 5, 2, 5, 3, 3, 5, 7, -1, -1, -1, -1, -1, -1, -1],
    [7, 9, 5, 7, 8, 9, 3, 11, 2, -1, -1, -1, -1, -1, -1, -1],
    [9, 5, 7, 9, 7, 2, 9, 2, 0, 2, 7, 11, -1, -1, -1, -1],
    [2, 3, 11, 0, 1, 8, 1, 7, 8, 1, 5, 7, -1, -1, -1, -1],
    [11, 2, 1, 11, 1, 7, 7, 1, 5, -1, -1, -1, -1, -1, -1, -1],
    [9, 5, 8, 8, 5, 7, 10, 1, 3, 10, 3, 11, -1, -1, -1, -1],
    [5, 7, 0, 5, 0, 9, 7, 11, 0, 1, 0, 10, 11, 10, 0, -1],
    [11, 10, 0, 11, 0, 3, 10, 5, 0, 8, 0, 7, 5, 7, 0, -1],
    [11, 10, 5, 7, 11, 5, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1],
    [10, 6, 5, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1],
    [0, 8, 3, 5, 10, 6, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1],
    [9, 0, 1, 5, 10, 6, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1],
    [1, 8, 3, 1, 9, 8, 5, 10, 6, -1, -1, -1, -1, -1, -1, -1],
    [1, 6, 5, 2, 6, 1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1],
    [1, 6, 5, 1, 2, 6, 3, 0, 8, -1, -1, -1, -1, -1, -1, -1],
    [9, 6, 5, 9, 0, 6, 0, 2, 6, -1, -1, -1, -1, -1, -1, -1],
    [5, 9, 8, 5, 8, 2, 5, 2, 6, 3, 2, 8, -1, -1, -1, -1],
    [2, 3, 11, 10, 6, 5, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1],
    [11, 0, 8, 11, 2, 0, 10, 6, 5, -1, -1, -1, -1, -1, -1, -1],
    [0, 1, 9, 2, 3, 11, 5, 10, 6, -1, -1, -1, -1, -1, -1, -1],
    [5, 10, 6, 1, 9, 2, 9, 11, 2, 9, 8, 11, -1, -1, -1, -1],
    [6, 3, 11, 6, 5, 3, 5, 1, 3, -1, -1, -1, -1, -1, -1, -1],
    [0, 8, 11, 0, 11, 5, 0, 5, 1, 5, 11, 6, -1, -1, -1, -1],
    [3, 11, 6, 0, 3, 6, 0, 6, 5, 0, 5, 9, -1, -1, -1, -1],
    [6, 5, 9, 6, 9, 11, 11, 9, 8, -1, -1, -1, -1, -1, -1, -1],
    [5, 10, 6, 4, 7, 8, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1],
    [4, 3, 0, 4, 7, 3, 6, 5, 10, -1, -1, -1, -1, -1, -1, -1],
    [1, 9, 0, 5, 10, 6, 8, 4, 7, -1, -1, -1, -1, -1, -1, -1],
    [10, 6, 5, 1, 9, 7, 1, 7, 3, 7, 9, 4, -1, -1, -1, -1],
    [6, 1, 2, 6, 5, 1, 4, 7, 8, -1, -1, -1, -1, -1, -1, -1],
    [1, 2, 5, 5, 2, 6, 3, 0, 4, 3, 4, 7, -1, -1, -1, -1],
    [8, 4, 7, 9, 0, 5, 0, 6, 5, 0, 2, 6, -1, -1, -1, -1],
    [7, 3, 9, 7, 9, 4, 3, 2, 9, 5, 9, 6, 2, 6, 9, -1],
    [3, 11, 2, 7, 8, 4, 10, 6, 5, -1, -1, -1, -1, -1, -1, -1],
    [5, 10, 6, 4, 7, 2, 4, 2, 0, 2, 7, 11, -1, -1, -1, -1],
    [0, 1, 9, 4, 7, 8, 2, 3, 11, 5, 10, 6, -1, -1, -1, -1],
    [9, 2, 1, 9, 11, 2, 9, 4, 11, 7, 11, 4, 5, 10, 6, -1],
    [8, 4, 7, 3, 11, 5, 3, 5, 1, 5, 11, 6, -1, -1, -1, -1],
    [5, 1, 11, 5, 11, 6, 1, 0, 11, 7, 11, 4, 0, 4, 11, -1],
    [0, 5, 9, 0, 6, 5, 0, 3, 6, 11, 6, 3, 8, 4, 7, -1],
    [6, 5, 9, 6, 9, 11, 4, 7, 9, 7, 11, 9, -1, -1, -1, -1],
    [10, 4, 9, 6, 4, 10, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1],
    [4, 10, 6, 4, 9, 10, 0, 8, 3, -1, -1, -1, -1, -1, -1, -1],
    [10, 0, 1, 10, 6, 0, 6, 4, 0, -1, -1, -1, -1, -1, -1, -1],
    [8, 3, 1, 8, 1, 6, 8, 6, 4, 6, 1, 10, -1, -1, -1, -1],
    [1, 4, 9, 1, 2, 4, 2, 6, 4, -1, -1, -1, -1, -1, -1, -1],
    [3, 0, 8, 1, 2, 9, 2, 4, 9, 2, 6, 4, -1, -1, -1, -1],
    [0, 2, 4, 4, 2, 6, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1],
    [8, 3, 2, 8, 2, 4, 4, 2, 6, -1, -1, -1, -1, -1, -1, -1],
    [10, 4, 9, 10, 6, 4, 11, 2, 3, -1, -1, -1, -1, -1, -1, -1],
    [0, 8, 2, 2, 8, 11, 4, 9, 10, 4, 10, 6, -1, -1, -1, -1],
    [3, 11, 2, 0, 1, 6, 0, 6, 4, 6, 1, 10, -1, -1, -1, -1],
    [6, 4, 1, 6, 1, 10, 4, 8, 1, 2, 1, 11, 8, 11, 1, -1],
    [9, 6, 4, 9, 3, 6, 9, 1, 3, 11, 6, 3, -1, -1, -1, -1],
    [8, 11, 1, 8, 1, 0, 11, 6, 1, 9, 1, 4, 6, 4, 1, -1],
    [3, 11, 6, 3, 6, 0, 0, 6, 4, -1, -1, -1, -1, -1, -1, -1],
    [6, 4, 8, 11, 6, 8, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1],
    [7, 10, 6, 7, 8, 10, 8, 9, 10, -1, -1, -1, -1, -1, -1, -1],
    [0, 7, 3, 0, 10, 7, 0, 9, 10, 6, 7, 10, -1, -1, -1, -1],
    [10, 6, 7, 1, 10, 7, 1, 7, 8, 1, 8, 0, -1, -1, -1, -1],
    [10, 6, 7, 10, 7, 1, 1, 7, 3, -1, -1, -1, -1, -1, -1, -1],
    [1, 2, 6, 1, 6, 8, 1, 8, 9, 8, 6, 7, -1, -1, -1, -1],
    [2, 6, 9, 2, 9, 1, 6, 7, 9, 0, 9, 3, 7, 3, 9, -1],
    [7, 8, 0, 7, 0, 6, 6, 0, 2, -1, -1, -1, -1, -1, -1, -1],
    [7, 3, 2, 6, 7, 2, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1],
    [2, 3, 11, 10, 6, 8, 10, 8, 9, 8, 6, 7, -1, -1, -1, -1],
    [2, 0, 7, 2, 7, 11, 0, 9, 7, 6, 7, 10, 9, 10, 7, -1],
    [1, 8, 0, 1, 7, 8, 1, 10, 7, 6, 7, 10, 2, 3, 11, -1],
    [11, 2, 1, 11, 1, 7, 10, 6, 1, 6, 7, 1, -1, -1, -1, -1],
    [8, 9, 6, 8, 6, 7, 9, 1, 6, 11, 6, 3, 1, 3, 6, -1],
    [0, 9, 1, 11, 6, 7, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1],
    [7, 8, 0, 7, 0, 6, 3, 11, 0, 11, 6, 0, -1, -1, -1, -1],
    [7, 11, 6, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1],
    [7, 6, 11, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1],
    [3, 0, 8, 11, 7, 6, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1],
    [0, 1, 9, 11, 7, 6, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1],
    [8, 1, 9, 8, 3, 1, 11, 7, 6, -1, -1, -1, -1, -1, -1, -1],
    [10, 1, 2, 6, 11, 7, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1],
    [1, 2, 10, 3, 0, 8, 6, 11, 7, -1, -1, -1, -1, -1, -1, -1],
    [2, 9, 0, 2, 10, 9, 6, 11, 7, -1, -1, -1, -1, -1, -1, -1],
    [6, 11, 7, 2, 10, 3, 10, 8, 3, 10, 9, 8, -1, -1, -1, -1],
    [7, 2, 3, 6, 2, 7, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1],
    [7, 0, 8, 7, 6, 0, 6, 2, 0, -1, -1, -1, -1, -1, -1, -1],
    [2, 7, 6, 2, 3, 7, 0, 1, 9, -1, -1, -1, -1, -1, -1, -1],
    [1, 6, 2, 1, 8, 6, 1, 9, 8, 8, 7, 6, -1, -1, -1, -1],
    [10, 7, 6, 10, 1, 7, 1, 3, 7, -1, -1, -1, -1, -1, -1, -1],
    [10, 7, 6, 1, 7, 10, 1, 8, 7, 1, 0, 8, -1, -1, -1, -1],
    [0, 3, 7, 0, 7, 10, 0, 10, 9, 6, 10, 7, -1, -1, -1, -1],
    [7, 6, 10, 7, 10, 8, 8, 10, 9, -1, -1, -1, -1, -1, -1, -1],
    [6, 8, 4, 11, 8, 6, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1],
    [3, 6, 11, 3, 0, 6, 0, 4, 6, -1, -1, -1, -1, -1, -1, -1],
    [8, 6, 11, 8, 4, 6, 9, 0, 1, -1, -1, -1, -1, -1, -1, -1],
    [9, 4, 6, 9, 6, 3, 9, 3, 1, 11, 3, 6, -1, -1, -1, -1],
    [6, 8, 4, 6, 11, 8, 2, 10, 1, -1, -1, -1, -1, -1, -1, -1],
    [1, 2, 10, 3, 0, 11, 0, 6, 11, 0, 4, 6, -1, -1, -1, -1],
    [4, 11, 8, 4, 6, 11, 0, 2, 9, 2, 10, 9, -1, -1, -1, -1],
    [10, 9, 3, 10, 3, 2, 9, 4, 3, 11, 3, 6, 4, 6, 3, -1],
    [8, 2, 3, 8, 4, 2, 4, 6, 2, -1, -1, -1, -1, -1, -1, -1],
    [0, 4, 2, 4, 6, 2, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1],
    [1, 9, 0, 2, 3, 4, 2, 4, 6, 4, 3, 8, -1, -1, -1, -1],
    [1, 9, 4, 1, 4, 2, 2, 4, 6, -1, -1, -1, -1, -1, -1, -1],
    [8, 1, 3, 8, 6, 1, 8, 4, 6, 6, 10, 1, -1, -1, -1, -1],
    [10, 1, 0, 10, 0, 6, 6, 0, 4, -1, -1, -1, -1, -1, -1, -1],
    [4, 6, 3, 4, 3, 8, 6, 10, 3, 0, 3, 9, 10, 9, 3, -1],
    [10, 9, 4, 6, 10, 4, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1],
    [4, 9, 5, 7, 6, 11, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1],
    [0, 8, 3, 4, 9, 5, 11, 7, 6, -1, -1, -1, -1, -1, -1, -1],
    [5, 0, 1, 5, 4, 0, 7, 6, 11, -1, -1, -1, -1, -1, -1, -1],
    [11, 7, 6, 8, 3, 4, 3, 5, 4, 3, 1, 5, -1, -1, -1, -1],
    [9, 5, 4, 10, 1, 2, 7, 6, 11, -1, -1, -1, -1, -1, -1, -1],
    [6, 11, 7, 1, 2, 10, 0, 8, 3, 4, 9, 5, -1, -1, -1, -1],
    [7, 6, 11, 5, 4, 10, 4, 2, 10, 4, 0, 2, -1, -1, -1, -1],
    [3, 4, 8, 3, 5, 4, 3, 2, 5, 10, 5, 2, 11, 7, 6, -1],
    [7, 2, 3, 7, 6, 2, 5, 4, 9, -1, -1, -1, -1, -1, -1, -1],
    [9, 5, 4, 0, 8, 6, 0, 6, 2, 6, 8, 7, -1, -1, -1, -1],
    [3, 6, 2, 3, 7, 6, 1, 5, 0, 5, 4, 0, -1, -1, -1, -1],
    [6, 2, 8, 6, 8, 7, 2, 1, 8, 4, 8, 5, 1, 5, 8, -1],
    [9, 5, 4, 10, 1, 6, 1, 7, 6, 1, 3, 7, -1, -1, -1, -1],
    [1, 6, 10, 1, 7, 6, 1, 0, 7, 8, 7, 0, 9, 5, 4, -1],
    [4, 0, 10, 4, 10, 5, 0, 3, 10, 6, 10, 7, 3, 7, 10, -1],
    [7, 6, 10, 7, 10, 8, 5, 4, 10, 4, 8, 10, -1, -1, -1, -1],
    [6, 9, 5, 6, 11, 9, 11, 8, 9, -1, -1, -1, -1, -1, -1, -1],
    [3, 6, 11, 0, 6, 3, 0, 5, 6, 0, 9, 5, -1, -1, -1, -1],
    [0, 11, 8, 0, 5, 11, 0, 1, 5, 5, 6, 11, -1, -1, -1, -1],
    [6, 11, 3, 6, 3, 5, 5, 3, 1, -1, -1, -1, -1, -1, -1, -1],
    [1, 2, 10, 9, 5, 11, 9, 11, 8, 11, 5, 6, -1, -1, -1, -1],
    [0, 11, 3, 0, 6, 11, 0, 9, 6, 5, 6, 9, 1, 2, 10, -1],
    [11, 8, 5, 11, 5, 6, 8, 0, 5, 10, 5, 2, 0, 2, 5, -1],
    [6, 11, 3, 6, 3, 5, 2, 10, 3, 10, 5, 3, -1, -1, -1, -1],
    [5, 8, 9, 5, 2, 8, 5, 6, 2, 3, 8, 2, -1, -1, -1, -1],
    [9, 5, 6, 9, 6, 0, 0, 6, 2, -1, -1, -1, -1, -1, -1, -1],
    [1, 5, 8, 1, 8, 0, 5, 6, 8, 3, 8, 2, 6, 2, 8, -1],
    [1, 5, 6, 2, 1, 6, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1],
    [1, 3, 6, 1, 6, 10, 3, 8, 6, 5, 6, 9, 8, 9, 6, -1],
    [10, 1, 0, 10, 0, 6, 9, 5, 0, 5, 6, 0, -1, -1, -1, -1],
    [0, 3, 8, 5, 6, 10, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1],
    [10, 5, 6, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1],
    [11, 5, 10, 7, 5, 11, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1],
    [11, 5, 10, 11, 7, 5, 8, 3, 0, -1, -1, -1, -1, -1, -1, -1],
    [5, 11, 7, 5, 10, 11, 1, 9, 0, -1, -1, -1, -1, -1, -1, -1],
    [10, 7, 5, 10, 11, 7, 9, 8, 1, 8, 3, 1, -1, -1, -1, -1],
    [11, 1, 2, 11, 7, 1, 7, 5, 1, -1, -1, -1, -1, -1, -1, -1],
    [0, 8, 3, 1, 2, 7, 1, 7, 5, 7, 2, 11, -1, -1, -1, -1],
    [9, 7, 5, 9, 2, 7, 9, 0, 2, 2, 11, 7, -1, -1, -1, -1],
    [7, 5, 2, 7, 2, 11, 5, 9, 2, 3, 2, 8, 9, 8, 2, -1],
    [2, 5, 10, 2, 3, 5, 3, 7, 5, -1, -1, -1, -1, -1, -1, -1],
    [8, 2, 0, 8, 5, 2, 8, 7, 5, 10, 2, 5, -1, -1, -1, -1],
    [9, 0, 1, 5, 10, 3, 5, 3, 7, 3, 10, 2, -1, -1, -1, -1],
    [9, 8, 2, 9, 2, 1, 8, 7, 2, 10, 2, 5, 7, 5, 2, -1],
    [1, 3, 5, 3, 7, 5, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1],
    [0, 8, 7, 0, 7, 1, 1, 7, 5, -1, -1, -1, -1, -1, -1, -1],
    [9, 0, 3, 9, 3, 5, 5, 3, 7, -1, -1, -1, -1, -1, -1, -1],
    [9, 8, 7, 5, 9, 7, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1],
    [5, 8, 4, 5, 10, 8, 10, 11, 8, -1, -1, -1, -1, -1, -1, -1],
    [5, 0, 4, 5, 11, 0, 5, 10, 11, 11, 3, 0, -1, -1, -1, -1],
    [0, 1, 9, 8, 4, 10, 8, 10, 11, 10, 4, 5, -1, -1, -1, -1],
    [10, 11, 4, 10, 4, 5, 11, 3, 4, 9, 4, 1, 3, 1, 4, -1],
    [2, 5, 1, 2, 8, 5, 2, 11, 8, 4, 5, 8, -1, -1, -1, -1],
    [0, 4, 11, 0, 11, 3, 4, 5, 11, 2, 11, 1, 5, 1, 11, -1],
    [0, 2, 5, 0, 5, 9, 2, 11, 5, 4, 5, 8, 11, 8, 5, -1],
    [9, 4, 5, 2, 11, 3, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1],
    [2, 5, 10, 3, 5, 2, 3, 4, 5, 3, 8, 4, -1, -1, -1, -1],
    [5, 10, 2, 5, 2, 4, 4, 2, 0, -1, -1, -1, -1, -1, -1, -1],
    [3, 10, 2, 3, 5, 10, 3, 8, 5, 4, 5, 8, 0, 1, 9, -1],
    [5, 10, 2, 5, 2, 4, 1, 9, 2, 9, 4, 2, -1, -1, -1, -1],
    [8, 4, 5, 8, 5, 3, 3, 5, 1, -1, -1, -1, -1, -1, -1, -1],
    [0, 4, 5, 1, 0, 5, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1],
    [8, 4, 5, 8, 5, 3, 9, 0, 5, 0, 3, 5, -1, -1, -1, -1],
    [9, 4, 5, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1],
    [4, 11, 7, 4, 9, 11, 9, 10, 11, -1, -1, -1, -1, -1, -1, -1],
    [0, 8, 3, 4, 9, 7, 9, 11, 7, 9, 10, 11, -1, -1, -1, -1],
    [1, 10, 11, 1, 11, 4, 1, 4, 0, 7, 4, 11, -1, -1, -1, -1],
    [3, 1, 4, 3, 4, 8, 1, 10, 4, 7, 4, 11, 10, 11, 4, -1],
    [4, 11, 7, 9, 11, 4, 9, 2, 11, 9, 1, 2, -1, -1, -1, -1],
    [9, 7, 4, 9, 11, 7, 9, 1, 11, 2, 11, 1, 0, 8, 3, -1],
    [11, 7, 4, 11, 4, 2, 2, 4, 0, -1, -1, -1, -1, -1, -1, -1],
    [11, 7, 4, 11, 4, 2, 8, 3, 4, 3, 2, 4, -1, -1, -1, -1],
    [2, 9, 10, 2, 7, 9, 2, 3, 7, 7, 4, 9, -1, -1, -1, -1],
    [9, 10, 7, 9, 7, 4, 10, 2, 7, 8, 7, 0, 2, 0, 7, -1],
    [3, 7, 10, 3, 10, 2, 7, 4, 10, 1, 10, 0, 4, 0, 10, -1],
    [1, 10, 2, 8, 7, 4, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1],
    [4, 9, 1, 4, 1, 7, 7, 1, 3, -1, -1, -1, -1, -1, -1, -1],
    [4, 9, 1, 4, 1, 7, 0, 8, 1, 8, 7, 1, -1, -1, -1, -1],
    [4, 0, 3, 7, 4, 3, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1],
    [4, 8, 7, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1],
    [9, 10, 8, 10, 11, 8, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1],
    [3, 0, 9, 3, 9, 11, 11, 9, 10, -1, -1, -1, -1, -1, -1, -1],
    [0, 1, 10, 0, 10, 8, 8, 10, 11, -1, -1, -1, -1, -1, -1, -1],
    [3, 1, 10, 11, 3, 10, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1],
    [1, 2, 11, 1, 11, 9, 9, 11, 8, -1, -1, -1, -1, -1, -1, -1],
    [3, 0, 9, 3, 9, 11, 1, 2, 9, 2, 11, 9, -1, -1, -1, -1],
    [0, 2, 11, 8, 0, 11, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1],
    [3, 2, 11, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1],
    [2, 3, 8, 2, 8, 10, 10, 8, 9, -1, -1, -1, -1, -1, -1, -1],
    [9, 10, 2, 0, 9, 2, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1],
    [2, 3, 8, 2, 8, 10, 0, 1, 8, 1, 10, 8, -1, -1, -1, -1],
    [1, 10, 2, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1],
    [1, 3, 8, 9, 1, 8, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1],
    [0, 9, 1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1],
    [0, 3, 8, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1],
    [-1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1]];
    
    const g_edges_indexs = [[0,1],[1,2],[2,3],[3,0],[4,5],[5,6],[6,7],[7,4],[0,4],[1,5],[2,6],[3,7],[0,0],[0,0],[0,0],[0,0]];
    const EPSILON  = 10;

    class MetaGridEdge {
        v_index;
        set;
        indexs;

        constructor () {

            this.v_index = 0;
            this.indexs = new Uint32Array(2);
            this.set = 0;

        }
    };

    class MetaGridCell {

        edge_indexs;
        indexs;
        cubeindex;
        edgeindex;

        constructor(){
            
            this.edge_indexs = new Uint32Array(12);
            this.indexs = new Uint32Array(12);

            this.cubeindex = 0;
            this.edgeindex = 0;

            //this.EdgeArray = new Uint32Array(12 * 4);
            //this.Edges = new Array(12);
        }
    };

class MetaBallGrid {
    
    total_edge;
    w;
    h;
    d;
    
    grid_pos;
    grid_rot;
    
    Cells;
    Edges;
    
    isolevel;
    vals;
    points;

    norms;

 
    constructor(width, height, depth, sx = 1, sy = 1, sz = 1)
    {
        this.trans_matrix = new THREE.Matrix4();
        this.scal_matrix = new THREE.Matrix4();
        this.matrix = new THREE.Matrix4();
        this.pos = new THREE.Vector3();

        this.grid_pos = new THREE.Vector3();
        this.grid_rot = new THREE.Quaternion();

        this.cell_size = new THREE.Vector3( sx , sy , sz);

        this.vIndices = new Uint32Array(65000 * 3);
        this.vIndices.fill(0xFFFFFFFF,0, 65000 * 3);

        this.vBalls = new Float32Array(1024 * 4);
        this.nBalls = 0,

        this.initialize( width, height, depth );
    }


    initialize(width, height, depth)
    {
        let ne,n;		
        let i,j,k;
        let ei1,ei2;
        let ind,ind2;
        let hd;
        
        this.w			=	width;
        this.h			=	height;
        this.d			=	depth;
        
        this.isolevel	=	120;

        this.hd			=	this.h*this.d;
        const total_size=	(this.w+1)*(this.h+1)*(this.d+1);
        this.total_edge	=	( (this.d)*(this.w+1)*(this.h+1))+( (this.d+1)*( this.w*(this.h+1)+this.h*(this.w+1) )  );

        //norms		=new nif[total_size];

        n			=	this.w*this.h*this.d;
        this.Cells	=	new Uint32Array(n * 22);

    
        while(n--)
        {
            //edge index
            this.Cells[n * 22 + 0]  = 0xFFFFFFFF;
            this.Cells[n * 22 + 1]  = 0xFFFFFFFF;
            this.Cells[n * 22 + 2]  = 0xFFFFFFFF;
            this.Cells[n * 22 + 3]  = 0xFFFFFFFF;
            this.Cells[n * 22 + 4]  = 0xFFFFFFFF;
            this.Cells[n * 22 + 5]  = 0xFFFFFFFF;

            this.Cells[n * 22 + 6]  = 0xFFFFFFFF;
            this.Cells[n * 22 + 7]  = 0xFFFFFFFF;
            this.Cells[n * 22 + 8]  = 0xFFFFFFFF;
            this.Cells[n * 22 + 9]  = 0xFFFFFFFF;
            this.Cells[n * 22 + 10] = 0xFFFFFFFF;
            this.Cells[n * 22 + 11] = 0xFFFFFFFF;

            //indexs
            this.Cells[n * 22 + 12] = 0;
            this.Cells[n * 22 + 13] = 0;
            this.Cells[n * 22 + 14] = 0;
            this.Cells[n * 22 + 15] = 0;
            this.Cells[n * 22 + 16] = 0;
            this.Cells[n * 22 + 17] = 0;
            this.Cells[n * 22 + 18] = 0;
            this.Cells[n * 22 + 19] = 0;

            //cubeindex
            this.Cells[n * 22 + 20] = 0;
            //edgeindex
            this.Cells[n * 22 + 21] = 0;
        }

        

        this.Triangles = null;
        this.nt = 0;
        this.nta = 0;

        /*
        this.Materi		=	new Array(this.NSurfs);
        this.Surfaces[0]	=	new Surface();
        this.Surfaces[0].Name    = "default";
        */

        this.Edges		    =	new Uint32Array(this.total_edge * 3);
        this.EdgesSet	    =	new Uint8Array(this.total_edge);
        this.Vertices	    =	new Float32Array(this.total_edge * 3);
        this.VerticesNormals=	new Float32Array(this.total_edge * 3);

        for(let n=0;n<this.total_edge;n++)
        {
            //this.Edges[n] = new MetaGridEdge();
            this.Edges[n * 3 + 0] = n;
            this.Edges[n * 3 + 1] = 0;
            this.Edges[n * 3 + 2] = 0;
        }
        this.NVertices = this.total_edge;
    
    
        this.vals		= new Float32Array(total_size);
        this.points		= new Uint8Array(total_size*3);

        for(k=0;k<this.d;k++)
        {
            for(j=0;j<this.h;j++)
            {
                for(i=0;i<this.w;i++)
                {
                    const ind  = this.calcindex(i,j,k);
                    const ind2 = ind+this.hd;
                    
                    this.Cells[ind * 22 + 12]=ind;
                    this.Cells[ind * 22 + 13]=ind2;
                    this.Cells[ind * 22 + 14]=ind2+this.d;
                    this.Cells[ind * 22 + 15]=ind+this.d;

                    this.Cells[ind * 22 + 16]=ind+1;
                    this.Cells[ind * 22 + 17]=ind2+1;
                    this.Cells[ind * 22 + 18]=ind2+this.d+1;
                    this.Cells[ind * 22 + 19]=ind+this.d+1;
                }
            }
        }
              
        const idxs = new Uint32Array(4);

        ne=0;
        for(i=0;i<=this.w;i++)			
        {
            for(j=0;j<=this.h;j++)
            {
                for(k=0;k<=this.d;k++)
                {
                    ind	= this.calcindex(i,j,k);
                    
                    this.points[ind*3+0] = i;
                    this.points[ind*3+1] = j;
                    this.points[ind*3+2] = k;
                    this.vals[ind] = 0.0;
                    
                    if(i<this.w)
                    {
                        ei1	= ind;
                        ei2	= this.calcindex(i+1,j,k);

                        this.Edges[ne * 3 + 1] = ei1;
                        this.Edges[ne * 3 + 2] = ei2;

                        idxs[0]	= this.calcindex	(i  ,j+0,k+0);
                        idxs[1]	= this.calcindex	(i  ,j-1,k+0);
                        idxs[2]	= this.calcindex	(i  ,j-1,k-1);
                        idxs[3]	= this.calcindex	(i  ,j+0,k-1);
                        
                        for(let ii=0;ii<4;ii++) {

                            if(idxs[ii]==0xFFFFFFFF)continue;
                            
                            //const Cell = this.Cells[idxs[ii]];

                            const cellIIDX = idxs[ii] * 22;
                            
                            for(let n=0;n<12;n++) {
                                let ci1 = g_edges_indexs[n][0];
                                let ci2 = g_edges_indexs[n][1];

                                if(((this.Cells[cellIIDX + 12 + ci1]==ei1)&&(this.Cells[cellIIDX + 12 + ci2]==ei2))||
                                   ((this.Cells[cellIIDX + 12 + ci2]==ei1)&&(this.Cells[cellIIDX + 12 + ci1]==ei2))) {

                                    this.Cells[cellIIDX + n] =ne * 3;;
                                    break;
                                }
                            }
                        }
                        ne++;
                    }

                    if(j<this.h) {
                        ei1	= ind;
                        ei2	= this.calcindex(i, j+1, k);

                        this.Edges[ne * 3 + 1] = ei1;
                        this.Edges[ne * 3 + 2] = ei2;

                        idxs[0]	= this.calcindex	(i+0 ,j ,k+0);
                        idxs[1]	= this.calcindex	(i-1 ,j ,k+0);
                        idxs[2]	= this.calcindex	(i-1 ,j ,k-1);
                        idxs[3]	= this.calcindex	(i+0 ,j ,k-1);
                        
                        for(let ii=0;ii<4;ii++) {
                            if(idxs[ii]==0xFFFFFFFF)continue;
                            
                            //const Cell = this.Cells[idxs[ii]];

                            const cellIIDX = idxs[ii] * 22;

                            for(let n=0;n<12;n++) {
                                const ci1=g_edges_indexs[n][0];
                                const ci2=g_edges_indexs[n][1];

                                if( ((this.Cells[cellIIDX + 12 +ci1]==ei1)&&(this.Cells[cellIIDX + 12 +ci2]==ei2))|| 
                                    ((this.Cells[cellIIDX + 12 +ci2]==ei1)&&(this.Cells[cellIIDX + 12 +ci1]==ei2))  ) {

                                    this.Cells[cellIIDX + n] = ne * 3;
                                    break;
                                }
                            }
                        }
                        ne++;
                    }

                    if(k<this.d) {
                        ei1 = ind;
                        ei2	= this.calcindex(i,j ,k+1);

                        this.Edges[ne * 3 + 1] = ei1;
                        this.Edges[ne * 3 + 2] = ei2;

                        idxs[0]	= this.calcindex	(i+0  ,j+0,k);
                        idxs[1]	= this.calcindex	(i-1  ,j+0,k);
                        idxs[2]	= this.calcindex	(i-1  ,j-1,k);
                        idxs[3]	= this.calcindex	(i+0  ,j-1,k);
                        
                        for(let ii=0;ii<4;ii++) {

                            if(idxs[ii]==0xFFFFFFFF)continue;

                            const cellIIDX = idxs[ii] * 22;

                            for(let n=0;n<12;n++)
                            {
                                const ci1=g_edges_indexs[n][0];
                                const ci2=g_edges_indexs[n][1];

                                if( ((this.Cells[cellIIDX + 12 +ci1]==ei1)&&(this.Cells[cellIIDX + 12 +ci2]==ei2))|| 
                                    ((this.Cells[cellIIDX + 12 +ci2]==ei1)&&(this.Cells[cellIIDX + 12 +ci1]==ei2)) ) {
                                        this.Cells[cellIIDX +n]	= ne * 3;
                                    break;
                                }
                            }
                        }
                        ne++;
                    }
                }
            }
        }
    }


    
    add_ball(x, y, z, r, w)
    {

        this.vBalls[this.nBalls * 4 + 0] = x;
        this.vBalls[this.nBalls * 4 + 1] = y;
        this.vBalls[this.nBalls * 4 + 2] = z;
        this.vBalls[this.nBalls * 4 + 3] = r;

        this.nBalls++;
        /*
        const my_ball ={center : new THREE.Vector3(x, y, z), radius : r, weight : w};
        const idx = this.vBalls.length;
        this.vBalls.push(my_ball);

        return idx;
        */
    }

    //=|=|=|=|=|=|=|=|=|=|=|=|=|=|=|=|=|=|=|=|=|=|=|=|=|=|=|=|=|=|=|=|=|=|=|=|=|=|=|=|=|=|=|=|  
    calcindex( i, j,  k)
    //=|=|=|=|=|=|=|=|=|=|=|=|=|=|=|=|=|=|=|=|=|=|=|=|=|=|=|=|=|=|=|=|=|=|=|=|=|=|=|=|=|=|=|=|  
    {
        if(i< 0)return 0xFFFFFFFF;
        if(i>=this.w)return 0xFFFFFFFF;
    
        if(j< 0)return 0xFFFFFFFF;
        if(j>=this.h)return 0xFFFFFFFF;
    
        if(k< 0)return 0xFFFFFFFF;
        if(k>=this.d)return 0xFFFFFFFF;
    
        return i*this.hd + j*this.d + k;
    }

 
    ComputeBall(x ,y, z, r) {
    
        const sr = r;
        const rrad =  sr / this.cell_size.x;
        const sqrad = rrad*rrad;

        const scx = x/ this.cell_size.x;
        const scy = y/ this.cell_size.y;
        const scz = z/ this.cell_size.z;
    
        const minx=(scx-rrad);
        const maxx=(scx+rrad);
        
        const miny=(scy-rrad);
        const maxy=(scy+rrad);

        const minz=(scz-rrad);
        const maxz=(scz+rrad);
                
        for(let i=minx;i<maxx;i++)
        {	
            for(let j=miny;j<maxy;j++)
            {
                for(let k=minz;k<maxz;k++)
                {
                    const ii	=Math.floor(i);
                    const ij	=Math.floor(j);
                    const ik	=Math.floor(k);

                    let vind =	this.calcindex(ii, ij, ik);
                    if(vind === 0xFFFFFFFF)
                        continue;

                    if((ik<this.d)&&(ik>=0)&&(ii<this.w)&&(ii>=0)&&(ij<=this.h)&&(ij>=0)) {

                        const deltaX	=	(ii-scx);
                        const deltaY	=	(ij-scy);
                        const deltaZ	=	(ik-scz);
                        const ssqr	    =	deltaX*deltaX+deltaY*deltaY+deltaZ*deltaZ;

                        if(ssqr<sqrad)
                        {
                            const sqd		=	Math.sqrt(ssqr);
               

                            this.vals[vind]	+=	(this.isolevel*sr * 4.8)/(ssqr);
                        }
                    }
                }
            }
        }
    }
   

    CalcEdges (EdgeIndex) {
        
        let mu;
        let x,y,z;

        const i1 = this.Edges[EdgeIndex + 1];
        const i2 = this.Edges[EdgeIndex + 2];
                
        let pi1	= i1*3;
        let pi2	= i2*3;
    
        const val1=	this.vals[i1];
        const val2=	this.vals[i2];
    
        if ( Math.abs(this.isolevel-val1) < EPSILON) {
            x=this.points[pi1+0];
            y=this.points[pi1+1];
            z=this.points[pi1+2];
        }else if (Math.abs(this.isolevel-val2) < EPSILON) {
            x=this.points[pi2+0];
            y=this.points[pi2+1];
            z=this.points[pi2+2];
        }else {
        
            mu = (this.isolevel - val1) / (val2 - val1);
                
            x=this.points[pi1+0];
            y=this.points[pi1+1];
            z=this.points[pi1+2];
            
            if(this.points[pi2]>this.points[pi1])
                x+=mu;
            else if(this.points[pi2]<this.points[pi1])
                x-=mu;
            
            pi1++;
            pi2++;

            if(this.points[pi2]>this.points[pi1])
                y+=mu;
            else if(this.points[pi2]<this.points[pi1])
                y-=mu;
            
            pi1++;
            pi2++;

            if(this.points[pi2]>this.points[pi1])
                z+=mu;
            else if(this.points[pi2]<this.points[pi1])
                z-=mu;
        }
    
        const edix = this.Edges[EdgeIndex] * 3;

        this.Vertices[ edix + 0] = x * this.cell_size.x;
        this.Vertices[ edix + 1] = y * this.cell_size.y;
        this.Vertices[ edix + 2] = z * this.cell_size.z;
        /*
        if(!this.Edges[EdgeIndex + 3]){
            this.Vertices[ edix + 0] = x;
            this.Vertices[ edix + 1] = y;
            this.Vertices[ edix + 2] = z;
        }else{
            this.Vertices[ edix + 0] = (this.Vertices[ edix + 0] + x) / 2.0;
            this.Vertices[ edix + 1] = (this.Vertices[ edix + 1] + y) / 2.0
            this.Vertices[ edix + 2] = (this.Vertices[ edix + 2] + z) / 2.0;
    
        }
        */
        

    }
    
    CalcCubeIndex(cellID) {

        let cubeindex = 0;
        
        if(this.vals[this.Cells[cellID + 12]]<this.isolevel)cubeindex |= 1;
        if(this.vals[this.Cells[cellID + 13]]<this.isolevel)cubeindex |= 2;
        if(this.vals[this.Cells[cellID + 14]]<this.isolevel)cubeindex |= 4;
        if(this.vals[this.Cells[cellID + 15]]<this.isolevel)cubeindex |= 8;
        if(this.vals[this.Cells[cellID + 16]]<this.isolevel)cubeindex |= 16;
        if(this.vals[this.Cells[cellID + 17]]<this.isolevel)cubeindex |= 32;
        if(this.vals[this.Cells[cellID + 18]]<this.isolevel)cubeindex |= 64;
        if(this.vals[this.Cells[cellID + 19]]<this.isolevel)cubeindex |= 128;
        
        this.Cells[cellID + 20] = cubeindex;
        this.Cells[cellID + 21] = edgeTable[cubeindex];
    }
        
    Compute()
    {
        this.vals.fill(0, 0 , (this.w+1)*(this.d+1)*(this.h+1));
        this.EdgesSet.fill(0, 0, this.total_edge);

        for(let i=0;i<this.nBalls * 4;i+=4) {
            this.ComputeBall(this.vBalls[i+0], this.vBalls[i+1], this.vBalls[i+2], this.vBalls[i+3]);
        }
    
         for(let i=0;i<this.w;i++) {
            for(let j=0;j<this.h;j++) {
                for(let k=0;k<this.d;k++) {
                    
                    //const Cell = this.getCell(i,j,k);
                    const cellIDX = this.calcindex(i,j,k) * 22;
                    this.CalcCubeIndex	(cellIDX);
                  
                    //compute vertex position from isovalue
                    for(let n=0;n<12;n++) {
                        const eid = this.Cells[cellIDX + n];

                        if( eid === 0xFFFFFFFF)
                            continue;
                
                        if(this.EdgesSet[eid / 3] != 0)
                            continue;

                        if (this.Cells[cellIDX + 21] & (1<<n)) {
                            this.CalcEdges(eid);
                            this.EdgesSet[eid / 3] = 1;
                        }
                    }
                }
            }
        }
    }
}

class Octree {
    static _v = new THREE.Vector3();

    constructor(vertices, maxVerticesPerNode = 8) {
        if (vertices instanceof THREE.BufferGeometry) {
            vertices = vertices.attributes.position.array;
        }
        this.maxVerticesPerNode = maxVerticesPerNode;
        this.vertices = vertices;
        const num = vertices.length / 3;
        this.bounds = new THREE.Box3().setFromArray(vertices);
        this.subTrees = [{ box: this.bounds, subTrees: [], indices: [] }];
        if (num <= maxVerticesPerNode) {
            this.subTrees[0].indices = Array(num).fill().map((_, i) => i);
            return;
        }

        const boxes = Octree.boxSplit(this.bounds);
        for (const b of boxes) {
            const t = { box: b, subTrees: [], indices: [] };
            for (let i = 0; i < num; ++i) {
                if (b.containsPoint(Octree._v.fromArray(vertices, i * 3))) {
                    t.indices.push(i);
                }
            }
            this.subTrees[0].subTrees.push(t);
        }

        const trees = this.subTrees[0].subTrees.slice();
        while (trees.length) {
            const curr = trees.at(-1);
            trees.pop();
            if (curr.indices.length > maxVerticesPerNode) {
                this.splitCell(curr);
                trees.push(...curr.subTrees);
            }
        }
    }

    addVertex(v) {
        const newIndex = this.vertices.length / 3;
        if (!this.vertices.push){
            this.vertices = Array.from(this.vertices);
        }
        this.vertices.push(v.x, v.y, v.z);
        let curr = this.subTrees[0];
        const found = false;
        while (curr.subTrees.length) {
            for (const t of curr.subTrees) {
                if (t.box.containsPoint(v)) {
                    curr = t;
                    break;
                }
            }
        }
        curr.indices.push(newIndex);
        if (curr.indices.length > this.maxVerticesPerNode) {
            this.splitCell(curr);
        }
    }

    search(position, radius) {
        const sphere = (position instanceof THREE.Sphere) ? position : new THREE.Sphere(position, radius);
        const ret = [];
        const trees = this.subTrees.slice();
        while (trees.length) {
            const curr = trees.at(-1);
            trees.pop();
            if (curr.box.intersectsSphere(sphere)) {
                if (curr.subTrees.length) {
                    for (const t of curr.subTrees) {
                        trees.push(t);
                    }
                } else {
                    for (const i of curr.indices) {
                        if (sphere.containsPoint(Octree._v.fromArray(this.vertices, i * 3))) {
                            ret.push(i);
                        }
                    }
                }
            }
        }
        return ret;
    }

    mergeVertices(threshold = 1e-4) {
        const sphere = new THREE.Sphere(undefined, threshold);
        const verticesToIndices = []
        for (let i = 0, il = this.vertices.length / 3; i < il; ++i) {
            if (verticesToIndices[i] === undefined) {
                sphere.center.fromArray(this.vertices, i * 3);
                const found = this.search(sphere);
                const newIndex = Math.min(...found);
                found.forEach(ind => verticesToIndices[ind] = newIndex);
            }
        }
        return verticesToIndices;
    }

    splitCell(cell) {
        const boxes = Octree.boxSplit(cell.box);
        for (const b of boxes) {
            const t = { box: b, subTrees: [], indices: [] };
            for (const i of cell.indices) {
                if (b.containsPoint(Octree._v.fromArray(this.vertices, i * 3))) {
                    t.indices.push(i);
                }
            }

            cell.subTrees.push(t);
        }
        cell.indices = [];
    }

    static boxSplit(box) {
        const boxes = [];
        const halfsize = box.getSize(new THREE.Vector3()).multiplyScalar(0.5);
        for (let x = 0; x < 2; x++) {
            for (let y = 0; y < 2; y++) {
                for (let z = 0; z < 2; z++) {
                    const curr = new THREE.Box3();
                    curr.min.set(x, y, z).multiply(halfsize).add(box.min);
                    curr.max.copy(curr.min).add(halfsize);
                    boxes.push(curr);
                }
            }
        }
        return boxes;
    }
}


class Grass{

    mesh;
    nGrass;

    constructor(geo, mat, n){
        this.nGrass = n;
        this.pts = new Float32Array(this.nGrass * 3);

        this.geo = geo;
        this.mat = mat;
        this.transforms = new Array(n);
        for(let i=0;i<n;i++){
            this.transforms[i]= new Transformation3();
        }
        this.boxSize = new THREE.Vector3();
        this.grassFolds = new Array(this.nGrass);

        for(let n=0;n<this.nGrass;n++){
            this.grassFolds[n] ={oq: new THREE.Quaternion(), dq: new THREE.Quaternion(), t: 0, active : false}
        }

        this.geo.computeBoundingBox();
        this.geo.boundingBox.getSize(this.boxSize);
        
        this.tmpM = new THREE.Matrix4();
        this.tmpQ = new THREE.Quaternion();
        this.oQ = new THREE.Quaternion();
        this.tmpV = new THREE.Vector3();

        this.uscale = new THREE.Vector3(1,1,1);
        this.up   = new THREE.Vector3(0,1,0);

      
        this.OCTree = null;
    }

    doBillBoard(q){
        for(let n=0;n<this.nGrass;n++){
            this.tmpV.set(this.pts[n*3+0], this.pts[n*3+1], this.pts[n*3+2]);
            this.tmpM.compose(this.tmpV , q, this.uscale);
            this.mesh.setMatrixAt(n,  this.tmpM);
        }

    }
    setTransform(n, t){
        //this.mesh.setMatrixAt(n, m);
        this.transforms[n].set(t.position, t.quaternion);
        this.pts[n * 3 + 0 ] = t.position.x;
        this.pts[n * 3 + 1 ] = t.position.y;
        this.pts[n * 3 + 2 ] = t.position.z;
    }

    updateFolds(time, q = null){


  
        let upt = false;


        for(let n=0;n<this.nGrass;n++){
            const oq = q ? q : this.grassFolds[n].oq;

            if(this.grassFolds[n].active){
                const dt = time - this.grassFolds[n].t;
            
                if(dt<2){
                    this.tmpQ.slerpQuaternions(oq, this.grassFolds[n].dq, dt / 2)

                    if(this.mesh.meshGPU){
                        this.mesh.meshGPU.transformations[n].set(null, this.tmpQ);
                    }else{
                        this.mesh.getMatrixAt(n, this.tmpM);
                        this.tmpV.set( this.tmpM.elements[12],  this.tmpM.elements[13],  this.tmpM.elements[14]);
                        this.tmpM.compose(this.tmpV, this.tmpQ, this.uscale);
                        this.mesh.setMatrixAt(n,  this.tmpM);
                        upt = true;
                    }
                   
                }else if(dt<4){

                }else if(dt<6){
                    this.tmpQ.slerpQuaternions(oq, this.grassFolds[n].dq, (6.0 - dt) / 2.0);

                    if(this.mesh.meshGPU){
                        this.mesh.meshGPU.transformations[n].set(null, this.tmpQ);
                    }else{
                        this.mesh.getMatrixAt(n, this.tmpM);
                        this.tmpV.set( this.tmpM.elements[12],  this.tmpM.elements[13],  this.tmpM.elements[14]);
                        this.tmpM.compose(this.tmpV, this.tmpQ, this.uscale);
                        this.mesh.setMatrixAt(n, this.tmpM);
                         upt = true;
                    }
                   
                }else{
                    this.grassFolds[n].active = false;
                }

                
            }else{
                this.tmpV.set(this.pts[n*3+0], this.pts[n*3+1], this.pts[n*3+2]);
                this.tmpM.compose(this.tmpV , oq, this.uscale);
                this.mesh.setMatrixAt(n,  this.tmpM);
            }
        }

        if(upt === true){
            this.mesh.instanceMatrix.needsUpdate = true;
        }
    }

    testPoint(sphere, time){

        if(!this.mesh)
            return;

        if(!this.bbox)
            return;

        if(!this.bbox.containsPoint(sphere.center))
            return;
        
        const idxs = this.OCTree.search(sphere);
        
        for(let id of idxs){

            if(!this.grassFolds[id].active){

                if(this.mesh.meshGPU){
                    this.grassFolds[id].oq.set( this.mesh.meshGPU.transformations[id].quaternion.x, this.mesh.meshGPU.transformations[id].quaternion.y, this.mesh.meshGPU.transformations[id].quaternion.z, this.mesh.meshGPU.transformations[id].quaternion.w);
                }else{
                    this.mesh.getMatrixAt(id, this.tmpM);
                    this.grassFolds[id].oq.setFromRotationMatrix(this.tmpM);
                }
               

                this.tmpV.set(this.pts[id * 3 + 0], this.pts[id * 3 + 1], this.pts[id * 3 + 2]).sub(sphere.center).normalize().negate().cross(this.up);
                this.grassFolds[id].dq.set(this.tmpV.x, this.tmpV.y, this.tmpV.z , 1.0).normalize();

                this.grassFolds[id].t=time;
                this.grassFolds[id].active = true;

            }else if((time - this.grassFolds[id].t)>4){
                this.grassFolds[id].t = time;
            }
        }


    }

    buildOcTree(){
        const ggeo = new THREE.BufferGeometry();
        ggeo.setAttribute('position', new THREE.BufferAttribute(this.pts, 3));
        this.OCTree = new Octree(ggeo);
    }

}

class RootBone {
    quat;
    rot;
    pos;
    root_pos;
    rpos;
    children;
    size;

    constructor(pos, quat, size = null) { 
        this.quat = new THREE.Quaternion(quat.x, quat.y, quat.z, quat.w);
        this.rot = this.quat.clone();
        this.size = size;
        this.pos  = new THREE.Vector3(pos.x, pos.y, pos.z);
        this.root_pos = new THREE.Vector3(0, 0, 0);
        this.children = [];
        this.name = "root";
    }

    addChild(c) {
        this.children.push(c);
    }
    getEuler(){

        return new THREE.Euler().setFromQuaternion(this.quat);
    }
    findBone(name) {

        if(this.name === name)
            return this;
        
        for(let c of this.children) {
            const nc = c.findBone(name);
            if(nc !== null)
                return nc;
        }
        return null;
    }
}


class SimpleBone {
    name;
    length;
    radius;
    jradius;
    quat;
    color;
    children;
    life;
    
    
    constructor(name, quat, length, radius, jradius, color = null, size = null){
        this.name = name;
        this.quat = new THREE.Quaternion(quat.x, quat.y, quat.z, quat.w);
        this.rot = this.quat.clone();
        this.iq = this.quat.clone().invert();
        this.length = length;
        this.radius = radius;
        this.jradius = jradius;
        this.size = size;
        this.color = color ? color : new THREE.Vector3(Math.random(), Math.random(), Math.random());
        this.children = [];
        this.life = -1;
        this.total_life = -1;
        //this.pos = new THREE.Vector3();
    }

    copy(src){
        this.name = src.name;
        this.quat = src.quat.clone();
        this.rot = this.quat.clone();
        this.iq = this.quat.clone().invert();
        this.pos = src.pos ? src.pos.clone() : null;
        this.length = src.length;
        this.radius = src.radius;
        this.jradius = src.jradius;
        this.color = src.color.clone();
        this.life = src.life;
        this.total_life = src.total_life;
        this.idx = src.idx;
        this.id = src.id;
        this.netID = src.netID;
        this.subsystem = src.subsystem;
        this.children = [];

        for(let c of src.children){

            const newc =  new SimpleBone(c.name, new THREE.Quaternion(), c.length, c.radius, c.jradius, c.color.clone());
            newc.copy(c);

            if(c.pos)
                newc.pos = c.pos.clone();

            this.children.push(newc);
        }
    }

    getEuler(){

        return new THREE.Euler().setFromQuaternion(this.quat);
    }

    loadChildren(children){

        for(let c of children){
            const newBone = new SimpleBone(c.name, c.quat, c.length, c.radius, c.jradius);
            newBone.loadChildren(c.children);
            this.addChild(newBone);
        }
    }

    serialize(parent){

        const bone = {
            name : this.name,
            length : this.length,
            radius : this.radius,
            life :  this.life,
            size : this.size,
            jradius : this.jradius,
            pos : this.pos ? {x:this.pos.x, y:this.pos.y, z:this.pos.z} : null,
            quat : {x : this.quat.x, y : this.quat.y, z : this.quat.z, w : this.quat.w},
            rot : {x : this.rot.x, y : this.rot.y, z : this.rot.z, w : this.rot.w},
            children : []
        }  
        parent.push(bone);

        for(let c of this.children) {
            c.serialize(bone.children);
        }
    }
    
    addChild(c) {
        this.children.push(c);
    }

    findBone(name) {

        if(this.name === name)
            return this;
        
        for(let c of this.children) {
            const nc = c.findBone(name);
            if(nc !== null)
                return nc;
        }
        return null;
    }
 
    getInfos(infos, pos= null, quat= null){

        const bonePos = pos ? pos.clone() : new THREE.Vector3(0,0,0);
        const boneQuat = quat ? quat.clone().multiply(this.quat) : this.quat.clone();
        const boneVec = new THREE.Vector3(0, this.length, 0).applyQuaternion(boneQuat);
        const boneDir = boneVec.clone().normalize();
        const halfDir = boneVec.clone().multiplyScalar(0.5);
        const boneCenter = this.name.startsWith('foot') ? bonePos.clone() : new THREE.Vector3().addVectors(bonePos, halfDir);
        const col = new THREE.Vector3(0, 1, 0);

        const leaf = this.children.length > 0 ? false: true;

        const info = {boneName : this.name, color : this.color, radius : this.radius, jradius : this.jradius, pos : bonePos, dir : boneDir, length : this.length, center : boneCenter, quat : boneQuat, mesh : null, smesh : null, leaf: leaf};

        infos.push(info);

        const nextPos = new THREE.Vector3().addVectors(bonePos, boneVec);
        for(let c of this.children) {
            c.getInfos(infos, nextPos, boneQuat);
        }
    }

    getMap(maps, cnt){

        this.id = cnt.id++;
        maps[this.name] = this.id;
        for(let c of this.children) {
            c.getMap(maps, cnt);
        }
    }

    addWeigh(weights, name, w){

        if(w >= 0.99){
            while(weights.length>0){weights.pop()}
            weights.push({name : name, weight : w});   
            return
        }

        let rem = 1.0 - w;
        let total = 0.0;
        for(let mw = 0; mw < weights.length; mw++){
            total += w.weight;
        }

        for(let mw = 0; mw < weights.length;    mw++){
            weights[mw].weight = mw.weight * rem / total;
        }

        weights.push({name : name, weight : w});
        return weights;


    }

    getWeights(weights, v, exclude = [], pr = null, pos = null, quat = null){

        const bonePos = pos ? pos.clone() : new THREE.Vector3(0,0,0);
        const boneQuat = quat ? quat.clone().multiply(this.quat) : this.quat.clone();
        const boneVec = new THREE.Vector3(0, this.length, 0).applyQuaternion(boneQuat);
        const halfDir = boneVec.clone().multiplyScalar(0.5);
        const boneCenter = this.name.startsWith('foot') ? bonePos.clone() : new THREE.Vector3().addVectors(bonePos, halfDir);

        const tv = v.clone().sub(bonePos).applyQuaternion(boneQuat.clone().conjugate());
        const nextPos = new THREE.Vector3().addVectors(bonePos, boneVec);

        const sr = this.jradius;
        const dr = sr * 2.0;

        const ss = this.length - sr;
        const ts = ss + dr;
                        
        const ss2 = -sr;
        const ts2 = ss2 + dr;

        const dist = new THREE.Vector2(tv.x, tv.z).length();

        if(exclude.indexOf(this.name)<0) {

            if(this.name.startsWith('hand')) {
                const psr = this.jradius;
                const pdr = psr * 2.0;
                                        
                const pss2 = -psr;
                const pts2 = pss2 + pdr;

                //const boneCenter = new THREE.Vector3().addVectors(bonePos, halfDir);
                const cdist = v.distanceTo(boneCenter);

                if(cdist < this.radius ){

                    this.addWeigh(weights, this.name, 1.0);
                }
                              
            } else if(this.name.startsWith('foot')) {
                const cdist = v.distanceTo(boneCenter);

                if( (dist < this.radius) && (tv.y < this.length )){

                    this.addWeigh(weights, this.name, 1.0);
                }

            }else{

                let weight;

                if (dist < this.radius){

                    if((tv.y < this.length * 1.2 )&& (tv.y > -0.2 )){
                        
                        this.addWeigh(weights, this.name, 1.0);
                    }
                }
            }
        }
        
        for(let c of this.children) {
            c.getWeights(weights, v, exclude, sr, nextPos, boneQuat);
        }
    }

    getThree(bones, map, parent, plength, pos = null, quat = null, size = 1){

        const bonePos = pos ? pos.clone().multiplyScalar(size) : new THREE.Vector3(0,0,0);
        const boneQuat = quat ? quat.clone().multiply(this.quat) : this.quat.clone();
        const boneVec = new THREE.Vector3(0, this.length * size, 0).applyQuaternion(boneQuat);
        const nextPos = this.boneOffset ? new THREE.Vector3().addVectors(bonePos, this.boneOffset) : new THREE.Vector3().addVectors(bonePos, boneVec);

        const bone = new THREE.Bone();
        bone.name = this.name;

        //bone.position.set(pdir.x, pdir.y, pdir.z);

        if(this.boneOffset)
            bone.position.set(this.boneOffset.x, this.boneOffset.y, this.boneOffset.z);
        else
            bone.position.set(0, plength * size, 0);

        bone.quaternion.set(this.quat.x, this.quat.y, this.quat.z, this.quat.w);

        //console.log('getThree '+bone.name+' '+bone.position.x.toFixed(2) +' '+bone.position.y.toFixed(2)+' '+bone.position.z.toFixed(2));
        //console.log('getThree '+bone.name+' '+bone.quaternion.x.toFixed(2)+' '+bone.quaternion.y.toFixed(2)+' '+bone.quaternion.z.toFixed(2)+' '+bone.quaternion.w.toFixed(2));
        
        parent.add(bone);

        const idx = map[this.name];
        bones[idx] = bone;

        for(let c of this.children) {
            c.getThree(bones, map, bone, this.length, nextPos, boneQuat, size);
        }
    }
}

class SimpleSkeleton{
    
    root;
    exclude;

    constructor(pos, quat, exclude = [], matExclude = [], rootSize = 1){
        this.root = new RootBone(pos, quat, rootSize);
        this.exclude = exclude;
        this.matExclude = matExclude;
    }

    toPhys(){
        const root = {
                        name : this.root.name,
                        pos : 
                        {
                            x: this.root.pos.x, 
                            y: this.root.pos.y, 
                            z: this.root.pos.z
                        },
                        rot : {x : this.root.rot.x, y : this.root.rot.y, z : this.root.rot.z, w : this.root.rot.w},
                        quat : 
                        {
                            x: this.root.quat.x, 
                            y: this.root.quat.y, 
                            z: this.root.quat.z,
                            w: this.root.quat.w
                        },
                        size: this.root.size,
                        children:[]
                    };

        for(let c of this.root.children){
            c.serialize(root.children);
        }

        return {root : root,  exclude :this.exclude, matExclude :this.matExclude}
    }

    serialize(){
        const obj = {
                        pos : 
                        {
                            x: this.root.pos.x, 
                            y: this.root.pos.y, 
                            z: this.root.pos.z
                        },
                        quat : 
                        {
                            x: this.root.quat.x, 
                            y: this.root.quat.y, 
                            z: this.root.quat.z,
                            w: this.root.quat.w
                        },
                        exclude :this.exclude,
                        matExclude :this.matExclude,
                        children:[]
                    };

        for(let c of this.root.children){
            c.serialize(obj.children);
        }

        return JSON.stringify(obj);
    }

    findBone(name){

        for(let c of this.root.children){
            const nc = c.findBone(name);
            if(nc !== null)
                return nc;
        }

        return null;
    }

    setBoneQuat(name, q){
        for(let c of this.root.children){
            const nc = c.findBone(name);
            if(nc !== null){
                nc.quat.set(q.x, q.y, q.z, q.w);
                return;
            }
              
        }
    }

    setBoneJRadius (name, r){

        for(let c of this.root.children){
            const nc = c.findBone(name);
            if(nc !== null){
                nc.jradius = r;
                return;
            }
        }

    }
    setBoneRadius (name , r){
        for(let c of this.root.children){
            const nc = c.findBone(name);
            if(nc !== null){
                nc.radius = r;
                return;
            }
        }

    }
    setBoneLength(name , l){
        for(let c of this.root.children){
            const nc = c.findBone(name);
            if(nc !== null){
                nc.length = l;
                return;
            }
        }
    }

    addBone(bone){
        this.root.addChild(bone);
    }



    getInfos(){
        const infos = [];
        for(let c of this.root.children){
            c.getInfos(infos, this.root.pos, this.root.quat);
        }
        return infos;
    }

    getBoneWeights(v, ex = true){

        const weights = [];
        for(let c of this.root.children){
            c.getWeights(weights, v, ex === true ? this.exclude : [] , 0.0, this.root.pos, this.root.quat);
        }
        return weights;
    }

    getBoneMap(){

        let curId = 0;
        const map = {"root" : curId++};

        let cnt = {id : curId};
        
        for(let c of this.root.children){
            c.getMap(map, cnt)
        }
        return map;
    }

    getThree(size = 1){

        const map = this.getBoneMap();
        
        const rootBone = new THREE.Bone();
        rootBone.name = 'root';
        rootBone.position.set(this.root.pos.x * size, this.root.pos.y* size, this.root.pos.z* size);
        //rootBone.quaternion.set(this.root.quat.x, this.root.quat.y, this.root.quat.z, this.root.quat.w);

        const bones = [rootBone];
        
        for(let c of this.root.children){
            c.getThree(bones, map, rootBone, 0, null, this.root.quat, size);
        }

        return bones;
    }
}




class MaterialPool{

    materials;
    type;
    count;

    constructor(materials, types, num) {

        this.count = num;

        this.materials = new Array(types.length);
        
        for(let t=0;t<types.length;t++) {
            
            this.materials[types[t]] = new Array(this.count);

            for(let n=0;n<num;n++) {
                this.materials[types[t]][n] = {type : types[t], mat : materials[t].clone(), used : false};
            }
        }
    }

    getMat(type) {
        if(!this.materials[type])
            return null;

        for(let n=0;n<this.count;n++) {
            if( (this.materials[type][n].used === false)&&
                (this.materials[type][n].type === type)) {
                return this.materials[type][n];
            }
        }

        return null;
    }

    dispose(mat){
        mat.used = false;
    }

}


class EngineBase {
    camera;
    lastTime;
    sceneUI;
    cameraUI;
    manager;
    camMode;
    digits;
    MTLLoader;

    tireCubeMesh = null;
    tireNameMesh = null;
    motorCubeMesh = null;
    motorNameMesh = null;
    tireBox = {};
    motorBox = {};
    constructor(audio = false, assetBase = '') {

        this.sceneUI = new THREE.Scene();
        this.CameraSpaceScene = new THREE.Scene();

        
        this.tmpcol = new THREE.Color();
        this.tmpemm = new THREE.Color();

        this.assetBase = assetBase;
        
        this.cameraTransform = new Transformation3();
        this.vt = new Transformation3();

        this.DMGgeo1 = new THREE.BoxGeometry(6, 0.8, 0.8);

        const dmgColors = new Float32Array(this.DMGgeo1.attributes.position.count * 3);
        dmgColors.fill(1);
        this.DMGgeo1.setAttribute('color', new THREE.Float32BufferAttribute(dmgColors, 3));





        //this.DMGgeo2 = new THREE.BoxGeometry(6, 0.4, 0.4);

        this.numDmgBar = 0;



        this.upVector = new THREE.Vector3(0,1,0);
        this.droneMeshCache = {};

        this.CameraSpaceLight = new THREE.DirectionalLight(0xFFFFFFFF, 0.2);
        this.CameraSpaceLight.position.set(0, 0, 2);

        this.CameraSpaceScene.add(this.CameraSpaceLight);

        this.cameraUI = new THREE.OrthographicCamera(-100, 100, 100, -100, -10000, 10000);
        this.cameraUI.updateProjectionMatrix();

        this.fovAdd = 15;

         this.nitroColor1 = new THREE.Vector3(1.0, 0.02, 0.0);
         this.nitroColor2 = new THREE.Vector3(1.0, 0.01, 0.0);


        this.down = new THREE.Vector3(0, -1, 0);
        this.up = new THREE.Vector3(0, 1, 0);
        this.pos = new THREE.Vector3();
        this.t = new Transformation3();
        this.m = new THREE.Matrix4();
        this.rc = new THREE.Raycaster();
        this.tvec = new THREE.Vector3();
        this.uv = new THREE.Vector2();
        this.color1 = new THREE.Vector3(1, 1, 0);
        this.color2 = new THREE.Vector3(1, 0, 0);
        this.colorMix = new THREE.Vector3();
        this.color = new THREE.Color();
        this.scaleVec = new THREE.Vector3();

        this.tmpV2 = new THREE.Vector2();
        this.tmpV = new THREE.Vector3();

        this.mt = new THREE.Vector3();
        this.mq= new THREE.Quaternion();
        this.ms= new THREE.Vector3();


        this.enableRagDollDBG=false;
        this.sndMeshes = [];
        this.dialogAnswerHeight = 0.6;
        this.dialogBorder = 0.2;
        this.dialogHeaderHeight = 0.2;

        this.cameraUI.position.z = 10;
        this.cameraUI.lookAt(0, 0, 0);

        this.tmpEul = new THREE.Euler();
        this.tmpQ = new THREE.Quaternion();
        this.tmpQ2 = new THREE.Quaternion();
        this.tmpVec3 = new THREE.Vector3();

        this.eidxs = new Uint32Array(4);
        this.v1 = new THREE.Vector3();
        this.v2 = new THREE.Vector3();
        this.norm = new THREE.Vector3();

        this.txtSize= new THREE.Vector3();

        this.manager = new THREE.LoadingManager();
        this.texLoader = new THREE.TextureLoader();
        this.objLoader = new OBJLoader(this.manager);



        




        this.fairies = {};

        THREE.Cache.enabled = true;

        THREE.BufferGeometry.prototype.computeBoundsTree = computeBoundsTree;
        THREE.BufferGeometry.prototype.disposeBoundsTree = disposeBoundsTree;
        THREE.Mesh.prototype.raycast = acceleratedRaycast;

        this.targetSubSystemMat = new THREE.MeshBasicMaterial({color: 0xAAAAAAAA});
        this.targetMat = new THREE.MeshBasicMaterial({color: 0xAAAAAAAA});

        this.sceneUI.add(this.cameraUI);
        this.lastCamAngle = null;
        this.camMode = 1;

        this.targetID = 1;

        this.splitMaterial = new THREE.MeshBasicMaterial({color: 0x888888, name:"splitmaterial"});
        this.CamUIratio1 = 0.84;
        this.CamUIratio2 = 0.55;
        this.CamUIratio3 = 0.35;

        this.mapCurveMat = new THREE.MeshBasicMaterial({color : 0xFFFFFF, depthTest: false, transparent: true});
        this.pathMaterial = new THREE.MeshBasicMaterial({color:0xFFFFFFFF, transparent: true, map: this.texLoader.load(this.assetBase+"/assets/textures/patharrow.png"), alphaMap: this.texLoader.load(this.assetBase+"/assets/textures/patharrowa.png")})

        this.pathMaterial.map.wrapS = THREE.RepeatWrapping;
        this.pathMaterial.map.wrapT = THREE.RepeatWrapping;
        this.pathMaterial.alphaMap.wrapS = THREE.RepeatWrapping;
        this.pathMaterial.alphaMap.wrapT = THREE.RepeatWrapping;

        
        this.cameraFOV = 30.0;

        this.renderMainScene = true;

        this.selectMat = new THREE.MeshBasicMaterial({color: '#FFF'});
        this.selectScene = new THREE.Scene();

        this.LeftAxis = false;
        this.RightAxis = false;
        
        this.XRPads = [];
        this.xrpadsState = [];

        this.digits = [];

        this.numFrames = 0;

        this.MTLLoader = new MTLLoaderP();

        /*
        switch (matType) {
            default:
            case 0:
                this.MTLLoader = new MTLLoader();
                break;
            case 1:
                this.MTLLoader = new MTLLoaderP();
                break;
        }
        */

        this.skidTex = this.texLoader.load(this.assetBase+"/assets/textures/skid.png");
        this.skidTex.wrapS = THREE.RepeatWrapping;
        this.skidTex.wrapT = THREE.RepeatWrapping;
        this.skidTex.repeat.set(1, 1);


        const neon = this.texLoader.load(this.assetBase+"/assets/textures/plateforme.png");
        neon.wrapS = THREE.RepeatWrapping;
        neon.wrapT = THREE.RepeatWrapping;
        neon.repeat.set(1, 1);
        neon.name = 'plateform';


        const targetTexture =  new THREE.TextureLoader().load('assets/img/target.png');
        targetTexture.name = 'targettexture';
        const targetMaterial = new THREE.MeshBasicMaterial({color: 0xAAAAAAA, depthWrite : true, depthTest : false, transparent : true, map : targetTexture});
        targetMaterial.name = 'target';

        const targetGeo = new THREE.PlaneGeometry(2,2);
        this.targetMesh = new THREE.Mesh(targetGeo, targetMaterial);
        this.targetMesh.name = 'target';

        this.platformMaterial = new THREE.MeshPhongMaterial({ color: 0xffFFFF });
        this.platformMaterial.map = neon;
        this.platformMaterial.emissiveMap = neon;
        this.platformMaterial.name = 'platform material';


        this.toonFlippedMat = new THREE.MeshBasicMaterial({color: 0x000000, side: THREE.FrontSide, polygonOffset: true, polygonOffsetFactor: -1.1});


        const lightningTexture = new THREE.TextureLoader().load(this.assetBase+'/assets/textures/nopower.png');
        this.LightningBoxGeom = new THREE.PlaneGeometry(1, 2, 1, 1);
        this.LightningBoxMat = new THREE.MeshBasicMaterial({color: 0xFFFFFF, map: lightningTexture, transparent: true});


        //this.arrowTex = this.texLoader.load("assets/textures/border/color_map.jpg");
        
        this.wallTex = this.texLoader.load(this.assetBase+"/assets/textures/border.png");
        this.wallTex.wrapS = THREE.RepeatWrapping;
        this.wallTex.wrapT = THREE.RepeatWrapping;
        this.wallTex.repeat.set(1, 40);

        this.wallRTex = this.texLoader.load(this.assetBase+"/assets/textures/border_r.png");
        this.wallRTex.wrapS = THREE.RepeatWrapping;
        this.wallRTex.wrapT = THREE.RepeatWrapping;
        this.wallRTex.repeat.set(1, 40);

        this.wallNTex = this.texLoader.load(this.assetBase+"/assets/textures/border_n.png");
        this.wallNTex.wrapS = THREE.RepeatWrapping;
        this.wallNTex.wrapT = THREE.RepeatWrapping;
        this.wallNTex.repeat.set(1, 40);

        /*
        this.arrowEmm = this.texLoader.load("assets/textures/wall1.png");
        this.arrowEmm.wrapS = THREE.RepeatWrapping;
        this.arrowEmm.wrapT = THREE.RepeatWrapping;
        this.arrowEmm.repeat.set(1, 20);
        */
        this.wallMat = new THREE.MeshPhysicalMaterial({ map: this.wallTex, roughnessMap:this.wallRTex, normalMap: this.wallNTex });
        

        this.camTex = [];
        this.camImg = ["assets/img/cam1.png", "assets/img/cam2.png", "assets/img/cam3.png", "assets/img/cam4.png"];
        
        this.camTex[0] = this.texLoader.load(this.assetBase+this.camImg[0]);
        this.camTex[1] = this.texLoader.load(this.assetBase+this.camImg[1]);
        this.camTex[2] = this.texLoader.load(this.assetBase+this.camImg[2]);
        this.camTex[3] = this.texLoader.load(this.assetBase+this.camImg[3]);



        this.woodTexture = this.texLoader.load(this.assetBase+"/assets/textures/wood.jpg");
        this.woodTexture.wrapS = THREE.RepeatWrapping;
        this.woodTexture.wrapT = THREE.RepeatWrapping;
        this.woodTexture.repeat.set(1, 1);

        this.woodNormal = this.texLoader.load(this.assetBase+"/assets/textures/wood_n.png");
        this.woodNormal.wrapS = THREE.RepeatWrapping;
        this.woodNormal.wrapT = THREE.RepeatWrapping;
        this.woodNormal.repeat.set(1, 1);

        this.material2 = new THREE.MeshPhongMaterial({ color: 0xffFFFF, map :this.woodTexture, normalMap : this.woodNormal});

        const metal = new THREE.TextureLoader().load(this.assetBase+"/assets/textures/metal.jpg");
        metal.wrapS = THREE.RepeatWrapping;
        metal.wrapT = THREE.RepeatWrapping;
        metal.repeat.set(1, 1);

        const metalnormals = new THREE.TextureLoader().load(this.assetBase+"/assets/textures/metal_n.png");
        metalnormals.wrapS = THREE.RepeatWrapping;
        metalnormals.wrapT = THREE.RepeatWrapping;
        metalnormals.repeat.set(1, 1);

        this.bonusMaterial = new THREE.MeshPhongMaterial({ color: 0xffFFFF });
        this.bonusMaterial.map = metal;
        this.bonusMaterial.normalMap = metalnormals;
        this.bonusMaterial.emissive = new THREE.Color().setRGB(0, 0.4, 0.4);

        this.SphereMaterial = new THREE.MeshBasicMaterial({ color: 0x0000ff });

        this.dropTarget = [];
        this.dropDist = [];
        this.dropMarks = [];

        this.RainMaterial = new THREE.MeshPhongMaterial({ color: 0x0000FF, emissive: 0x2222FF, emissiveIntensity: 8.0});

        this.rainColor = new THREE.Vector3(0.3,0.3,1);
        this.rainEmm = new THREE.Vector3(0.0,0.8,1.0);
        
        this.rainTex = new THREE.TextureLoader().load(this.assetBase+"/assets/textures/drop.png");
        const raina = new THREE.TextureLoader().load(this.assetBase+"/assets/textures/drop_a.png");
        this.RainDropMat = new THREE.PointsMaterial({ map: this.rainTex, alphaMap:raina, fog:false, color: 0xFFFFff, size:1, transparent:true });

        this.bitmapFont= new THREE.TextureLoader().load(this.assetBase+"/assets/textures/font.png");
        this.bitmapFontA= new THREE.TextureLoader().load(this.assetBase+"/assets/textures/font_a.png");
        this.bitmapFontL = new THREE.TextureLoader().load(this.assetBase+"/assets/textures/font2.png");

        this.steelLogo = new THREE.TextureLoader().load("assets/img/steel-logo.png");
        this.NitroIcon = new THREE.TextureLoader().load("assets/img/nitro-icon.png");
        this.timeIcon = new THREE.TextureLoader().load("assets/img/alarm-clock-icon.jpg");
        this.lapIcon = new THREE.TextureLoader().load("assets/img/lap.png");


        this.dmgMaterial = new THREE.MeshBasicMaterial({ color: 0x00CC00});
        this.dmgMaterial2 = new THREE.MeshBasicMaterial({ color: 0xCC0000});


        this.carNameMat = new THREE.MeshPhongMaterial({color: 0x00FF00, emissive: 0x00FF00});

        const gradArray = new Uint8Array([64,255]);
        this.gradientMap = new THREE.DataTexture( gradArray, gradArray.length, 1, THREE.RedFormat );
        this.gradientMap.minFilter = THREE.NearestFilter;
        this.gradientMap.magFilter = THREE.NearestFilter;
        this.gradientMap.needsUpdate = true;

        

        const self = this;
       
        this.tmpTransformation = new Transformation3();

        this.clock = new THREE.Clock(true);
        this.lastTime = this.clock.getElapsedTime();

        this.camFollowCar = true;

        this.materialInteractive = new THREE.MeshStandardMaterial({ color: 0xffFFFF });

        this.cameraAnchor = new THREE.PerspectiveCamera(0.1,1,0.1,100);

        this.audioSamples = {};

        this.pads = [];

        this.GAME_PAD_KEY_MAPPING = { 'PadTop': 12, 'PadBottom': 13, 'PadLeft': 14, 'PadRight': 15 };

        this.tclassNames = ['C', 'B', 'A'];
        this.tdiffNames = ['easy', 'medium', 'hard'];

        this.numSamples = 0;
        this.lastSample = new Date().getTime();
        
        this.AudioMusicPlaying = false;
        this.engineSound = [];

        this.hasAudio = audio;

        this.AudioMusic = null;

        this.audio = audio;

        this.bumpTex = null;
        this.bumpAtlas = null;

        this._xrInvPositionCache = new THREE.Vector3();
        this._xrInvQuaternionCache = new THREE.Quaternion();

        this.XRPosition = new THREE.Vector3();
        this.XRRotationQuaternion  = new THREE.Quaternion();

        this._referencedPosition = new THREE.Vector3();
        this._referenceQuaternion = new THREE.Quaternion();

        this.useRightHandedSystem = false;

        this.isVr = false;
        this.rainMesh = null;
        this.sensor = null;
        this.audioCtx =null;


        this.garageTxtMat = new THREE.MeshPhysicalMaterial({ color: '#44F', emissive: new THREE.Color().setRGB(0.2,0.4, 0.2), emissiveIntensity: 1.5, transparent: true, depthTest: false });
    }

    async loadParticles(){

        for(let p of particles){
            if(p.particleTextureUrl){
                await loadParticleTexture(this.assetBase + p.particleTextureUrl);
            }
        }
    }


    async createTrackMap(track) {

        const gltf = await this.loadGLTF('boardmap');
        track.mapBoard = this.deGroupGLB(gltf);

        track.mapCanvas = document.createElement('canvas');
        track.mapCanvas.width = 256;
        track.mapCanvas.height = 512;

        track.mapCanvasTex = this.createTextureCanvas(track.mapCanvas, false);

        track.mapCanvasColor = '224422';

        const newMat = new THREE.MeshBasicMaterial({color: 0xFFFFFF});
        newMat.map = track.mapCanvasTex;

        let geocenter = null;
        const self = this;

        track.mapBoard.traverse(function(c){
            if(!c.isMesh)
                return;

            for(let n = 0;n<c.material.length;n++){

                const m  = c.material[n];
                if(m.name.startsWith('screen')){
                    c.material[n] = newMat;
                }

                if(m.name.startsWith('map')){
                    const cc = self.getGeoGrpCenter(c.geometry, n);
                    geocenter = cc.c;
                }
            }
        })

        const mat = new THREE.MeshBasicMaterial({color : 0xFFFFFF, depthTest: false, transparent: true});

        track.mapPts = new Array(track.curveC.points.length);

        const sx = 0.22 / (track.ptsMax[0] - track.ptsMin[0]) ;
        const sz = 0.22 / (track.ptsMax[2] - track.ptsMin[2]) ;

        const s = Math.min(sx, sz);
                
        for(let n=0;n<track.curveC.points.length;n++){
            track.mapPts[n] = new THREE.Vector3(track.curveC.points[n].x * s / track.gscale, track.curveC.points[n].z *s / track.gscale, 0);
        }

        track.mapCurve = new THREE.CatmullRomCurve3(track.mapPts, true, "catmullrom", 1.0)
        const tubeGeometry1 = new THREE.TubeGeometry(track.mapCurve, track.curveC.points.length, 0.01, 4, true );

        track.miniMap = new THREE.Mesh(tubeGeometry1, mat );
        
        

        track.boardMapPos  =  {x: 0.33 , y: 0.2, z: -1};
        track.miniMapPos  =  {x: track.boardMapPos.x + geocenter.x , y: track.boardMapPos.y + geocenter.y, z: -1.001};
    }

    addTrackMap(world) {

        const track = world.track;


        track.mapBoard.meshGPU = this.addCameraSpaceMesh(track.mapBoard, track.boardMapPos);
        track.miniMap.meshGPU = this.addCameraSpaceMesh(track.miniMap, track.miniMapPos);

        track.mapBoard.renderOrder = 200;
        track.miniMap.renderOrder = 201;

        //this.addMeshChild(track.mapBoard, track.miniMap);

        const geo = new THREE.SphereGeometry(0.01, 4, 4);

        for(let n =0 ; n< world.cars.length;n++) {

            const car = world.cars[n];

            const m = new THREE.MeshBasicMaterial({color : carColors[n], depthTest: false, transparent: true});
            car.mapSphere = new THREE.Mesh(geo, m);
            car.mapSphere.name = 'car' +n+' map sphere';

            //car.mapSpherePos = {x:track.miniMapPos.x, y:track.miniMapPos.y, z : track.miniMapPos.z -0.0001};
            car.mapSpherePos = {x:0, y:0, z : -0.0001};
            car.mapSphere.meshGPU = this.addCameraSpaceMesh(car.mapSphere, car.mapSpherePos, false);
            this.addMeshChild(track.miniMap, car.mapSphere);
            car.mapSphere.renderOrder = 202;
        }
        

    }

    
    setBumpSize(w,h,c){

        if(this.bumpAtlas){

            const nTilesX = this.bumpTexWidth / this.bumptileW ;
            const nTilesY = this.bumpTexHeight / this.bumptileH;

            for(let y=0;y<nTilesY;y++){
    
                for(let x=0;x<nTilesX;x++){

                    if(this.bumpAtlas[y][x].tex)
                        this.destroyTex(this.bumpAtlas[y][x].tex);
                }
            }

            this.bumpAtlas= null;
        }


        this.bumpTexHeight = h;
        this.bumpTexWidth = w;
        this.driftSize = c;

        if(this.bumpTex){
            this.destroyTex(this.bumpTex);
            this.bumpTex = null;
        }

      
        if((this.enableParallax !== true)&&(this.roadMaT)){

            this.roadMaT.driftMap = null;
            return;
        }
            
        /*
        this.roadMapArray = new Uint8Array(this.bumpTexWidth * this.bumpTexHeight);
        this.roadMapArray.fill(0);
        */
        this.bumpIMG = new Image(this.bumpTexWidth, this.bumpTexHeight);
        this.bumpTex = this.createGPUTextureFromImge(this.bumpIMG, true);
        /*
        new THREE.Texture(this.bumpIMG, THREE.Texture.DEFAULT_MAPPING,
            THREE.RepeatWrapping,
            THREE.RepeatWrapping,
            THREE.LinearFilter,
            THREE.LinearFilter,
            THREE.RedFormat,
            THREE.UnsignedByteType,
            1,
            THREE.NoColorSpace);
        */

        this.bumpTex.flipY = false;
        this.bumpTex.generateMipmaps  = false;
        this.bumpTex.normalized = true;
        this.bumpTex.normalize = true;
        this.bumpTex.needsUpdate = true;

        this.bumptileW = 128;
        this.bumptileH = 128;

        const nTilesX = this.bumpTexWidth / this.bumptileW ;
        const nTilesY = this.bumpTexHeight / this.bumptileH;

        this.bumpAtlas = new Array(nTilesY);

        for(let y=0;y<nTilesY;y++){

            this.bumpAtlas[y] = new Array(nTilesX);

            for(let x=0;x<nTilesX;x++){

                const newTexData = new Uint8Array(this.bumptileW * this.bumptileH);
                newTexData.fill(0);

                
                const newTex = this.device? null : this.createGPUDataTexture(this.bumptileW, this.bumptileH, newTexData)
                /*
                const newTex = new DataTexture(newTexData, this.bumptileW, this.bumptileH,
                THREE.RedFormat,
                THREE.UnsignedByteType,
                THREE.Texture.DEFAULT_MAPPING,
                THREE.RepeatWrapping,
                THREE.RepeatWrapping,
                THREE.LinearFilter,
                THREE.LinearFilter,
                1,
                THREE.NoColorSpace);
                newTex.flipY = false;
                newTex.generateMipmaps  = false;
                */

                this.bumpAtlas[y][x] = {tex : newTex, data: newTexData};

                if(newTex)
                    this.copyTexToTex({x:x * this.bumptileW, y : y * this.bumptileH}, newTex, this.bumpTex);
                
                this.writeToTex({x:x * this.bumptileW, y : y * this.bumptileH}, newTexData, this.bumpTex);
            }
        }

        if(this.roadMaT){
            this.roadMaT.driftMap = this.bumpTex;
            this.roadMaT.driftMapScale = new THREE.Vector2(1, 1);
            this.roadMaT.needsUpdate = true;
        }

    }


    drawCircle(x, y, dep){
        const circleSize = this.driftSize;
        const circlehalf = circleSize / 2.0;
        const xScale = this.bumpTexWidth / this.bumpTexHeight;

        x = parseInt(x * this.bumpTexWidth);
        y = parseInt(y * this.bumpTexHeight);

        const nTilesX = Math.floor(this.bumpTexWidth / this.bumptileW) ;
        const nTilesY = Math.floor(this.bumpTexHeight / this.bumptileH);

        const updates = [];
        
        for(let yy=-circlehalf * xScale;yy<circlehalf * xScale;yy++){
            for(let xx=-circlehalf;xx<circlehalf;xx++){
                let tx = x + xx;
                let ty = y + yy;

                if((tx<0)||(tx>=this.bumpTexWidth))
                    continue;

                if(ty<0)
                    ty += this.bumpTexHeight;

                if(ty>=this.bumpTexHeight)
                    ty -= this.bumpTexHeight;
            
                const p = Math.sqrt(xx*xx + yy*yy);
                if(p>circlehalf)
                    continue;

                const ip =  1.0-p / circlehalf;

                const atx = parseInt(tx / this.bumptileW);
                const aty = parseInt(ty / this.bumptileH);

                const ax = tx - atx * this.bumptileW;
                const ay = ty - aty * this.bumptileH;

                updates[aty * nTilesX + atx] = {x:atx, y: aty};

                const bumpA = this.bumpAtlas[aty][atx].data;

                const op = bumpA[(ay * this.bumptileW + ax) + 0];
                bumpA[(ay * this.bumptileW + ax) + 0] = Math.min(255, Math.max(0.0, op + Math.pow(ip, 1.2 ) * dep));
            }
        }

        for(let uk in updates){
            const u = updates[uk];
            const ba = this.bumpAtlas[u.y][u.x].tex;
            const data = this.bumpAtlas[u.y][u.x].data;

            if(ba){
                this.writeToTex({x:0,y:0}, data, ba);
                this.copyTexToTex({x: u.x * this.bumptileW, y : u.y * this.bumptileH}, ba, this.bumpTex)
            }else
                this.writeToTex({x: u.x * this.bumptileW, y : u.y * this.bumptileH}, data, this.bumpTex);
            


            
        }


    }
    initSensor(relative){
        
        const options = { frequency: 60, referenceFrame: 'device' };
        console.log('initSensor : ' + JSON.stringify(options));
        try{
            this.sensor = relative ? new RelativeOrientationSensor(options) : new AbsoluteOrientationSensor(options);
        }catch(e){
            console.log('no sensor '+e.message);
        }
        
    }

    startAccel() {

        if (navigator.permissions) {
            // https://w3c.github.io/orientation-sensor/#model
            Promise.all([navigator.permissions.query({ name: "gyroscope" })])
                .then(results => {
                    if (results.every(result => result.state === "granted")) {
                        this.initSensor(true);
                    } else {
                        console.log("Permission to use sensor was denied.");
                        console.log(results);
                    }
                }).catch(err => {
                    console.log("Integration with Permissions API is not enabled, still try to start app.");
                    this.initSensor(true);
                });
        } else {
            console.log("Permissions API, still try to start app.");
            this.initSensor(true);
        }
    }

    resetDrop(drops, n, roadObj, pos){

        /*
        const s  = new THREE.Vector3();
        roadObj.geometry.boundingBox.getSize(s);

        s.x = 1000;
        s.z = 1000;
        */

        this.dropDist[n] = null;

        //while(this.dropDist[n] === null){
        this.dropR.set(pos.x + (Math.random() - 0.5) * 500, Math.random() * 100, pos.z + (Math.random() - 0.5) * 500); 
        this.dropO.set(this.dropR.x, this.dropR.y + 100, this.dropR.z);
        this.dropRc.set(this.dropO, this.dropD);
        this.dropTarget.length=0;
        this.dropRc.intersectObjects(roadObj, false, this.dropTarget);
        if(this.dropTarget.length >0)
            this.dropDist[n] = this.dropTarget[0].point;

        //}
        
        drops[n*3+0] = this.dropR.x;
        drops[n*3+1] = this.dropR.y;
        drops[n*3+2] = this.dropR.z;

        /*
        drops[n*6+3] = this.dropR.x;
        drops[n*6+4] = this.dropR.y-1.0;
        drops[n*6+5] = this.dropR.z;
        */
    }

    updateRain(time, dt, roadObj){

        if(!this.rainMesh)
            return;
        
        for(let n=0;n<this.numDrops;n++){

            this.rainMesh.geometry.attributes.position.array[n*3+1] -= 40.0 * dt;
            //this.rainMesh.geometry.attributes.position.array[n*3+4] -= 40.0 * dt;

            const ry =this.rainMesh.geometry.attributes.position.array[n*3+1];

            if(this.dropDist[n]){

                if(ry<this.dropDist[n].y){

                    for(let d=0;d<8;d++){
                        /*
                        const sm = new THREE.Mesh(this.sphereGeom, this.RainMaterial);
                        sm.position.set(this.dropDist[n].x, this.dropDist[n].y, this.dropDist[n].z);
                        sm.scale.set(0.05,0.05, 0.05);
                        this.scene.add(sm);
                        */
                        const sm = {};
                        this.addSphere(sm, 0.05, this.dropDist[n], this.rainEmm, this.rainColor, 1.0, RT_DIFF);

                        sm.object.name='drop sphere';
                        sm.object.material.emissiveIntensity = 4.0;
                        sm.object.frustumCulled = false;

                        const a = d * Math.PI * 2.0 / 8.0 + Math.random();
                        const dx = Math.cos(a);
                        const dy = Math.sin(a);

                        this.setMeshStatic(sm, false);
    
                        this.dropMarks.push({mesh : sm, y:this.dropDist[n].y, dx : dx, dy : dy, time : time});
                    }

                    this.resetDrop(this.rainMesh.geometry.attributes.position.array, n,  roadObj);
                }

            }else if(ry<-10){
                this.resetDrop(this.rainMesh.geometry.attributes.position.array, n,  roadObj);
            }
        }

        let n=0;
        while(n<this.dropMarks.length){
            const ddt = time - this.dropMarks[n].time;
            if(ddt>0.5){

                this.removeSphere(this.dropMarks[n].mesh, false);
                //this.scene.remove(this.dropMarks[n].mesh);
                this.dropMarks.splice(n,1);
            }else{
                this.setSpherePosition(this.dropMarks[n].mesh, null, this.dropMarks[n].y + (Math.sin(ddt * 2.0 * Math.PI)+1.0) * 0.5, null, 0.05);
                this.moveSpherePosition(this.dropMarks[n].mesh, this.dropMarks[n].dx *dt, null, this.dropMarks[n].dy *dt, 0.05);

               
                
                /*
                this.dropMarks[n].mesh.position.x +=this.dropMarks[n].dx *dt; 
                this.dropMarks[n].mesh.position.y = ;
                this.dropMarks[n].mesh.position.z +=this.dropMarks[n].dy *dt;
                */

                n++;
            }
        }
        this.rainMesh.geometry.attributes.position.needsUpdate = true;
    }

    createRain(numDrops, roadObj, pos = null){
        const drops = new Float32Array(numDrops * 3);

        this.dropR = new THREE.Vector3();
        this.dropRc = new THREE.Raycaster();
        this.dropO = new THREE.Vector3();
        this.dropD = new THREE.Vector3(0,-1,0);

        for(let n=0; n<numDrops; n++){
            this.resetDrop(drops, n,  roadObj, pos);
        }

        this.numDrops = numDrops;
        const rainGeo = new THREE.BufferGeometry();
        rainGeo.setAttribute('position', new THREE.Float32BufferAttribute(drops, 3));
        this.rainMesh = new THREE.Points(rainGeo, this.RainDropMat);
        this.rainMesh.frustumCulled = false;


        
    }

    
    updatePrimitives(mesh, geo){}
    resetRain(){}
    enableTrackRT(){}
    setShadowBias (bias){}
    clearCachedCyls(){}
    removeAllParticles(){}
    addDirLight(){}
    addFog(){}
    updateSprite(sprite) { }
    addSphere(c, radius, position, emission, color, roughness, type) { }
    setSpherePosition(c, x, y, z, r) { }
    setSphereColor(c, r, g, b) { }
    addBox(c, size, position, color, roughness, type) { }
    removeSphere(c) { }
    removeBox(c) { }
    removeMesh(m) { }
    updateTransform(m, t, index = 0) {}
    setShadowSource(position, target) {}
    setShadowProj(x,y) {}
    initPostProcessing(p) {};
    updateBoneTransform(m, boneName, q) {}
    addFirework(pos){}
    addCyber(pos){}
    setMeshStatic(mesh, st){}
    initEngineUI(target){}
    setBloomParams(a,b,c){}
    addRain(numDrops){}
    addMesh(m, matType, transformation, flip, useTex, skeleton, instanceID) { }
    enableRender(on, glDomRef) {}
    copyTexToTex(pos, src, dst) {}
    initScene() { }
    resetTex(){}
    

    updateNPCSubSystem(bone, skel){

        if(bone.subsystem){

            const tbone = skel.getBoneByName(bone.name)

            const boneX = tbone.matrixWorld ? tbone.matrixWorld.elements[12] : tbone.bonePos.x;
            const boneY = tbone.matrixWorld ? tbone.matrixWorld.elements[13] : tbone.bonePos.y;
            const boneZ = tbone.matrixWorld ? tbone.matrixWorld.elements[14] : tbone.bonePos.z;

            bone.subsystem.pos.set(boneX, boneY, boneZ);
            
            this.updateDMGBar(bone.dmgBar, bone.subsystem.pos,  bone.life);

            /*
            bone.dmgBar.DMGBar1.position.set(boneX, boneY + 2, boneZ);
            bone.dmgBar.DMGBar2.position.set(boneX, boneY + 2, boneZ);

            bone.dmgBar.DMGBar1.lookAt(this.cameraAnchor.position);
            bone.dmgBar.DMGBar2.lookAt(this.cameraAnchor.position);

            this.tmpTransformation.set(bone.dmgBar.DMGBar1.position, bone.dmgBar.DMGBar1.quaternion);
            this.updateTransform(bone.dmgBar.DMGBar1, this.tmpTransformation)

            this.tmpTransformation.set(bone.DMGBar2.position, bone.DMGBar2.quaternion);
            this.updateTransform(bone.DMGBar2, this.tmpTransformation)

            bone.dmgBar.DMGBar1.scale.set(bone.life / bone.total_life , 1, 1 );
            */
        }

        for(let c of bone.children){
            this.updateNPCSubSystem(c, skel);
        }
        return;
    }

    async init(){
        const mobSTR = localStorage.getItem('MobileUI');
        this.useMobileUI = mobSTR !== null ? parseInt(mobSTR) >0 : true;

        const st = localStorage.getItem('hdSkybox');
        this.hdSkybox = st !== null? parseInt(st)>0 : false;

        const enableParallax = localStorage.getItem('parallax');
        if (enableParallax === null)
            this.enableParallax = false;
        else {
            this.enableParallax = parseInt(enableParallax) === 1 ? true : false;
        }
      
        this.panelTex = await  new THREE.TextureLoader().loadAsync(this.assetBase+"/assets/textures/panel.png");
        this.speedFont = await new FontLoader().loadAsync(this.assetBase+'/assets/fonts/DS-Digital_Bold.json');
        this.impactFont = await new FontLoader().loadAsync(this.assetBase+'/assets/fonts/Impact_Regular.json');
        this.armourFont = await new FontLoader().loadAsync(this.assetBase+'/assets/fonts/Futuristic Armour_Regular.json');

        this.blueNoiseTex = await new THREE.TextureLoader().loadAsync(this.assetBase+'/assets/textures/BlueNoise_RGBA256.png');
        this.blueNoiseTex.wrapS = THREE.RepeatWrapping;
        this.blueNoiseTex.wrapT = THREE.RepeatWrapping;
        this.blueNoiseTex.minFilter = THREE.LinearFilter;

        this.dialogFont =   { font: this.impactFont, depth : 30, size: 0.5, height: 0.01, curveSegments: 4};
        
        const nextLinetxtSize = new THREE.Vector3();
        this.nextLinetxtGeo = new TextGeometry("next line", this.dialogFont)
        this.nextLinetxtGeo.computeBoundingBox();
        this.nextLinetxtGeo.boundingBox.getSize(nextLinetxtSize);
        this.nextLinetxtGeo.translate(- nextLinetxtSize.x/2, 0, 0);

        this.nextLineMaterial = new THREE.MeshBasicMaterial({color:0x888888, side : THREE.DoubleSide, map: null, opacity:1.0, transparent:false, depthTest: false, depthWrite: false, toneMapped: false})
        this.nextLinetxtMesh = new THREE.Mesh(this.nextLinetxtGeo, this.nextLineMaterial);

        if (this.audio) {
            var sfxVolume = localStorage.getItem('sfxVolume');
            var musicVolume = localStorage.getItem('musicVolume');
            var engineVolume = localStorage.getItem('engineVolume');

            if (sfxVolume !== null) {
                SoundCfg.sfxVolume = sfxVolume;
            }
            if (musicVolume !== null) {
                SoundCfg.musicVolume = musicVolume;
            }
            if (engineVolume !== null) {
                SoundCfg.engineVolume = engineVolume;
            }

            this.AudioListener = new THREE.AudioListener();
        }

        await this.loadParticles();
        const gltf = await this.loadGLTF('dialogback');
        this.dialogBack = this.deGroupGLB(gltf);
        this.destroyObj(gltf);

    }

    initControllers(){
        const self = this;

        window.addEventListener('gamepadconnected', (e) => self.handleGamePadConnected(e));
        window.addEventListener('gamepaddisconnected', (e) => self.handleGamePadDisconnected(e));

    }
    
    getPad(index) {

        return this.pads.find(p => p.index == index);
    }

    handleGamePadDisconnected(e) {
        this.removePad(e.gamepad.id);
        //$('#pad').attr('src', 'assets/img/gamepad-icon-off.png');
    }

    addGamePad(gamepad){
        const pad = {
            index: gamepad.index,
            id: gamepad.id,
            nButtons: gamepad.buttons.length,
            nAxes: gamepad.axes.length,
            pressed: [],
            axes: [],
        };

        for (let i = 0; i < gamepad.buttons.length; i++) {
            pad.pressed[i] = gamepad.buttons[i].pressed;
        }

        for (let i = 0; i < gamepad.axes.length; i++) {
            pad.axes[i] = gamepad.axes[i];
        }
        return pad;
    }

    hasVrPad(index){
        if(this.XRPads[index])
            return true;

        return false;
    }

    addVrGamepad(index, gamepad){
        
        this.XRPads[index] = gamepad;

        const pad = this.addGamePad(gamepad);
        pad.index = index;
        this.xrpadsState[index] = pad;
    }

    handleGamePadConnected(e) {

        const pad = this.addGamePad(e.gamepad);
        this.pads.push(pad);
    }

    updatePadsStatus() {
        const navigator = window.navigator;
        const gamepads = navigator.getGamepads ? navigator.getGamepads() : (navigator.webkitGetGamepads ? navigator.webkitGetGamepads() : []);

        var events = [];

        for (const gamepad of gamepads) {
            if (!gamepad) {
                continue;
            }

            const pad = this.getPad(gamepad.index);
            if (pad != null) {
                for (let n = 0; n < gamepad.buttons.length; n++) {
                    if (gamepad.buttons[n].pressed == pad.pressed[n]) {
                        continue;
                    }
                    pad.pressed[n] = gamepad.buttons[n].pressed;
                    events.push({ idx: pad.index, btn: n , xr: false});
                }

                for (let n = 0; n < gamepad.axes.length; n++) {
                    if (Math.abs(gamepad.axes[n] - pad.axes[n]) < 0.1) {
                        continue;
                    }
                    pad.axes[n] = gamepad.axes[n];
                    events.push({ idx: pad.index, axis: n , xr: false});
                }
            }
        }

        for (let n =0; n< this.XRPads.length; n++) {

            if (!this.XRPads[n]) {
                continue;
            }

            const gamepad = this.XRPads[n];
            const pad = this.xrpadsState[n];

            if (pad != null) {
                for (let n = 0; n < gamepad.buttons.length; n++) {
                    if (gamepad.buttons[n].pressed == pad.pressed[n]) {
                        continue;
                    }
                    pad.pressed[n] = gamepad.buttons[n].pressed;
                    events.push({ idx: pad.index, btn: n, xr: true });
                }

                for (let n = 0; n < gamepad.axes.length; n++) {
                    if (Math.abs(gamepad.axes[n] - pad.axes[n]) < 0.1) {
                        continue;
                    }
                    pad.axes[n] = gamepad.axes[n];
                    events.push({ idx: pad.index, axis: n , xr: true});
                }
            }
        }
        return events;
    }

    



    async loadSkeleton(name){

        const url = this.assetBase+'/assets/skels/'+name+'.json';
        const res = await fetch(url);
        const data = await res.json();

        const skel = new SimpleSkeleton(data.pos, data.quat, data.exclude, data.matExclude);
        
        for(let c of data.children) {
            const newBone = new SimpleBone(c.name, c.quat, c.length, c.radius, c.jradius);
            newBone.loadChildren(c.children);
            skel.root.addChild(newBone);
        }

        //console.log(skel);

        return skel;
    }

    setCamFov(fov) {
        this.cameraFOV = fov;
        this.camera.fov = this.cameraFOV;
        this.camera.updateProjectionMatrix();
    }

    removeGeoGrp(m, ids) {

        const geo = m.geometry;

        if(!m.material.length)
            return geo;
        
        let cntDel =0;
        let delMats = [];

        for(let g = 0; g < geo.groups.length; g++){

            const grp = geo.groups[g];

            for(let i of ids){

                if(m.material[grp.materialIndex].name.startsWith(i)){
                    cntDel += grp.count;
                    delMats.push(grp.materialIndex);
                    break;
                }
            }
        }

        if(delMats.length<=0)
            return;

        const newGeo = new THREE.BufferGeometry();
        for (let a in geo.attributes) {

            let buff = (a=='skinIndex') ? new Uint16Array((geo.attributes[a].count - cntDel )* geo.attributes[a].itemSize) : new Float32Array((geo.attributes[a].count - cntDel) * geo.attributes[a].itemSize);
            newGeo.setAttribute( a, new THREE.BufferAttribute( buff, geo.attributes[a].itemSize , geo.attributes[a].normalized) );
        }

        let curV = 0;
        let matIdx = 0;

        for(let g =0;g<geo.groups.length; g++){
            const grp=geo.groups[g];

            if(delMats.indexOf(grp.materialIndex)>=0)
                continue;

            newGeo.addGroup(curV, grp.count , matIdx);

            for (let v = 0; v < grp.count; v++) {

                for (let a in geo.attributes) {

                    const is = geo.attributes[a].itemSize;
                    for(let s=0;s<is;s++){
                        newGeo.attributes[a].array[curV * is + s] = geo.attributes[a].array[(grp.start + v) * is + s];
                    }
                }
                curV++;
            }
            matIdx++;
        }
        
        for (let a in geo.attributes) {
            newGeo.attributes[a].needsUpdate = true;
        }

        const newMats = [];

        for(let mat=0; mat<m.material.length; mat++){

            if(delMats.indexOf(mat)<0){
                newMats.push(m.material[mat]);
            }
        }

        m.geometry = newGeo;
        m.material = newMats;

    }

    getGeoGrpCenter(geo, n)
    {
        const grp = geo.groups[n];

        let minX = null;
        let minY = null;
        let minZ = null;
                        
        let maxX = null;
        let maxY = null;
        let maxZ = null;
        
        

        for (let v = 0; v < grp.count; v++) {
            let iv = geo.index ? geo.index.array[grp.start + v] : grp.start + v;

            const x = geo.attributes.position.array[iv * 3 + 0];
            const y = geo.attributes.position.array[iv * 3 + 1];
            const z = geo.attributes.position.array[iv * 3 + 2];

            if(minX === null)
                minX = x;
            else
                minX = Math.min(minX, x);

            if(maxX === null)
                maxX = x;
            else
                maxX = Math.max(maxX, x);

            if(minY === null)
                minY = y;
            else
                minY = Math.min(minY, y);

            if(maxY === null)
                maxY = y;
            else   
                maxY = Math.max(maxY, y);

            if(minZ === null)
                minZ = z;
            else   
                minZ = Math.min(minZ, z);

            if(maxZ === null)
                maxZ = z;
            else   
                maxZ = Math.max(maxZ, z);
        }

        const s = new THREE.Vector3((maxX-minX), (maxY-minY), (maxZ-minZ));
        const hs = s.clone().multiplyScalar(0.5);
        const c =  new THREE.Vector3(minX + hs.x, minY + hs.y, minZ + hs.z);
        let r = 0;

        
        for (let v = 0; v < grp.count; v++) {

            let iv = geo.index ? geo.index.array[grp.start + v] : grp.start + v;

            const vec = new THREE.Vector3(  geo.attributes.position.array[iv * 3 + 0], 
                                            geo.attributes.position.array[iv * 3 + 1], 
                                            geo.attributes.position.array[iv * 3 + 2]);
            const d = vec.distanceTo(c);
            r = Math.max(r, d);
        }

        let dir = 0;
        
        if(s.x > s.y){

            if(s.x > s.z)
                dir = 0;
            else
                dir = 2;
        } else {
            if(s.y > s.z)
                dir = 1;
            else
                dir = 2;
        }

        return {c:c, r: r, s:s, hs:hs, d : dir};
    }

    addBoneInfluenceSphere(geo, flip, bone, boneid, idx)
    {
        const sx = flip.x ? -1 : 1;
        const sy = flip.y ? -1 : 1;
        const sz = flip.z ? -1 : 1;

        const det = sx * sy * sz;
        const ids =(det > 0) ? [0,1,2]:[2,1,0];

         const nTriangles =  geo.attributes.position.count / 3;

         if(!geo.attributes.skinIndex){
            const indexes = new Uint16Array(nTriangles * 3 * 4);
            geo.setAttribute( 'skinIndex', new THREE.Uint16BufferAttribute(indexes, 4 ) );
        }

        if(!geo.attributes.skinWeight){

            const weights = new Float32Array(nTriangles * 3 * 4);
            for(let t=0; t< weights.length ; t+=4) {
                weights[t + 0] = 1.0;
                weights[t + 1] = 0.0;
                weights[t + 2] = 0.0;
                weights[t + 3] = 0.0;
            }

            geo.setAttribute( 'skinWeight', new THREE.Float32BufferAttribute(weights, 4 ) );
        }

        for(let t=0; t< nTriangles * 3; t+=3) {
            
            for(let i =0; i<3; i++) {

                const x = geo.attributes.position.array[(t + ids[i]) * 3 + 0];
                const y = geo.attributes.position.array[(t + ids[i]) * 3 + 1];
                const z = geo.attributes.position.array[(t + ids[i]) * 3 + 2];

                const dvec = new THREE.Vector3(x, y, z).sub(bone.center);
                const dist = dvec.length();

                if(dist > bone.radius)
                    continue;

                const myw = 1.0 - (dist / bone.radius);
                const rw = 1.0 - myw;

                geo.attributes.skinIndex.array[(t + ids[i]) * 4 + idx] = boneid;

                for(let ii =0;ii<4;ii++) {
                    if(ii === idx)
                        geo.attributes.skinWeight.array[(t + ids[i]) * 4 + ii] = myw;
                    else
                        geo.attributes.skinWeight.array[(t + ids[i]) * 4 + ii] *= rw;
                }
            }
        }
        
    }

    addBoneInfluenceCube(geo, flip, bone, boneid, idx)
    {
        const sx = flip.x ? -1 : 1;
        const sy = flip.y ? -1 : 1;
        const sz = flip.z ? -1 : 1;

        const det = sx * sy * sz;
        const ids =(det > 0) ? [0,1,2]:[2,1,0];

         const nTriangles =  geo.attributes.position.count / 3;

         if(!geo.attributes.skinIndex){
            const indexes = new Uint16Array(nTriangles * 3 * 4);
            geo.setAttribute( 'skinIndex', new THREE.Uint16BufferAttribute(indexes, 4 ) );
        }

        if(!geo.attributes.skinWeight){

            const weights = new Float32Array(nTriangles * 3 * 4);
            for(let t=0; t< weights.length ; t+=4) {
                weights[t + 0] = 1.0;
            }

            geo.setAttribute( 'skinWeight', new THREE.Float32BufferAttribute(weights, 4 ) );
        }

        for(let t=0; t< nTriangles * 3; t+=3) {
            
            for(let i =0; i<3; i++) {

                const x = geo.attributes.position.array[(t + ids[i]) * 3 + 0];
                const y = geo.attributes.position.array[(t + ids[i]) * 3 + 1];
                const z = geo.attributes.position.array[(t + ids[i]) * 3 + 2];

                const dvec = new THREE.Vector3(x, y, z).sub(bone.center).divide(bone.halfSize);
                
                if(Math.abs(dvec.x) > 1.0)
                    continue;

                if(Math.abs(dvec.y) > 1.0)
                    continue;

                if(Math.abs(dvec.z) > 1.0)
                    continue;

                let dist;

                switch(bone.dir){
                    case 0: dist = Math.abs(dvec.x);break;
                    case 1: dist = Math.abs(dvec.y);break;
                    case 2: dist = Math.abs(dvec.z);break;
                }
                
                const myw = 1.0 - dist;
                const rw = 1.0 - myw;

                geo.attributes.skinIndex.array[(t + ids[i]) * 4 + idx] = boneid;

                for(let ii =0;ii<4;ii++) {
                    if(ii === idx)
                        geo.attributes.skinWeight.array[(t + ids[i]) * 4 + ii] = myw;
                    else
                        geo.attributes.skinWeight.array[(t + ids[i]) * 4 + ii] *= rw;
                }
            }
        }
    }

    updateUICamera(cam) {

        const texId = this.camMode - 1;
        cam.cam.material.map = this.camTex[texId];
        cam.cam.material.needsUpdate = true;
    }



    makeDamageController() { 

       

        const DMG = new THREE.Sprite(this.dmgMaterial);
        DMG.scale.set(10, 80, 1)
        DMG.position.set(80, 0, 1);

        
        const DMG2 = new THREE.Sprite(this.dmgMaterial2);
        DMG2.scale.set(10, 80, 1)
        DMG2.position.set(80, 0, 1);

        return {dmg1: DMG, dmg2: DMG2};
    }

    
    makeEnergyController() { 

       
        const energyMaterial = new THREE.SpriteMaterial({ color: 0x0000FF, rotation: 0, depthTest: false });
        const energyMaterial2 = new THREE.SpriteMaterial({ color: 0x0000088, rotation: 0 , depthTest: false});

        const Energy = new THREE.Sprite(energyMaterial);
        Energy.scale.set(10, 80, 1)
        Energy.position.set(65, 0, 1);
        
        const Energy2 = new THREE.Sprite(energyMaterial2);
        Energy2.scale.set(10, 80, 1)
        Energy2.position.set(65, 0, 1);

        return {energy1: Energy, energy2: Energy2};
    }


    makeDialogController() { 
       
        const dlgMaterial = new THREE.SpriteMaterial({ color: 0x000000, opacity : 0.66, rotation: 0, depthTest: true });

        const DLG = new THREE.Sprite(dlgMaterial);
        DLG.scale.set(180, 180, 1)
        DLG.position.set(0, 0, 0);

        return {dlg: DLG};
    }


    mat4toTransform(mat4){

        const pos = new THREE.Vector3();
        const scale = new THREE.Vector3();
        const quat = new THREE.Quaternion();

        mat4.decompose(pos, quat, scale);
        return new Transformation3(pos, quat);

    }

    async makeRPMController() {

        const RPMTexture = await this.texLoader.loadAsync(this.assetBase+"/assets/textures/engine.png");
        const RpmMaterial = new THREE.SpriteMaterial({ map: RPMTexture, color: 0xffffff, rotation: 0, depthTest: false });

        const RPMTexture2 = await this.texLoader.loadAsync(this.assetBase+"/assets/textures/engine2.png");
        const RpmMaterial2 = new THREE.SpriteMaterial({ map: RPMTexture2, color: 0xffffff, rotation: 0, depthTest: false });

        const RPM = new THREE.Sprite(RpmMaterial);
        RPM.scale.set(80, 20, 1)
        RPM.position.set(-60, -65, 1);

        const RPM2 = new THREE.Sprite(RpmMaterial2);
        RPM2.scale.set(80, 20, 1)
        RPM2.position.set(-60, -65, 1);

        //curEngine.sceneUI.add(UIItems.RPM);
        //curEngine.sceneUI.add(UIItems.RPM2);  
        return {rpm1: RPM, rpm2: RPM2};
    }

    addRpmController(c){
        this.sceneUI.add(c.rpm1);
        this.sceneUI.add(c.rpm2);
    }
    
    removeRpmController(c){
        this.sceneUI.remove(c.rpm1);
        this.sceneUI.remove(c.rpm2);
    }

    async makeNitroController(){
        
        const NitroTexture1 =  await this.texLoader.loadAsync(this.assetBase+"/assets/textures/nitro_ui.png");
        const NitroMaterial1 = new THREE.SpriteMaterial({ map: NitroTexture1, color: 0xffffff, rotation: 0, depthTest: false });

        const NitroTexture2 =  await this.texLoader.loadAsync(this.assetBase+"/assets/textures/nitro_ui2.png");
        const NitroMaterial2 = new THREE.SpriteMaterial({ map: NitroTexture2, color: 0xffffff, rotation: 0, depthTest: false });

        const NitroTexture3 =  await this.texLoader.loadAsync(this.assetBase+"/assets/textures/nitro_ui3.png");
        const NitroMaterial3 = new THREE.SpriteMaterial({ map: NitroTexture3, color: 0xffffff, rotation: 0, depthTest: false });

        const Nitro1 = new THREE.Sprite(NitroMaterial1);
        Nitro1.scale.set(80, 20, 1)
        Nitro1.position.set(-60, -85, 1);

        const Nitro2 = new THREE.Sprite(NitroMaterial2);
        Nitro2.scale.set(80, 20, 1)
        Nitro2.position.set(-60, -85, 1);

        const Nitro3 = new THREE.Sprite(NitroMaterial3);
        Nitro3.scale.set(80, 20, 1)
        Nitro3.position.set(-60, -85, 1);

        return {nitro1: Nitro1, nitro2: Nitro2, nitro3: Nitro3};
    }

    addNitroController(c) {
        this.sceneUI.add(c.nitro1);
        this.sceneUI.add(c.nitro2);
        this.sceneUI.add(c.nitro3);
    }

    removeNitroController(c){
        this.sceneUI.remove(c.nitro1);
        this.sceneUI.remove(c.nitro2);
        this.sceneUI.remove(c.nitro3);
    }


    async createInfosUI(racing = true ){


        const board = { coinsIcon : null, coinIconPos   : new THREE.Vector3( 0.7   , 1.85 , -4), 
                        coins : null    , coinPos       : new THREE.Vector3( 0.7   , 1.85 , -4),
                        timeIcon : null , timeIconPos   : new THREE.Vector3( 0.9   , 1.85 , -4), 
                        time : null     , timePos       : new THREE.Vector3( 0.9   , 1.85 , -4),
                        techsIcon : null , techsIconPos   : new THREE.Vector3( 1.1 , 1.85 , -4), 
                        techs : null     , techsPos       : new THREE.Vector3( 1.1 , 1.85 , -4) };

        const geo1 = new THREE.PlaneGeometry(0.3,0.3);
        const geo2 = new THREE.PlaneGeometry(0.5,0.25);

        board.coinIconTex = this.texLoader.load("assets/img/steel-icon.png");
        /*
        const coinIconMat = new THREE.MeshBasicMaterial({map : board.coinIconTex})
        coinIconMat.color.setRGB(0.2 , 0.2 , 0.2);
        board.coinsIcon = new THREE.Mesh(geo1, coinIconMat);
        */

        board.coinCanvas = document.createElement('canvas');
        board.coinCanvas.width = 256;
        board.coinCanvas.height = 64;
        const coinsTex = new THREE.CanvasTexture(board.coinCanvas)
        const coinsMat = new THREE.MeshBasicMaterial({map : coinsTex})

        coinsMat.color.setRGB(0.6,0.6,0.6);
        board.coins = new THREE.Mesh(geo2, coinsMat);

        board.timeIconTex = this.texLoader.load("assets/img/alarm-clock-icon.jpg");
        /*
        const timeIconMat = new THREE.MeshBasicMaterial({map : board.timeIconTex})
        timeIconMat.color.setRGB(0.2 , 0.2 , 0.2);
        board.timeIcon = new THREE.Mesh(geo1, timeIconMat);
        */

        board.timeCanvas = document.createElement('canvas');
        board.timeCanvas.width = 256;
        board.timeCanvas.height = 64;

        const timeTex = new THREE.CanvasTexture(board.timeCanvas)
        const timeMat = new THREE.MeshBasicMaterial({map : timeTex})
        timeMat.color.setRGB(0.6,0.6,0.6);
        board.time = new THREE.Mesh(geo2, timeMat);

        board.techIconTex = this.texLoader.load("assets/img/tech-icon.png");

        /*
        const techIconMat = new THREE.MeshBasicMaterial({map : board.techIconTex})
        techIconMat.color.setRGB(0.2 , 0.2 , 0.2);
        board.techsIcon = new THREE.Mesh(geo1, techIconMat);
        */

        board.techCanvas = document.createElement('canvas');
        board.techCanvas.width = 256;
        board.techCanvas.height = 64;

        const techsTex = new THREE.CanvasTexture(board.techCanvas)
        const techsMat = new THREE.MeshBasicMaterial({map : techsTex})
        techsMat.color.setRGB(0.6,0.6,0.6);
        board.techs = new THREE.Mesh(geo2, techsMat);

        return board;
    }

    async createNitroUI(){

        const self = this;
        const board = {mesh : null, screens : {}, gradients : {}}
        const gltf = await this.loadGLTF('nitroboard');
        board.mesh = this.deGroupGLB(gltf.children[0]);
        board.mesh = board.mesh.children[0];
        board.mesh.renderOrder = 4000;
        board.mesh.receiveShadow = false;

        board.mesh.traverse(function(c){

            if(!c.isMesh)
                return;

            for(let imat = 0; imat < c.material.length; imat++){

                const mat = c.material[imat];
                mat.transparent = true;
                mat.depthTest = false;
                mat.transmission = 0.0;

                if(mat.name.startsWith('gradient-')){
                    
                    const arr = mat.name.split('-');
                    const name = arr[1];

                    
                    const img = mat.map ? mat.map.source.data : null;

                  

                    const screenCanvas = document.createElement('canvas');
                    screenCanvas.width = 256;
                    screenCanvas.height = 64;
                    
                    mat.emissiveMap = self.createTextureCanvas(screenCanvas, false);
                    mat.emissiveIntensity = 0.8;
                    mat.emissive.setRGB(0.5,0.5,0.5);
                    board.gradients[name] = {mat: mat, img : img, canvas : screenCanvas };


                    if(name === "nitro"){
                        board.gradients[name].img1 = new Image();
                        board.gradients[name].img2 = new Image();
                        board.gradients[name].img3 = new Image();
                        
                        board.gradients[name].img1.src = self.assetBase+'/assets/textures/nitro_ui.png';
                        board.gradients[name].img2.src = self.assetBase+'/assets/textures/nitro_ui2.png';
                        board.gradients[name].img3.src = self.assetBase+'/assets/textures/nitro_ui3.png';
                    }
                }

                if(mat.name.startsWith('screen-')){
                    const arr = mat.name.split('-');
                    const name = arr[1];
    
                    const screenCanvas = document.createElement('canvas');
                    screenCanvas.width = 256;
                    screenCanvas.height = 64;
                    mat.map = null
                    mat.emissiveMap = self.createTextureCanvas(screenCanvas, false);
                    mat.emissiveIntensity = 0.8;
                    board.screens[name] = {mat: mat, color: mat.emissive.clone(), canvas : screenCanvas };
                }
            }
        });

        /*
        board.mesh.smearIntensity = 0.0;
        board.mesh.samples = 0;
        */

        return board;
    }

    async createEngineUI(){

        const self = this;
        const board = {mesh : null, screens : {}, gradients : {}}
        const gltf = await this.loadGLTF('engineboard');
        board.mesh = this.deGroupGLB(gltf.children[0]);
        board.mesh = board.mesh.children[0];
        board.mesh.renderOrder = 4000;
        board.mesh.receiveShadow = false;

        board.mesh.traverse(function(c){

            if(!c.isMesh)
                return;

            for(let imat = 0; imat < c.material.length; imat++){

                const mat = c.material[imat];

                mat.transparent = true;
                mat.depthTest = true;
                mat.transmission = 0.0;

                if(mat.name.startsWith('gradient-')){
                    
                    const arr = mat.name.split('-');
                    const name = arr[1];

                    
                    const img = mat.map ? mat.map.source.data : null;

                    const screenCanvas = document.createElement('canvas');
                    screenCanvas.width = 256;
                    screenCanvas.height = 64;
                    
                    mat.emissiveMap = self.createTextureCanvas(screenCanvas, false);
                    mat.emissiveIntensity = 1.8;
                    mat.emissive.setRGB(0.5,0.5,0.5);
                    board.gradients[name] = {mat: mat, img : img, canvas : screenCanvas };
                }

                if(mat.name.startsWith('screen-')){
                    const arr = mat.name.split('-');
                    const name = arr[1];
    
                    const screenCanvas = document.createElement('canvas');
                    screenCanvas.width = 256;
                    screenCanvas.height = 64;
                    mat.map = null
                    mat.emissiveMap = self.createTextureCanvas(screenCanvas, false);
                    mat.emissiveIntensity = 0.8;
                    board.screens[name] = {mat: mat, color: mat.emissive.clone(), canvas : screenCanvas };
                }
                
            }
        });

        /*
        board.mesh.smearIntensity = 0.0;
        board.mesh.samples = 0;
        */

        return board;
    }

    
    async createEnergyUI(){

        const self = this;
        const board = {mesh : null, screens : {}, gradients : {}}
        const gltf = await this.loadGLTF('energyboard');
        board.mesh = this.deGroupGLB(gltf.children[0]);
        board.mesh = board.mesh.children[0];
        board.mesh.renderOrder = 4000;
        board.mesh.receiveShadow = false;

        
        

        board.mesh.traverse(function(c){

            if(!c.isMesh)
                return;

            c.renderOrder = 4000;

            for(let imat = 0; imat < c.material.length; imat++){

                const mat = c.material[imat];

                mat.transparent = true;
                mat.depthTest = false;
                mat.transmission = 0.0;

                if(mat.name.startsWith('gradient-')){
                    
                    const arr = mat.name.split('-');
                    const name = arr[1];
                                 

                    const screenCanvas = document.createElement('canvas');
                    screenCanvas.width = 256;
                    screenCanvas.height = 64;
                    
                    mat.emissiveMap = self.createTextureCanvas(screenCanvas, false);
                    mat.emissiveIntensity = 0.8;
                    mat.emissive.setRGB(0.5,0.5,0.5);
                    board.gradients[name] = {mat: mat,canvas : screenCanvas };
                }

                if(mat.name.startsWith('screen-')){
                    const arr = mat.name.split('-');
                    const name = arr[1];
    
                    const screenCanvas = document.createElement('canvas');

                    if((name === 'radar')||(name === 'battery')){
                        screenCanvas.width = 256;
                        screenCanvas.height = 256;
                        mat.map =  self.createTextureCanvas(screenCanvas, false);
                        mat.emissiveMap = null;
                        mat.emissiveIntensity = 0.0;
                    }else{

                        if((name === 'range')||(name === 'angle'))
                            screenCanvas.width = 128;
                        else
                            screenCanvas.width = 256;

                        screenCanvas.height = 64;
                        mat.map = null
                        mat.emissiveMap = self.createTextureCanvas(screenCanvas, false);
                        mat.emissiveIntensity = 0.8;
                    }
                   
                    
                    board.screens[name] = {mat: mat, color: mat.emissive.clone(), canvas : screenCanvas }
                }
            }
        });

        return board;
    }


    addInfosUI(board){
        
        board.coins.meshGPU = this.addCameraSpaceMesh(board.coins, board.coinPos);
        //board.coinsIcon.meshGPU = this.addCameraSpaceMesh(board.coinsIcon, board.coinIconPos);

        board.time.meshGPU = this.addCameraSpaceMesh(board.time, board.timePos);
        //board.timeIcon.meshGPU = this.addCameraSpaceMesh(board.timeIcon, board.timeIconPos);

        board.techs.meshGPU = this.addCameraSpaceMesh(board.techs, board.techsPos);
        //board.techsIcon.meshGPU = this.addCameraSpaceMesh(board.techsIcon, board.techsIconPos);

    }

    removeInfosUI(board){
        this.removeCameraSpaceMesh(board.coins);
        //board.coinsIcon.meshGPU = this.addCameraSpaceMesh(board.coinsIcon, board.coinIconPos);

        this.removeCameraSpaceMesh(board.time);
        //board.timeIcon.meshGPU = this.addCameraSpaceMesh(board.timeIcon, board.timeIconPos);

        this.removeCameraSpaceMesh(board.techs);
    }


    addNitroUI(board){
        board.pos = new THREE.Vector3(1.7, 0, -4);

        //board.mesh.scale.set(0.8,0.8,0.8);
        board.mesh.meshGPU = this.addCameraSpaceMesh(board.mesh, board.pos);
    }

    addEngineUI(board){
        board.pos = new THREE.Vector3(-1.7, 0, -4);
        //board.mesh.scale.set(0.8,0.8,0.8);
        board.mesh.meshGPU = this.addCameraSpaceMesh(board.mesh, board.pos);
    }
    addEnergyUI(board){
        board.pos = new THREE.Vector3(0.0, 1.5, -4);
        board.mesh.meshGPU = this.addCameraSpaceMesh(board.mesh,  board.pos);
    }

    async removeBoardUI(board){
        

        this.removeCameraSpaceMesh(board.mesh);
        
        for(let i=0;i<board.screens.length;i++){
            const mat = board.screens[i].mat;

            if(mat.emissiveMap)
                mat.emissiveMap.dipose();

            if(mat.map)
                mat.map.dipose();
        }
    }

    
    updateInfosUI(board , coins , multiplier, time, tech){
    
        this.updateCameraSpaceMesh(board.coins, board.coinPos);
        //this.updateCameraSpaceMesh(board.coinsIcon, board.coinIconPos);

        this.updateCameraSpaceMesh(board.time, board.timePos);
        //this.updateCameraSpaceMesh(board.timeIcon, board.timeIconPos);

        this.updateCameraSpaceMesh(board.techs, board.techsPos);
        //this.updateCameraSpaceMesh(board.techsIcon, board.techsIconPos);

        let ctx;

        const coinCanvas = board.coinCanvas;
        ctx = coinCanvas.getContext('2d');
        ctx.fillStyle = '#000000';
        ctx.fillRect(0, 0, coinCanvas.width, coinCanvas.height);

        ctx.drawImage(board.coinIconTex.source.data, 0, 0, coinCanvas.height, coinCanvas.height)

        ctx.fillStyle = '#FFFFFF';
        ctx.strokeStyle = '#FFFFFF';
        ctx.globalAlpha = 1.0;
        ctx.font = "bold 48px impact";
        ctx.textAlign = 'left';
        ctx.fillText(coins.toFixed(1).toString() +' x'+multiplier.toFixed(1), 14 + coinCanvas.height, 48, coinCanvas.width);


        const timeCanvas = board.timeCanvas;
        ctx = timeCanvas.getContext('2d');
        ctx.fillStyle = '#000000';
        ctx.fillRect(0, 0, timeCanvas.width, timeCanvas.height);

        ctx.drawImage(board.timeIconTex.source.data, 0, 0, timeCanvas.height, timeCanvas.height)

        ctx.fillStyle = '#FFFFFF';
        ctx.strokeStyle = '#FFFFFF';
        ctx.globalAlpha = 1.0;
        ctx.font = "bold 48px impact";
        ctx.textAlign = 'left';
        ctx.fillText(time.toFixed(1).toString(), 14 + timeCanvas.height, 48, timeCanvas.width);


        const techCanvas = board.techCanvas;
        ctx = techCanvas.getContext('2d');
        ctx.fillStyle = '#000000';
        ctx.fillRect(0, 0, techCanvas.width, techCanvas.height);

        ctx.drawImage(board.techIconTex.source.data, 0, 0, techCanvas.height, techCanvas.height)

        ctx.fillStyle = '#FFFFFF';
        ctx.strokeStyle = '#FFFFFF';
        ctx.globalAlpha = 1.0;
        ctx.font = "bold 48px impact";
        ctx.textAlign = 'left';
        ctx.fillText(tech.toFixed(1).toString(), 14 + techCanvas.height, 48, techCanvas.width);


        board.techs.material.map.needsUpdate = true;
        board.time.material.map.needsUpdate = true;
        board.coins.material.map.needsUpdate = true;
        
    }

 
    updateNitroUI(board ,nitros, nitrostate , nitrotime, nitromax){
        let ctx = null;
        
        const nitroCanvas = board.screens['nitro'].canvas;
        ctx = nitroCanvas.getContext('2d');
        ctx.fillStyle = '#'+board.screens['nitro'].color.getHexString();
        ctx.fillRect(0, 0, nitroCanvas.width, nitroCanvas.height);

        ctx.fillStyle = '#000000';
        ctx.strokeStyle = '#000000';
        ctx.globalAlpha = 1.0;
        ctx.font = "bold 48px impact";
        ctx.textAlign = 'left';
        ctx.fillText(nitros.toFixed(1).toString(), 14, 48, nitroCanvas.width);

        if((board.gradients['nitro'].img1.naturalWidth>0)&&(board.gradients['nitro'].img2.naturalWidth>0)&&(board.gradients['nitro'].img3.naturalWidth>0)){

            const nitroGradCanvas = board.gradients['nitro'].canvas;
            ctx = nitroGradCanvas.getContext('2d');
                
            const nratio = Math.max(nitrotime / nitromax, 0);
            if (nitrostate === 1) { 
                // (5000 - world.myCar.nextNitro) / 2000;

                const nitro1 = board.gradients['nitro'].img1;
                const nitro3 = board.gradients['nitro'].img3;

                ctx.drawImage(nitro1, 0, 0, nratio * nitro1.width, nitro1.height, 0 , 0, nratio * nitroGradCanvas.width, nitroGradCanvas.height);
                ctx.drawImage(nitro3,   (1.0 - nratio) * nitro3.width           , 0, nitro3.width           * nratio    , nitro3.height, 
                                        (1.0 - nratio) * nitroGradCanvas.width  , 0, nitroGradCanvas.width  * nratio    , nitroGradCanvas.height);

            } else { //Math.max(world.myCar.nextNitro / 5000, 0);

                const nitro1 = board.gradients['nitro'].img1;
                const nitro2 = board.gradients['nitro'].img2;

                ctx.drawImage(nitro1, 0, 0, nitro1.width, nitro1.height, 0 , 0, nitroGradCanvas.width, nitroGradCanvas.height);
                ctx.drawImage(nitro2, (0.5 * (1.0 - nratio)) * nitro2.width , 0, nitro2.width * nratio, nitro2.height, 
                                    (0.5 * (1.0 - nratio)) * nitroGradCanvas.width , 0, nitroGradCanvas.width * nratio, nitroGradCanvas.height);
        
            }



            board.gradients['nitro'].mat.emissiveMap.needsUpdate = true;
        }

        board.screens['nitro'].mat.emissiveMap.needsUpdate = true;

        this.updateETex(board.mesh,  board.gradients['nitro'].mat.name, board.gradients['nitro'].canvas);
        this.updateETex(board.mesh,  board.screens['nitro'].mat.name, board.screens['nitro'].canvas);
        
        this.updateCameraSpaceMesh(board.mesh,  board.pos);
    }



    updateEngineUI(board, rpm, speed, dmg, maxdmg){
        let ctx = null;
        
        const speedCanvas = board.screens['speed'].canvas;
        ctx = speedCanvas.getContext('2d');
        ctx.fillStyle = '#'+board.screens['speed'].color.getHexString();
        ctx.fillRect(0, 0, speedCanvas.width, speedCanvas.height);

        ctx.fillStyle = '#000000';
        ctx.strokeStyle = '#000000';
        ctx.globalAlpha = 1.0;
        ctx.font = "bold 48px impact";
        ctx.textAlign = 'left';
        ctx.fillText(speed.toFixed(1).toString(), 14, 48, speedCanvas.width);

        const rpmCanvas = board.gradients['rpm'].canvas;
        ctx = rpmCanvas.getContext('2d');
        ctx.fillStyle = '#000000';
        ctx.fillRect(0, 0, rpmCanvas.width, rpmCanvas.height);
        ctx.drawImage(board.gradients['rpm'].img, 0, 0, board.gradients['rpm'].img.width * rpm / 8000, board.gradients['rpm'].img.height, 0 ,0, rpmCanvas.width * rpm / 8000, rpmCanvas.height);

        const damageCanvas = board.gradients['damage'].canvas;
        ctx = damageCanvas.getContext('2d');
        ctx.fillStyle = '#FF0000';
        ctx.fillRect(0, 0, damageCanvas.width, damageCanvas.height);

        ctx.fillStyle = '#00FF00';
        ctx.fillRect(0 ,0, damageCanvas.width * (maxdmg - dmg) / maxdmg, damageCanvas.height);
        

        board.gradients['rpm'].mat.emissiveMap.needsUpdate = true;
        board.gradients['damage'].mat.emissiveMap.needsUpdate = true;
        board.screens['speed'].mat.emissiveMap.needsUpdate = true;

        this.updateETex(board.mesh,  board.gradients['rpm'].mat.name, board.gradients['rpm'].canvas);
        this.updateETex(board.mesh,  board.gradients['damage'].mat.name, board.gradients['damage'].canvas);
        this.updateETex(board.mesh,  board.screens['speed'].mat.name, board.screens['speed'].canvas);

        this.updateCameraSpaceMesh(board.mesh,  board.pos);
    }

    updateEnergyUI(board, energy, maxenergy, target, subsystem){
        let ctx = null;



        const powerCanvas = board.gradients['energybar'].canvas;
        ctx = powerCanvas.getContext('2d');
        ctx.fillStyle = '#000000';
        ctx.fillRect(0, 0, powerCanvas.width, powerCanvas.height);

        ctx.fillStyle = '#00FFFF';
        ctx.fillRect(0 ,0, powerCanvas.width * energy / maxenergy, powerCanvas.height);
        
        const targetCanvas = board.screens['target'].canvas;
        ctx = targetCanvas.getContext('2d');
        ctx.fillStyle = '#'+board.screens['target'].color.getHexString();
        ctx.fillRect(0, 0, targetCanvas.width, targetCanvas.height);

        ctx.fillStyle = '#000000';
        ctx.strokeStyle = '#000000';
        ctx.globalAlpha = 1.0;
        ctx.font = "bold 48px impact";
        ctx.textAlign = 'center';
        ctx.fillText(target, targetCanvas.width/2, 48, targetCanvas.width);

        const subsystemCanvas = board.screens['subsystem'].canvas;
        ctx = subsystemCanvas.getContext('2d');
        ctx.fillStyle = '#'+board.screens['target'].color.getHexString();
        ctx.fillRect(0, 0, subsystemCanvas.width, subsystemCanvas.height);

        ctx.fillStyle = '#000000';
        ctx.strokeStyle = '#000000';
        ctx.globalAlpha = 1.0;
        ctx.font = "bold 48px impact";
        ctx.textAlign = 'center';
        ctx.fillText(subsystem, subsystemCanvas.width/2, 48, subsystemCanvas.width);

        board.gradients['energybar'].mat.emissiveMap.needsUpdate = true;
        board.screens['target'].mat.emissiveMap.needsUpdate = true;
        board.screens['subsystem'].mat.emissiveMap.needsUpdate = true;

        
        this.updateETex(board.mesh,  board.gradients['energybar'].mat.name, board.gradients['energybar'].canvas);
        this.updateETex(board.mesh,  board.screens['target'].mat.name, board.screens['target'].canvas);
        this.updateETex(board.mesh,  board.screens['subsystem'].mat.name, board.screens['subsystem'].canvas);

        this.updateCameraSpaceMesh(board.mesh,  board.pos);
    }

    updateEnergyUIStats(board, carDef){

        const boardUI = board.screens;
        let radarImage = 'noradar.png';
        let batteryImage = 'nobattery.png';
        
        if(carDef.radar) {
            radarImage = this.getAssetImage(carDef.radar, 'radars');
            radarImage = radarImage.substring(0, radarImage.length -3) +'png';
        }

        if(carDef.battery) {
            batteryImage = this.getAssetImage(carDef.battery, 'batteries');
            batteryImage = batteryImage.substring(0, batteryImage.length -3) +'png';
        }
       
        const batteryIMG = new Image();
        const radarIMG = new Image();
        const self = this;
      
        radarIMG.onload = function () {
            const radarCanvas = boardUI['radar'].canvas;
            const context = radarCanvas.getContext('2d');

            context.fillStyle = '#FFFFFF';
            context.fillRect(0,0, radarCanvas.width, radarCanvas.height);

            context.drawImage(radarIMG, 0, 0, radarIMG.width, radarIMG.height,0 ,0, radarCanvas.width, radarCanvas.height);
            boardUI['radar'].mat.map.needsUpdate = true;

             self.updateTex(board.mesh,   boardUI['radar'].mat.name,  boardUI['radar'].canvas);


        };
        radarIMG.src = this.assetBase+'/assets/nfts/'+radarImage;
        
        batteryIMG.onload = function(){
            const batteryCanvas = boardUI['battery'].canvas;
            const context = batteryCanvas.getContext('2d');
            
            context.fillStyle = '#FFFFFF';
            context.fillRect(0,0, batteryCanvas.width, batteryCanvas.height);
            
            context.drawImage(batteryIMG, 0, 0, batteryIMG.width, batteryIMG.height,0 ,0, batteryCanvas.width, batteryCanvas.height);
            boardUI['battery'].mat.map.needsUpdate = true;

            self.updateTex(board.mesh,   boardUI['battery'].mat.name,  boardUI['battery'].canvas);
        };
        batteryIMG.src = this.assetBase+'/assets/nfts/'+batteryImage;

        let ctx;
        const powerCanvas = boardUI['power'].canvas;
        const txtBattery = carDef.battery ? parseInt(carDef.battery.power).toString() : "0";

        ctx = powerCanvas.getContext('2d');
        ctx.fillStyle = '#'+boardUI['power'].color.getHexString();
        ctx.fillRect(0, 0, powerCanvas.width, powerCanvas.height);

        ctx.fillStyle = '#000000';
        ctx.strokeStyle = '#000000';
        ctx.globalAlpha = 1.0;
        ctx.font = "48px impact";
        ctx.textAlign = 'center';
        ctx.fillText(txtBattery, powerCanvas.width/2, 48, powerCanvas.width);

        const txtRange = carDef.radar ? parseInt(carDef.radar.range).toString(): "0";
        const rangeCanvas = boardUI['range'].canvas;
        ctx = rangeCanvas.getContext('2d');
        ctx.fillStyle = '#'+board.screens['range'].color.getHexString();
        ctx.fillRect(0, 0, rangeCanvas.width, rangeCanvas.height);

        ctx.fillStyle = '#000000';
        ctx.strokeStyle = '#000000';
        ctx.globalAlpha = 1.0;
        ctx.font = "48px impact";
        ctx.textAlign = 'center';
        ctx.fillText(txtRange, rangeCanvas.width/2, 48, rangeCanvas.width);

        const txtAngle = carDef.radar ? parseInt(carDef.radar.angle).toString(): "0";
        const angleCanvas = boardUI['angle'].canvas;
        ctx = angleCanvas.getContext('2d');
        ctx.fillStyle = '#'+board.screens['angle'].color.getHexString();
        ctx.fillRect(0, 0, angleCanvas.width, angleCanvas.height);

        ctx.fillStyle = '#000000';
        ctx.strokeStyle = '#000000';
        ctx.globalAlpha = 1.0;
        ctx.font = "48px impact";
        ctx.textAlign = 'center';
        ctx.fillText(txtAngle, angleCanvas.width/2, 48, angleCanvas.width);

        boardUI['angle'].mat.emissiveMap.needsUpdate = true;
        boardUI['range'].mat.emissiveMap.needsUpdate = true;
        boardUI['power'].mat.emissiveMap.needsUpdate = true;
      
        this.updateTex(board.mesh,  boardUI['angle'].mat.name, boardUI['angle'].canvas);
        this.updateTex(board.mesh,   boardUI['range'].mat.name,  boardUI['range'].canvas);
        this.updateTex(board.mesh,  boardUI['power'].mat.name, boardUI['power'].canvas);

        
    }
   
    async createRaceUI(){

        const gltf = await this.loadGLTF("board1");
        const board = this.deGroupGLB(gltf);

        const digits = new Array(3);

        let backMesh;
        const self = this;

        let rpmPos;
        let nitrosPos;
        let nitroumCanvas;
        let nitroMat;
        let timeMat;

        const raceInfosCanvas = document.createElement('canvas');
        raceInfosCanvas.width = 256;
        raceInfosCanvas.height = 512;
        const raceInfosTex = self.createTextureCanvas(raceInfosCanvas, false);
        

        board.traverse(function(child){

            if(!child.isMesh)
                return;

            child.geometry.scale(0.4,0.4,0.4);

            if(child.name.startsWith("rpm")){

                const size = new THREE.Vector3();
                const center = new THREE.Vector3();

                child.geometry.computeBoundingBox();
                child.geometry.boundingBox.getSize(size);
                child.geometry.boundingBox.getCenter(center);

                center.x -= 1.0;
                center.y += 0.085;
                
                rpmPos = {c : center, s : size};
            }


            
            if(child.name.startsWith("nitros")){

                const size = new THREE.Vector3();
                const center = new THREE.Vector3();

                child.geometry.computeBoundingBox();
                child.geometry.boundingBox.getSize(size);
                child.geometry.boundingBox.getCenter(center);

                center.x -= 1.0;
                center.y -= 0.085;
                
                nitrosPos = {c : center, s : size};
            }

            if(child.name.startsWith("board")){

                backMesh = child.clone();


                backMesh.geometry.rotateY(-Math.PI /2);
                backMesh.geometry.rotateX(-Math.PI * 12 /8);
                backMesh.rotation.x = Math.PI /4;
                backMesh.depthTest = false;
                backMesh.transparent = true;

                for(let n=0;n< backMesh.material.length; n++){

                    const mat =backMesh.material[n];
                
                    if(mat.name === 'speed-1'){
                        mat.depthTest = false;
                        mat.transparent = true;
                        digits[0] = {obj : backMesh, mat: mat}
                    }
                    
                    if(mat.name === 'speed-2'){
                        mat.depthTest = false;
                        mat.transparent = true;
                        digits[1] = {obj : backMesh, mat: mat}
                    }
    
                    if(mat.name === 'speed-3'){
                        mat.depthTest = false;
                        mat.transparent = true;
                        digits[2] = {obj : backMesh, mat: mat}
                    }

                    if(mat.name === "nitros-num"){

                        nitroumCanvas = document.createElement('canvas');
                        nitroumCanvas.width = 256;
                        nitroumCanvas.height = 128;
                        mat.map = self.createTextureCanvas(nitroumCanvas, false);
                        nitroMat = mat;
                    }

                    
                    if(mat.name === "time"){
                        mat.map = raceInfosTex;
                        timeMat = mat;
                    }
                }
            }
        });


        const backpos =  {x: 0.0, y: 0.6, z: -2.2};

        const pos =  {x: -0.14, y: 0.47, z: -1.0};

        const tpos =  {x: -0.18, y: 0.40, z: -1.0};
        const lpos =  {x:  0.14, y: 0.40, z: -1.0};

        const rpmpos =  {x: -0.45, y: -0.3, z: -2.4};

        const nitropos =  {x: -0.45, y: -0.4, z: -1.0};
        const nitropos2 =  {x: -0.45, y: -0.4, z: -1.001};

        const dmgpos  =  {x: 0.43 , y: -0.3, z: -1};
        const nrjpos  =  {x: 0.39 , y: -0.3, z: -1};
        
        const npos = {x:  -0.2, y: -0.2, z: -1.0};
        const size = 0.05;
        const nsize = 0.065;

        const square = new THREE.PlaneGeometry(size, size);
        
        const nsquare = new THREE.PlaneGeometry(nsize, nsize);
        const rpmsquare = new THREE.PlaneGeometry(0.5 , 0.1);
        rpmsquare.rotateX(-Math.PI * 16 /8);
        rpmsquare.translate(0.25, 0.0 , 0.01);
        

        //rpmPos.c.x -= 0.5;
        const nitrosquare = new THREE.PlaneGeometry(0.5 , 0.1);
        nitrosquare.rotateX(-Math.PI * 16 /8);
        nitrosquare.translate(0.25, 0.0 , -0.01);
        
        const dmgsquare = new THREE.PlaneGeometry(0.05 , 0.5);
        dmgsquare.translate(0 , 0.25, 0);
        

        const mat = new THREE.MeshBasicMaterial({color : '#BBB', map : this.steelLogo, depthTest : false, depthWrite: false,transparent : true});
        const mat2 = new THREE.MeshBasicMaterial({color : '#BBB', map : this.NitroIcon, depthTest : false, depthWrite: false,transparent : true});
        const mat3 = new THREE.MeshBasicMaterial({color : '#BBB', map : this.timeIcon, depthTest : false, depthWrite: false,transparent : true});
        const mat4 = new THREE.MeshBasicMaterial({color : '#BBB', map : this.lapIcon, depthTest : false, depthWrite: false,transparent : true});

        const RPMTexture = this.texLoader.load(this.assetBase+"/assets/textures/engine.png");
        const RpmMaterial = new THREE.MeshBasicMaterial({ map: RPMTexture, color: 0xCCCCCC /*, depthTest: false*/ ,transparent : true});

        const RPMTexture2 = this.texLoader.load(this.assetBase+"/assets/textures/engine2.png");
        const RpmMaterial2 = new THREE.MeshBasicMaterial({ map: RPMTexture2, color: 0xCCCCCC/*, depthTest: false */ ,transparent : true});

        const NitroTexture1 =  this.texLoader.load(this.assetBase+"/assets/textures/nitro_ui.png");
        const NitroMaterial1 = new THREE.MeshBasicMaterial({ map: NitroTexture1, color: 0xCCCCCC, depthTest: false  ,transparent : true});

        const NitroTexture2 =  this.texLoader.load(this.assetBase+"/assets/textures/nitro_ui2.png");
        const NitroMaterial2 = new THREE.MeshBasicMaterial({ map: NitroTexture2, color: 0xCCCCCC, depthTest: false  ,transparent : true});

        const NitroTexture3 =  this.texLoader.load(this.assetBase+"/assets/textures/nitro_ui3.png");
        const NitroMaterial3 = new THREE.MeshBasicMaterial({ map: NitroTexture3, color: 0xCCCCCC, depthTest: false  ,transparent : true});

        const texLoader = new THREE.TextureLoader();
        
        this.digitsTex = [];
        //this.digitsMat = [];

        for (let n = 0; n < 10; n++) {
            this.digitsTex[n] = this.texLoader.load(this.assetBase+"/assets/fonts/" + n + ".png");
            this.digitsTex[n].wrapS = THREE.ClampToEdgeWrapping;
            this.digitsTex[n].wrapT = THREE.ClampToEdgeWrapping;
            this.digitsTex[n].flipY = false ;
            this.digitsTex[n].repeat.set(1, 1);
            //this.digitsMat[n] = new THREE.MeshBasicMaterial({ map: this.digitsTex[n], color: 0xCCCCCC, depthTest: false  ,transparent : true });
        }

        /*
        const digits0 = new THREE.Mesh(ssquare, this.digitsMat[0]);
        const digits1 = new THREE.Mesh(ssquare, this.digitsMat[0]);
        const digits2 = new THREE.Mesh(ssquare, this.digitsMat[0]);

        digits.push({mesh :digits0, pos : {x: -0.42, y: -0.2, z: -1.0}})
        digits.push({mesh :digits1, pos : {x: -0.37, y: -0.2, z: -1.0}})
        digits.push({mesh :digits2, pos : {x: -0.32, y: -0.2, z: -1.0}})
        */


        const DMG = new THREE.Mesh(dmgsquare, this.dmgMaterial);
        const DMG2 = new THREE.Mesh(dmgsquare, this.dmgMaterial2);

        const energyMaterial = new THREE.MeshBasicMaterial({ color: 0x0000CC,  depthTest: false, transparent : true});
        const energyMaterial2 = new THREE.MeshBasicMaterial({ color: 0x000066, depthTest: false, transparent : true});

        const NRJ = new THREE.Mesh(dmgsquare, energyMaterial);
        const NRJ2 = new THREE.Mesh(dmgsquare, energyMaterial2);

        const RPMMesh = new THREE.Mesh(rpmsquare, RpmMaterial);
        const RPMMesh2 = new THREE.Mesh(rpmsquare, RpmMaterial2);

        const Nitro1 = new THREE.Mesh(nitrosquare, NitroMaterial1);
        const Nitro2 = new THREE.Mesh(nitrosquare, NitroMaterial2);
        const Nitro3 = new THREE.Mesh(nitrosquare, NitroMaterial3);
        Nitro1.renderOrder = 0;
        Nitro2.renderOrder = 1;
        Nitro3.renderOrder = 1;


        //const backMesh = new THREE.Mesh(back, mat5);
        const bonusMesh = new THREE.Mesh(square, mat);
        const nitroMesh = new THREE.Mesh(nsquare, mat2);
        const timerMesh = new THREE.Mesh(square, mat3);
        const lapMesh = new THREE.Mesh(square, mat4);

    
        

        const nBonus = this.createBitmapText(3, pos.x + size, pos.y, pos.z, '#FFF', size);
        const nBonusM = this.createBitmapText(4, pos.x + size * 3, pos.y, pos.z, '#F00', size);
        const nNitros = this.createBitmapText(4, npos.x + size, npos.y, npos.z, '#FFF', nsize * 0.8);
        const timer = this.createBitmapText(5, tpos.x + size, tpos.y, tpos.z, '#FFF', size);
        const laps = this.createBitmapText(3, lpos.x + size, lpos.y, lpos.z, '#FFF', size);

        const raceUI = {
                            back : backMesh,
                            backpos: backpos,
                            bonus : bonusMesh, 
                            raceInfosCanvas: raceInfosCanvas,
                            raceInfosTex: raceInfosTex,
                            rpmPos : rpmPos,
                            rpm : RPMMesh, 
                            nitrosPos : nitrosPos, 
                            nitroumCanvas : nitroumCanvas,
                            nitroMat : nitroMat,
                            rpm2 : RPMMesh2, 
                            rpmpos: rpmpos,
                            speed: digits,
                            dmg1 : DMG, 
                            dmg2 : DMG2, 
                            nrjpos : nrjpos,
                            nrj1 : NRJ, 
                            nrj2 : NRJ2, 
                            dmgpos: dmgpos,
                            nitro1 : Nitro1, 
                            nitro2 : Nitro2, 
                            nitro3 : Nitro3, 
                            nitropos: nitropos,
                            nitropos2: nitropos2,
                            bpos: pos, 
                            nitro: nitroMesh, 
                            npos: npos, 
                            timer: timerMesh, 
                            tpos: tpos, 
                            lap : lapMesh,
                            lpos : lpos,
                            bonusTxt : nBonus, 
                            bonusM : nBonusM, 
                            nitrosTxt: nNitros,
                            lapsTxt: laps,
                            timerTxt : timer };
        return raceUI;
    }

    addRaceUI(raceUI){

        raceUI.rpm.meshGPU = this.addCameraSpaceMesh(raceUI.rpm, raceUI.rpmPos.c, false);
        raceUI.rpm2.meshGPU = this.addCameraSpaceMesh(raceUI.rpm2, raceUI.rpmPos.c, false);

        raceUI.nitro1.meshGPU = this.addCameraSpaceMesh(raceUI.nitro1, raceUI.nitropos, false);
        raceUI.nitro2.meshGPU = this.addCameraSpaceMesh(raceUI.nitro2, raceUI.nitropos, false);
        raceUI.nitro3.meshGPU = this.addCameraSpaceMesh(raceUI.nitro3, raceUI.nitropos, false);

        raceUI.back.meshGPU = this.addCameraSpaceMesh(raceUI.back, raceUI.backpos);

        this.addMeshChild(raceUI.back, raceUI.rpm);
        this.addMeshChild(raceUI.back, raceUI.rpm2);

        this.addMeshChild(raceUI.back, raceUI.nitro1);
        this.addMeshChild(raceUI.back, raceUI.nitro2);
        this.addMeshChild(raceUI.back, raceUI.nitro3);

        //raceUI.nitro1.rotation.X = Math.PI / 4;

        /*
        */


        raceUI.bonus.meshGPU = this.addCameraSpaceMesh(raceUI.bonus, raceUI.bpos);
        raceUI.nitro.meshGPU = this.addCameraSpaceMesh(raceUI.nitro, raceUI.npos);
        raceUI.timer.meshGPU = this.addCameraSpaceMesh(raceUI.timer, raceUI.tpos);
        raceUI.lap.meshGPU = this.addCameraSpaceMesh(raceUI.lap, raceUI.lpos);

        raceUI.dmg1.meshGPU = this.addCameraSpaceMesh(raceUI.dmg1, raceUI.dmgpos);
        raceUI.dmg2.meshGPU = this.addCameraSpaceMesh(raceUI.dmg2, raceUI.dmgpos);

        raceUI.nrj1.meshGPU = this.addCameraSpaceMesh(raceUI.nrj1, raceUI.nrjpos);
        raceUI.nrj2.meshGPU = this.addCameraSpaceMesh(raceUI.nrj2, raceUI.nrjpos);
        
        /*
        for(let d of raceUI.speed){
            d.mesh.meshGPU = this.addCameraSpaceMesh(d.mesh, d.pos);
        }
            */


        this.setMeshStatic(raceUI.bonus, false);
        this.setMeshStatic(raceUI.nitro, false);
        this.setMeshStatic(raceUI.timer, false);
        this.setMeshStatic(raceUI.lap, false);

        this.setMeshStatic(raceUI.nitro1, false);
        this.setMeshStatic(raceUI.nitro2, false);
        this.setMeshStatic(raceUI.nitro3, false);

        this.setMeshStatic(raceUI.dmg1, false);
        this.setMeshStatic(raceUI.dmg2, false);

        this.setMeshStatic(raceUI.rpm, false);
        this.setMeshStatic(raceUI.rpm2, false);


        this.setMeshStatic(raceUI.nrj1, false);
        this.setMeshStatic(raceUI.nrj2, false);

        this.setMeshStatic(raceUI.timer, false);
        this.setMeshStatic(raceUI.lap, false);

        this.addBitmapText(raceUI.bonusTxt);
        this.addBitmapText(raceUI.bonusM);
        this.addBitmapText(raceUI.nitrosTxt);
        this.addBitmapText(raceUI.timerTxt);
        this.addBitmapText(raceUI.lapsTxt);
    }
    
    updateRaceUI(raceUI, nBonus, mul, nitros, time, lap, nlap, rpm, rpmmax, nitrostate, nitrotime, nitromax, dmg, dmgmax, nrj, nrjmax, speed){
        


        const ratio = rpm / rpmmax;
        raceUI.rpm.ox = ratio * 0.5;// this.CamUIratio1;
        raceUI.rpm.oy = 0;
        raceUI.rpm.sx = (1.0 - ratio);
        raceUI.rpm.sy = 1.0;
        //raceUI.rpm.material.map.offset.set(ratio, 0);
        //raceUI.rpm.material.map.repeat.set(1.0-ratio, 1);
        this.setTextureOffset(raceUI.rpm, null, ratio, 0 , 1.0 - ratio, 1);

        raceUI.rpm2.ox = 0;
        raceUI.rpm2.oy = 0;
        raceUI.rpm2.sx = ratio;
        raceUI.rpm2.sy = 1;
        //raceUI.rpm2.material.map.repeat.set(ratio, 1);
        this.setTextureOffset(raceUI.rpm2, null, 0, 0 , ratio, 1);


        const nratio = Math.max(nitrotime / nitromax, 0);
        if (nitrostate === 1) { // (5000 - world.myCar.nextNitro) / 2000;
            raceUI.nitro2.sx = 0.0;
            raceUI.nitro2.sy = 1;
            raceUI.nitro2.ox = 0.0;
            raceUI.nitro2.oy = 0;

            raceUI.nitro1.sx = (1.0 - nratio);
            raceUI.nitro1.sy = 1;
            
            raceUI.nitro1.ox = nratio * 0.5;
            raceUI.nitro1.oy = 0;

            this.setTextureOffset(raceUI.nitro1, null, nratio, 0.0 , 1.0 - nratio, 1);
            /*
            raceUI.nitro1.material.map.repeat.set(1.0 - nratio, 1.0);
            raceUI.nitro1.material.map.offset.set(nratio, 0.0);
            */

            raceUI.nitro3.sx = nratio;
            raceUI.nitro3.sy = 1;
            raceUI.nitro3.ox = 0;
            raceUI.nitro3.oy = 0;
            //raceUI.nitro3.material.map.repeat.set(nratio, 1.0);
            this.setTextureOffset(raceUI.nitro3, null, 0.0, 0.0 , nratio, 1);

        } else { //Math.max(world.myCar.nextNitro / 5000, 0);

            raceUI.nitro3.ox = 0.0;
            raceUI.nitro3.oy = 0;

            raceUI.nitro3.sx = 0.0;
            raceUI.nitro3.sy = 1.0;

            raceUI.nitro1.ox = 0.0;
            raceUI.nitro1.oy = 0;

            raceUI.nitro1.sx = 1.0;
            raceUI.nitro1.sy = 1.0;

            this.setTextureOffset(raceUI.nitro1, null, 0.0, 0.0 , 1.0, 1);
            
            /*
            raceUI.nitro1.material.map.repeat.set(1.0, 1.0);
            raceUI.nitro1.material.map.offset.set(0.0, 0.0);
            */

            raceUI.nitro2.sx = nratio;
            raceUI.nitro2.sy = 1.0;

            raceUI.nitro2.ox = 0.25 * (1.0 - nratio);
            raceUI.nitro2.oy = 0;
            

            /*
            raceUI.nitro2.material.map.repeat.set(nratio, 1.0);
            raceUI.nitro2.material.map.offset.set((1.0 - nratio) / 2.0, 0);
            */

            this.setTextureOffset(raceUI.nitro2, null, (1.0 - nratio) / 2.0, 0 , nratio, 1.0);
        }

        raceUI.rpm.scale.set(raceUI.rpm.sx, raceUI.rpm.sy, 1.0);
        raceUI.rpm2.position.set(raceUI.rpmPos.c.x + raceUI.rpm2.ox, raceUI.rpmPos.c.y, raceUI.rpmPos.c.z + raceUI.rpm2.oy);

        raceUI.rpm2.scale.set(raceUI.rpm2.sx, raceUI.rpm2.sy, 1.0);
        raceUI.rpm.position.set(raceUI.rpmPos.c.x + raceUI.rpm.ox, raceUI.rpmPos.c.y, raceUI.rpmPos.c.z + raceUI.rpm.oy);
        
        raceUI.nitro1.scale.set(raceUI.nitro1.sx, raceUI.nitro1.sy, 1.0);
        raceUI.nitro1.position.set(raceUI.nitrosPos.c.x + raceUI.nitro1.ox, raceUI.nitrosPos.c.y, raceUI.nitrosPos.c.z + raceUI.nitro1.oy);

        raceUI.nitro2.scale.set(raceUI.nitro2.sx, raceUI.nitro2.sy, 1.0);
        raceUI.nitro2.position.set(raceUI.nitrosPos.c.x + raceUI.nitro2.ox, raceUI.nitrosPos.c.y, raceUI.nitrosPos.c.z + raceUI.nitro2.oy);

        raceUI.nitro3.scale.set(raceUI.nitro3.sx, raceUI.nitro3.sy, 1.0);
        raceUI.nitro3.position.set(raceUI.nitrosPos.c.x + raceUI.nitro3.ox, raceUI.nitrosPos.c.y, raceUI.nitrosPos.c.z + raceUI.nitro3.oy);

        const dratio = dmg / dmgmax;

        raceUI.dmg1.sy = (1.0-dratio);
        raceUI.dmg1.oy = 0;

        raceUI.dmg2.sy = dratio;
        raceUI.dmg2.oy = (1.0-dratio) * 0.5;

        const nrratio = nrj / nrjmax;

        raceUI.nrj1.sy = nrratio;
        raceUI.nrj1.oy = 0;

        raceUI.nrj2.sy = (1.0-nrratio);
        raceUI.nrj2.oy = nrratio * 0.5;

        const text = Math.abs(speed).toFixed(0).toString();
        const nc = text.length;
        const nnc = 3 - nc;

        if(nnc>=0){

            for (let n = 0; n < nnc; n++) {
                raceUI.speed[n].mat.map = this.digitsTex[0];
            }

            for (let n = nnc; n < 3; n++) {
                const nm = parseInt(text[n - nnc]);
                raceUI.speed[n].mat.map = this.digitsTex[nm] ? this.digitsTex[nm] : this.digitsTex[0];
            }
            /*
            for(let d of raceUI.speed){
                this.updateCameraSpaceMesh(d.mesh, d.pos);
            }
            */
        }

        this.updateCameraSpaceMesh(raceUI.back, raceUI.backpos);
        
        this.updateCameraSpaceMesh(raceUI.bonus, raceUI.bpos);
        //this.updateCameraSpaceMesh(raceUI.nitro, raceUI.npos);
        this.updateCameraSpaceMesh(raceUI.timer, raceUI.tpos);
        this.updateCameraSpaceMesh(raceUI.lap, raceUI.lpos);

        /*
        this.updateCameraSpaceMesh(raceUI.rpm, raceUI.rpmpos);
        this.updateCameraSpaceMesh(raceUI.rpm2, raceUI.rpmpos);
        
        
        this.updateCameraSpaceMesh(raceUI.nitro1, raceUI.nitropos2);
        this.updateCameraSpaceMesh(raceUI.nitro2, raceUI.nitropos);
        this.updateCameraSpaceMesh(raceUI.nitro3, raceUI.nitropos);
        */

        this.updateCameraSpaceMesh(raceUI.dmg1, raceUI.dmgpos);
        this.updateCameraSpaceMesh(raceUI.dmg2, raceUI.dmgpos);

        this.updateCameraSpaceMesh(raceUI.nrj1, raceUI.nrjpos);
        this.updateCameraSpaceMesh(raceUI.nrj2, raceUI.nrjpos);

        const ctx = raceUI.nitroumCanvas.getContext('2d');
        ctx.fillStyle = '#000000';
        ctx.fillRect(0, 0, raceUI.nitroumCanvas.width, raceUI.nitroumCanvas.height);

        ctx.fillStyle = '#FFFFFF';
        ctx.strokeStyle = '#000000';
        ctx.globalAlpha = 1.0;
        ctx.font = "96px impact";
        ctx.textAlign = 'left';
        ctx.fillText('x'+nitros.toString(), 14, 96, 128);

        raceUI.nitroMat.map.needsUpdate = true;


        const ctx2 = raceUI.raceInfosCanvas.getContext('2d');
        ctx2.fillStyle = '#000000';
        ctx2.fillRect(0, 0, raceUI.raceInfosCanvas.width, raceUI.raceInfosCanvas.height);

        ctx2.fillStyle = '#FFFFFF';
        ctx2.strokeStyle = '#000000';
        ctx2.globalAlpha = 1.0;
        ctx2.font = "48px impact";
        ctx2.textAlign = 'center';
        ctx2.fillText(nBonus.toString(), 64, 300, 128);

        ctx2.fillStyle = '#FF0000';
        ctx2.strokeStyle = '#000000';
        ctx2.globalAlpha = 1.0;
        ctx2.font = "48px impact";
        ctx2.textAlign = 'left';
        ctx2.fillText("x" + mul.toString(), 128, 300, 128);

        ctx2.fillStyle = '#FFFFFF';
        ctx2.strokeStyle = '#000000';
        ctx2.globalAlpha = 1.0;
        ctx2.font = "48px impact";
        ctx2.textAlign = 'left';
        ctx2.fillText(time.toString(), 14, 200, 256);

        ctx2.fillStyle = '#FFFFFF';
        ctx2.strokeStyle = '#000000';
        ctx2.globalAlpha = 1.0;
        ctx2.font = "48px impact";
        ctx2.textAlign = 'center';
        ctx2.fillText(lap.toString(), 64, 64, 256);

        ctx2.fillStyle = '#FFFFFF';
        ctx2.strokeStyle = '#000000';
        ctx2.globalAlpha = 1.0;
        ctx2.font = "48px impact";
        ctx2.textAlign = 'center';
        ctx2.fillText("3", 192, 64, 256);

        raceUI.raceInfosTex.needsUpdate = true;


        this.setBitmapText(raceUI.bonusTxt, 'x'+nBonus.toString());
        this.setBitmapText(raceUI.bonusM, 'x'+mul.toString());
        //this.setBitmapText(raceUI.nitrosTxt, 'x'+nitros.toString());
        this.setBitmapText(raceUI.timerTxt, time.toString());
        this.setBitmapText(raceUI.lapsTxt, lap +'/' + nlap);

        for(let c of raceUI.bonusTxt.chars){
            this.updateCameraSpaceMesh(c.mesh, c.pos);
        }
        
        for(let c of raceUI.bonusM.chars){
            this.updateCameraSpaceMesh(c.mesh, c.pos);
        }
        
        /*
        for(let c of raceUI.nitrosTxt.chars){
            this.updateCameraSpaceMesh(c.mesh, c.pos);
        }
        */
            

        for(let c of raceUI.timerTxt.chars){
            this.updateCameraSpaceMesh(c.mesh, c.pos);
        }

        for(let c of raceUI.lapsTxt.chars){
            this.updateCameraSpaceMesh(c.mesh, c.pos);
        }
    }

    removeRaceUI(raceUI){
    //raceUI.back.meshGPU = this.addCameraSpaceMesh(raceUI.back, raceUI.backpos);
     
        /*
        for(let d of raceUI.speed){
            d.mesh.meshGPU = this.removeMesh(d.mesh);
        }
        */



        this.removeMesh(raceUI.back);
        this.removeMesh(raceUI.bonus);
        this.removeMesh(raceUI.rpm);
        this.removeMesh(raceUI.rpm2);
        this.removeMesh(raceUI.nitro);
        this.removeMesh(raceUI.nitro1);
        this.removeMesh(raceUI.nitro2);
        this.removeMesh(raceUI.nitro3);
        this.removeMesh(raceUI.dmg1);
        this.removeMesh(raceUI.dmg2);

        this.removeMesh(raceUI.nrj1);
        this.removeMesh(raceUI.nrj2);


        this.removeMesh(raceUI.timer);
        this.removeMesh(raceUI.lap);

        this.removeBitmapText(raceUI.bonusTxt);
        this.removeBitmapText(raceUI.bonusM);
        this.removeBitmapText(raceUI.nitrosTxt);
        this.removeBitmapText(raceUI.timerTxt);
        this.removeBitmapText(raceUI.lapsTxt);
    }

    addDamageController(c) {
        this.sceneUI.add(c.dmg1);
        this.sceneUI.add(c.dmg2);
    }

       
    addDialogController(c) {
        this.sceneUI.add(c.dlg);
    }

       
    addLoadingController(c) {
        this.sceneUI.add(c.loading1);
        this.sceneUI.add(c.loading2);
        this.sceneUI.add(c.txt);
    }

    removeDamageController(c){
        this.sceneUI.remove(c.dmg1);
        this.sceneUI.remove(c.dmg2);
    }

    removeEnergyController(c){
        this.sceneUI.remove(c.energy1);
        this.sceneUI.remove(c.energy2);
    }

    removeDialogController(c){
        this.sceneUI.remove(c.dlg);
    }

    updateRPM(rpm, value, max) { //world.myCar.RPM / 8000

        const ratio = value / max;

        rpm.rpm1.scale.x = 80 * (1.0 - ratio);
        rpm.rpm1.position.x = ratio * 40 - 60;
        rpm.rpm1.material.map.repeat.set(1.0-ratio, 1);
        rpm.rpm1.material.map.offset.set(ratio,0);
        
        rpm.rpm2.scale.x = 80 * ratio;
        rpm.rpm2.position.x = - (1.0 - ratio) * 40 - 60;
        rpm.rpm2.material.map.repeat.set(ratio, 1);
    }

    updateNitro(nitro, state, value, max) { //(world.myCar.nextNitro > 3000)&&(pressed['nitro'])
       
        const ratio = Math.max(value / max, 0);
        if (state) { // (5000 - world.myCar.nextNitro) / 2000;
            nitro.nitro2.scale.x = 0.0;

            nitro.nitro3.scale.x = 80.0 * ratio;
            nitro.nitro3.position.x = -(1.0 - ratio) * 40.0 - 60.0;
            nitro.nitro3.material.map.repeat.set(ratio, 1.0);
            //UIItems.Nitro3.material.map.offset.set(1.0-ratio,0);
            
            nitro.nitro1.scale.x = 80.0 * (1.0 - ratio);
            nitro.nitro1.position.x = ratio * 40.0 - 60.0;
            nitro.nitro1.material.map.repeat.set(1.0 - ratio, 1.0);
            nitro.nitro1.material.map.offset.set(ratio, 0.0);

        } else { //Math.max(world.myCar.nextNitro / 5000, 0);

            nitro.nitro3.scale.x = 0.0;

            nitro.nitro1.scale.x = 80.0;
            nitro.nitro1.position.x = -60.0;
            nitro.nitro1.material.map.repeat.set(1.0, 1.0);
            nitro.nitro1.material.map.offset.set(0.0, 0.0);

            nitro.nitro2.scale.x = 80.0 * ratio;
            nitro.nitro2.material.map.repeat.set(ratio, 1.0);
            nitro.nitro2.material.map.offset.set((1.0 - ratio) / 2.0, 0);
        }
    }

    updateDamage(c, value, max) {
        const ratio = value / max;

        c.dmg1.scale.y = 80.0 * (1.0-ratio);
        c.dmg1.position.y = -ratio * 40;

        c.dmg2.scale.y = 80.0 * (ratio);
        c.dmg2.position.y = (1.0-ratio) * 40;
    }

    updateEnergy(c, value, max) {
        const ratio = max > 0 ? value / max : 0.0;

        c.energy1.scale.y = 80.0 * (1.0-ratio);
        c.energy1.position.y = -ratio * 40;

        c.energy2.scale.y = 80.0 * (ratio);
        c.energy2.position.y = (1.0-ratio) * 40;
    }

    
    addUIMesh(meshGroup, matType = RT_DIFF, flip = {x: false,y: false,z: false}) {
        this.sceneUI.add(meshGroup);

        return meshGroup;
    }

    removeUIMesh(m){
        this.sceneUI.remove(m);
    }
    
    setUIPos(m, x, y){

        if(!m.mesh)
            return;

        m.mesh.position.set(x,y, -10);
    }

    makeRope(rope, object) {
        const a = new THREE.SphereGeometry(0.2);
        rope.anchor.object = new THREE.Mesh(a, this.platformMaterial);
        rope.anchor.object.name = 'ropeanchor-'+rope.id;

        rope.anchor.object.position.x = rope.anchor.pos.x;
        rope.anchor.object.position.y = rope.anchor.pos.y;
        rope.anchor.object.position.z = rope.anchor.pos.z;

        if (object) {

            const size = new THREE.Vector3();
            rope.obj.object = object.clone();
            rope.obj.object.geometry.computeBoundingBox();
            rope.obj.object.geometry.boundingBox.getSize(size);
            rope.obj.object.scale.set(rope.obj.size.x / size.x, rope.obj.size.y / size.y, rope.obj.size.z / size.z);
        }
        else {
            const t = rope.obj.shape === 0 ? new THREE.BoxGeometry(rope.obj.size.x, rope.obj.size.y, rope.obj.size.z) : new THREE.CylinderGeometry(rope.obj.size.x / 2, rope.obj.size.x / 2, rope.obj.size.y, 12);
            rope.obj.object = new THREE.Mesh(t, this.platformMaterial);
            rope.obj.object.name = 'ropeobject-'+rope.id;
        }

        rope.obj.object.position.x = rope.obj.pos.x;
        rope.obj.object.position.y = rope.obj.pos.y;
        rope.obj.object.position.z = rope.obj.pos.z;

        rope.obj.object.rotation.x = rope.obj.angles.x;
        rope.obj.object.rotation.y = rope.obj.angles.y;
        rope.obj.object.rotation.z = rope.obj.angles.z;
    }

    updateGridRot(grid){

        const q = new THREE.Quaternion(grid.rot.x, -grid.rot.y, grid.rot.z, grid.rot.w);
        for(let n=0;n<6;n++){
            const pos = grid.borderInfos[n].pos.clone().sub(grid.MetaGrid.grid_pos).applyQuaternion(q).add(grid.MetaGrid.grid_pos);
            grid.borderMeshes[n].position.set(pos.x, pos.y, pos.z);
            grid.borderMeshes[n].quaternion.set(q.x, q.y, q.z, q.w);
        }

    }

    makeGrid(grid) {

        const MetaGrid = new MetaBallGrid(grid.width, grid.height, grid.depth, grid.cell_size.x, grid.cell_size.y, grid.cell_size.z);
        MetaGrid.grid_pos.set(grid.pos.x,  grid.pos.y , grid.pos.z);
        MetaGrid.grid_rot.set(grid.rot.x,  grid.rot.y , grid.rot.z , grid.rot.w);

        MetaGrid.isolevel = grid.isolevel;
        
        for(let n = 0;n<grid.balls.length; n++){
            MetaGrid.add_ball(grid.balls[n].center.x, grid.balls[n].center.y, grid.balls[n].center.z, grid.balls[n].radius, grid.balls[n].weight);
        }


        grid.geo = new THREE.BufferGeometry();
        grid.geoVertices = new Float32Array(65000 * 3);
        grid.geoNormals = new Float32Array(65000 * 3);
        grid.geoIndices = new Uint32Array(65000 * 3);

        grid.geo.setAttribute('position', new THREE.Float32BufferAttribute(grid.geoVertices, 3));
        grid.geo.setAttribute('normal', new THREE.Float32BufferAttribute(grid.geoNormals, 3));
        grid.geo.setIndex(new THREE.Uint32BufferAttribute(grid.geoIndices, 1));
        
        grid.mat = new THREE.MeshPhysicalMaterial({color : 0x44FF44, transparent: true/*, blending: THREE.AdditiveBlending*/, opacity:0.8, reflectivity : 0.2, metalness: 0.2, ior: 1.8, roughness : 0.4});
        grid.mesh = new THREE.Mesh(grid.geo , grid.mat);

        grid.mesh.renderOrder = 1000;

        grid.mesh.position.set(MetaGrid.grid_pos.x, MetaGrid.grid_pos.y, MetaGrid.grid_pos.z);
        grid.mesh.quaternion.set(MetaGrid.grid_rot.x, MetaGrid.grid_rot.y, MetaGrid.grid_rot.z, MetaGrid.grid_rot.w);
        grid.mesh.frustumCulled = false;

        const sgeo = new THREE.SphereGeometry(5 * MetaGrid.cell_size.x);
        const smat = new THREE.MeshPhongMaterial();
        const smesh = new THREE.Mesh(sgeo, smat);

        smesh.name= 'grid sphere';

        grid.fallBall  = {mesh : smesh, pos : new THREE.Vector3(), rot : new THREE.Quaternion()};

        let texture = this.texLoader.load(this.assetBase+"/assets/textures/crane_a.png");

        grid.borderMeshes = [];
        const borderMat = new THREE.MeshPhysicalMaterial({color : 0xFFFFFFFF, transparent: true, roughness: 0.1, alphaMap: texture, opacity: 1.0, ior : 1.9, reflectivity: 0.8, metalness: 0.6, thickness : 0.01})
        for(let n=0;n<6;n++){
            const borderGeo = new THREE.BoxGeometry(grid.borderInfos[n].size.x , grid.borderInfos[n].size.y , grid.borderInfos[n].size.z);
            const borderMesh = new THREE.Mesh(borderGeo, borderMat);
            borderMesh.name = 'grid border mesh';

            const pos = grid.borderInfos[n].pos.clone().sub(MetaGrid.grid_pos).applyQuaternion(grid.rot).add(MetaGrid.grid_pos);

            borderMesh.position.set(pos.x, pos.y, pos.z);
            borderMesh.quaternion.set(grid.rot.x, grid.rot.y, grid.rot.z, grid.rot.w);
            borderMesh.renderOrder = 10000;
            grid.borderMeshes.push(borderMesh);
        }

        grid.MetaGrid = MetaGrid;
    }

    makeSpring(spring, object) {
        const a = new THREE.SphereGeometry(0.2);
        spring.anchor.object = new THREE.Mesh(a, this.platformMaterial);
        spring.anchor.object.name = 'springanchor-'+spring.id;

        spring.anchor.object.position.x = spring.anchor.pos.x;
        spring.anchor.object.position.y = spring.anchor.pos.y;
        spring.anchor.object.position.z = spring.anchor.pos.z;

        if (object) {
            spring.obj.object = object.clone();

            if(!spring.obj.object.geometry.boundingBox)
                spring.obj.object.geometry.computeBoundingBox();

            spring.obj.object.geometry.boundingBox.getSize(spring.obj.size);
            spring.obj.object.scale.set(spring.size.x / spring.obj.size.x, spring.size.y /spring.obj.size.y, spring.size.z /spring.obj.size.z);
        }
        else {
            const t = spring.obj.shape === 0 ? new THREE.BoxGeometry(spring.size.x, spring.size.y, spring.size.z) : new THREE.CylinderGeometry(spring.obj.size.x / 2, spring.obj.size.x / 2, spring.obj.size.y, 12);
            spring.obj.object = new THREE.Mesh(t, this.platformMaterial);
            spring.obj.object.name = 'springobject-'+spring.id;
        }

        spring.obj.object.position.x = spring.obj.pos.x;
        spring.obj.object.position.y = spring.obj.pos.y;
        spring.obj.object.position.z = spring.obj.pos.z;

        spring.obj.object.rotation.x = spring.obj.angles.x;
        spring.obj.object.rotation.y = spring.obj.angles.y;
        spring.obj.object.rotation.z = spring.obj.angles.z;
    }

    makeBonus(bonus, object = null) {

        if (object === null) {

            if(bonus.type === 0){
                const t = new THREE.CylinderGeometry(bonus.size, bonus.size, bonus.size * 0.2);
                bonus.object = new THREE.Mesh(t, this.bonusMaterial);
            }
        } else {

            object.traverse((child) => {

                if (child.type == 'Mesh') {
                    bonus.object = child.clone();

                    bonus.object.material = child.material;
                    bonus.object.castShadow = true;
                    bonus.object.receiveShadow = false;
                }
            });
        }

        bonus.object.position.x = bonus.pos.x;
        bonus.object.position.y = bonus.pos.y;
        bonus.object.position.z = bonus.pos.z;

        bonus.object.rotation.x = bonus.angles.x;
        bonus.object.rotation.y = bonus.angles.y;
        bonus.object.rotation.z = bonus.angles.z;

        bonus.object.scale.x = bonus.size;
        bonus.object.scale.y = bonus.size;
        bonus.object.scale.z = bonus.size;

        bonus.rot = new THREE.Quaternion();

        bonus.light = new THREE.PointLight(0xffffff, 4, 4);
        bonus.light.position.x = bonus.pos.x;
        bonus.light.position.y = bonus.pos.y + 1;
        bonus.light.position.z = bonus.pos.z;

        bonus.enabled = true;
    }

    makeTrackWoods(wood, object = null) {
        for (let n = 0; n < wood.objects.length; n++) {

            if (object === null) {
                const t = new THREE.CylinderGeometry(wood.radius, wood.radius, wood.width, 24, 1);
                //t.rotateZ(Math.PI/2)
                wood.objects[n].object = new THREE.Mesh(t, this.material2);
            } else {
                object.traverse((child) => {
                    if (child.type == 'Mesh') {
                        wood.objects[n].object = child.clone();
                        wood.objects[n].object.material = object.material;
                        wood.objects[n].object.castShadow = true;
                        wood.objects[n].object.receiveShadow = true;
                    }
                });
            }

            wood.objects[n].object.position.x = wood.objects[n].pos.x;
            wood.objects[n].object.position.y = wood.objects[n].pos.y;
            wood.objects[n].object.position.z = wood.objects[n].pos.z;

            const q = new THREE.Quaternion().setFromEuler(new THREE.Euler(wood.objects[n].angles.x, wood.objects[n].angles.y, wood.objects[n].angles.z, "XYZ"));

            wood.objects[n].object.quaternion.x = q.x;
            wood.objects[n].object.quaternion.y = q.y;
            wood.objects[n].object.quaternion.z = q.z;
            wood.objects[n].object.quaternion.w = q.w;
        }
    }

    async makeJumper(jumper, object = null)
    {
        if (object === null) {

            object = await this.loadObj('jumper');
        }

        object.traverse((child) => {

            if (child.type == 'Mesh') {
                jumper.object = child.clone();

                jumper.object.material = child.material;
                jumper.object.castShadow = true;
                jumper.object.receiveShadow = false;
            }
        });


        jumper.object.position.x = jumper.pos.x;
        jumper.object.position.y = jumper.pos.y;
        jumper.object.position.z = jumper.pos.z;

        jumper.object.rotation.x = jumper.angles.x;
        jumper.object.rotation.y = jumper.angles.y;
        jumper.object.rotation.z = jumper.angles.z;

        jumper.object.scale.x = jumper.size.x;
        jumper.object.scale.y = jumper.size.y;
        jumper.object.scale.z = jumper.size.z;

        jumper.object.name = "jumper-"+jumper.id;
    }

    createDMGBar(paleMesh, paleOffset, life){
      
        /*
        const DMGBar1 = new THREE.Mesh(this.DMGgeo1, this.dmgMaterial);
        const DMGBar2 = new THREE.Mesh(this.DMGgeo2, this.dmgMaterial2);

        DMGBar1.name = "dmgbar-"+paleMesh.name;
        DMGBar2.name = "dmgbar-"+paleMesh.name;
                        
        DMGBar1.position.set(paleMesh.x + paleOffset.x, paleMesh.y + paleOffset.y, paleMesh.z  + paleOffset.z);
        DMGBar2.position.set(paleMesh.x + paleOffset.x, paleMesh.y + paleOffset.y, paleMesh.z  + paleOffset.z);
        */

        return {instanceID : this.numDmgBar++, paleOffset: paleOffset, color1 : new THREE.Color().setHex(0x00CC00), color2 : new THREE.Color().setHex(0xCC0000), scale1 : new THREE.Vector3(1,1,1), scale2 : new THREE.Vector3(1,1,1),  position: new THREE.Vector3(paleMesh.x + paleOffset.x, paleMesh.y + paleOffset.y, paleMesh.z  + paleOffset.z), maxlife:life};
    }
  
    updateDMGBar(dmgbar, pos, life){
        
        dmgbar.position.set(pos.x + dmgbar.paleOffset.x, pos.y  + dmgbar.paleOffset.y, pos.z + dmgbar.paleOffset.z);
        //dmgbar.DMGBar1.position.set(pos.x + dmgbar.paleOffset.x, pos.y  + dmgbar.paleOffset.y, pos.z + dmgbar.paleOffset.z);
        //dmgbar.DMGBar2.position.set(pos.x + dmgbar.paleOffset.x, pos.y  + dmgbar.paleOffset.y, pos.z + dmgbar.paleOffset.z);

        //dmgbar.DMGBar1.lookAt(this.cameraAnchor.position);
        //dmgbar.DMGBar2.lookAt(this.cameraAnchor.position);

        dmgbar.scale1.set(life / dmgbar.maxlife, 1, 1 );
        dmgbar.scale2.set(1 , 0.8, 0.8 );


        this.tmpM.compose(dmgbar.position, this.cameraAnchor.quaternion, dmgbar.scale1)
        this.DMGBarsInstanceMesh.setMatrixAt(dmgbar.instanceID * 2 + 0, this.tmpM);


        this.tmpM.compose(dmgbar.position, this.cameraAnchor.quaternion, dmgbar.scale2)
        this.DMGBarsInstanceMesh.setMatrixAt(dmgbar.instanceID * 2 + 1, this.tmpM);

        this.DMGBarsInstanceMesh.instanceMatrix.needsUpdate = true;
        /*
        if(dmgbar.DMGBar1.meshGPU)
            dmgbar.DMGBar1.meshGPU.size.x = life / dmgbar.maxlife;
        

        this.tmpTransformation.set(dmgbar.DMGBar1.position, dmgbar.DMGBar1.quaternion);
        this.updateTransform(dmgbar.DMGBar1, this.tmpTransformation);
        this.updateTransform(dmgbar.DMGBar2, this.tmpTransformation);
        */
    }
    addDMGBar(dmgBar){

        dmgBar.DMGBar1.meshGPU = this.addWorldUIMesh(dmgBar.DMGBar1.position, dmgBar.DMGBar1.geometry, dmgBar.DMGBar1.material.color);
        dmgBar.DMGBar2.meshGPU = this.addWorldUIMesh(dmgBar.DMGBar2.position, dmgBar.DMGBar2.geometry, dmgBar.DMGBar2.material.color);
    }

    async makeDrone(drone)
    {
        let paleID = 1;
        
        const self = this;

        if((!drone.model)||(drone.model.length<=0))
            drone.model = 'drone.glb';

        const geo = new TextGeometry('drone-'+drone.id, { font: this.impactFont, size: 0.1, height: 0.1, curveSegments: 4});
        geo.computeBoundingSphere();
        geo.translate(-geo.boundingSphere.radius, 0, 0);
        drone.target = {id : this.targetID++, type : 'drone', name : 'drone-'+drone.id, pos: { x: drone.pos.x, y: drone.pos.y, z :drone.pos.z}, txt: new THREE.Mesh(geo, this.targetMat), subsystems: []};        

        if(!this.droneMeshCache[drone.model]){

            let mesh = null;

            this.droneMeshCache[drone.model] = {  instanceMesh : null, usedInstances : 0, nInstances : 0, bodyGeometry : null, instanceGeoId: -1, droneSize: new THREE.Vector3(), meshCenter: new THREE.Vector3(), pales :[], wings : [] }
            
            if(drone.model.endsWith('.glb')){
                const gltf = await this.loadGLTF(drone.model.substring(0, drone.model.length - 4));
                mesh = this.deGroupGLB(gltf);
            }else
                mesh = await this.loadObj(drone.model);
            

             mesh.traverse((child) => {
            
                if (!child.isMesh) 
                    return;
                

                if(child.material.length > 0){
                    for(let mi =0; mi<child.material.length; mi++){

                        if(child.material[mi].name.startsWith('Toon'))
                            child.material[mi] = self.setToon(child.material[mi]);
                    }
                }else if(child.material.name.startsWith('Toon'))
                    child.material = self.setToon(child.material);
                
                if(child.name=='Body') {

                    this.removeGeoGrp(child, ['center', 'bone', 'ccube', 'Anchor']);

                    self.droneMeshCache[drone.model].bodyGeometry = child.geometry;
                    self.droneMeshCache[drone.model].bodyGeometry.computeBoundingBox();
                    self.droneMeshCache[drone.model].bodyGeometry.boundingBox.getCenter(self.droneMeshCache[drone.model].meshCenter);
                    self.droneMeshCache[drone.model].bodyGeometry.boundingBox.getSize(self.droneMeshCache[drone.model].droneSize);
                    self.droneMeshCache[drone.model].bodyGeometry.translate(-self.droneMeshCache[drone.model].meshCenter.x, -self.droneMeshCache[drone.model].meshCenter.y, -self.droneMeshCache[drone.model].meshCenter.z)
                    self.droneMeshCache[drone.model].bodyGeometry.computeBoundingBox();
                    self.droneMeshCache[drone.model].bodyGeometry.computeBoundingSphere();

                    self.droneMeshCache[drone.model].material = child.material;

                    if (self.rtEngine) {
                        const rtObj = child.clone();
                        rtObj.position.set(0,0,0);
                        rtObj.rotation.set(0,0,0);
                        rtObj.scale.set(drone.size.x / this.droneMeshCache[drone.model].droneSize.x, drone.size.y / this.droneMeshCache[drone.model].droneSize.y, drone.size.z / this.droneMeshCache[drone.model].droneSize.z)
                        self.droneMeshCache[drone.model].rtMesh = self.rtEngine.addMesh(rtObj, RT_COAT, new Transformation3(child.position, child.quaternion), true);
                    }
                        

                    /*
                    drone.object.castShadow = true;
                    drone.object.receiveShadow = true;
                    */

                    /*
                    self.maxDroneGeoVtx += self.droneMeshCache[drone.model].bodyGeometry.attributes.position.count;

                    if(self.droneMeshCache[drone.model].bodyGeometry.index)
                        self.maxDroneGeoIdx += self.droneMeshCache[drone.model].bodyGeometry.index.count;
                    */
                }
                if(child.name.startsWith('Wing')) {
                    self.droneMeshCache[drone.model].wings.push(wing);
                }

                if(child.name.startsWith('Pales')) {
                    
                    const pale = {name: child.name, instanceMesh : null, usedInstances : 0, nInstances : 0, paleGeo: child.geometry, center: new THREE.Vector3(), size : new THREE.Vector3()}

                    //const paleMesh = child.clone();;
                    //pale.geometry = child.geometry.clone();
                    
                    pale.paleGeo.computeBoundingBox();
                    pale.paleGeo.boundingBox.getSize(pale.size);
                    pale.paleGeo.boundingBox.getCenter(pale.center);
                    pale.paleGeo.translate( -pale.center.x, -pale.center.y, -pale.center.z);

                    pale.paleGeo.computeBoundingSphere();
                    pale.paleGeo.computeBoundingBox();

                    /*
                    paleMesh.castShadow = true;
                    paleMesh.receiveShadow = true;
                    */
                    pale.material = child.material;
                    pale.position = child.position.clone();

                    pale.rpos = pale.position.clone().sub(self.droneMeshCache[drone.model].meshCenter).add(pale.center);
                 

                    pale.txtgeo = new TextGeometry(pale.name, { font: self.impactFont, size: 0.1, height: 0.1, curveSegments: 4});
                    pale.txtgeo.computeBoundingSphere();
                    pale.txtgeo.translate(-pale.txtgeo.boundingSphere.radius, 0, 0);
                    pale.txtgeo.computeBoundingSphere();

                    /*
                    drone.object.castShadow = true;
                    drone.object.receiveShadow = true;
                    */
                    if (self.rtEngine){

                        const rtObj = child.clone();
                        rtObj.position.set(0,0,0);
                        rtObj.rotation.set(0,0,0);
                        rtObj.scale.set(drone.size.x / self.droneMeshCache[drone.model].droneSize.x, drone.size.y / self.droneMeshCache[drone.model].droneSize.y, drone.size.z / self.droneMeshCache[drone.model].droneSize.z)
                        pale.rtMesh = self.rtEngine.addMesh(rtObj, RT_COAT, new Transformation3(child.position, child.quaternion), true);
                    }
                        

                    /*
                    self.maxDroneGeoVtx += pale.paleGeo.attributes.position.count;

                    if( pale.paleGeo.index)
                        self.maxDroneGeoIdx += pale.paleGeo.index.count;
                    */

                    self.droneMeshCache[drone.model].pales.push(pale);
                }
            });

            const audioLoader = new THREE.AudioLoader();
            this.droneMeshCache[drone.model].buffer = await audioLoader.loadAsync(this.assetBase+ '/assets/snd/chopper.wav');
            this.droneMeshCache[drone.model].bufffer = await audioLoader.loadAsync(this.assetBase+ '/assets/snd/missile.wav');

        }

        this.droneMeshCache[drone.model].nInstances++;

        drone.droneSize = this.droneMeshCache[drone.model].droneSize.clone();
        drone.scale = new THREE.Vector3(drone.size.x / drone.droneSize.x, drone.size.y / drone.droneSize.y, drone.size.z / drone.droneSize.z);
        drone.rtMesh = [];

        for(let p of this.droneMeshCache[drone.model].wings){
            drone.wings.push(p);
        }

             
        for(let pale of this.droneMeshCache[drone.model].pales){

            const dpale = {rtMesh : []}

            //const paleMesh = pale.mesh;
            dpale.id = paleID ++;
            dpale.name = pale.name;

            dpale.rot = new THREE.Quaternion();
            

            dpale.rpos = new THREE.Vector3( pale.rpos.x * drone.scale.x, 
                                            pale.rpos.y * drone.scale.y,
                                            pale.rpos.z * drone.scale.z);


            dpale.pos = new THREE.Vector3().addVectors(drone.pos, dpale.rpos);

            dpale.radiusX = pale.size.x * drone.scale.x / 2;
            dpale.height = pale.size.y * drone.scale.y / 2;
            dpale.radiusZ = pale.size.z * drone.scale.z / 2;

            pale.nInstances++;

            if(drone.oPales && drone.oPales.length>0) {

                const nPales =  this.droneMeshCache[drone.model].pales.length;

                if(!drone.oPales[ nPales])
                    drone.oPales[ nPales] = {life: drone.oPales[0].life}

                dpale.life = drone.oPales[ nPales].life;
            }
            else
                dpale.life = -1;

            //paleMesh.position.set (pale.pos.x, pale.pos.y, pale.pos.z);

            dpale.hitParts = this.createParticles(spark_params, dpale.pos);
            if(dpale.hitParts){
                dpale.hitParts.pinned = true;
                dpale.hitParts.name = 'pale sparks';
            }

            const subsystem = {id : dpale.id, name : pale.name, pos: dpale.pos.clone(), txt : new THREE.Mesh(pale.txtgeo, this.targetSubSystemMat)};
            drone.target.subsystems.push(subsystem);
            drone.pales.push(dpale);
        }

        drone.lastShot = 0;

        //drone.object.scale.set(drone.size.x, drone.size.x, drone.size.x);

        if(this.hasAudio){


            drone.positionalAudio = new THREE.PositionalAudio( this.AudioListener );
            drone.positionalAudio.setBuffer( this.droneMeshCache[drone.model].buffer );
            drone.positionalAudio.setRefDistance( 2.0 );
            drone.positionalAudio.setRolloffFactor  (2.0);
            drone.positionalAudio.setMaxDistance ( 500.0 );

            drone.shotSndBuffer = this.droneMeshCache[drone.model].bufffer;
        }
        
        for(let w of drone.weapons){

            w.projPts.x *= drone.scale.x;
            w.projPts.y *= drone.scale.y;
            w.projPts.z *= drone.scale.z;

            if(w.projPart === 'smoke')
                w.projPartParams = smoke_params;
            else if(w.projPart === 'dronefire')
                w.projPartParams = dronefire_params;
            else if(w.projPart === 'bullet')
                w.projPartParams = bullet_params            
            else
                w.projPartParams = null;

            if(w.projModel){
                //const obj = await this.loadObj(w.projModel);

                const gltf = await this.loadGLTF(w.projModel);
                const obj = await this.deGroupGLB(gltf);

                obj.traverse((child) => {

                    if (!child.isMesh) 
                        return;

                    w.shotObject = child.clone();
                    w.shotObject.castShadow = true;
                    w.shotObject.frustumCulled = false;
                });

            }else{
                
                w.shotSphere = {};

                this.addSphere(w.shotSphere,
                    w.projSize,
                    new THREE.Vector3(0, 0, 0),
                    new THREE.Vector3(0.8, 0.8, 0.2),
                    new THREE.Vector3(0.8, 0.8, 0.2),
                    0.2,
                    RT_SPEC
                );

                w.shotSphere.object.name = 'drone '+drone.model+' shot';
                this.setMeshStatic(w.shotSphere, false);
                w.shotSphere.frustumCulled = false;

                if(this.rtEngine){
                    w.shotSphere.rtSphere = {};
                    this.rtEngine.addSphere(w.shotSphere.rtSphere, w.projSize, new THREE.Vector3(0, 0, 0), new THREE.Vector3(0.8, 0.8, 0.2), new THREE.Vector3(0.8, 0.8, 0.2), 0.2, RT_SPEC);
                }
            }
        }

   

    }

    async makeInterestPoint(ip, object = null)
    {
        if (object === null) {

            object = await this.loadObj('ip');
        }

        object.traverse((child) => {

            if (child.type == 'Mesh') {
                ip.object = child.clone();

                ip.object.geometry.computeBoundingBox();

                console.log('ip');
                console.log(ip.object.geometry);

                ip.object.material = child.material;
                ip.object.castShadow = true;
                ip.object.receiveShadow = false;
            }
        });

        if((ip.lobject)&&(ip.lobject.length>0)) {
            const obj = await this.loadObj(ip.lobject);

            obj.traverse((child) => {

                if (child.type == 'Mesh') {
                    ip.lmesh = child;
                    ip.lmesh.castShadow = true;
                    ip.lmesh.receiveShadow = false;
                }
            });
    
        } else {
            ip.lg = new THREE.BoxGeometry(1, 1, 1, 1, 1, 1);
            ip.lmat = new THREE.MeshBasicMaterial({color:'#888'});
            ip.lmesh = new THREE.Mesh(ip.lg, ip.lmat);
            ip.lmesh.name ='ip object';
        }

        const size = new THREE.Vector3();

        ip.object.geometry.computeBoundingBox();

         ip.object.geometry.boundingBox.getSize(size);

        ip.object.position.set(ip.pos.x, ip.pos.y, ip.pos.z);
        ip.object.rotation.set(ip.angles.x, ip.angles.y, ip.angles.z);
        ip.object.scale.set(ip.size.x, ip.size.y, ip.size.z );
        ip.object.updateMatrix();

        ip.lmesh.geometry.computeBoundingBox();
        ip.lmesh.geometry.boundingBox.getSize(size);

        const lp = new THREE.Vector3(ip.loffset.x, ip.loffset.y, ip.loffset.z).applyMatrix4(ip.object.matrix);
        ip.lmesh.position.set(lp.x,lp.y,lp.z);
        ip.lmesh.rotation.set(ip.angles.x, ip.angles.y, ip.angles.z);
        ip.lmesh.scale.set(ip.lsize / size.x , ip.lsize / size.y, ip.lsize / size.z );

        /*ip.object.add(ip.lmesh);*/
    }

    getBoneData(bone, array, offset){

        array[offset + 0] = bone.pos.x;
        array[offset + 1] = bone.pos.y;
        array[offset + 2] = bone.pos.z;
    
        array[offset + 3] = bone.rot.x;
        array[offset + 4] = bone.rot.y;
        array[offset + 5] = bone.rot.z;
        array[offset + 6] = bone.rot.w;
    
        offset += 7;
    
        for(let child of bone.children){
          offset = this.getBoneData(child, array, offset);
        }
    
        return offset;
    }

    setBoneData(bone, array, offset){

        bone.pos.x = array[offset + 0];
        bone.pos.y = array[offset + 1];
        bone.pos.z = array[offset + 2] ;
    
        bone.rot.x = array[offset + 3];
        bone.rot.y = array[offset + 4];
        bone.rot.z = array[offset + 5];
        bone.rot.w = array[offset + 6];
        offset+=7;
    
        for(let child of bone.children){
          offset = this.setBoneData(child, array, offset);
        }

        return offset;
    
      }

      getSpiderSubSystem(bone, subsystems, id = 0){

        if(bone.name.startsWith('leg')||bone.name.startsWith('fleg')||bone.name.startsWith('body')){
            subsystems.push({id : id, name : bone.name, pos : new THREE.Vector3()});
            id++;
        }

        for(let c of bone.children){
            id = this.getSpiderSubSystem(c, subsystems, id);
        }

        return id;
    }


    updateSpiderSubSystem(bone, subsystems, id = 0){

        if(bone.name.startsWith('leg')||bone.name.startsWith('fleg')||bone.name.startsWith('body')){
            subsystems[id].pos.x = bone.obj.position.x;
            subsystems[id].pos.y = bone.obj.position.y;
            subsystems[id].pos.z = bone.obj.position.z;
            id++;
        }

        for(let c of bone.children){
            id = this.updateSpiderSubSystem(c, subsystems, id);
        }

        return id;


    }

    async makeSpider(spider) {

        spider.object = await this.loadSpider(spider.model);


        /*
        spider.object.scale.x = spider.size.x;
        spider.object.scale.y = spider.size.y;
        spider.object.scale.z = spider.size.z;
        */


        spider.skel = this.getSpiderSkel(spider.object);
        spider.nBones = this.getNbones(spider.skel.root);
        spider.bindPose = new Float32Array(spider.nBones * 7);

        let ofs = 0;
        this.getBoneData(spider.skel.root, spider.bindPose, ofs);

        spider.object.position.x = spider.pos.x;
        spider.object.position.y = spider.pos.y;
        spider.object.position.z = spider.pos.z;

        spider.object.rotation.x = spider.angles.x;
        spider.object.rotation.y = spider.angles.y;
        spider.object.rotation.z = spider.angles.z;

        spider.target = {id : this.targetID++, pos: spider.object.position.clone(), type : 'spider', name : 'spider-'+spider.id, subsystems: [], txt: null};
        const geo = new TextGeometry(spider.target.name, { font: this.impactFont, size: 0.1, height: 0.1, curveSegments: 4});
        geo.computeBoundingSphere();
        geo.translate(-geo.boundingSphere.radius, 0, 0);

        spider.target.txt = new THREE.Mesh(geo, this.targetMat);
        this.getSpiderSubSystem(spider.skel.root, spider.target.subsystems, 1);


        spider.shotSphere = {};

        this.addSphere( spider.shotSphere,
                        spider.projSize,
                        new THREE.Vector3(0, 0, 0),
                        new THREE.Vector3(0.8, 0.8, 0.2),
                        new THREE.Vector3(0.8, 0.8, 0.2),
                        0.2,
                        RT_SPEC
                    );
        this.setMeshStatic(spider.shotSphere, false);

        spider.shotSphere.object.name='spider shot '+spider.model;

        if(this.rtEngine){
            spider.shotSphere.rtSphere = {};
            this.rtEngine.addSphere(spider.shotSphere.rtSphere, spider.projSize, new THREE.Vector3(0, 0, 0), new THREE.Vector3( 0.8, 0.8, 0.2), new THREE.Vector3(0.8, 0.8, 0.2), 0.2, RT_SPEC);
        }else
            spider.shotSphere.rtSphere = null;

    }

    
    async makeTurret(turret) {
        const name = turret.model ? turret.model : "turret";
        let object = null;

        if(name.endsWith('glb')){
            const gltf = await this.loadGLTF(name);
            object = this.deGroupGLB(gltf);
        }else {
            object = await this.loadObj(name);
        }

        

        object.traverse((child) => {

            if (child.type == 'Mesh') {

                if (child.name === 'body') {
                    turret.object = child;
                    //turret.object.material = child.material;
                    turret.object.castShadow = true;
                    turret.object.receiveShadow = true;

                    turret.object.geometry.computeBoundingSphere();
                    turret.object.geometry.computeBoundingBox();
                    const bodySize = new THREE.Vector3();
                     turret.bodyCenter = new THREE.Vector3();

                    turret.object.geometry.boundingBox.getSize( bodySize);
                    turret.object.geometry.boundingBox.getCenter( turret.bodyCenter);
                    turret.object.geometry.translate(- turret.bodyCenter.x, - turret.bodyCenter.y, - turret.bodyCenter.z);

                    turret.bodyRadius = turret.object.geometry.boundingSphere.radius;
                    turret.bodyHeight = bodySize.y;

                    turret.object.rotation.x = turret.angles.x;
                    turret.object.rotation.y = turret.angles.y;
                    turret.object.rotation.z = turret.angles.z;

                    turret.object.scale.x = turret.size.x;
                    turret.object.scale.y = turret.size.y;
                    turret.object.scale.z = turret.size.z;
                }
                if (child.name === 'head') {
                    turret.head = child;
                    //turret.head.material = child.material;

                    turret.head.geometry.computeBoundingSphere();
                    turret.head.geometry.computeBoundingBox();

                    const headSize = new THREE.Vector3();
                    turret.headCenter  = new THREE.Vector3();

                    turret.head.geometry.boundingBox.getSize(headSize);
                    turret.head.geometry.boundingBox.getCenter(turret.headCenter);
                    turret.head.geometry.translate( -turret.headCenter.x, -turret.headCenter.y, -turret.headCenter.z);

                    turret.headRadius = headSize.y  / 2;
                    turret.headHeight = headSize.x;

                    turret.head.scale.x = turret.size.x;
                    turret.head.scale.y = turret.size.y;
                    turret.head.scale.z = turret.size.z;

                    turret.head.castShadow = true;
                    turret.head.receiveShadow = true;
                    
                }
                if (child.name === 'canon') {
                    turret.canon = child;

                    turret.canon.geometry.computeBoundingSphere();
                    turret.canon.geometry.computeBoundingBox();

                    const canonSize = new THREE.Vector3();
                    turret.canonCenter  = new THREE.Vector3();

                    turret.canon.geometry.boundingBox.getSize(canonSize);
                    turret.canon.geometry.boundingBox.getCenter(turret.canonCenter);
                    turret.canon.geometry.translate(-turret.canonCenter.x, -turret.canonCenter.y, 0);
                    turret.canon.geometry.rotateY(Math.PI);

                    turret.cannonRadius = Math.max(canonSize.y, canonSize.x) / 2;
                    turret.cannonHeight = canonSize.z;

                    turret.canon.castShadow = true;
                    turret.canon.receiveShadow = true;

                }
            }
        });

        const geo = new TextGeometry('turret-'+turret.id,  { font: this.impactFont, size: 0.1, height: 0.1, curveSegments: 4});
        geo.computeBoundingSphere();
        geo.translate(-geo.boundingSphere.radius, 0, 0);

       
        turret.dmgBar = this.createDMGBar(turret.pos, new THREE.Vector3(0, 4.2 * turret.size.y, 0.0), turret.life);
        
        turret.bodyPos = turret.object.position.clone().add( turret.bodyCenter).multiply(turret.size);
        turret.headPos = turret.headCenter.clone().add(turret.head.position);
        turret.cannonPos = new THREE.Vector3(turret.canonCenter.x, turret.canonCenter.y, 0).add(turret.canon.position);

        turret.target = {   id : this.targetID++, 
                            type : 'turret', 
                            name : 'turret-'+turret.id, 
                            pos: { x: turret.headPos.x * turret.size.x + turret.pos.x, y: turret.headPos.y  * turret.size.y + turret.pos.y, z :turret.headPos.z * turret.size.z + turret.pos.z}, 
                            txt: new THREE.Mesh(geo, this.targetMat), subsystems: []};        



        turret.object.position.copy(turret.pos).add(turret.bodyPos);
        turret.head.position.copy(turret.headPos).multiply(turret.size).add(turret.pos);
        turret.head.rotation.y = turret.projAngle;

        turret.canon.position.copy(turret.cannonPos).sub(turret.headPos);
        turret.canon.rotation.x = -turret.projAngle2;

         if(turret.projPart === 'smoke')
            turret.projPartParams = missile_params;
        else if(turret.projPart === 'dronefire')
            turret.projPartParams = dronefire_params;
        else if(turret.projPart === 'bullet')
            turret.projPartParams = bullet_params            
        else
            turret.projPartParams = null;

        if ((turret.shotModel) && (turret.shotModel.length > 0)) {

            const shotObject = await this.loadObj(turret.shotModel);

            shotObject.traverse(function(child){
                if(!child.isMesh)
                    return;

                turret.shotObject = child;

                turret.shotObject = child;
                turret.shotObject.geometry.computeBoundingBox();
                const gs = new THREE.Vector3();
                const gc = new THREE.Vector3();
                turret.shotObject.geometry.boundingBox.getSize(gs);
                turret.shotObject.geometry.boundingBox.getCenter(gc);
                turret.shotObject.geometry.translate(-gc.x,-gc.y, -gc.z);
                turret.shotObject.geometry.scale(turret.projSize /gs.x,turret.projSize /gs.y,turret.projSize /gs.z);

            });

        }else{
            
            turret.shotSphere = {};

            this.addSphere(turret.shotSphere,
                turret.projSize,
                new THREE.Vector3(0, 0, 0),
                new THREE.Vector3(0.8, 0.8, 0.2),
                new THREE.Vector3(0.8, 0.8, 0.2),
                0.2,
                RT_SPEC
             );

             this.setMeshStatic(turret.shotSphere.object, false);

             turret.shotSphere.object.name ='turret '+turret.model+' shot';

             if(this.rtEngine){
                turret.shotSphere.rtSphere = {};
                this.rtEngine.addSphere(turret.shotSphere.rtSphere, turret.projSize, new THREE.Vector3(0, 0, 0), new THREE.Vector3(0.8, 0.8, 0.2), new THREE.Vector3(0.8, 0.8, 0.2), 0.2, RT_SPEC);
             }
        }

        
        const audioLoader = new THREE.AudioLoader();
        audioLoader.load( this.assetBase+'/assets/snd/'+turret.projSND, function( buffer ) {
            turret.shotSndBuffer = buffer;
        });
    }




    updateShots(obj , shotLerper, dt, time, r = 0.8, g = 0.8, b = 0.2, partParams = null){

        let j = 0;
        let existingIDs = [];
        
        let projSize = 0;
        let shotSphere = 0;
        let shotObject = 0;
        let weapon = null;
        while (j < obj.shots.length) {

            let fnd = shotLerper.getInterpolatedValue(obj.shots[j].id, time, this.tmpTransformation, tmpV31);

            if (!fnd) {

                if(obj.shots[j].particles){
                    obj.shots[j].particles.pinned = false;
                    obj.shots[j].particles.emitterAlive = false;
                    obj.shots[j].particles = null;
                }

                if(obj.shots[j].isSphere)
                    this.removeSphere(obj.shots[j], false);
                else{
                    this.removeMesh(obj.shots[j].object);
                }
                obj.shots.splice(j, 1);
            } else {

                if(obj.weapons){

                    let min = 100000;
               
                    for(let wi=0;wi<obj.weapons.length;wi++){

                        const w  = obj.weapons[wi];
                        const np = new THREE.Vector3(w.projPts.x, w.projPts.y, w.projPts.z).applyQuaternion(obj.rot).add(obj.pos);
                        const d = np.distanceTo(this.tmpTransformation.position);
        
                        if(d<min){
                            weapon= w;
                            min = d;
                        }
                    }

                }else{
                    weapon = obj.carDef? obj.carDef.weapon : null;
                }

                projSize = weapon ? weapon.projSize : obj.projSize;
                shotSphere = weapon ? weapon.shotSphere : obj.shotSphere;
                shotObject = weapon ? weapon.shotObject : obj.shotObject;

                existingIDs.push(obj.shots[j].id);

                if( obj.shots[j].particles){
                    obj.shots[j].particles.positionBase.set(this.tmpTransformation.position.x, this.tmpTransformation.position.y, this.tmpTransformation.position.z);
                    obj.shots[j].particles.positionRadius = Math.max(0, obj.shots[j].particles.positionRadius -dt * 0.5);
                    //obj.shots[j].particles.velocityBase.set(tmpV31.x / this.deltaTime * 0.75, tmpV31.y/ this.deltaTime * 0.75, tmpV31.z/ this.deltaTime * 0.75);
                }

                if(obj.shots[j].isSphere ){
                    this.setSpherePosition(obj.shots[j], this.tmpTransformation.position.x, this.tmpTransformation.position.y, this.tmpTransformation.position.z, projSize);

                    if((this.rtEngine)&&(shotSphere)){
                        shotSphere.rtSphere.meshGPU.object.position.x = this.tmpTransformation.position.x;
                        shotSphere.rtSphere.meshGPU.object.position.y = this.tmpTransformation.position.y;
                        shotSphere.rtSphere.meshGPU.object.position.z = this.tmpTransformation.position.z;
                        this.rtEngine.drawMesh(shotSphere.rtSphere.meshGPU);
                    }

                    if(obj.shots[j].object)
                        obj.shots[j].object.updateMatrixWorld(true);

                }else {
                    if(obj.shots[j].object.position){
                        obj.shots[j].object.position.set(this.tmpTransformation.position.x, this.tmpTransformation.position.y, this.tmpTransformation.position.z);
                        obj.shots[j].object.quaternion.set(this.tmpTransformation.quaternion.x, this.tmpTransformation.quaternion.y, this.tmpTransformation.quaternion.z, this.tmpTransformation.quaternion.w);

                        if(obj.projRoll === true)
                            obj.shots[j].object.rotation.x = time * 5;
                    }
                    this.updateTransform(obj.shots[j].object, this.tmpTransformation);
                }
                j++;
            }
        }

        obj.newShots = 0;

        for (let i = 0; i < shotLerper.nElements; i++) {
            const sid = parseInt(shotLerper.array[i * 8 + 0]);
                        
            if (existingIDs.indexOf(sid) < 0) {

                var newshot = { id: sid };

                shotLerper.getInterpolatedValue(sid, time, this.tmpTransformation, tmpV31);

                if(obj.weapons){

                    let min = 100000;
               
                    for(let wi=0;wi<obj.weapons.length;wi++){

                        const w  = obj.weapons[wi];
                        const np = new THREE.Vector3(w.projPts.x, w.projPts.y, w.projPts.z).applyQuaternion(obj.rot).add(obj.pos);
                        const d = np.distanceTo(this.tmpTransformation.position);
        
                        if(d<min){
                            weapon= w;
                            min = d;
                        }
                    }
                 

                }else{
                    weapon = obj.weapon;
                }

                projSize = weapon ? weapon.projSize : obj.projSize;
                shotSphere = weapon ? weapon.shotSphere : obj.shotSphere;
                shotObject = weapon ? weapon.shotObject : obj.shotObject;

                obj.lastShot = time;
                let pos = null;

                if (shotObject) {

                    newshot.isSphere = false;
                    newshot.object = this.addMeshInstance(shotObject, RT_SPEC, this.tmpTransformation);
                    this.setMeshStatic(newshot.object, false);

                    pos = newshot.object.position ? newshot.object.position: new THREE.Vector3(newshot.object.transformations[0].position.x, newshot.object.transformations[0].position.y, newshot.object.transformations[0].position.z);

                }else{

                    newshot.isSphere = true;

                    if(shotSphere.meshGPU){
                        shotSphere.meshGPU.primitives[0].material.emissive.r=r;
                        shotSphere.meshGPU.primitives[0].material.emissive.g=g;
                        shotSphere.meshGPU.primitives[0].material.emissive.b=b;
                    }
                    else
                        shotSphere.mat.emissive.setRGB(r, g, b);
                        
                    this.addSphereInstance(newshot, shotSphere, this.tmpTransformation.position.x, this.tmpTransformation.position.y, this.tmpTransformation.position.z);

                    pos = new THREE.Vector3(this.tmpTransformation.position.x, this.tmpTransformation.position.y, this.tmpTransformation.position.z);
                }

                if(partParams){
                    newshot.particles = this.createParticles(partParams, pos, true);
                    newshot.particles.name = 'shot-smoke';
                }
                
                if(weapon){
                    if(weapon.particles){
                        if(!weapon.particles.emitterAlive){
                            weapon.particles.emitterAge = 0;
                            weapon.particles.emitterAlive = true;
                            weapon.particles.positionBase.set(this.tmpTransformation.position.x, this.tmpTransformation.position.y, this.tmpTransformation.position.z).applyQuaternion(obj.object.quaternion).add(obj.object.position);
                        }
                    }

                }else{
                    if(obj.particles){
                        if(!obj.particles.emitterAlive){
                            obj.particles.emitterAge = 0;
                            obj.particles.emitterAlive = true;
                            obj.particles.positionBase.set(obj.projPts.x, obj.projPts.y, obj.projPts.z).applyQuaternion(obj.object.quaternion).add(obj.object.position);
                        }
                    }
                }

                obj.shots.push(newshot);
                obj.newShots++;
            }
        }
    }



    updateCars(world, time, dt) {

        const cars = world.lastCarData.cars;
        const characters = world.lastCarData.characters;
        const npcs = world.lastCarData.npcs;
        const woods = world.lastCarData.woods;
        const ips = world.lastCarData.ips;
        const bonuses = world.lastCarData.bonuses;
        const turrets = world.lastCarData.turrets;
        const springs = world.lastCarData.springs;
        const ropes = world.lastCarData.ropes;
        const spiders = world.lastCarData.spiders;
        const drones = world.lastCarData.drones;
        const MO = world.lastCarData.movingObjects;
        const cyls = world.lastCarData.cyls;
        const cubes = world.lastCarData.cubes;
        const xmeshes = world.lastCarData.xmeshes;
        const splits = world.lastCarData.splits;
        const blastImpacts = world.lastCarData.blastImpacts;

        const sphere = new THREE.Sphere();
        

        const ids = [];

        for (let n = 0; n < npcs.length; n++) {

            const npc = npcs[n];
            
            if(!npc.active)
                continue;

            const wnpc = world.track.npcs[n];
            if(!wnpc)
                continue;

            ids.push(wnpc.id);

            if(wnpc.dialog){
                if(wnpc.dialogOptions){
                    wnpc.dialogOptions.lookAt(this.cameraAnchor.position);
                }

                if((npcs[n].dialogLine!=wnpc.dialogInstance.line)||(npcs[n].dialogSubLine!=wnpc.currentDialogLine)){
                    wnpc.dialogInstance.line = npcs[n].dialogLine;
                    wnpc.currentDialogLine = npcs[n].dialogSubLine;

                    if(wnpc.dialogOptions)
                        this.updateDialog(wnpc);
                    else
                        this.OpenDialog(wnpc);
                }
            }

            if(wnpc.avatarMesh){

                if(!wnpc.avatarMesh.parent)
                    this.addMesh(wnpc.avatarMesh);

                npc.lerper.getInterpolatedValue(0, time , this.tmpTransformation);

                this.tmpTransformation.position.y += wnpc.worldOffset.y;

                if((wnpc.nInstances <= 1)){

                    wnpc.avatarMesh.position.set(this.tmpTransformation.position.x, this.tmpTransformation.position.y, this.tmpTransformation.position.z);
                    wnpc.avatarMesh.quaternion.set(this.tmpTransformation.quaternion.x, this.tmpTransformation.quaternion.y, this.tmpTransformation.quaternion.z, this.tmpTransformation.quaternion.w);

                    wnpc.avatarMesh.updateMatrix();
                    this.updateTransform(wnpc.avatarMesh, this.tmpTransformation, wnpc.instanceID);
    
                }else{

                    if((!wnpc.avatarMesh.meshGPU)&&(!wnpc.avatarMesh.skeleton)){
                        this.mt.set(this.tmpTransformation.position.x, this.tmpTransformation.position.y , this.tmpTransformation.position.z);
                        this.mq.set(this.tmpTransformation.quaternion.x, this.tmpTransformation.quaternion.y, this.tmpTransformation.quaternion.z, this.tmpTransformation.quaternion.w);
                        this.scaleVec.set(1,1,1);
    
                        this.m.identity();
                        this.m.compose(this.mt, this.mq, this.scaleVec);
                        wnpc.avatarMesh.setMatrixAt(wnpc.instanceID, this.m);

                        wnpc.avatarMesh.instanceMatrix.needsUpdate = true;
                    }else{
                        wnpc.avatarMesh.position.set(this.tmpTransformation.position.x, this.tmpTransformation.position.y, this.tmpTransformation.position.z);
                        wnpc.avatarMesh.quaternion.set(this.tmpTransformation.quaternion.x, this.tmpTransformation.quaternion.y, this.tmpTransformation.quaternion.z, this.tmpTransformation.quaternion.w);
                    }

                    this.updateTransform(wnpc.avatarMesh, this.tmpTransformation, wnpc.instanceID);
                }

                wnpc.pos.x = this.tmpTransformation.position.x;
                wnpc.pos.y = this.tmpTransformation.position.y;
                wnpc.pos.z = this.tmpTransformation.position.z;

                wnpc.rot.x = this.tmpTransformation.quaternion.x;
                wnpc.rot.y = this.tmpTransformation.quaternion.y;
                wnpc.rot.z = this.tmpTransformation.quaternion.z;
                wnpc.rot.w = this.tmpTransformation.quaternion.w;

                if(wnpc.avatarDef.ragdoll === true){

                    if(wnpc.tskel){
                        this.interpolateRagDoll(wnpc.tskel                      , wnpc.skeleton.root, npc.ragdollLerper, time, wnpc.instanceID);
                    }else{
                        this.interpolateRagDoll(wnpc.avatarMesh.meshGPU.rootBone, wnpc.skeleton.root, npc.ragdollLerper, time, wnpc.instanceID);
                    }
                    /*
                    if(this.rtEngine){
                        this.rtEngine.updateSkinnedMesh(wnpc.avatarMesh, this.renderer);
                    }
                    */
                    
                    for(let s of wnpc.splitMesh){

                        npc.ragdollLerper.getInterpolatedValue(s.skel.root.idx, time, this.tmpTransformation);
                        s.mesh.position.set(this.tmpTransformation.position.x, this.tmpTransformation.position.y, this.tmpTransformation.position.z);

                        if(s.mesh.meshGPU){
                            s.mesh.meshGPU.transformations[0].set(this.tmpTransformation.position, this.tmpTransformation.quaternion);
                            this.interpolateRagDoll(s.mesh.meshGPU.rootBone, s.skel.root.children[0], npc.ragdollLerper, time);
                        } else{
                            s.mesh.skeleton.bones[0].quaternion.set(this.tmpTransformation.quaternion.x, this.tmpTransformation.quaternion.y, this.tmpTransformation.quaternion.z, this.tmpTransformation.quaternion.w);

                            if(s.skel.root.children.length>0)
                                this.interpolateRagDoll(s.mesh.skeleton, s.skel.root.children[0], npc.ragdollLerper, time);
                        }
                    }

                    for(let g=0;g<world.track.terrain.grasses.length;g++){

                        const grass= world.track.terrain.grasses[g];

                        if(wnpc.skeleton){
                            const bones= ['foot-left', 'foot-right', 'hand-left', 'hand-right'];

                            sphere.radius = 8.0;

                            for(let bone of bones){

                                let fnd = false;

                                for(let i=0;i<wnpc.nInstances;i++){

                                    if((wnpc.avatarMesh.skeleton)){
                                        let foot = wnpc.avatarMesh.skeleton.getBoneByName(bone);
                                        if(foot){
                                            fnd  = true;
                                            sphere.center.set(foot.matrixWorld.elements[12], foot.matrixWorld.elements[13], foot.matrixWorld.elements[14]);
                                        }
                                            
                                    }
                                    
                                    if((wnpc.avatarMesh.meshGPU)&&(wnpc.avatarMesh.meshGPU.rootBone)){
                                        let foot = wnpc.avatarMesh.meshGPU.rootBone.findBone(bone);
                                        if(foot){
                                            fnd = true;
                                            sphere.center.set(foot.transforms[i].position.x, foot.transforms[i].position.y, foot.transforms[i].position.z);
                                        }
                                    }

                                    if(fnd){
                                        grass.testPoint(sphere, time);
                                    }
                                        
                                }
                            }
                        }
                    }

                    if(wnpc.tskel)
                        this.updateNPCSubSystem(wnpc.skeleton.root, wnpc.tskel);
                    else
                        this.updateNPCSubSystem(wnpc.skeleton.root, wnpc.avatarMesh.meshGPU.rootBone);
                    
                    if(wnpc.avatarMesh.meshGPU)
                        wnpc.avatarMesh.meshGPU.updateBones = true;
                    //this.updateRagDoll(wnpc);
                }else{
                    wnpc.activeAnims = [];
                    for(let a of npc.anims){
                        wnpc.activeAnims.push(a);
                    }

                    for(let g=0;g<world.track.terrain.grasses.length;g++){

                        sphere.radius = 8.0;
                        sphere.center.copy(wnpc.pos);

                        world.track.terrain.grasses[g].testPoint(sphere, time);
                    }
                }
            }
        }

        if(world.lastCarData.flags & 1){
            for (let n = 0; n < world.track.npcs.length; n++) {
                const widx = ids.indexOf(world.track.npcs[n].id);
                if((widx<0)&&(world.track.npcs[n].avatarMesh.parent)){
                    this.removeMesh(world.track.npcs[n].avatarMesh);
                }
            }
        }

        for(let n=0;n<world.lastCarData.nBlastImpacts;n++){
            const impact = world.lastCarData.blastImpacts[n];
            const p = new THREE.Vector3(impact.pos.x, impact.pos.y, impact.pos.z);
            let settings = null;
            let sample = null;

            switch(impact.type){
                case 1:
                    settings = impact_params;
                break;
                case 2:
                    settings = bomb_params;
                    sample = 'bombexpl';
                break;
                case 8:
                    settings = null;
                    sample = 'collision';
                break;
            }

            const particles = settings ? this.createParticles(settings, p) : null;            

            if((this.sndMeshes.length<16)&&(sample !== null)&&(this.audioSamples[sample])){
                const positionalAudio = new THREE.PositionalAudio( this.AudioListener );
                positionalAudio.setBuffer(this.audioSamples[sample].buffer );
                positionalAudio.setRefDistance( 4.0 );
                positionalAudio.setRolloffFactor  (1.0);
                positionalAudio.setMaxDistance ( 100.0 );
                positionalAudio.setDistanceModel('exponential');
                positionalAudio.getOutput().connect(this.sfxGainNode);
                                
                if(particles){
                    particles.particleMesh.add(positionalAudio);
                }else{
                    const sndMesh = new THREE.Group();
                    sndMesh.name = 'positional audio '+sample;
                    sndMesh.position.set(p.x, p.y, p.z);
                    this.scene.add(sndMesh);
                    sndMesh.add(positionalAudio);
                    this.sndMeshes.push(sndMesh);
                }
                positionalAudio.play();
            }
        }
    
        let n=0;
        while(n<this.sndMeshes.length){
            const snd = this.sndMeshes[n].children[0];
            if(!snd.isPlaying){
                this.scene.remove(this.sndMeshes[n]);
                snd.getOutput().disconnect();
                this.sndMeshes.splice(n, 1);
            }else
                n++;
        }

        world.lastCarData.nBlastImpacts=0;
        world.myChar = null;
        
        for (let n = 0; n < characters.length; n++) {

            mychar = world.track.characters[n];

            for (let i = 0; i < world.characters.length; i++) {
                if (world.characters[i].netID == characters[n].netID) {
                    world.myChar = world.characters[i];
                    break;
                }
            }

            if((mychar === null)&&(!characters[n].loading)){
                characters[n].loading = true;
                
                fetch('/assets/chars/avatar.json').then((res)=>{
                    res.json().then((charDef)=>{
                        mychar = new Character(characters[n].netID, charDef , world.myCar); 

                        mychar.anims = [];
                        
                        world.track.loadAnim(mychar.avatarDef.idleAnim).then((a) => {
                            mychar.anims[mychar.avatarDef.idleAnim]=a;
                        });

                        world.track.loadAnim(mychar.avatarDef.walkAnim).then((a) => {
                            mychar.anims[mychar.avatarDef.walkAnim]=a;
                        });

                        world.track.loadAnim(mychar.avatarDef.waveAnim).then((a) => {
                            mychar.anims[mychar.avatarDef.waveAnim]=a;
                        });

                        world.track.loadAnim(mychar.avatarDef.talkAnim).then((a) => {
                            mychar.anims[mychar.avatarDef.talkAnim]=a;
                        });

                        mychar.active = true;
                        world.characters.push(mychar);
                    });
                })
            }

            if((mychar)&&(mychar.avatarMesh)){
                mychar.avatarMesh.position.set(characters[n].pos.x, characters[n].pos.y, characters[n].pos.z);
                mychar.avatarMesh.quaternion.set(characters[n].quat.x, characters[n].quat.y, characters[n].quat.z, characters[n].quat.w);

                this.tmpTransformation.set(mychar.avatarMesh.position, mychar.avatarMesh.quaternion);
                this.updateTransform(mychar.avatarMesh, this.tmpTransformation);

                mychar.animId = characters[n].animId;
                mychar.animTime =  characters[n].animTime;
            }


            const ismychar = (mychar.netID === world.myCar.servId) ? true : false;
            if(!characters[n].active){

                this.removeChar(mychar);
                world.characters.splice(wid,1);
                characters.splice(n, 1);

                if(ismychar) {
                    this.removeCharEvents();
                    this.setupCarEvents();
                }
                break;

            }else if ((mychar.avatarMesh == null )&&(!mychar.loading)){

                mychar.loading = true;

                if(ismychar)
                    this.removeCarEvents();

                this.addChar(mychar).then(() => {

                    if(ismychar){
                        world.myChar = mychar;
                        this.setupCharEvents();
                    }
                });
            }

            if(mychar.animId == 0) {
                this.applyAnimation(mychar, mychar.anims[mychar.avatarDef.idleAnim], mychar.animTime, myChar.instanceID);
            }
            if(mychar.animId == 1) {
                this.applyAnimation(mychar, mychar.anims[mychar.avatarDef.walkAnim], mychar.animTime, myChar.instanceID);
            }
        }


        

        if(this.rtEngine){
            this.rtEngine.resetSpheres();
        }


        
        for (let n = 0; n < cars.length; n++) {

            let mycar = null;

            for (let i = 0; i < world.cars.length; i++) {
                if (world.cars[i].servId == cars[n].id) {
                    mycar = world.cars[i];
                    break;
                }
            }


            
            mycar.curLapTime = cars[n].curLapTime
            mycar.remainingTime = world.track.lapTime - cars[n].curLapTime;
            mycar.speed = cars[n].speed;
            mycar.RPM = cars[n].RPM;
            mycar.damage = cars[n].damage;
            mycar.power = cars[n].power;
            const trigger = cars[n].triggerIDX <0 ? null : world.track.triggers[cars[n].triggerIDX];

            if((world.triggerObject != null) && ((trigger === null)|| (mycar.trigger === null) || (trigger.type !== mycar.trigger.type))){
                this.removeMesh(world.triggerObject.obj);

                for(let mn =0;mn<world.track.terrainGroup.material.length;mn++) {
                    if(world.track.terrainGroup.material[mn].name === 'trigger-'+world.triggerObject.trig.type){
                        world.track.terrainGroup.material[mn].color.setRGB(1,1,1);
                        world.track.terrainGroup.material[mn].needsUpdate = true;
                    }
                }

                world.triggerObject = null;
            }

            mycar.trigger = trigger;
            mycar.blueprints = cars[n].blueprints;

            mycar.newAttachedObjects = cars[n].newAttachedObjects;
            mycar.curSeg = parseInt(cars[n].curSeg);

            let lapChanged = false;

            if(cars[n].nLaps != mycar.nLaps)
                lapChanged = true;
                
            mycar.nLaps = parseInt(cars[n].nLaps);

            mycar.selectedTargetId = cars[n].targetID;
            mycar.selectedSubsystemId = cars[n].targetSID;

            const mytarget = world.getCarTarget( mycar,  mycar.selectedTargetId,  mycar.selectedSubsystemId);
            if(( mytarget)&&(mytarget.type =='npc')){
            
                for(let npc of world.track.npcs) {

                    if(npc.id == mytarget.eid) {
                        if(npc.dialog){
                            if(npc.selectedAnswer!= mycar.selectedSubsystemId){
                                npc.selectedAnswer= mycar.selectedSubsystemId;

                                if(npc.selectedAnswer<0)
                                    this.removeDialog(npc);
                                else{


                                    if(npc.dialogOptions)
                                        this.updateDialog(npc);
                                    else
                                        this.OpenDialog(npc);
                                }
                                    
                            }
                        }
                    }
                }
            }
            

            if(mycar.weapon){
                mycar.weapon.head.rotation.y = cars[n].weapon.headAngle;
                mycar.weapon.canon.rotation.x = cars[n].weapon.cannonAngle;

                this.tmpTransformation.set(mycar.weapon.headRel, mycar.weapon.head.quaternion);
                this.updateTransform(mycar.weapon.head, this.tmpTransformation);

                this.tmpTransformation.set(mycar.weapon.canonRel, mycar.weapon.canon.quaternion);
                this.updateTransform(mycar.weapon.canon, this.tmpTransformation);
            }


            mycar.nitros = cars[n].nitros;
            mycar.targetNode = cars[n].targetNode;
            mycar.nextNitro = cars[n].nextNitro;
            mycar.nBonus = cars[n].nbonuses;
            mycar.nTechs = cars[n].ntechs;
            
            mycar.multiplier = cars[n].multiplier;

            cars[n].lerper.getInterpolatedValue(0, time , this.tmpTransformation);
            
            mycar.ChassisMesh.position.set(this.tmpTransformation.position.x, this.tmpTransformation.position.y, this.tmpTransformation.position.z);
            mycar.ChassisMesh.quaternion.set(this.tmpTransformation.quaternion.x, this.tmpTransformation.quaternion.y, this.tmpTransformation.quaternion.z, this.tmpTransformation.quaternion.w);
            this.updateTransform(mycar.ChassisMesh, this.tmpTransformation);

            mycar.pos.x = this.tmpTransformation.position.x;
            mycar.pos.y = this.tmpTransformation.position.y;
            mycar.pos.z = this.tmpTransformation.position.z;

            this.updateShots(mycar , cars[n].shotLerper, dt, time,  0, 1, 1, mycar.projParts);
    
            for (let i = 0; i < cars[n].wheels.length; i++) {

                cars[n].wheelsLerper.getInterpolatedValue(i, time , this.tmpTransformation);

                if (mycar.wheels[i].mesh) {
                    mycar.wheels[i].mesh.position.set(this.tmpTransformation.position.x, this.tmpTransformation.position.y, this.tmpTransformation.position.z);
                    mycar.wheels[i].mesh.quaternion.set(this.tmpTransformation.quaternion.x, this.tmpTransformation.quaternion.y, this.tmpTransformation.quaternion.z, this.tmpTransformation.quaternion.w);
                    mycar.wheels[i].mesh.updateMatrix();
                    this.updateTransform(mycar.wheels[i].mesh, this.tmpTransformation);
                }

                
                for(let grass of world.track.terrain.grasses){
                    sphere.radius = 4.0;
                    sphere.center.set(this.tmpTransformation.position.x, this.tmpTransformation.position.y - mycar.wheelRadius, this.tmpTransformation.position.z);
                    grass.testPoint(sphere, time);
                }

                mycar.wheels[i].skid = 1.0 - cars[n].wheels[i].skidding;
                mycar.wheels[i].inContact= cars[n].wheels[i].inContact;
                mycar.wheels[i].contactPoint.set(cars[n].wheels[i].contactPoint.x, cars[n].wheels[i].contactPoint.y, cars[n].wheels[i].contactPoint.z);

                if((this.enableParallax) && (world.track.trackPoints.length>1)){
                    tmpV31.set(mycar.wheels[i].contactPoint.x, mycar.wheels[i].contactPoint.y + 1.0 , mycar.wheels[i].contactPoint.z);
                    tmpV32.set(0, -1, 0);
                    this.rc.set(tmpV31, tmpV32);
                    const pts = this.rc.intersectObject(world.track.roadObj);
                    if(pts.length>0){
                        mycar.wheels[i].contactUv.set(pts[0].uv3.x, pts[0].uv3.y);
                    }
                }

                if (mycar.wheels[i].skid > 0.0) {
                    if ((time - world.lastScreech) > 0.1) {

                        this.playAudio('screech', mycar.wheels[i].skid / 40.0);
                        world.lastScreech = time;
                    }
                    
                    //}
                }
                cars[n].wheels[i].lastSkid = time;
            }

            for (let i = 0; i < cars[n].ncontacts; i++) {

                mycar.addContact(cars[n].contacts[i], cars[n].contactsDMG[i], time);

                if(mycar.servId == cars[n].id)
                    this.playAudio('impact', Math.sqrt(cars[n].contactsDMG[i]) / 4.0);
                
                mycar.lastContact = time;
            }
            cars[n].ncontacts = 0;

            for (let i = 0; i < world.lastCarData.cars[n].nnewBonuses; i++) { 

                const Txt = new TextGeometry(world.lastCarData.cars[n].newbonus[i].toString(), { font: this.speedFont, size: 1, height: 0.05, curveSegments: 4, bevelEnabled: true, bevelThickness: 0.1, bevelSize: 0.01, bevelOffset: 0, bevelSegments: 1 });
                Txt.computeBoundingSphere();
                Txt.translate(- Txt.boundingSphere.radius, 0, 0);
        
                const txtMat = new THREE.MeshBasicMaterial({ color: '#0F0', transparent: true, side : THREE.DoubleSide});
                const bMesh = new THREE.Mesh(Txt, txtMat);

                bMesh.position.set(0, 0, 3);
                //bMesh.meshGPU = this.addMesh(bMesh, RT_DIFF, new Transformation3(bMesh.position, bMesh.quaternion));
                
                this.addMeshChild(world.cars[n].ChassisMesh, bMesh);
                this.setMeshStatic(bMesh, false);
                world.track.newBonuses.push(bMesh);
            }

            for (let i = 0; i < world.lastCarData.cars[n].nnewTech; i++) {

                const Txt = new TextGeometry(world.lastCarData.cars[n].newtech[i].toString(), { font: this.speedFont, size: 1, height: 0.05, curveSegments: 4, bevelEnabled: true, bevelThickness: 0.1, bevelSize: 0.01, bevelOffset: 0, bevelSegments: 1 });
                Txt.computeBoundingSphere();
                Txt.translate(- Txt.boundingSphere.radius, 0, 0);
        
                const txtMat = new THREE.MeshBasicMaterial({ color: '#00F', transparent: true, side : THREE.DoubleSide});
                const bMesh = new THREE.Mesh(Txt, txtMat);

                bMesh.position.set(0, 0, 3);
                //bMesh.meshGPU = this.addMesh(bMesh, RT_DIFF, new Transformation3(bMesh.position, bMesh.quaternion));

                
                this.addMeshChild(world.cars[n].ChassisMesh, bMesh);
                this.setMeshStatic(bMesh, false);
                world.track.newBonuses.push(bMesh);
            }

            if(world.cars[n].nameMesh){
                world.cars[n].nameMesh.position.set(world.cars[n].ChassisMesh.position.x, world.cars[n].ChassisMesh.position.y + 2.4, world.cars[n].ChassisMesh.position.z);
                world.cars[n].nameMesh.lookAt(this.cameraAnchor.position);

                this.tmpTransformation.set(world.cars[n].nameMesh.position, world.cars[n].nameMesh.quaternion);
                this.updateTransform(world.cars[n].nameMesh, this.tmpTransformation);
            }

            /*
            world.cars[n].DMGBar1.position.set(world.cars[n].ChassisMesh.position.x, world.cars[n].ChassisMesh.position.y + 2.0, world.cars[n].ChassisMesh.position.z);
            world.cars[n].DMGBar2.position.set(world.cars[n].ChassisMesh.position.x, world.cars[n].ChassisMesh.position.y + 2.0, world.cars[n].ChassisMesh.position.z);
            world.cars[n].DMGBar1.scale.set(1.0 - (world.myCar.damage / world.myCar.maxdmg),1,1)
            world.cars[n].DMGBar1.lookAt(this.cameraAnchor.position);
            world.cars[n].DMGBar2.lookAt(this.cameraAnchor.position);
            */
    
            //this.tmpTransformation.set(this.camera.position, this.camera.quaternion);
            //this.tmpTransformation.getInverse(this.t);

            for(let wi=0;wi<world.cars[n].wheels.length;wi++){

                const w = world.cars[n].wheels[wi];
                w.parts.positionBase.set(w.mesh.position.x, w.mesh.position.y - world.cars[n].wheelRadius, w.mesh.position.z);
                w.parts.velocityBase.set(0,0,1).applyQuaternion(mycar.ChassisMesh.quaternion).multiplyScalar(5);
                
                //w.parts.velocityBase.set(v.x, v.y, v.z);
                w.parts.particlesPerSecond = 100.0 * w.skid;

                w.parts.colorBase.set(world.track.tubecolor.r, world.track.tubecolor.g, world.track.tubecolor.b);
                w.parts.colorTween.values[0].set(world.track.tubecolor.r, world.track.tubecolor.g, world.track.tubecolor.b);
                w.parts.update(dt);
            }

            if (world.lastCarData.cars[n].nextNitro > 3000) {
                const d = (5000 - world.lastCarData.cars[n].nextNitro) / 2000;
                tmpV31.set(0, 0, -2.8).applyQuaternion(world.cars[n].ChassisMesh.quaternion).add(world.cars[n].ChassisMesh.position);

                let nitro = {};

                //this.addSphere(nitro, (d + 1.0) / 30.0, offset, new THREE.Vector3(1.0, 0.2, 0.0).multiplyScalar(0.1), new THREE.Vector3(1.0, 0.1, 0.0).multiplyScalar(0.1), 0.1, RT_POINT_LIGHT)
                this.addSphere(nitro, (d + 1.0) / 3, tmpV31, this.nitroColor1, this.nitroColor2, 0.1, RT_POINT_LIGHT)

                nitro.object.name= 'nitro sphere';
                this.setMeshStatic(nitro.object, false);

                if(this.renderMode == 0 )
                    nitro.object.material.blending = THREE.AdditiveBlending;
    
                nitro.start = time;
                world.cars[n].isNitro = true;
                world.cars[n].nitroSpheres.push(nitro);
            }
            else
                world.cars[n].isNitro = false;

            let i = 0;

            while (i < world.cars[n].nitroSpheres.length) {
                const nitro = world.cars[n].nitroSpheres[i];

                if ((time - nitro.start) > 0.3) {
                    this.removeSphere(nitro);
                    world.cars[n].nitroSpheres.splice(i, 1);
                    continue;
                } 

                let radius = 0;

                if ((time - nitro.start) < 0.2) {

                    radius = 0.5 - (time - nitro.start) * 2.5;

                    this.moveSpherePosition(nitro, (Math.random() - 0.5) * 0.1, dt * 10.8 + Math.random() * 0.1, (Math.random() - 0.5) * 0.1, radius);
                    this.setSphereColor(nitro, 1.0 - (time - nitro.start) * 5, null, null, 0.25 - (time - nitro.start) * 1.25);
                    this.setSphereEmissive(nitro, null, (time - nitro.start) * 2.5, null);

                }else{
                    radius = 0.2;
                    const ndt = (time - nitro.start) - 0.2;
                    this.moveSpherePosition(nitro, (Math.random() - 0.5) * 0.1, dt * 1.8 + Math.random() * 0.1, (Math.random() - 0.5) * 0.1, 0.2);
                    this.setSphereEmissive(nitro, 0, 0, 0);
                    this.setSphereColor(nitro, 0.2 - ndt*0.2, 0.2 - ndt*0.2, 0.2 - ndt*0.2, 0.15);
                }

                if(this.rtEngine){
                        
                    rtContactSphere.object.position.set(nitro.object.position.x, nitro.object.position.y, nitro.object.position.z);

                    rtContactSphere.mat.color.r = nitro.object.material.color.r;
                    rtContactSphere.mat.color.g = nitro.object.material.color.g;
                    rtContactSphere.mat.color.b = nitro.object.material.color.b;
                    
                    rtContactSphere.sphere.emission.r = nitro.object.material.emissive.r * 4;
                    rtContactSphere.sphere.emission.g = nitro.object.material.emissive.g * 4;
                    rtContactSphere.sphere.emission.b = nitro.object.material.emissive.b * 4;
                    rtContactSphere.sphere.radius = radius;

                    this.rtEngine.drawMesh(rtContactSphere);
                }
                
                    //nitro.mat.opacity = 1.0 - (time - nitro.start) * 5;
                    //nitro.mat.color.r = 1.0 - (time - nitro.start) * 5;
                    //nitro.object.scale.x = 1.0 - (time - nitro.start) * 5;
                    //nitro.object.scale.y = 1.0 - (time - nitro.start) * 5;
                    //nitro.object.scale.z = 1.0 - (time - nitro.start) * 5;
                    //nitro.object.position.y += dt * 10.8;
                    
                    i++;
                
            }
        }

        for(let n=0;n<world.lastCarData.nTriggerEvents;n++) {

            const event = world.lastCarData.triggerEvents[n];
            const trigger = world.track.triggers[event.triggerIDX];

            if(trigger.type === "computer") {

                if(event.state === 1){

                    if(world.triggerObject){
                        this.removeMesh(world.triggerObject.obj);
                        world.triggerObject = null;
                    }

                    if(world.states['computer'] !== true) {
                        for(let mn =0;mn<world.track.terrainGroup.material.length;mn++) {
                            if(world.track.terrainGroup.material[mn].name === 'liftlight'){
                                world.track.terrainGroup.material[mn].emissive.setRGB(0,1,0);
                                world.track.terrainGroup.material[mn].needsUpdate = true;
                                world.states['computer'] = true;
                            }
                        }
                    }
                }else{

                    for(let mn =0;mn<world.track.terrainGroup.material.length;mn++) {
                        if(world.track.terrainGroup.material[mn].name === 'trigger-computer'){
                            world.track.terrainGroup.material[mn].color.setRGB(1,0,0);
                            world.track.terrainGroup.material[mn].needsUpdate = true;
                        }
                    }

                    const LightningBox = new THREE.Mesh(this.LightningBoxGeom, this.LightningBoxMat);
                    LightningBox.position.set(trigger.pos.x, trigger.pos.y + 2.0, trigger.pos.z);
                    this.addMesh(LightningBox);
                    world.triggerObject = {obj : LightningBox, trig : trigger};
                }
            }
        }
        world.lastCarData.nTriggerEvents=0;


        for(let n=0;n<world.cars.length;n++) {

            const car = world.cars[n];
            const lpos = tmpV31.set(0.0, 0.0, car.chassisLength / 2 + 0.01).applyQuaternion(car.ChassisMesh.quaternion);
            const ldir = tmpV32.set(0.0, 0.0, -1.0).applyQuaternion(car.ChassisMesh.quaternion);

            switch(car.lightState){
                case 2:
                    this.setLightPosition(car.CarLight, lpos.x + car.ChassisMesh.position.x, lpos.y + car.ChassisMesh.position.y + 0.4, lpos.z + car.ChassisMesh.position.z, 45.5);
                    this.setLightDir(car.CarLight, ldir.x, ldir.y, ldir.z, 0.25);
                    this.setLightColor(car.CarLight, 1, 1, 0.5);
                break;
                case 1:
                    this.setLightPosition(car.CarLight, lpos.x + car.ChassisMesh.position.x, lpos.y + car.ChassisMesh.position.y + 0.4, lpos.z + car.ChassisMesh.position.z, 20.5);
                    this.setLightDir(car.CarLight, ldir.x, ldir.y, ldir.z, 0.15);
                    this.setLightColor(car.CarLight, 0.5, 0.5, 0.25);
                break;
                case 0:
                    this.setLightPosition(car.CarLight, lpos.x + car.ChassisMesh.position.x, lpos.y + car.ChassisMesh.position.y + 0.4, lpos.z + car.ChassisMesh.position.z, 0);
                    this.setLightDir(car.CarLight, ldir.x, ldir.y, ldir.z, 0.15);
                    this.setLightColor(car.CarLight, 0, 0, 0);
                break;
            }

            if((car.RPM)&&(car.exhaustParticles)) {
                car.getExhaustPos(car.exhaustParticles.particles.positionBase);
                car.exhaustParticles.particles.particlesPerSecond = car.RPM * 90 / 8000.0 + 10.0;
            }
            
            /*
            tmpT.set(car.ChassisMesh.position, car.ChassisMesh.quaternion)
            this.updateTransform(car.ChassisMesh, tmpT);

            for(let w of car.wheels){
                tmpT.set(w.mesh.position, w.mesh.quaternion);
                this.updateTransform(w.mesh, tmpT);
            }
            */
        
            if(car.contactMesh){
                const contactLT = 1;
                for (let n = 0;n < 100;n++) {

                    this.m.identity();

                    if(n>=car.ncontacts){
                        this.scaleVec.set(0,0,0);
                        this.m.scale(this.scaleVec);
                        car.contactMesh.setMatrixAt(n, this.m);
                        continue;
                    }

                    const sdt = time - car.contacts[n].time;

                    car.contacts[n].pos.y -= 1 * dt;
                    car.contacts[n].pos.x += Math.random() * 0.1 - 0.05;
                    car.contacts[n].pos.z += Math.random() * 0.1 - 0.05;

                    if (sdt > contactLT) {

                        this.scaleVec.set(0,0,0);
                        this.m.scale(this.scaleVec);
                        car.contactMesh.setMatrixAt(n, this.m);
                        car.ncontacts--;

                        //car.contacts.splice(n, 1);
                    } else {

                        const ratio = sdt / contactLT;

                        this.scaleVec.set(1.0 - ratio, 1.0 - ratio, 1.0 - ratio);

                        this.m.makeTranslation(car.contacts[n].pos.x, car.contacts[n].pos.y, car.contacts[n].pos.z);
                        this.m.scale(this.scaleVec);

                        this.colorMix.lerpVectors(this.color1, this.color2, ratio);
                        this.color.setRGB(this.colorMix.x, this.colorMix.y, this.colorMix.z);

                        car.contactMesh.setMatrixAt(n, this.m);
                        car.contactMesh.setColorAt(n, this.color);

                        if(this.rtEngine){

                            rtContactSphere.object.position.set(car.contacts[n].pos.x, car.contacts[n].pos.y, car.contacts[n].pos.z);

                            rtContactSphere.mat.color.r = this.color.r;
                            rtContactSphere.mat.color.g = this.color.g;
                            rtContactSphere.mat.color.b = this.color.b;
                            
                            rtContactSphere.sphere.emission.r = this.color.r;
                            rtContactSphere.sphere.emission.g = this.color.g;
                            rtContactSphere.sphere.emission.b = this.color.b;
                            rtContactSphere.sphere.radius = 0.1 *(1.0 - ratio);

                            this.rtEngine.drawMesh(rtContactSphere);
                        }

                        
                        //car.contacts[n].object.material.color.r = 1.0;
                        //car.contacts[n].object.material.color.g = dt;
                        //car.contacts[n].object.scale.set(1.0 - dt, 1.0 - dt, 1.0 - dt);

                        //this.setSphereColor(car.contacts[n], 1.0, 0.5 + (1.0-dt), dt *2.0);
                        //this.setSpherePosition(car.contacts[n], car.contacts[n].pos.x, car.contacts[n].pos.y, car.contacts[n].pos.z, (1.0 - dt) * 0.03);
                    }
                }

                car.contactMesh.instanceMatrix.needsUpdate = true;

                if(car.contactMesh.instanceColor)
                    car.contactMesh.instanceColor.needsUpdate = true;
            }

            //if(world.track.trackPoints.length > 1)
            if(this.enableParallax === true) {

                for(let i=0;i<car.wheels.length;i++){
                    if( car.wheels[i].skid>0){
                        if(world.track.trackPoints.length<=1){
                            const x = this.worldToGroundUvX(cars[n].wheels[i].contactPoint.x - world.track.ground.position.x);
                            const y = this.worldToGroundUvZ(cars[n].wheels[i].contactPoint.z - world.track.ground.position.z);
                            this.drawCircle(x, y, 255.0 * car.wheels[i].skid);
                        }else
                            this.drawCircle(car.wheels[i].contactUv.x, car.wheels[i].contactUv.y, 64.0 * car.wheels[i].skid);
                    }                
                }
            }
        }

        /*$('#user-bonus-multiplier').html('x ' + world.myCar.multiplier);*/

        let c = 0;
        for (let n = 0; n < world.track.woods.length; n++) {
            for (let i = 0; i < world.track.woods[n].objects.length; i++) {
                woods.lerper.getInterpolatedValue(c, time , this.tmpTransformation )
                this.setCylinderPos(world.track.woods[n].objects[i], world.track.woods[n].radius, this.tmpTransformation.position, world.track.woods[n].width, this.tmpTransformation.quaternion);
                c++;
            }
        }

        for (let n = 0; n < world.track.interestPoints.length; n++) {
            ips.lerper.getInterpolatedValue(n, time , this.tmpTransformation );
            world.track.interestPoints[n].lmesh.position.set(this.tmpTransformation.position.x, this.tmpTransformation.position.y, this.tmpTransformation.position.z);
            world.track.interestPoints[n].lmesh.quaternion.set(this.tmpTransformation.quaternion.x, this.tmpTransformation.quaternion.y, this.tmpTransformation.quaternion.z, this.tmpTransformation.quaternion.w);
            this.updateTransform(world.track.interestPoints[n].lmesh, this.tmpTransformation);
        }

        if (drones.length > 0) {

            for (let n = 0; n < world.track.drones.length; n++) {
                drones[n].lerper.getInterpolatedValue(0, time , this.tmpTransformation );
                /*
                world.track.drones[n].object.position.set(this.tmpTransformation.position.x, this.tmpTransformation.position.y, this.tmpTransformation.position.z);
                world.track.drones[n].object.quaternion.set(this.tmpTransformation.quaternion.x, this.tmpTransformation.quaternion.y, this.tmpTransformation.quaternion.z, this.tmpTransformation.quaternion.w);
                */


                this.tmpQ.set(this.tmpTransformation.quaternion.x, this.tmpTransformation.quaternion.y, this.tmpTransformation.quaternion.z, this.tmpTransformation.quaternion.w);
                tmpV31.set(this.tmpTransformation.position.x, this.tmpTransformation.position.y, this.tmpTransformation.position.z);
                this.scaleVec.set(1,1,1);
                this.tmpM.identity();
                this.tmpM.compose(tmpV31, this.tmpQ, this.scaleVec);
                world.track.drones[n].object.setMatrixAt( world.track.drones[n].instanceID, this.tmpM );
                world.track.drones[n].object.instanceMatrix.needsUpdate = true;
                world.track.drones[n].positionalAudio.matrix.copy(this.tmpM);

                if(this.rtEngine){
                    this.rtEngine.updateTransform(world.track.drones[n].rtMesh[world.track.drones[n].instanceID], this.tmpTransformation);
                }
                

                //this.updateTransform(world.track.drones[n].object, this.tmpTransformation);

                world.track.drones[n].target.pos.x = this.tmpTransformation.position.x;
                world.track.drones[n].target.pos.y = this.tmpTransformation.position.y;
                world.track.drones[n].target.pos.z = this.tmpTransformation.position.z;

                this.updateShots(world.track.drones[n], drones[n].shotLerper, dt, time, 0.8, 0.6, 0.2, world.track.drones[n].weapons ? world.track.drones[n].weapons[0].projPartParams : null);

                for (let p = 0; p < world.track.drones[n].pales.length; p++) {
                    const pale = world.track.drones[n].pales[p];

                    drones[n].palesLerper.getInterpolatedValue(p, time , this.tmpTransformation );

                    this.tmpEul.set(0, time * Math.PI, 0);
                    this.tmpQ2.setFromEuler(this.tmpEul);
                    
                    this.tmpQ.set(this.tmpTransformation.quaternion.x, this.tmpTransformation.quaternion.y, this.tmpTransformation.quaternion.z, this.tmpTransformation.quaternion.w);
                    this.tmpQ.multiply(this.tmpQ2);

                    tmpV31.set(this.tmpTransformation.position.x, this.tmpTransformation.position.y, this.tmpTransformation.position.z);
                    this.scaleVec.set(1,1,1);
                    this.tmpM.identity();
                    this.tmpM.compose(tmpV31, this.tmpQ, this.scaleVec);
                    pale.mesh.setMatrixAt( pale.instanceID, this.tmpM );

                    if(this.rtEngine){
                        this.rtEngine.updateTransform(pale.rtMesh[pale.instanceID], this.tmpTransformation);
                    }


                   pale.mesh.instanceMatrix.needsUpdate = true;

                    /*
                    pale.mesh.position.set(this.tmpTransformation.position.x, this.tmpTransformation.position.y, this.tmpTransformation.position.z);
                    pale.mesh.quaternion.set(this.tmpTransformation.quaternion.x, this.tmpTransformation.quaternion.y, this.tmpTransformation.quaternion.z, this.tmpTransformation.quaternion.w);
                    this.updateTransform(pale.mesh, this.tmpTransformation);
                    */

                    this.updateDMGBar(pale.dmgBar, this.tmpTransformation.position, drones[n].pales[p].life );
                    
                    pale.hitParts.positionBase.set(this.tmpTransformation.position.x, this.tmpTransformation.position.y, this.tmpTransformation.position.z);
                    pale.hitParts.particlesPerSecond = 30 * (pale.dmgBar.maxlife - drones[n].pales[p].life) / pale.dmgBar.maxlife;



                    /*
                    pale.DMGBar1.position.set(this.tmpTransformation.position.x, this.tmpTransformation.position.y + 2.2, this.tmpTransformation.position.z);
                    pale.DMGBar2.position.set(this.tmpTransformation.position.x, this.tmpTransformation.position.y + 2.2, this.tmpTransformation.position.z);

                    pale.DMGBar1.lookAt(this.cameraAnchor.position);
                    pale.DMGBar2.lookAt(this.cameraAnchor.position);

                    pale.DMGBar1.scale.set(drones[n].pales[p].life / pale.life , 1, 1 );

                    this.tmpTransformation.set(pale.DMGBar1.position, pale.DMGBar1.quaternion);
                    this.updateTransform(pale.DMGBar1, this.tmpTransformation);
                    this.updateTransform(pale.DMGBar2, this.tmpTransformation);

                    */

                    world.track.drones[n].target.subsystems[p].pos.x = this.tmpTransformation.position.x;
                    world.track.drones[n].target.subsystems[p].pos.y = this.tmpTransformation.position.y;
                    world.track.drones[n].target.subsystems[p].pos.z = this.tmpTransformation.position.z;
                }
            }
        }

        if (turrets.length > 0) {

            for (let n = 0; n < world.track.turrets.length; n++) {

                
                turrets[n].lerper.getInterpolatedValue(0, time , this.tmpTransformation);

                world.track.turrets[n].head.position.set(this.tmpTransformation.position.x, this.tmpTransformation.position.y, this.tmpTransformation.position.z);
                world.track.turrets[n].head.quaternion.set(this.tmpTransformation.quaternion.x, this.tmpTransformation.quaternion.y, this.tmpTransformation.quaternion.z, this.tmpTransformation.quaternion.w);
                this.updateTransform(world.track.turrets[n].head, this.tmpTransformation);

                world.track.turrets[n].life = turrets[n].life
                this.updateDMGBar( world.track.turrets[n].dmgBar, this.tmpTransformation.position, world.track.turrets[n].life);

                /*
                world.track.turrets[n].headAngle = world.lastCarData.turrets[n].headAngle  - Math.PI/2;
                this.tmpEul.set(0, world.track.turrets[n].headAngle, 0, "XYZ");
                this.tmpQ.setFromEuler(this.tmpEul);
                world.track.turrets[n].head.quaternion.set(this.tmpQ.x, this.tmpQ.y, this.tmpQ.z, this.tmpQ.w);
                this.tmpVec3.set(world.track.turrets[n].object.position.x, world.track.turrets[n].object.position.y + world.track.turrets[n].projPts.y * world.track.turrets[n].size.y, world.track.turrets[n].object.position.z);
                this.tmpTransformation.set(this.tmpVec3, this.tmpQ);
                this.updateTransform(world.track.turrets[n].head, this.tmpTransformation);
                */
                
                this.updateShots(world.track.turrets[n], turrets[n].shotLerper, dt, time, 0.8, 0.6, 0.2, world.track.turrets[n].projPartParams);
            }
        }

        if (bonuses.length > 0) {

            //console.log(bonuses)

            for (let n = 0; n < world.track.bonuses.length; n++) {
                world.track.bonuses[n].taken = bonuses[n].taken;
                world.track.bonuses[n].scale = bonuses[n].scale;

                world.track.bonuses[n].object.scale.x = world.track.bonuses[n].size;
                world.track.bonuses[n].object.scale.y = world.track.bonuses[n].size;
                world.track.bonuses[n].object.scale.z = world.track.bonuses[n].size;

                if (bonuses[n].taken !== 0) {
                    if (world.track.bonuses[n].enabled === true) {
                        world.track.bonuses[n].object.visible = false;
                        world.track.bonuses[n].enabled = false;

                        this.playAudio('coin', 0.8);
                        //$('#user-bonus').html('x' + world.myCar.nBonus);
                    }
                }
                else if (world.track.bonuses[n].enabled === false) {
                    world.track.bonuses[n].object.visible = true;
                    world.track.bonuses[n].enabled = true;
                }
            }
        }

        if(spiders.length > 0 ){
            for (let n = 0; n < world.track.spiders.length; n++) {
                this.interpolateSpiderSkel(world.track.spiders[n].skel.root, spiders[n].lerper, time );
                this.updateShots(world.track.spiders[n], spiders[n].shotLerper, dt, time);

                for(let ss of world.track.spiders[n].target.subsystems) {

                    const bone = world.track.findSpiderBone(world.track.spiders[n].skel.root, ss.name);
                    if(!bone)  
                        continue;

                    ss.pos.x = bone.obj.position.x;
                    ss.pos.y = bone.obj.position.y;
                    ss.pos.z = bone.obj.position.z;
                }


                let s = 0;
                while(s < world.track.spiders[n].samples.length){

                    if(!world.track.spiders[n].samples[s].isPlaying){
                        world.track.spiders[n].object.remove(world.track.spiders[n].samples[s]);
                        world.track.spiders[n].samples[s].getOutput().disconnect();
                        world.track.spiders[n].samples.splice(s, 1);
                    }else
                        s++;
                }

                if(this.sfxGainNode){
                    for(let i=0;i<32;i+=4){
                        const mask = 0xF << i;
                        if((spiders[n].sndBits&mask)!=0){
    
                            const sndID = ((spiders[n].sndBits >> i) & 0xF) - 1;

                            if(sndID >= world.track.spiders[n].sounds.length)
                                continue;
    
                            const positionalAudio = new THREE.PositionalAudio( this.AudioListener );
                            positionalAudio.setBuffer(world.track.spiders[n].sounds[sndID].sndBuffer );
                            positionalAudio.setRefDistance( 4.0 );
                            positionalAudio.setRolloffFactor  (1.0);
                            positionalAudio.setMaxDistance ( 1000.0 );
                            positionalAudio.setDistanceModel('exponential');
                            positionalAudio.getOutput().connect(this.sfxGainNode);
                                
                            world.track.spiders[n].object.add(positionalAudio); 
                            positionalAudio.play();
                            world.track.spiders[n].samples.push(positionalAudio);
                        }
                    }
                }
                
                spiders[n].sndBits = 0;
            }
        }


        for (let n = 0; n < world.track.springs.length; n++) {
            springs.lerper.getInterpolatedValue(n, time , this.tmpTransformation);
            world.track.springs[n].obj.object.position.set(this.tmpTransformation.position.x, this.tmpTransformation.position.y, this.tmpTransformation.position.z);
            world.track.springs[n].obj.object.quaternion.set(this.tmpTransformation.quaternion.x, this.tmpTransformation.quaternion.y, this.tmpTransformation.quaternion.z, this.tmpTransformation.quaternion.w);
            this.updateTransform(world.track.springs[n].obj.object, this.tmpTransformation);
        }

        for (let n = 0; n < world.track.ropes.length; n++) {
            ropes.lerper.getInterpolatedValue(n, time , this.tmpTransformation);
            world.track.ropes[n].obj.object.position.set(this.tmpTransformation.position.x, this.tmpTransformation.position.y, this.tmpTransformation.position.z);
            world.track.ropes[n].obj.object.quaternion.set(this.tmpTransformation.quaternion.x, this.tmpTransformation.quaternion.y, this.tmpTransformation.quaternion.z, this.tmpTransformation.quaternion.w);
            this.updateTransform(world.track.ropes[n].obj.object, this.tmpTransformation);
        }

        for (let n = 0; n < world.track.terrain.cyls.length; n++) {

            if(!cyls.lerper.active[n])
                continue;

            cyls.lerper.getInterpolatedValue(n, time , this.tmpTransformation);

            if(world.track.terrain.cyls[n].isCyl)
                this.setCylinderPos(world.track.terrain.cyls[n].mesh, world.track.terrain.cyls[n].radiusX, this.tmpTransformation.position, world.track.terrain.cyls[n].height, this.tmpTransformation.quaternion);
            else{

                if(world.track.terrain.cyls[n].name.startsWith('lamp'))
                    this.tmpTransformation.position.y -= 4;

                world.track.terrain.cyls[n].position.set(this.tmpTransformation.position.x, this.tmpTransformation.position.y, this.tmpTransformation.position.z);
                world.track.terrain.cyls[n].quaternion.set(this.tmpTransformation.quaternion.x, this.tmpTransformation.quaternion.y, this.tmpTransformation.quaternion.z, this.tmpTransformation.quaternion.w);
                this.updateTransform(world.track.terrain.cyls[n], this.tmpTransformation);
                world.track.terrain.cyls[n].updateMatrix();

                if(world.track.terrain.cyls[n].EngineMI) {
                    const mi = world.track.terrain.cyls[n].EngineMI;
                    mi.it.mesh.setMatrixAt(mi.idx, world.track.terrain.cyls[n].matrix);
                    mi.it.mesh.instanceMatrix.needsUpdate = true;
                }
                //world.track.terrain.cyls[n].updateWorldMatrix();
            }
        }

        for (let n = 0; n < world.track.terrain.cubes.length; n++) {

            if(!cubes.lerper.active[n])
                    continue;
            
            cubes.lerper.getInterpolatedValue(n, time , this.tmpTransformation);

            if(world.track.terrain.cubes[n].isCube)
                this.setCubePos(world.track.terrain.cubes[n].mesh, world.track.terrain.cubes[n].radiusX, this.tmpTransformation.position, world.track.terrain.cubes[n].height, this.tmpTransformation.quaternion);
            else{

                if(world.track.terrain.cubes[n].instanceOf){
                    this.updateTransform(world.track.terrain.cubes[n].instanceOf, this.tmpTransformation, world.track.terrain.cubes[n].instanceID);
                }else{

                    if(!world.track.terrain.cubes[n].meshGPU){
                        world.track.terrain.cubes[n].position.set(this.tmpTransformation.position.x, this.tmpTransformation.position.y, this.tmpTransformation.position.z);
                        world.track.terrain.cubes[n].quaternion.set(this.tmpTransformation.quaternion.x, this.tmpTransformation.quaternion.y, this.tmpTransformation.quaternion.z, this.tmpTransformation.quaternion.w);
                        world.track.terrain.cubes[n].updateMatrix();
                    }
                    this.updateTransform(world.track.terrain.cubes[n], this.tmpTransformation);
                }
                
                if(world.track.terrain.cubes[n].EngineMI) {
                    world.track.terrain.cubes[n].updateMatrix();
                    const mi = world.track.terrain.cubes[n].EngineMI;
                    mi.it.mesh.setMatrixAt(mi.idx, world.track.terrain.cubes[n].matrix);
                    mi.it.mesh.instanceMatrix.needsUpdate = true;
                }

                //mc.EngineMI = {name : lname, idx : track.meshInstances[lname].nFilled};

                //world.track.terrain.cubes[n].updateWorldMatrix(true);
            }
        }
        
        for(let i=0;i<world.track.splits.length;i++)
        {
            const split = world.track.splits[i];

            //console.log('new split '+' a:'+splits[s +1].toFixed(1)+' b:'+splits[s +2].toFixed(1)+' c:'+splits[s +3].toFixed(1)+' d:'+splits[s +4].toFixed(1));

            const n = split.id;
            const newMesh = world.track.splitMesh( split.a, split.b, split.c, split.d, world.track.xmeshes[n]);

            this.updatePrimitives( world.track.xmeshes[n].mesh.meshGPU,  world.track.xmeshes[n].mesh.geometry);

            if(newMesh){
                newMesh.mesh.meshGPU = this.addMesh(newMesh.mesh);
                this.setMeshStatic(newMesh.mesh, false);
            }
        }

        world.track.splits = [];

        

        //world.lastCarData.splits = [];

        for (let n = 0; n < xmeshes.length; n++) {

            /*
            const xii = xmeshes[n].idx;
            if(world.track.xmeshes[xii].phyMesh){
                this.updateTransform(world.track.xmeshes[xii].phyMesh, new Transformation3(world.track.xmeshes[xii].mesh.position, world.track.xmeshes[xii].mesh.quaternion ));
            }
            */

            if(!xmeshes[n].lerper.active[0])
                    continue;
            
            xmeshes[n].lerper.getInterpolatedValue(0, time , this.tmpTransformation);

            const xi = xmeshes[n].idx;

            if(xi>=world.track.xmeshes.length)
                continue;

            if(world.track.xmeshes[xi] == null)
                continue;

            if(world.track.xmeshes[xi].mesh == null)
                continue;

             

            //console.log('xmesh '+xi+' a:'+this.tmpTransformation.position.x.toFixed(1)+' b:'+this.tmpTransformation.position.x.toFixed(1)+' c:'+this.tmpTransformation.position.x.toFixed(1));
      
            world.track.xmeshes[xi].mesh.position.set(this.tmpTransformation.position.x, this.tmpTransformation.position.y, this.tmpTransformation.position.z);
            world.track.xmeshes[xi].mesh.quaternion.set(this.tmpTransformation.quaternion.x, this.tmpTransformation.quaternion.y, this.tmpTransformation.quaternion.z, this.tmpTransformation.quaternion.w);
            this.updateTransform(world.track.xmeshes[xi].mesh, this.tmpTransformation);

       

            if(world.track.xmeshes[xi].meshBox){
                world.track.xmeshes[xi].meshBox.position.set(this.tmpTransformation.position.x, this.tmpTransformation.position.y, this.tmpTransformation.position.z);
                world.track.xmeshes[xi].meshBox.quaternion.set(this.tmpTransformation.quaternion.x, this.tmpTransformation.quaternion.y, this.tmpTransformation.quaternion.z, this.tmpTransformation.quaternion.w);
            }
        }
        
        let bi = 0;

        while(bi < world.track.newBonuses.length){

            const b = world.track.newBonuses[bi];

            if(b.material.opacity<=0){
               this.removeMesh(b);
               this.destroyObj(b);
                world.track.newBonuses.splice(bi, 1);
                continue;
            }

            b.lookAt(this.cameraAnchor.position.x,this.cameraAnchor.position.y,this.cameraAnchor.position.z);
            b.position.y += dt * 2;
            b.material.opacity -= dt * 0.5;

            this.tmpTransformation.set(b.position, b.quaternion);
            this.updateTransform(b, this.tmpTransformation);
            bi++;
        }

        for (let bonus of world.track.bonuses) {
            let q;
            
            if(bonus.type === 0)
                this.tmpEul.set(dt * 10.0, 0, 0);
            else
                this.tmpEul.set(0, dt * 10.0, 0,"XYZ");
            
            this.tmpQ.setFromEuler(this.tmpEul).premultiply(bonus.object.quaternion);    

            bonus.object.quaternion.set(this.tmpQ.x, this.tmpQ.y, this.tmpQ.z, this.tmpQ.w);
            this.tmpTransformation.set(bonus.object.position, bonus.object.quaternion);
            this.updateTransform(bonus.object, this.tmpTransformation);
        }


        for (let n = 0; n < world.characters.length; n ++) {
            if(world.characters[n].avatarMesh){
                this.tmpTransformation.set(world.characters[n].avatarMesh.position, world.characters[n].avatarMesh.quaternion);
                this.updateTransform(world.characters[n].avatarMesh, this.tmpTransformation);
            }
        }


        /*
        for (let n = 0; n < xmeshes.length; n++) {

            if(!xmeshes[n].lerper.active[0])
                    continue;
            
            xmeshes[n].lerper.getInterpolatedValue(0, time , this.tmpTransformation);
        }
        */

        const mok = Object.keys(world.track.movingObjects);
        

        for (let n=0;n<mok.length;n++) {

            const k =mok[n];

            for(let oi=0;oi<world.track.terrain.movingTerrain.length;oi++) {

                const o = world.track.terrain.movingTerrain[oi];
                const oname = o.name;
                const anchorName = 'Anchor-'+k;
                let nn = world.track.levelMovingObjects.indexOf(k);

                if(nn >=0 ){

                    if((o.name === k)||(o.name === anchorName)) {
                        o.quaternion.set(MO[nn].quat.x,MO[nn].quat.y,MO[nn].quat.z,MO[nn].quat.w);
                        break;
                        //continue;
                    }else{
                         this.updateBoneTransform(o, k, MO[nn].quat);
                    }
                    //q = MO[n].quat.clone();
                    
                } else {
                    const dira = k.split('-');
                    const axis = dira[1];

                    if(axis === 'x'){
                        this.tmpEul.set(Math.sin(time), 0 ,  0);
                        this.tmpQ.setFromEuler(this.tmpEul);
                    }else if(axis === 'y'){
                        this.tmpEul.set(0 , Math.sin(time), 0);
                        this.tmpQ.setFromEuler(this.tmpEul);
                    }else if(axis === 'z'){
                        this.tmpEul.set(0 , 0 , Math.sin(time));
                        this.tmpQ.setFromEuler(this.tmpEul);
                    }
                    this.updateBoneTransform(o, k, this.tmpQ);
                }
            }
        }
        
        for (let n = 0; n <npcs.length; n++) {
            const char = npcs[n];
            const wchar = world.track.npcs[n];
            
            if((this.enableRagDollDBG === true)&&(wchar.avatarDef.ragdoll))
                this.updateRagDollCyls(wchar);

            if(!wchar.avatarDef.ragdoll){
                
                for(let a of char.anims){
                    if(!a)
                        continue;
                    switch(a.a){
                        case 0:this.applyAnimation(wchar, wchar.anims[wchar.avatarDef.idleAnim], a.t, wchar.instanceID);break;
                        case 1:this.applyAnimation(wchar, wchar.anims[wchar.avatarDef.walkAnim], a.t, wchar.instanceID);break;
                        case 2:this.applyAnimation(wchar, wchar.anims[wchar.avatarDef.waveAnim], a.t, wchar.instanceID);break;
                    }
                }
            }
        }

        for (let i = 0; i < world.lastCarData.npcs.length; i++) {

            if(!world.track.npcs[i].skeleton)
                continue;

            let targetPos = null;

            if((world.myChar !== null)&&(world.track.npcs[i].pos.distanceTo(world.myChar.pos)<world.track.npcs[i].avatarDef.activation ))
                targetPos=world.myChar.pos;

            if((world.myCar !== null)&&(world.track.npcs[i].pos.distanceTo(world.myCar.pos)<world.track.npcs[i].avatarDef.activation * world.track.npcs[i].skelScale ))
                targetPos=world.myCar.pos;                        
          
            if(targetPos){

                if(world.track.npcs[i].dialog){

                    if(world.track.npcs[i].dialogOptions === null){
                        if(world.track.npcs[i].selectedAnswer<127)
                            this.OpenDialog(world.track.npcs[i]);
                    }else{
                        if(world.track.npcs[i].selectedAnswer==127)
                            this.removeDialog(world.track.npcs[i]);
                    }
                }
            }else{

                if(world.track.npcs[i].dialogOptions !== null){
                    this.removeDialog(world.track.npcs[i]);
                }

            }

            if(world.track.npcs[i].avatarDef.ragdoll)
                continue;
             
            if(targetPos){
          
                tmpV31.set(0,0,1).applyQuaternion(world.track.npcs[i].rot);
                tmpV32.subVectors(world.track.npcs[i].pos, targetPos);

                tmpV32.y = 0;
                tmpV32.normalize();

                this.mq.setFromUnitVectors(tmpV31 , tmpV32);
          
                this.mq.w = Math.max(-0.3, Math.min(0.3, this.mq.w));
                this.mq.normalize();

                this.tmpQ.set(this.mq.x, this.mq.y, this.mq.z, this.mq.w/2).normalize();

                if(world.track.npcs[i].tskel){
                    const bone = world.track.npcs[i].tskel.getBoneByName('head');
                    bone.quaternion.set(this.mq.x,this.mq.y,this.mq.z,this.mq.w);

                    const bone2 = world.track.npcs[i].tskel.getBoneByName('spine');
                    bone2.quaternion.set(this.tmpQ.x,this.tmpQ.y,this.tmpQ.z,this.tmpQ.w);
                }else  {

                    const omesh = world.track.npcs[i].instanceOf >= 0 ? world.track.npcs[world.track.npcs[i].instanceOf].avatarMesh.meshGPU : world.track.npcs[i].avatarMesh.meshGPU;

                    const bone = omesh.rootBone.findBone('head');
                    bone.transforms[ world.track.npcs[i].instanceID].set(null, this.mq);

                    const bone2 = omesh.rootBone.findBone('spine');
                    bone2.transforms[world.track.npcs[i].instanceID].set(null, this.tmpQ);
                }

            }else{

                if(world.track.npcs[i].tskel){
                    const bone = world.track.npcs[i].tskel.getBoneByName('head');
                    bone.quaternion.set(0,0,0,1);

                    const bone2 = world.track.npcs[i].tskel.getBoneByName('spine');
                    bone2.quaternion.copy(world.track.npcs[i].orig_spine);
                }else{

                    const omesh = world.track.npcs[i].instanceOf >= 0 ? world.track.npcs[world.track.npcs[i].instanceOf].avatarMesh.meshGPU : world.track.npcs[i].avatarMesh.meshGPU;

                    const bone = omesh.rootBone.findBone('head');
                    if(bone)
                        bone.transforms[world.track.npcs[i].instanceID].set(null, q);

                    const bone2 = omesh.rootBone.findBone('spine');
                    if(bone2)
                        bone2.transforms[world.track.npcs[i].instanceID].set(null, q);
                }
            }
        }


        if(this.rtEngine){
            for(let p of this.particles){

                if(p.pinned)
                    continue;

                if(!p.rtSphere){
                    p.rtSphere= {};
                    this.rtEngine.addSphere(p.rtSphere, 2, new THREE.Vector3(p.positionBase.x, p.positionBase.y, p.positionBase.z), new THREE.Vector3( p.colorBase.r, p.colorBase.g, p.colorBase.v), new THREE.Vector3(p.colorBase.r, p.colorBase.g, p.colorBase.v), 0.2, RT_SPEC);
                }
                this.rtEngine.drawMesh(p.rtSphere);
            }
        }
    }


    getAssetImage(item, type) {

        if (type == 'batteries') {
            if (item === null)
                return null;

            if (item.model == 'battery1')
                return 'battery1.gif';    
        }

        if (type == 'radars') {
            if (item === null)
                return null;

            if (item.model == 'radar1')
                return 'radar1.gif';    
        }
        
        if (type == 'cars') {

            if (item.chassis == 'car1')
                return 'car1.png';

            if (item.chassis == 'car2')
                return 'car2.png';

            if (item.chassis == 'car3'){

                let fileName = item.chassis;

                if(item.motor)
                    fileName += '_'+item.motor.motor;

                if(item.suspension)
                    fileName += '_'+item.suspension.tire;

                return fileName+'.gif';
            }
        
            if (item.chassis == 'car4'){

                let fileName = item.chassis;

                if(item.motor)
                    fileName += '_'+item.motor.motor;

                if(item.suspension)
                    fileName += '_'+item.suspension.tire;

                return fileName+'.gif';
            }

            if (item.chassis == 'wuf'){

                let fileName = item.chassis;

                if(item.motor)
                    fileName += '_'+item.motor.motor;

                if(item.suspension)
                    fileName += '_'+item.suspension.tire;

                return fileName+'.gif';
            }


            if (item.chassis == 'buggy'){
                let fileName = item.chassis;

                if(item.motor)
                    fileName += '_'+item.motor.motor;

                if(item.suspension)
                    fileName += '_'+item.suspension.tire;

                return fileName+'.gif';
            }

            if (item.chassis == 'davinci')
                return 'davinci.gif';

            if (item.chassis == 'car5')
                return 'car4.gif';



            if (item.chassis == 'buggyB')
                return 'buggyB.gif';

            if (item.chassis == 'lambo')
                return 'lambo.gif';

            if (item.chassis == 'terradyne')
                return 'terradyne.gif';
            return null;
        }

        if (type == 'tires') {

            if (item === null)
                return null;

            if (item.tire == 'tire')
                return 'tire.gif';                

            if (item.tire == 'tire1')
                return 'tire1.gif';

            if (item.tire == 'tire2')
                return 'tire2.gif';

            if (item.tire == 'tire3')
                return 'tire3.gif';

            if (item.tire == 'tirew')
                return 'tirew.gif';


            if (item.tire == 'tire4')
                return 'tire4.gif';

            if (item.tire == 'tire5')
                return 'tire5.gif';                

            if (item.tire == 'tire6')
                return 'tire6.gif';

            return null;
        }

        if (type == 'motors') {

            if (item === null)
                return null;

            if (item.motor == 'motor1')
                return 'motor1.gif';

            if (item.motor == 'motor2')
                return 'motor2.gif';

            return 'motor1.gif';
        }
    }

    removeTireBox() {

        if (this.tireCubeMesh) {
            this.removeMesh(this.tireCubeMesh);
            this.tireCubeMesh.geometry.dispose();
            this.tireCubeMesh.material.dispose();
            this.tireCubeMesh = null;
        }

        if (this.tireNameMesh) {
            this.removeMesh(this.tireNameMesh);
            this.tireNameMesh.geometry.dispose();
            this.tireNameMesh.material.dispose();
            this.tireNameMesh = null;
        }

        if(this.tireBox)
            this.removeCylinder(this.tireBox);
    }

    removeBatteryBox(){

        if (this.batteryCubeMesh) {
            this.removeMesh(this.batteryCubeMesh);
            this.destroyObj(this.batteryCubeMesh);
        }

        
        if (this.batteryNameMesh) {
            this.removeMesh(this.batteryNameMesh);
            this.batteryNameMesh.geometry.dispose();
            this.batteryNameMesh.material.dispose();
            this.batteryNameMesh = null;
        }
    }
    removeRadarBox(){

        if (this.radarCubeMesh) {
            this.removeMesh(this.radarCubeMesh);
            this.radarCubeMesh.geometry.dispose();
            this.radarCubeMesh.material.dispose();
            this.radarCubeMesh = null;
        }

        
        if (this.radarNameMesh) {
            this.removeMesh(this.radarNameMesh);
            this.radarNameMesh.geometry.dispose();
            this.radarNameMesh.material.dispose();
            this.radarNameMesh = null;
        }

    }
    removeWeaponBox(){
        if (this.weaponCubeMesh) {
            this.removeMesh(this.weaponCubeMesh);
            this.weaponCubeMesh.geometry.dispose();
            this.weaponCubeMesh.material.dispose();
            this.weaponCubeMesh = null;
        }

        
        if (this.weaponNameMesh) {
            this.removeMesh(this.weaponNameMesh);
            this.weaponNameMesh.geometry.dispose();
            this.weaponNameMesh.material.dispose();
            this.weaponNameMesh = null;
        }
    }


    removeChassisBox() {

        if (this.chassisCubeMesh) {
            this.removeMesh(this.chassisCubeMesh);
            this.chassisCubeMesh.geometry.dispose();
            this.chassisCubeMesh.material.dispose();
            this.chassisCubeMesh = null;
        }

        
        if (this.chassisNameMesh) {
            this.removeMesh(this.chassisNameMesh);
            this.chassisNameMesh.geometry.dispose();
            this.chassisNameMesh.material.dispose();
            this.chassisNameMesh = null;
        }

        if(this.chassisBox)
            this.removeSphere(this.chassisBox);
    }

    removeMotorBox() {
        
        if (this.motorCubeMesh) {
            this.removeMesh(this.motorCubeMesh);
            this.motorCubeMesh.geometry.dispose();
            this.motorCubeMesh.material.dispose();
            this.motorCubeMesh = null;
        }

        if (this.motorNameMesh) {
            this.removeMesh(this.motorNameMesh);
            this.motorNameMesh.geometry.dispose();
            this.motorNameMesh.material.dispose();
            this.motorNameMesh = null;
        }

        if(this.motorBox)
            this.removeBox(this.motorBox);
    }


    removeLoading(loadingController){

        this.removeUIMesh(loadingController.txt);
        this.removeSprite(loadingController.loading1);
        this.removeSprite(loadingController.loading2);
    }
    
    async createLoading(){

        const Txt = new TextGeometry("loading", {
            font: this.speedFont,
            size: 24,
            height: 4,
            curveSegments: 12,
            bevelEnabled: true,
            bevelThickness: 0.1,
            bevelSize: 0.01,
            bevelOffset: 0,
            bevelSegments: 4
        });
        Txt.computeBoundingBox();
        Txt.computeBoundingSphere();

        Txt.translate(- Txt.boundingSphere.radius, 0, 0);

        const txtMat = new THREE.MeshBasicMaterial({ color: '#000', side : THREE.DoubleSide});
        this.loadingMesh = new THREE.Mesh(Txt, txtMat);
   

        const loadingMaterial = new THREE.SpriteMaterial({ color: 0xFFFFFF, rotation: 0, depthTest: false });
        const loadingMaterial2 = new THREE.SpriteMaterial({ color: 0x000000, rotation: 0 , depthTest: false});

        const loading = new THREE.Sprite(loadingMaterial);
        loading.scale.set(80, 10, 1)
        loading.position.set(0, -20, 1);

        const loading2 = new THREE.Sprite(loadingMaterial2);
        loading2.scale.set(0, 8, 1)
        loading2.position.set(0, -20, 1);

        return {loading1: loading, loading2: loading2, txt : this.loadingMesh};

        //this.setUIPos(this.loadingMesh, 0, 0);
    }

    makeChassisBox(carDef) {
        
        this.chassisBox = {};

        const cubeG = new THREE.BoxGeometry(1.8, 0.8, 0.8, 1, 1, 1);
        
        /*const texture = await this.texLoader.loadAsync(this.assetBase+'/assets/nfts/' + carDef.chassis+'.png');*/
        const cubeMat = new THREE.MeshPhysicalMaterial({ color: '#FFF', roughness: 0.2, reflectivity: 0.2, metalness: 0.2, ior: 1.2, opacity: 1.0, transparent: true, thickness : 0.9, transmission: 0.95 });
        cubeMat.transmission = 0.9;
        this.chassisCubeMesh = new THREE.Mesh(cubeG, cubeMat);
        this.chassisCubeMesh.position.set(0, 1.8, 0.2);
        this.chassisCubeMesh.meshGPU = this.addMesh(this.chassisCubeMesh, RT_DIFF, new Transformation3(this.chassisCubeMesh.position));
        this.setMeshStatic(this.chassisCubeMesh, false);

        this.chassisCubeMesh.castShadow = false;
        this.chassisCubeMesh.receiveShadow = false;
        this.chassisCubeMesh.renderOrder = 0;

        //this.addSphere(this.chassisBox, 0.9, new THREE.Vector3(0, 2.2, 0.6), new THREE.Vector3(0, 0.0, 0), new THREE.Vector3(1, 1, 1), 0.2, RT_REFR);

        const Txt = new TextGeometry(carDef.chassis, {
            font: this.armourFont,
            size: 0.3,
            height: 0.1,
            curveSegments: 8,
            bevelEnabled: false,
            bevelThickness: 0.01,
            bevelSize: 0.01,
            bevelOffset: 0,
            bevelSegments: 2
        });
        Txt.computeBoundingBox();
        Txt.computeBoundingSphere();

        const sz = new THREE.Vector3();
        Txt.boundingBox.getSize(sz);

        Txt.translate(-sz.x/2, -sz.y/2, 0);

        
        this.chassisNameMesh = new THREE.Mesh(Txt, this.garageTxtMat);
        this.chassisNameMesh.position.set(0,  1.8, 0.2);
        this.chassisNameMesh.meshGPU = this.addMesh(this.chassisNameMesh, RT_METALCOAT, new Transformation3(this.chassisNameMesh.position));
        this.chassisNameMesh.castShadow = true;
        this.setMeshStatic(this.chassisNameMesh, false);
    }


    makeMotorBox(carDef) {

       
        const Txt = new TextGeometry(carDef.motor ? carDef.motor.name : 'no motor', {
            font: this.armourFont,
            size: 0.3,
            height: 0.1,
            curveSegments: 8,
            bevelEnabled: false,
            bevelThickness: 0.01,
            bevelSize: 0.01,
            bevelOffset: 0,
            bevelSegments: 2
        });
        Txt.computeBoundingBox();
        Txt.computeBoundingSphere();


        const sz = new THREE.Vector3();
        Txt.boundingBox.getSize(sz);

        Txt.translate(-sz.x/2, -sz.y/2, 0);

        const txtMat = new THREE.MeshPhysicalMaterial({ color: '#FFF', emissive: new THREE.Color().setRGB(0.9,0.8,1), emissiveIntensity: 2.0, transparent: true, depthTest: false });
        this.motorNameMesh = new THREE.Mesh(Txt, this.garageTxtMat);
        this.motorNameMesh.castShadow = true;
        this.motorNameMesh.position.set(0, 1, 1.6);
        this.motorNameMesh.meshGPU = this.addMesh(this.motorNameMesh, RT_METALCOAT, new Transformation3(this.motorNameMesh.position));

        this.setMeshStatic(this.motorNameMesh, false);

        const cubeG = new THREE.BoxGeometry(sz.x * 1.3, sz.y * 1.3, sz.z * 1.3, 1, 1, 1);
        const cubeMat = new THREE.MeshPhysicalMaterial({ color: '#FFF', roughness: 0.2, reflectivity: 0.2, metalness: 0.2, ior: 1.2, opacity: 1.0, transparent: true, thickness : 0.9, transmission: 0.95 });

        this.motorCubeMesh = new THREE.Mesh(cubeG, cubeMat);
        this.motorCubeMesh.position.set(0, 1, 1.6);
        this.motorCubeMesh.meshGPU = this.addMesh(this.motorCubeMesh, RT_DIFF, new Transformation3(this.motorCubeMesh.position));
        this.motorCubeMesh.castShadow = true;
        this.setMeshStatic(this.motorCubeMesh, false);
    }

    makeBatteryBox(carDef) {

       
        const Txt = new TextGeometry(carDef.battery ? carDef.battery.name : 'no battery', {
            font: this.armourFont,
            size: 0.3,
            height: 0.1,
            curveSegments: 8,
            bevelEnabled: false,
            bevelThickness: 0.01,
            bevelSize: 0.01,
            bevelOffset: 0,
            bevelSegments: 2
        });
        Txt.computeBoundingBox();
        Txt.computeBoundingSphere();


        const sz = new THREE.Vector3();
        Txt.boundingBox.getSize(sz);

        Txt.translate(-sz.x/2, -sz.y/2, 0);

        this.batteryNameMesh = new THREE.Mesh(Txt, this.garageTxtMat);
        this.batteryNameMesh.castShadow = true;
        this.batteryNameMesh.position.set(3, 1, 2);
        this.batteryNameMesh.meshGPU = this.addMesh(this.batteryNameMesh, RT_METALCOAT, new Transformation3(this.batteryNameMesh.position));
        this.setMeshStatic(this.batteryNameMesh, false);

        this.loadObj(carDef.battery.model).then((mesh) => {
            this.batteryCubeMesh = mesh;
            this.batteryCubeMesh.position.set(3, 0, 2);
            this.batteryCubeMesh.meshGPU = this.addMesh(this.batteryCubeMesh, RT_DIFF, new Transformation3(this.batteryCubeMesh.position));
            this.batteryCubeMesh.castShadow = true;
            this.setMeshStatic(this.batteryCubeMesh, false);
        })
    }

    makeRadarBox(carDef) {

        const Txt = new TextGeometry(carDef.radar ? carDef.radar.name : 'no radar', {
            font: this.armourFont,
            size: 0.3,
            height: 0.1,
            curveSegments: 8,
            bevelEnabled: false,
            bevelThickness: 0.01,
            bevelSize: 0.01,
            bevelOffset: 0,
            bevelSegments: 2
        });
        Txt.computeBoundingBox();
        Txt.computeBoundingSphere();


        const sz = new THREE.Vector3();
        Txt.boundingBox.getSize(sz);

        Txt.translate(-sz.x/2, -sz.y/2, 0);

        this.radarNameMesh = new THREE.Mesh(Txt,  this.garageTxtMat);
        this.radarNameMesh.castShadow = true;
        this.radarNameMesh.position.set(0, 1.5, -2);
        this.radarNameMesh.meshGPU = this.addMesh(this.radarNameMesh, RT_METALCOAT, new Transformation3(this.radarNameMesh.position));
        this.setMeshStatic(this.radarNameMesh, false);

        const cubeG = new THREE.BoxGeometry(sz.x * 1.3, sz.y * 1.3, sz.z * 1.3, 1, 1, 1);
        const cubeMat = new THREE.MeshPhysicalMaterial({ color: '#FFF', roughness: 0.2, reflectivity: 0.2, metalness: 0.2, ior: 1.2, opacity: 1.0, transparent: true, thickness : 0.9, transmission: 0.95 });
        this.radarCubeMesh = new THREE.Mesh(cubeG, cubeMat);
        this.radarCubeMesh.position.set(0, 1.5, -2);
        this.radarCubeMesh.meshGPU = this.addMesh(this.radarCubeMesh, RT_DIFF, new Transformation3(this.radarCubeMesh.position));
        this.radarCubeMesh.castShadow = true;
        this.setMeshStatic(this.radarCubeMesh, false);
    }

    makeWeaponBox(carDef) {

        const Txt = new TextGeometry(carDef.weapon ? carDef.weapon.name : 'no weapon', {
            font: this.armourFont,
            size: 0.3,
            height: 0.1,
            curveSegments: 8,
            bevelEnabled: false,
            bevelThickness: 0.01,
            bevelSize: 0.01,
            bevelOffset: 0,
            bevelSegments: 2
        });
        Txt.computeBoundingBox();
        Txt.computeBoundingSphere();


        const sz = new THREE.Vector3();
        Txt.boundingBox.getSize(sz);

        Txt.translate(-sz.x/2, -sz.y/2, 0);

        this.weaponNameMesh = new THREE.Mesh(Txt,  this.garageTxtMat);
        this.weaponNameMesh.castShadow = true;
        this.weaponNameMesh.position.set(0, 2, -1);
        this.weaponNameMesh.meshGPU = this.addMesh(this.weaponNameMesh, RT_METALCOAT, new Transformation3(this.weaponNameMesh.position));
        this.setMeshStatic(this.weaponNameMesh, false);

        const cubeG = new THREE.BoxGeometry(sz.x * 1.3, sz.y * 1.3, sz.z * 1.3, 1, 1, 1);
        const cubeMat = new THREE.MeshPhysicalMaterial({ color: '#FFF', roughness: 0.2, reflectivity: 0.2, metalness: 0.2, ior: 1.2, opacity: 1.0, transparent: true, thickness : 0.9, transmission: 0.95 });
        this.weaponCubeMesh = new THREE.Mesh(cubeG, cubeMat);
        this.weaponCubeMesh.position.set(0, 2, -1);
        this.weaponCubeMesh.meshGPU = this.addMesh(this.weaponCubeMesh, RT_DIFF, new Transformation3(this.weaponCubeMesh.position));
        this.weaponCubeMesh.castShadow = true;
        this.setMeshStatic(this.weaponCubeMesh, false);
    }

    makeTireBox(carDef) {

        const Txt = new TextGeometry(carDef.suspension ? carDef.suspension.name : 'no tires', {
            font: this.armourFont,
            size: 0.2,
            height: 0.1,
            curveSegments: 8,
            bevelEnabled: false,
            bevelThickness: 0.01,
            bevelSize: 0.01,
            bevelOffset: 0,
            bevelSegments: 2
        });
        Txt.computeBoundingBox();
        Txt.computeBoundingSphere();

        const sz = new THREE.Vector3();
        Txt.boundingBox.getSize(sz);

        Txt.translate(-sz.x/2, -sz.y/2, 0);

        const txtMat = new THREE.MeshPhysicalMaterial({ color: '#FFF', emissive: new THREE.Color().setRGB(0.9,0.8,1), emissiveIntensity: 2.0, transparent: true, depthTest: false });
        this.tireNameMesh = new THREE.Mesh(Txt,  this.garageTxtMat);
        this.tireNameMesh.position.set(0, 0.5, 0);
        this.tireNameMesh.meshGPU = this.addMesh(this.tireNameMesh, RT_METALCOAT, new Transformation3(this.tireNameMesh.position));
        this.tireNameMesh.castShadow = true;
        this.setMeshStatic(this.tireNameMesh, false);

        const cubeG = new THREE.BoxGeometry(sz.x * 1.3, sz.y * 1.3, sz.z * 1.3, 1, 1, 1);
        const cubeMat = new THREE.MeshPhysicalMaterial({ color: '#FFF', roughness: 0.2, reflectivity: 0.2, metalness: 0.2, ior: 1.2, opacity: 1.0, transparent: true, thickness : 0.9, transmission: 0.95 });
        
        this.tireCubeMesh = new THREE.Mesh(cubeG, cubeMat);
        this.tireCubeMesh.position.set(0, 0.5, 0);
        this.tireCubeMesh.meshGPU = this.addMesh(this.tireCubeMesh, RT_DIFF, new Transformation3(this.tireCubeMesh.position));
        this.tireCubeMesh.castShadow = true;
        this.setMeshStatic(this.tireCubeMesh, false);

    }

    async makeWalls(track, obj) {
        for (let wall of track.walls) {
            wall.object = obj.clone();
            wall.object.material = obj.material;
            wall.object.position.x = wall.pos.x;
            wall.object.position.y = wall.pos.y;
            wall.object.position.z = wall.pos.z;

            wall.object.rotation.x = wall.eul.x;
            wall.object.rotation.y = wall.eul.y;
            wall.object.rotation.z = wall.eul.z;
        }
    }

    resizeTexture(texture){

        if(this.hdSkybox !== false)
            return texture;

        const img = texture.source.data;

        if((img.width<1024)&&(img.height<1024))
            return texture;

        const resizeCanvas = document.createElement('canvas');

        if(img.width>img.height){
            resizeCanvas.width = 1024;
            resizeCanvas.height = 1024 * img.height / img.width;
        }else{
            resizeCanvas.width = 1024 * img.width / img.height;
            resizeCanvas.height = 1024;
        }

        const context = resizeCanvas.getContext('2d');
        context.drawImage(img, 0, 0, resizeCanvas.width, resizeCanvas.height);

        const newTex = new THREE.CanvasTexture(resizeCanvas, texture.mapping, texture.wrapS, texture.wrapT);
        return newTex;
    }

    resizeMaterial(mat){

        if(this.hdSkybox !== false)
            return;

        if(mat.map)
            mat.map=this.resizeTexture(mat.map);
        
        if(mat.normalMap)
            mat.normalMap=this.resizeTexture(mat.normalMap);

        if(mat.roughnessMap)
            mat.roughnessMap=this.resizeTexture(mat.roughnessMap);

    }

    async createRoadMat(track) {
        this.roadMaT = new THREE.MeshRoadPhysicalMaterial({ color: 0xffFFFF });
        this.roadMaT.name = "roadmat";

        this.roadMaT.transmission = 0.0;
        this.roadMaT.thickness = 0.0;
        

        this.roadMaT.envMap = this.skyTexture;
        
        this.roadMaT.envMapIntensity = track.roadParams.envMapIntensity;
        this.roadMaT.metalness = track.roadParams.metalness;
        this.roadMaT.roughness = track.roadParams.roughness;
        this.roadMaT.reflectivity = track.roadParams.reflectivity;
        
        this.roadMaT.side = THREE.DoubleSide;

        if(track.roadTex){
            this.roadMaT.map = this.resizeTexture(await this.texLoader.loadAsync(this.assetBase+"/assets/textures/" + track.roadTex));
            if(this.roadMaT.map){
                this.roadMaT.map.wrapS = THREE.RepeatWrapping;
                this.roadMaT.map.wrapT = THREE.RepeatWrapping;
                this.roadMaT.map.repeat.set(track.roadTexScale.x, track.roadTexScale.y);
            }
        }
        /*
        if(track.roadDisp){
            this.roadMaT.bumpMap = await this.texLoader.loadAsync(this.assetBase+"/assets/textures/" + track.roadDisp);
            this.roadMaT.bumpMap.wrapS = THREE.RepeatWrapping;
            this.roadMaT.bumpMap.wrapT = THREE.RepeatWrapping;
            this.roadMaT.bumpMap.repeat.set(4, 4);
            this.roadMaT.bumpScale = 0.05;
            this.roadMaT.bumpMapScale = new THREE.Vector2(0.1, 0.1);
        }
        */

        if (track.roadNorm) {
            this.roadMaT.normalMap = this.resizeTexture(await this.texLoader.loadAsync(this.assetBase+"/assets/textures/" + track.roadNorm));

            if(this.roadMaT.normalMap){
                this.roadMaT.normalMap.wrapS = THREE.RepeatWrapping;
                this.roadMaT.normalMap.wrapT = THREE.RepeatWrapping;
                this.roadMaT.normalMap.intensity = 2.4;
                this.roadMaT.normalScale = new THREE.Vector2(1.8, 1.8);
                this.roadMaT.normalMap.repeat.set(track.roadTexScale.x, track.roadTexScale.y);
            }
        }     
        
        if (track.roadAlpha) {
            
            this.roadMaT.alphaMap  = this.resizeTexture(await this.texLoader.loadAsync(this.assetBase+"/assets/textures/" + track.roadAlpha));
            this.roadMaT.alphaMap .wrapS = THREE.RepeatWrapping;
            this.roadMaT.alphaMap .wrapT = THREE.RepeatWrapping;
            this.roadMaT.alphaMap .repeat.set(track.roadTexScale.x, track.roadTexScale.y);
            this.roadMaT.transparent = true;
        }

        if (track.roadEmm) {
            this.roadMaT.emissiveMap = this.resizeTexture(await this.texLoader.loadAsync(this.assetBase+"/assets/textures/" + track.roadEmm));
            this.roadMaT.emissiveMap.wrapS = THREE.RepeatWrapping;
            this.roadMaT.emissiveMap.wrapT = THREE.RepeatWrapping;
            this.roadMaT.emissiveMap.repeat.set(track.roadTexScale.x, track.roadTexScale.y);
            this.roadMaT.emissive.r = 0.4;
            this.roadMaT.emissive.g = 0.4;
            this.roadMaT.emissive.b = 0.4;
            this.roadMaT.emissiveIntensity = 1.0;
        }

        if (track.roadRought) {
            this.roadMaT.roughnessMap = this.resizeTexture(await this.texLoader.loadAsync(this.assetBase+"/assets/textures/" + track.roadRought));

            if(this.roadMaT.roughnessMap){
                this.roadMaT.roughnessMap.wrapS = THREE.RepeatWrapping;
                this.roadMaT.roughnessMap.wrapT = THREE.RepeatWrapping;
                this.roadMaT.roughnessMap.repeat.set(track.roadTexScale.x, track.roadTexScale.y);
    
            }
        }
        
        if(this.enableParallax){
            
            if (track.fxMapTex) {
                this.roadMaT.fxMap = await this.texLoader.loadAsync(this.assetBase+"/assets/textures/" + track.fxMapTex);

                if(this.roadMaT.fxMap){
                    this.roadMaT.fxMap.wrapS = THREE.RepeatWrapping;
                    this.roadMaT.fxMap.wrapT = THREE.RepeatWrapping;
                    this.roadMaT.fxMap.channel = 3;
                    this.roadMaT.fxMap.flipY = false;
                    this.roadMaT.fxMap.repeat.set(1, 1);

                    const canvas = document.createElement('canvas');
                    canvas.width = this.roadMaT.fxMap.image.width;
                    canvas.height = this.roadMaT.fxMap.image.height;
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(this.roadMaT.fxMap.image, 0, 0);
                    track.fxMapData = ctx.getImageData(0, 0, canvas.width, canvas.height).data;

                    track.fxMapDataWidth = canvas.width;
                    track.fxMapDataHeight = canvas.height;

                    

                }
            }

            if (track.roadTex2) {
                this.roadMaT.secMap = await this.texLoader.loadAsync(this.assetBase+"/assets/textures/" + track.roadTex2);

                if(this.roadMaT.secMap){
                    this.roadMaT.secMap.wrapS = THREE.RepeatWrapping;
                    this.roadMaT.secMap.wrapT = THREE.RepeatWrapping;
                    this.roadMaT.secMap.repeat.set(track.roadTexScale.x, track.roadTexScale.y);
                }
            }

            if (track.roadNorm2) {
                this.roadMaT.normalMap2 =await this.texLoader.loadAsync(this.assetBase+"/assets/textures/" + track.roadNorm2);

                if(this.roadMaT.normalMap2){
                    this.roadMaT.normalMap2.wrapS = THREE.RepeatWrapping;
                    this.roadMaT.normalMap2.wrapT = THREE.RepeatWrapping;
                    this.roadMaT.normalMap2.intensity = 1.4;
                    this.roadMaT.normalMap2.repeat.set(track.roadTexScale.x, track.roadTexScale.y);
                }
            }
        }
     
    }


    CalculateTangentArray(vertexCount, triangleCount, indexes, vertex, normal, texcoord)
    {

        const tangent = new Float32Array(vertexCount * 4);
        const tan1 = new Float32Array(vertexCount * 3);
        const tan2 = new Float32Array(vertexCount * 3);
            
        for (let a = 0; a < triangleCount; a++)
        {
            const i1 = indexes[a*3+0];
            const i2 = indexes[a*3+1];
            const i3 = indexes[a*3+2];

            const v1 = new THREE.Vector3(vertex[i1*3+0],vertex[i1*3+1],vertex[i1*3+2]);
            const v2 = new THREE.Vector3(vertex[i2*3+0],vertex[i2*3+1],vertex[i2*3+2]);
            const v3 = new THREE.Vector3(vertex[i3*3+0],vertex[i3*3+1],vertex[i3*3+2]);
        
            const w1 = new THREE.Vector2(texcoord[i1*2+0],texcoord[i1*2+1]);
            const w2 = new THREE.Vector2(texcoord[i2*2+0],texcoord[i2*2+1]);
            const w3 = new THREE.Vector2(texcoord[i3*2+0],texcoord[i3*2+1]);
        
            const x1 = v2.x - v1.x;
            const x2 = v3.x - v1.x;
            const y1 = v2.y - v1.y;
            const y2 = v3.y - v1.y;
            const z1 = v2.z - v1.z;
            const z2 = v3.z - v1.z;
            
            const s1 = w2.x - w1.x;
            const s2 = w3.x - w1.x;
            const t1 = w2.y - w1.y;
            const t2 = w3.y - w1.y;
        
            const ar = (s1 * t2 - s2 * t1);
            if(Math.abs(ar) >0){
                const r = 1.0 / ar;
                const sdir = new THREE.Vector3((t2 * x1 - t1 * x2) * r, (t2 * y1 - t1 * y2) * r, (t2 * z1 - t1 * z2) * r);
                const tdir = new THREE.Vector3(((s1 * x2 - s2 * x1) * r, (s1 * y2 - s2 * y1) * r,(s1 * z2 - s2 * z1) * r));
            
                tan1[i1*3+0] += sdir.x;
                tan1[i2*3+1] += sdir.y;
                tan1[i3*3+2] += sdir.z;
                
                tan2[i1*3+0] += tdir.x;
                tan2[i2*3+1] += tdir.y;
                tan2[i3*3+2] += tdir.z;
            }else{
                console.error('error tangent '+a);
            }
        }
    
        for (let a = 0; a < vertexCount; a++)
        {
            const n = new THREE.Vector3(normal[a*3 + 0], normal[a*3 + 1], normal[a*3 + 2]);
            const t = new THREE.Vector3(tan1[a*3 + 0], tan1[a*3 + 1], tan1[a*3 + 2]);
           
            // Gram-Schmidt orthogonalize
            const nv = new THREE.Vector3().subVectors(t , n.clone().multiplyScalar(n.clone().dot(t))).normalize();
            tangent[a*4+0] = nv.x;
            tangent[a*4+1] = nv.y;
            tangent[a*4+2] = nv.z;
                        
            // Calculate handedness
            tangent[a*4+3] = n.clone().cross(t).dot(tan2[a]) < 0.0 ? -1.0 : 1.0;
        }
    
        return tangent;
    }

    createTrackGeometry(track) {

        if(track.indices){
            let geometry = new THREE.BufferGeometry();
            geometry.setIndex(track.indices);
            geometry.setAttribute('position', new THREE.Float32BufferAttribute(track.positions, 3));
            geometry.setAttribute('uv', new THREE.Float32BufferAttribute(track.texcoords, 2));
            geometry.computeVertexNormals();
            //geometry = geometry.toNonIndexed();

            track.roadObj = new THREE.Mesh(geometry, this.roadMaT);
            geometry.computeBoundsTree();

            const tangents = this.CalculateTangentArray(track.roadObj.geometry.index.count, track.roadObj.geometry.index.count / 3, track.roadObj.geometry.index.array, track.roadObj.geometry.attributes.position.array, track.roadObj.geometry.attributes.normal.array, track.roadObj.geometry.attributes.uv.array);
            track.roadObj.geometry.setAttribute('tangent', new THREE.Float32BufferAttribute(tangents, 4));
           
            if((track.texcoords2)&&(this.bumpTex)){
                track.roadObj.geometry.setAttribute('uv3', new THREE.Float32BufferAttribute(track.texcoords2,  2) );
                const tangents3 = this.CalculateTangentArray(track.roadObj.geometry.index.count, track.roadObj.geometry.index.count / 3, track.roadObj.geometry.index.array, track.roadObj.geometry.attributes.position.array, track.roadObj.geometry.attributes.normal.array, track.roadObj.geometry.attributes.uv3.array);
                track.roadObj.geometry.setAttribute('tangent3', new THREE.Float32BufferAttribute(tangents3,  4) );

                track.roadObj.geometry.attributes.tangent3.needsUpdate = true;
                track.roadObj.geometry.attributes.uv3.needsUpdate = true;

                
                this.bumpTex.channel = 3;
                this.roadMaT.driftMap = this.bumpTex;
                this.roadMaT.driftMapScale = new THREE.Vector2(1, 1);
                this.roadMaT.driftMapFlags = track.driftFlags;
                //this.roadMaT.bumpScale = 0.01;
                this.roadMaT.needsUpdate = true;
                
            }


            /*
            const tangents = generateTangents(track.roadObj.geometry.attributes.position.array, track.roadObj.geometry.attributes.normal.array, track.roadObj.geometry.attributes.uv.array);
            track.roadObj.geometry.setAttribute('tangent', new THREE.Float32BufferAttribute(tangents, 4));
            */
            /*
            for(let n=0;n<){
                vec3 p_dx = dFdx(pw_i);
                vec3 p_dy = dFdy(pw_i);
                // compute derivations of the texture coordinate
                vec2 tc_dx = dFdx(tc_i);
                vec2 tc_dy = dFdy(tc_i);
                // compute initial tangent and bi-tangent
                vec3 t = normalize( tc_dy.y * p_dx - tc_dx.y * p_dy );
                vec3 b = normalize( tc_dy.x * p_dx - tc_dx.x * p_dy ); // sign inversion
                // get new tangent from a given mesh normal
                vec3 n = normalize(n_obj_i);
                vec3 x = cross(n, t);
                t = cross(x, n);
                t = normalize(t);
                // get updated bi-tangent
                x = cross(b, n);
                b = cross(n, x);
                b = normalize(b);
            }
            */

            track.roadObj.name = "TrackMesh";
    
        }

        if(track.indices2){
            const geometry2 = new THREE.BufferGeometry();
            geometry2.setIndex(track.indices2);
            geometry2.setAttribute('position', new THREE.Float32BufferAttribute(track.positions, 3));
            geometry2.setAttribute('uv', new THREE.Float32BufferAttribute(track.texcoords, 2));
            geometry2.computeVertexNormals();


            track.roadWall = new THREE.Mesh(geometry2, this.wallMat);
            track.roadWall.name = "TrackWalls";
        }
    }

    async initAudio() {
        this.audioCtx = THREE.AudioContext.getContext();

        this.sfxGainNode = this.audioCtx.createGain();
        this.sfxGainNode.gain.value = SoundCfg.sfxVolume / 100.0;
        this.sfxGainNode.connect(this.audioCtx.destination);

        this.enginegainNode = this.audioCtx.createGain();
        this.enginegainNode.gain.value = SoundCfg.engineVolume / 100.0;
        this.enginegainNode.connect(this.audioCtx.destination);

        await this.loadSample("/assets/snd/impact.wav", "impact");
        await this.loadSample("/assets/snd/bombexpl.wav", "bombexpl");
        await this.loadSample("/assets/snd/beep.wav", "coin");
        await this.loadSample("/assets/snd/count.wav", "count");
        await this.loadSample("/assets/snd/screech.wav", "screech");
        await this.loadSample("/assets/snd/powerup.wav", "powerup");
        await this.loadSample("/assets/snd/collision.wav", "collision");

        this.setEngineSoundPath([   { file: "/assets/snd/engines/2/MavCommon_src_main_resources_Audio_engine-1d2.ogg", pitch: 13.5 },
                                    { file: "/assets/snd/engines/2/MavCommon_src_main_resources_Audio_engine-1.ogg", pitch: 26.75 },
                                    { file: "/assets/snd/engines/2/MavCommon_src_main_resources_Audio_engine-1x2.ogg", pitch: 53.5 },
                                    { file: "/assets/snd/engines/2/MavCommon_src_main_resources_Audio_engine-1x4.ogg", pitch: 107.0 }])
    }

    setMusicVolume(volume) {
        SoundCfg.musicVolume = volume;
        localStorage.setItem('musicVolume', SoundCfg.musicVolume);

        if (this.AudioMusic)
            this.AudioMusic.volume = SoundCfg.musicVolume / 100.0;

    }

    setEngineVolume(volume) {
        SoundCfg.engineVolume = volume;
        localStorage.setItem('engineVolume', SoundCfg.engineVolume);

        if (this.enginegainNode)
            this.enginegainNode.gain.value = SoundCfg.engineVolume / 100.0;
    }

    setSFXVolume(volume) {
        SoundCfg.sfxVolume = volume;
        localStorage.setItem('sfxVolume', SoundCfg.sfxVolume);

        if (this.sfxGainNode)
            this.sfxGainNode.gain.value = SoundCfg.sfxVolume / 100.0;
    }

    isBetween(min, v, max) {
        if (v < min)
            return false;

        if (v > max)
            return false;

        return true;
    }

    clamp(p, min, max) {
        return Math.min(Math.max(p, min), max);
    }

    async setEngineSoundPath(path) {
        this.engineSound = [];

        for (let n = 0; n < path.length; n++) {
            const response = await fetch(this.assetBase+path[n].file);
            const aBuffer = await response.arrayBuffer();
            const buffer = await this.audioCtx.decodeAudioData(aBuffer);

            const node = this.audioCtx.createBufferSource();
            node.buffer = buffer;
            node.loop = true;
            node.connect(this.enginegainNode);

            this.engineSound[n] = { path: path[n].file, buffer: buffer, node: node, pitch: path[n].pitch, playing: false, curPitch: path[n].pitch };
        }
    }

    findBestNode(pitch) {
        let result = null;
        let bestRatio = 0.0;

        for (let snd of this.engineSound) {
            if (!snd) continue;
            let recorded = snd.pitch;
            let ratio;
            if (pitch > recorded) {
                ratio = recorded / pitch;
            } else {
                ratio = pitch / recorded;
            }

            //console.log('pitch '+pitch+' rec:'+recorded+' ratio:'+ratio);

            //if( ratio > 0.0 && ratio < 1.0)

            if (ratio > bestRatio) {
                bestRatio = ratio;
                result = snd;
            }
        }

        if (bestRatio === 0)
            return this.engineSound[0];

        return result;
    }

    canAccuratelySimulate(node, pitch) {
        let minPitch = 0.5 * node.pitch;
        let maxPitch = 2 * node.pitch;
        return this.isBetween(minPitch, pitch, maxPitch);
    }

    setPitch(pitch) {

        if (this.activeNode == null || !this.canAccuratelySimulate(this.activeNode, pitch)) {
            // Find the best AudioNode for the desired pitch.
            var bestNode = this.findBestNode(pitch);

            if (bestNode != this.activeNode && this.activeNode != null) {

                this.activeNode.node.stop();
                this.activeNode.playing = false;

                //console.log("change pitch "+pitch);
                //console.log(bestNode);

            }
            this.activeNode = bestNode;
        }

        if (!this.activeNode) {
            return;
        }

        const playbackSpeed = pitch / this.activeNode.pitch;
        const clampedSpeed = this.clamp(playbackSpeed, 0.5, 2);

        if (playbackSpeed != clampedSpeed) {
            //console.log("Clamped playback speed: sound="+pitch+", pitch={"+this.activeNode.pitch+"} Hz to "+clampedSpeed);
        }

        if (clampedSpeed != this.activeNode.curPitch) {
            this.activeNode.curPitch = clampedSpeed;
            this.activeNode.node.playbackRate.value = this.activeNode.curPitch;
        }

        if (!this.activeNode.playing) {

            //console.log("start new node ");
            this.activeNode.node = this.audioCtx.createBufferSource();
            this.activeNode.node.buffer = this.activeNode.buffer;
            this.activeNode.node.loop = true;
            this.activeNode.node.connect(this.enginegainNode);
            this.activeNode.node.start(0);
            this.activeNode.playing = true;
        }
    }

    async stopEngine() {
        for (let snd of this.engineSound) {

            if (snd.playing)
                snd.node.stop();
        }
    }

    async loadSample(audioFile, name) {
        const keys = Object.keys(this.assetBase+this.audioSamples);
        if(keys.indexOf(name)>=0){
            return this.audioSamples[name];
        }

        const response = await fetch(audioFile);
        const aBuffer = await response.arrayBuffer();
        const buffer = await this.audioCtx.decodeAudioData(aBuffer);
        this.audioSamples[name] = { file: audioFile, buffer: buffer, lastSample : 0 };
    }

    stopMusic() {
        if(!this.AudioMusicPlaying)
            return;

        this.AudioMusic.pause();
        this.AudioMusic.src = null;
        this.AudioMusicPlaying = false;
    
    }
    playMusic(url) {
        if (!this.AudioMusicPlaying) {
            this.AudioMusic = new Audio();
            this.AudioMusic.src = this.assetBase + url;
            this.AudioMusic.volume = SoundCfg.musicVolume / 100;
            this.AudioMusic.loop = true;
            //this.AudioMusic.setLoopStart (3);
            this.AudioMusicPlaying = true;
        }
    }

    async playAudio(sample, volume) {

        const now = new Date().getTime();

        if (!this.audioSamples[sample])
            return;

        if (this.audioSamples[sample].buffer == null) {
            return;
        }

        if ((now - this.audioSamples[sample].lastSample) < 200)
            return;

        if (this.numSamples >= 15)
            return;

        const self = this;

        const track = this.audioCtx.createBufferSource();
        track.buffer = this.audioSamples[sample].buffer;
        
        const gainNode = this.audioCtx.createGain();
        gainNode.gain.value = volume;
        gainNode.connect(this.sfxGainNode);
        track.connect(gainNode);
        
        
        track.start(0);

        this.numSamples++;
        this.audioSamples[sample].lastSample = new Date().getTime();

        track.addEventListener('ended', () => {
            self.numSamples--;
        })
    }

    async loadDigitFont() {

        const texLoader = new THREE.TextureLoader();
        
        this.digitsTex = [];
        //this.digitsMat = [];

        for (let n = 0; n < 10; n++) {
            this.digitsTex[n] = await texLoader.loadAsync(this.assetBase+"/assets/fonts/" + n + ".png");
            this.digitsTex[n].wrapS = THREE.ClampToEdgeWrapping;
            this.digitsTex[n].wrapT = THREE.ClampToEdgeWrapping;
            this.digitsTex[n].flipY = true;

            
            this.digitsTex[n].repeat.set(1, 1);

            /*
            this.digitsMat[n] = new THREE.SpriteMaterial({ map: this.digitsTex[n], color: 0xffffff });

            this.digitsMat[n].depthTest = false;
            this.digitsMat[n].depthWrite = false;
            */
        }

        /*
        this.digits[0] = new THREE.Sprite(this.digitsMat[0]);
        this.digits[1] = new THREE.Sprite(this.digitsMat[0]);
        this.digits[2] = new THREE.Sprite(this.digitsMat[0]);

        this.digits[0].position.x = -90;
        this.digits[0].position.y = -45;
        this.digits[0].scale.set(15, 15, 1)

        this.digits[1].position.x = -75;
        this.digits[1].position.y = -45;
        this.digits[1].scale.set(15, 15, 1)

        this.digits[2].position.x = -60;
        this.digits[2].position.y = -45;
        this.digits[2].scale.set(15, 15, 1)
        */
    }

    addDigitFont(){
        this.sceneUI.add(this.digits[0].mesh);
        this.sceneUI.add(this.digits[1].mesh);
        this.sceneUI.add(this.digits[2].mesh);
    }

    removeDigitFont(){
        this.sceneUI.remove(this.digits[0].mesh);
        this.sceneUI.remove(this.digits[1].mesh);
        this.sceneUI.remove(this.digits[2].mesh);
    }


    drawText(text) {
        if (this.digits.length < 3)
            return;

        const nc = text.length;
        const nnc = 3 - nc;

        if(nnc<0)
            return;


        /*
        for (let n = 0; n < nnc; n++) {
            this.digits[n].material = this.digitsMat[0];
        }

        for (let n = nnc; n < 3; n++) {
            const nm = parseInt(text[n - nnc]);
            this.digits[n].material = this.digitsMat[nm] ? this.digitsMat[nm] : 0;
        }
            */
    }


    getPixel(img, x, y) {
        var canvas = document.createElement('canvas');
        var context = canvas.getContext('2d');
        context.drawImage(img, 0, 0);
        return context.getImageData(x, y, 1, 1).data;
      }
      

    async loadGLTF(name, manager, loadingController = null, size = 0){

        const loader = new GLTFLoader(manager);

        const dracoLoader = new DRACOLoader();
        dracoLoader.setDecoderPath( 'assets/js/draco/' );
        loader.setDRACOLoader( dracoLoader );

        try{
            const filename = name.endsWith('glb') ? name  : name+".glb";
            const obj = await loader.loadAsync(this.assetBase+"/assets/mesh/" +filename, function(xhr){

                const tsize = xhr.total > 0 ? xhr.total: size

                if((loadingController)&&(tsize>0))
                    loadingController.loading2.scale.x = xhr.loaded * 78 / tsize;
            });
            return obj.scenes[0];
        }
        catch(e){
            console.log('could not load file '+name+' '+e.message);
            return null;
        }
    }

    startSounds(world){

        if(world.myCar)
            world.myCar.ChassisMesh.add( this.AudioListener );

        for(let w of world.track.terrain.waterFlows){
            w.positionalAudio.setLoop(true);
            w.positionalAudio.getOutput().connect(this.sfxGainNode);
            w.positionalAudio.play();
        }

        for(let w in world.track.movingObjects){

            if(w === 'seagull-yf'){
                if(world.track.movingObjects[w].positionalAudio){
                    world.track.movingObjects[w].positionalAudio.getOutput().connect(this.sfxGainNode);
                    world.track.movingObjects[w].positionalAudio.play();
                }
            }
        }

        
        
        for(let drone of world.track.drones){
            if(drone.positionalAudio){
                drone.positionalAudio.setLoop(true);
                drone.positionalAudio.getOutput().connect(this.sfxGainNode);
                drone.positionalAudio.play();
            }
        }
        
    }

    stopSounds(world){

        if(world.myCar)
            world.myCar.ChassisMesh.remove( this.AudioListener );

        for(let w of world.track.terrain.waterFlows){
            w.positionalAudio.pause();
            w.positionalAudio.getOutput().disconnect();
        }

        for(let w in world.track.movingObjects){

            if(w === 'seagull-yf'){
                if(world.track.movingObjects[w].positionalAudio){
                    world.track.movingObjects[w].positionalAudio.pause();
                    world.track.movingObjects[w].positionalAudio.getOutput().disconnect();
                    //world.track.movingObjects[w].positionalAudio.disconnect();
                }
            }
        }
        
        for(let drone of world.track.drones){
            if(drone.positionalAudio){
                if(drone.positionalAudio.buffer)
                    drone.positionalAudio.pause();

                drone.positionalAudio.getOutput().disconnect();
            }
        }
        
    }

    async loadObj(name) {

        const mLoader = new MTLLoaderP();
        const oLoader = new OBJLoader(this.manager);


        const materials = await mLoader.loadAsync(this.assetBase+"/assets/mesh/" + name + ".mtl");
        await materials.preload();

        oLoader.setMaterials(materials);
        const object = await oLoader.loadAsync(this.assetBase+"/assets/mesh/" + name + ".obj");
        const self = this;

        await object.traverseAsync(async function (c){

            if(!c.isMesh)
                return;

            if(c.material.length)
            {
                const lc = c;
                for(let n=0;n<lc.material.length;n++)
                    lc.material[n] = await lc.material[n];
            }
            else{
                const lc = c;
                lc.material = await lc.material;
            }
                
        });

        const retGrp = new THREE.Group();

        const up = new THREE.Vector3(0, 1, 0);
    
        object.traverse(function (child)  {
            
            if (!child.isMesh) 
                return;

            const m = child.clone();
            const nMaterials = m.material.length ? m.material.length : 1;

            if(nMaterials <= 1) {
                retGrp.add(m);
                return;
            }
                    

            let hasBone = false;
            let Skeleton = null;
    
            for (let n = 0; n < nMaterials; n++) {

                if(m.material[n].name.startsWith('bone')){
                    if(Skeleton === null){

                        const gc = new THREE.Vector3();
                        //const skinG = m.geometry;//.clone();
                        m.geometry.computeBoundingBox();
                        m.geometry.boundingBox.getCenter(gc);
                        m.geometry.translate(-gc.x, -gc.y, -gc.z);
                        m.position.set(gc.x, gc.y, gc.z);
                        Skeleton = new SimpleSkeleton({x:0,y:0,z:0}, {x:0, y:0, z:0, w : 1.0});

                    }

                    const boneName = m.material[n].name.substr(5);
                    const sp = self.getGeoGrpCenter(m.geometry, n);

                    const q = new THREE.Quaternion(); //.setFromUnitVectors( up, usp );
                    const newBone = new SimpleBone('pre-'+boneName, q, 0.0, 0.0, 0.0);
                    newBone.boneOffset = sp.c;
                    Skeleton.addBone(newBone);
                    hasBone = true;
                }
            }
            

            if(!hasBone){

                if(m.name != "Plane"){
                    m.castShadow = true;
                    m.receiveShadow = true;
                }
                retGrp.add(m);
                return;
            }

            for (let n = 0; n < m.material.length; n++) {

                if(m.material[n].name.startsWith('center')){
                    const boneName = m.material[n].name.substr(7);
                    const pbone = Skeleton.findBone('pre-'+boneName);
                    if(pbone !== null){
                        const sp = self.getGeoGrpCenter(m.geometry, n);

                        const ppos = new THREE.Vector3(0, pbone.length, 0).applyQuaternion(pbone.quat);
                        const dpos = new THREE.Vector3().subVectors(sp.c, ppos);
                        const spl = dpos.length();
                        const usp = dpos.normalize();
                        const q = new THREE.Quaternion();//.setFromUnitVectors( up, usp );
                        const newBone = new SimpleBone(boneName, q, spl, sp.r, 0.0);

                        pbone.addChild(newBone);

                        newBone.center = sp.c;
                        newBone.infType = 'sphere';
                        self.addBoneInfluenceSphere(m.geometry, {x:false,y:false,z:false}, newBone, n, 1);
                    }
                }

                if(m.material[n].name.startsWith('ccube')){
                    const boneName = m.material[n].name.substr(6);
                    const pbone = Skeleton.findBone('pre-'+boneName);
                    if(pbone !== null){
                        const sp = self.getGeoGrpCenter(m.geometry, n);

                        const ppos = new THREE.Vector3(0, pbone.length, 0).applyQuaternion(pbone.quat);
                        const dpos = new THREE.Vector3().subVectors(sp.c, ppos);
                        const spl = dpos.length();
                        const usp = dpos.normalize();
                        const q = new THREE.Quaternion();//.setFromUnitVectors( up, usp );
                        const newBone = new SimpleBone(boneName, q, spl, sp.r, 0.0);
                        pbone.addChild(newBone);

                        newBone.center = sp.c;
                        newBone.halfSize = sp.hs;
                        newBone.dir = sp.d;
                        newBone.infType = 'cube';
                        self.addBoneInfluenceCube(m.geometry, {x:false,y:false,z:false}, newBone, n, 1);
                    }
                }
            } 
            
            const map = Skeleton.getBoneMap();

            for(let b in map){
                const bone = Skeleton.findBone(b);
                if(!bone)
                    continue;
                
                if(bone.infType === 'sphere')
                    self.addBoneInfluenceSphere(m.geometry, {x:false,y:false,z:false}, bone, map[b], 1);
                else if(bone.infType === 'cube')
                    self.addBoneInfluenceCube(m.geometry, {x:false,y:false,z:false}, bone, map[b], 1);
            }

            m.SimpleSkeleton = Skeleton;

            retGrp.add(m);
        });

        retGrp.traverse(function (child)  {
            
            if (!child.isMesh) 
                return;

            const m = child;
            const nMaterials = m.material.length ? m.material.length : 1;

            if(nMaterials > 1) {
    
                for (let n = 0; n < nMaterials; n++) {

                    if(m.material[n].name.startsWith('Toon')){
                      
                        if((m.material[n].map) && (m.material[n].map.image)){
                            const oldMat = m.material[n];
                            const color = self.getPixel(m.material[n].map.image, m.material[n].map.image.width - 1 , 0);
                            const toonMat = new THREE.MeshToonMaterial({gradientMap : self.gradientMap});
                            toonMat.color = new THREE.Color().setRGB(color[0]/255, color[1]/255, color[2]/255);
                           
                            m.material[n] = toonMat;
                            m.material[n].name = oldMat.name;
                            m.material[n].needsUpdate = true;
                            m.material[n].toonMap = oldMat.map;
                            m.needsUpdate = true;
    
                            oldMat.dispose();
                        }else{

    
                            const oldMat = m.material[n];
                            const color = oldMat.color.clone();

                            const toonMat = new THREE.MeshToonMaterial({gradientMap : self.gradientMap});
                            toonMat.color = color;//new THREE.Color().setRGB(color[0]/255, color[1]/255, color[2]/255);
                           
                            m.material[n] = toonMat;
                            m.material[n].name = oldMat.name;
                            m.material[n].needsUpdate = true;
                            m.needsUpdate = true;
    
                            oldMat.dispose();

                        }
                    }

                    

                    
                }
            }else{

                if((m.material.name.startsWith('Toon'))&&(m.material.map) && (m.material.map.image)){

                    const oldMat = m.material;
                    const color = self.getPixel(m.material.map.image, m.material.map.image.width - 1 , 0);
                    const toonMat = new THREE.MeshToonMaterial({gradientMap : self.gradientMap});
                    toonMat.color = new THREE.Color().setRGB(color[0]/255, color[1]/255, color[2]/255);
          
                    m.material = toonMat;
                    m.material.name = oldMat.name;
                    m.material.needsUpdate = true;
                    m.material.toonMap = oldMat.map;
                    m.needsUpdate = true;

                    oldMat.dispose();
                }
            }
        });
                    

        return retGrp;
    }

    async loadPlatform(name) {

        const self = this;
        const object = await this.loadObj(name);

        object.traverse((child) => {

            if (child.type == 'Mesh') {

                self.plateform = child;
                //this.plateform.material.envMap = this.skyTexture;
                self.plateform.material.transmission = 1.0;
                self.plateform.material.thickness = 0.2;
                self.plateform.material.roughness = 0.0;
                self.plateform.material.reflectivity = 0.1;
            }
        });
    }


    async loadStartLine(track, startname) {

        const self = this;

        const object = await this.loadObj(startname);

        object.traverse((child) => {

            if (child.type == 'Mesh') {
                self.startLine = child;

                for(let m of child.material){

                    if(m.name === 'Glass'){
                        m.color.r = track.tubecolor.r;
                        m.color.g = track.tubecolor.g;
                        m.color.b = track.tubecolor.b;
                    }
                    if(m.name === 'border'){
                        m.color.r = track.tubecolor.r;
                        m.color.g = track.tubecolor.g;
                        m.color.b = track.tubecolor.b;

                        m.blendStyle = THREE.AdditiveBlending;

                        m.emissive.r = track.tubecolor.r;
                        m.emissive.g = track.tubecolor.g;
                        m.emissive.b = track.tubecolor.b;

                        m.emissiveIntensity = 2.0;
                    }
                }
                

                const t = new Transformation3(track.trackPoints[track.startPts].pos, track.trackPoints[track.startPts].rot);
                t.apply(self.startLine.geometry);
            }
        });
    }
  

    createWheelMesh(radius, width) {

        const mat = new THREE.MeshStandardMaterial({ color: 0xffFFFF });

        var t = new THREE.CylinderGeometry(radius, radius, width, 24, 1);
        t.rotateZ(Math.PI / 2);
        const WheelMesh = new THREE.Mesh(t, mat);
        WheelMesh.add(new THREE.Mesh(new THREE.BoxGeometry(width * 1.5, radius * 1.75, radius * .25, 1, 1, 1), mat));
        return WheelMesh;
    }

    createChassisMesh(w, l, h) {

        var shape = new THREE.BoxGeometry(w, l, h, 1, 1, 1);
        this.ChassisMesh = new THREE.Mesh(shape, this.materialInteractive);
        return this.ChassisMesh;
    }

    
    fixmat(name, mat){

        if(mat.name.startsWith('Glass')){
            mat.transparent = true;
            mat.opacity = 0.75;
        }else if(name.startsWith('wall')){

            if(mat.name === 'wall-emm'){
                mat.toneMapped = false;
                mat.envMapIntensity = 32.0;
            }
        }else
            mat.envMapIntensity = 0.2;

        //mat.transmission = 0.0;

    }
    fixmesh(mesh){

        mesh.castShadow = true;
        mesh.receiveShadow = true;

        if(mesh.material.length > 0){
            for(let mat of mesh.material){
                this.fixmat(mesh.name, mat);
            }
        }
        else
            this.fixmat(mesh.name, mesh.material);
                     
    }

        deGroupGLB(gltf, prescale = true){

        const scene = new THREE.Scene();

        gltf.traverse((child) => {

            if(child.name === 'Scene')
                return;
    
            if (child.type === 'Mesh') {
                //child.geometry = child.geometry.clone()
                child.receiveShadow = true;
                child.castShadow = true;
    
                if(child.parent.name === 'Scene'){

                    /*
                    console.log('single mesh '+child.name);
                    console.log(child);
                    */
    
                    const cc = child.clone();
                    
                    if(prescale === true){
                        cc.geometry = child.geometry.clone();
                        /*
                        const mat = new THREE.Matrix4().compose(new THREE.Vector3(), child.quaternion, child.scale);
                        cc.geometry.applyMatrix4(mat);
                        cc.scale.set(1,1,1);
                        cc.quaternion.set(0,0,0,1);
                        */

                        const iq = child.quaternion.clone().invert();
                        //cc.geometry.applyQuaternion(child.quaternion);
                        cc.geometry.scale(child.scale.x, child.scale.y, child.scale.z);
                        //cc.geometry.applyQuaternion(iq);
                        cc.scale.set(1,1,1);

                    }

                    this.fixmesh(cc);

                    //console.log(cc);
                    scene.add(cc);
                }
    
            }else if (child.type === 'Group') {
    
                const geos = [];
                const mats = [];

                /*
                console.log('mesh '+child.name);
                console.log(child);
                */

                for(let cc of child.children){

                    if(!cc.isMesh)
                        continue;
    
                    if(cc.children.length > 0){
                        console.warn('depth > 2');
                    }
    
                    geos.push(cc.geometry);
    
                    if(cc.material.length > 0){
                        console.warn('multiple mats');
    
                    }else{
                        mats.push(cc.material);
                    }

                    /*
                    console.log('child '+cc.name);
                    console.log(cc);
                    */
                }
                if(geos.length <= 0)
                    return;

                let newgeo = mergeGeometries(geos, true);

                if(prescale === true){
                    /*
                    const mat = new THREE.Matrix4().compose(new THREE.Vector3(), child.quaternion, child.scale);
                    newgeo.applyMatrix4(mat);
                    */

                    const iq = child.quaternion.clone().invert();
                    //newgeo.applyQuaternion(child.quaternion);
                    newgeo.scale(child.scale.x, child.scale.y, child.scale.z);
                    //newgeo.applyQuaternion(iq);
                }



                let newMesh;
                if(child.children[0].skeleton){
                    newMesh = new THREE.SkinnedMesh(newgeo, mats);
                    const rootBone = child.children[0].skeleton.bones[ 0 ];
                    newMesh.bind( child.children[0].skeleton );
                }else{
    

                    const nMaterials = mats.length ? mats.length : 1;
                    let hasBone = false;
                    let Skeleton = null;

                    if(nMaterials > 1) {
                
                        for (let n = 0; n < nMaterials; n++) {

                            if(mats[n].name.startsWith('bone')){

                                if(Skeleton === null){

                                    /*
                                    const gc = new THREE.Vector3();
                                    //const skinG = m.geometry;//.clone();
                                    
                                    newgeo.computeBoundingBox();
                                    newgeo.boundingBox.getCenter(gc);
                                    newgeo.translate(-gc.x, -gc.y, -gc.z);
                                    newMesh.position.add(gc.x, gc.y, gc.z);
                                    */
                                    Skeleton = new SimpleSkeleton({x:0,y:0,z:0}, {x:0, y:0, z:0, w : 1.0});
                                }

                                const boneName = mats[n].name.substr(5);
                                const sp = this.getGeoGrpCenter(newgeo, n);

                                const q = new THREE.Quaternion(); //.setFromUnitVectors( up, usp );
                                const newBone = new SimpleBone('pre-'+boneName, q, 0.0, 0.0, 0.0);
                                newBone.boneOffset = sp.c;
                                Skeleton.addBone(newBone);
                                hasBone = true;
                            }
                        }
                    }
                    
                    if(hasBone){

                        newgeo = newgeo.toNonIndexed();

                        for (let n = 0; n < mats.length; n++) {

                            if(mats[n].name.startsWith('center')){
                                const boneName = mats[n].name.substr(7);
                                const pbone = Skeleton.findBone('pre-'+boneName);
                                if(pbone !== null){
                                    const sp = this.getGeoGrpCenter(newgeo, n);

                                    const ppos = new THREE.Vector3(0, pbone.length, 0).applyQuaternion(pbone.quat);
                                    const dpos = new THREE.Vector3().subVectors(sp.c, ppos);
                                    const spl = dpos.length();
                                    const usp = dpos.normalize();
                                    const q = new THREE.Quaternion();//.setFromUnitVectors( up, usp );
                                    const newBone = new SimpleBone(boneName, q, spl, sp.r, 0.0);

                                    pbone.addChild(newBone);

                                    newBone.center = sp.c;
                                    newBone.infType = 'sphere';
                                    this.addBoneInfluenceSphere(newgeo, {x:false,y:false,z:false}, newBone, n, 1);
                                }
                            }

                            if(mats[n].name.startsWith('ccube')){
                                const boneName = mats[n].name.substr(6);
                                const pbone = Skeleton.findBone('pre-'+boneName);
                                if(pbone !== null){
                                    const sp = this.getGeoGrpCenter(newgeo, n);

                                    const ppos = new THREE.Vector3(0, pbone.length, 0).applyQuaternion(pbone.quat);
                                    const dpos = new THREE.Vector3().subVectors(sp.c, ppos);
                                    const spl = dpos.length();
                                    const usp = dpos.normalize();
                                    const q = new THREE.Quaternion();//.setFromUnitVectors( up, usp );
                                    const newBone = new SimpleBone(boneName, q, spl, sp.r, 0.0);
                                    pbone.addChild(newBone);

                                    newBone.center = sp.c;
                                    newBone.halfSize = sp.hs;
                                    newBone.dir = sp.d;
                                    newBone.infType = 'cube';
                                    this.addBoneInfluenceCube(newgeo, {x:false,y:false,z:false}, newBone, n, 1);
                                }
                            }
                        } 
                    
                        const map = Skeleton.getBoneMap();

                        for(let b in map){
                            const bone = Skeleton.findBone(b);
                            if(!bone)
                                continue;
                            
                            if(bone.infType === 'sphere')
                                this.addBoneInfluenceSphere(newgeo, {x:false,y:false,z:false}, bone, map[b], 1);
                            else if(bone.infType === 'cube')
                                this.addBoneInfluenceCube(newgeo, {x:false,y:false,z:false}, bone, map[b], 1);
                        }

                        newMesh = new THREE.Mesh(newgeo, mats);
                        newMesh.SimpleSkeleton = Skeleton;
                    }else
                        newMesh = new THREE.Mesh(newgeo, mats);


                    newMesh.name = child.name;
                    newMesh.position.set(child.position.x, child.position.y, child.position.z);
                    newMesh.quaternion.set(child.quaternion.x, child.quaternion.y, child.quaternion.z, child.quaternion.w);
                }

              
                scene.add(newMesh);
            }
        });

        this.destroyObj(gltf);

        

        return scene;
    }
    
    worldToGroundUvX(x){
        return (x - this.groundGeo.boundingBox.min.x) / this.groundSize.x
    }

    worldToGroundUvZ(z){
        return (z - this.groundGeo.boundingBox.min.z) / this.groundSize.z;
    }

    computeBumpUv(channel = 3){

        const geo = this.groundGeo;

        const nVtx = geo.attributes.position.count;

        const newUvs = new Float32Array(nVtx * 2);
        for(let n = 0; n< nVtx;n++){
            newUvs[n * 2  + 0] = this.worldToGroundUvX(geo.attributes.position.array[n * 3 + 0]);
            newUvs[n * 2  + 1] = this.worldToGroundUvZ(geo.attributes.position.array[n * 3 + 2]);
        }

        const uvName = 'uv'+channel;
        const tName = 'tangent'+channel;
  
        geo.setAttribute(uvName, new THREE.Float32BufferAttribute(newUvs,  2) );
        const tangents = generateTangents(geo.attributes.position.array, geo.attributes.normal.array, geo.attributes[uvName].array);
        geo.setAttribute(tName, new THREE.Float32BufferAttribute(tangents,  4) );


        /*
         const matTang = new Float32Array(nVtx * 4);
        const ttName = 'totang';
        for(let n = 0; n< nVtx;n++){

            const t1x = geo.attributes.tangent.array[n*4 + 0];
            const t1z = geo.attributes.tangent.array[n*4 + 2];

            const t3x = geo.attributes.tangent3.array[n*4 + 0];
            const t3z = geo.attributes.tangent3.array[n*4 + 2];

            const dot = t1x * t3x + t3z * t3z;
            const a = Math.acos(dot);

            const m = new Array(4);

            matTang[n * 4 +0] = Math.cos(a);
            matTang[n * 4 +1] = -Math.sin(a);
            matTang[n * 4 +2] = Math.sin(a);
            matTang[n * 4 +3] = Math.cos(a);
        }
        geo.setAttribute(ttName, new THREE.Float32BufferAttribute(matTang,  4) );
        */




    }

    getInstName(name){
        if (name.startsWith('col-cube')){
            return name.substring(9, name.length);
        }else if (name.startsWith('col-cyl')){
            return name.substring(8, name.length);
        }else if (name.startsWith('Wheel')){
            return name.substring(7, name.length);
        }else if(name.startsWith('lamp')){
            return  'lamp';
        }else if (name.startsWith('col-barrel')){  
            return name.substring(11, name.length);
        }else if (name.startsWith('col-dcube')){
            return name.substring(10, name.length);
        }
        return name;
    }


        setAllCulled(obj, culled) {

                if(culled){
                    obj.traverse(function(c){
                        c.oldFC = c.frustumCulled;
                        c.frustumCulled = false;
                    })
                }else{
                    obj.traverse(function(c){
                        c.frustumCulled = c.oldFC;
                    })
                }
            }
    async loadTerrain(track, manager = null, loadingController = null) {


        if(track.terrain.name.endsWith('.glb')){
            const arr = track.terrain.name.split('.');
            const gltf = await this.loadGLTF(arr[0], manager, loadingController);
            track.terrain.mesh = this.deGroupGLB(gltf);
        }else
            track.terrain.mesh = await this.loadObj(track.terrain.name);

        track.terrain.mesh.position.set(track.terrain.offset.x, track.terrain.offset.y, track.terrain.offset.z);

        const self=this;

        let fxMap = null;
        const texLoader = new THREE.TextureLoader();

        if(track.terrain.fxmap !== null){
            fxMap = await texLoader.loadAsync(track.terrain.fxmap);
        }
        /*
        this.roadMaT.secMap = await this.texLoader.loadAsync(this.assetBase+"/assets/textures/" + track.roadTex2);
        this.roadMaT.normalMap2 =await this.texLoader.loadAsync(this.assetBase+"/assets/textures/" + track.roadNorm2);
        */

        let nmap2 = null;
        if(track.roadNorm2){
            nmap2 = await this.texLoader.loadAsync(this.assetBase+"/assets/textures/" + track.roadNorm2);
            nmap2.wrapS = THREE.RepeatWrapping;
            nmap2.wrapT = THREE.RepeatWrapping;
            nmap2.channel = 1;
            nmap2.flipY = false;
            nmap2.repeat.set(1, 1);
        }
        
        let map2 = null;
        
        if(track.roadTex2){
            map2= await this.texLoader.loadAsync(this.assetBase+"/assets/textures/" + track.roadTex2);
            map2.wrapS = THREE.RepeatWrapping;
            map2.wrapT = THREE.RepeatWrapping;
            map2.channel = 1;
            map2.flipY = false;
            map2.repeat.set(1, 1);
        }

        track.terrain.mesh.traverse(child => {

            if(!child.isMesh)
                return;

            if (child.name.startsWith('Axis')){

                const targetObject = child.name.substr(5);
                child.geometry.computeBoundingSphere();

                track.movingObjects[targetObject] = {angle : new THREE.Euler(), axis : child.geometry.boundingSphere.center.clone().add(child.position)};
            }


            if(child.material.length){
                
                for(let n=0;n<child.material.length;n++){
                    
                    if(child.material[n].opacity<1.0){
                        child.material[n].transparent = true;
                        child.material[n].alphaTest = 0.3;
                    }

                    if(child.material[n].name.startsWith('bone')){
                        const targetObject = child.material[n].name.substr(5);
                        const sp = self.getGeoGrpCenter(child.geometry, n);
                        track.movingObjects[targetObject] = {angle : new THREE.Euler(), rot : new THREE.Quaternion(), oPos : {x: child.position.x, y: child.position.y, z: child.position.z}, axis : {x:sp.c.x +child.position.x, y:sp.c.y +child.position.y, z:sp.c.z +child.position.z}};
                    }
                }
            }
        });
       

        /*
        if(track.terrain.lightMap){
            this.lightMap = await texLoader.loadAsync(this.assetBase+"/assets/textures/city_map/baketexture.png");
            this.lightMap.wrapS = THREE.RepeatWrapping;
            this.lightMap.wrapT = THREE.RepeatWrapping;
            this.lightMap.channel = 2;
            this.lightMap.flipY = false;
            this.lightMap.repeat.set(1, 1);
        }else*/
            this.lightMap = null;

        /*
        const lightMap2 = await texLoader.loadAsync(this.assetBase+"/assets/textures/city_map/signbake.png");
        lightMap2.wrapS = THREE.RepeatWrapping;
        lightMap2.wrapT = THREE.RepeatWrapping;
        lightMap2.channel = 2;
        lightMap2.flipY = false;
        lightMap2.repeat.set(1, 1);
        */

        let grassDef = {};


        track.terrain.mesh.traverse((child) => {
            
            if(!child.isMesh)
                return;

            if(child.name.startsWith('Area')){
            
                const lname = child.name.substring(5, child.name.length);

                if(this.rtEngine){
                    const arr = lname.split('-');
                    const num = parseInt(arr[1]);

                    const geo = child.geometry.clone();
                    geo.translate(child.position.x, child.position.y, child.position.z);
                    geo.computeBoundingBox();
                    this.rtEngine.areas.push({ name: lname, num: num, box : geo.boundingBox.clone(), geos :[]});
                }
            }


            const lname = this.getInstName(child.name);

            const it = track.findInstance(lname);
            if(it)
                it.nInstances ++;
            
            if ((child.name.startsWith('Wheel')) || (child.name.startsWith('SWheel')) || (child.name.startsWith('Crystal')) || (child.name.startsWith('buddha')) || (child.name.startsWith('fire-start'))|| (child.name.startsWith('cyberpart'))|| (child.name.startsWith('sandpart')) || (child.name.startsWith('Star'))||(child.name.startsWith('cascade'))) { 

                child.geometry.computeBoundingSphere();

                const ar = child.name.split('-');

                if((ar.length < 2)||((!ar[1].startsWith('cabin'))&&(!ar[1].startsWith('fairy')))){
                    const gc = { x: child.geometry.boundingSphere.center.x, y: child.geometry.boundingSphere.center.y, z: child.geometry.boundingSphere.center.z };
                    child.geometry.translate(-gc.x, -gc.y, -gc.z);
                    child.position.add(gc);
                }
            }else if(track.movingObjects.hasOwnProperty(child.name)){

                const targetObject = child.name;

                const deltaOffset = new THREE.Vector3().subVectors(child.position, track.movingObjects[targetObject].axis);


                child.geometry.translate(   deltaOffset.x, 
                                            deltaOffset.y, 
                                            deltaOffset.z);

                child.position.add( deltaOffset.negate());
                

                                    
            }else if(child.name.startsWith('Grass')){

                const ar =child.name.split('-');
                const grassName = ar[0];
                let gid = 0;

                if(grassName.length > 5){
                    const nnn = grassName.substr(5);
                    gid = parseInt(nnn) - 1;
                }
                
                /*
                if(ar.length>1){
                    const ar2 = ar[1].split('.');
                    gid = parseInt(ar2[0]);
                }
                */
                    
                child.updateMatrix();

                if(grassDef[gid] == undefined){
                    grassDef[gid] = {nGrass: 0, grassGeo:child.geometry,   grassMat : child.material, transforms : []};
                }

                const gdef = grassDef[gid];
                gdef.transforms[gdef.nGrass] = new Transformation3(child.position, child.quaternion);
                gdef.nGrass++;

            }else if(child.name.startsWith('Anchor')){

                const targetObject = child.name.substr(7);

                child.geometry.computeBoundingSphere();


                const objPos = child.geometry.boundingSphere.center.clone().add(child.position);

                const deltaOffset = new THREE.Vector3().subVectors(child.position, track.movingObjects[targetObject].axis);

                track.movingObjects[targetObject].deltaAnchor = new THREE.Vector3().subVectors(objPos, track.movingObjects[targetObject].axis);

                child.geometry.translate( deltaOffset.x, deltaOffset.y, deltaOffset.z);
                child.position.add( deltaOffset.negate());
      

            }else if (child.name.startsWith('path')){
                const arr = child.name.split('-');

                if(arr.length <2){
                    console.warn('bad path '+child.name);
                    return;
                }
                const name = arr[1];
                const num = arr[2];

                if(!track.pathes[name]){
                    track.pathes[name] = [];
                }
                track.pathes[name][num-1] = {pos : child.position.clone(), rot : child.quaternion.clone()};
                return;
            }else if (child.name == 'Plane') {

                child.receiveShadow = true;
                child.castShadow = false;
                
                
                /*
                child.geometry.translate(   child.position.x + track.terrain.offset.x, 
                                            child.position.y + track.terrain.offset.y , 
                                            child.position.z + track.terrain.offset.z) ;
                */
                

                
                child.geometry.computeBoundsTree();
                //child.position.set(0,0,0);
                child.updateMatrix();
                

                track.groundGeo = child.geometry.clone();
                track.groundGeo = track.groundGeo.toNonIndexed();
                track.groundGeo.applyMatrix4(child.matrix);

                track.ground = child;

                if(track.trackPoints.length <= 1){

                    this.groundSize = new THREE.Vector3();
                    this.groundGeo = track.ground.geometry;
                    this.groundGeo.computeBoundingBox();
                    this.groundGeo.boundingBox.getSize(this.groundSize);

                    this.computeBumpUv(3);

                    if(this.bumpTex)
                        this.bumpTex.channel = 3;


                    if(child.material.length>0){
                        for(let imat =0; imat < child.material.length; imat++){

                            const mat = child.material[imat];
    
                            /*if( (mat.name.startsWith("ground")) || 
                                (mat.name === "road") || 
                                (mat.name === "road2")|| 
                                (mat.name === "road3")|| 
                                (mat.name === "road4"))*/
                            {
    
                                const roadMat = new THREE.MeshRoadPhysicalMaterial();
                                roadMat.copy(mat);
                                    
                                if(this.lightMap){
                                    roadMat.lightMap = lightMap;
                                    roadMat.lightMapIntensity = 4.0;
                                }
        
                                roadMat.driftMap = this.bumpTex;
                                roadMat.driftMapScale = new THREE.Vector2(1, 1);
                                
                                if( fxMap){
                                    roadMat.fxMap = fxMap;
                                    roadMat.fxMap.wrapS = THREE.RepeatWrapping;
                                    roadMat.fxMap.wrapT = THREE.RepeatWrapping;
                                    roadMat.fxMap.channel = 3;
                                    roadMat.fxMap.flipY = false;
                                    roadMat.fxMap.repeat.set(1, 1);
                                }

                                
                                roadMat.envMapIntensity = 0.35;
    
                                //if(roadMat.name === 'road') {
                                    roadMat.normalMap2 = nmap2;
                                    roadMat.secMap = map2;
                                    roadMat.driftMapFlags = 7;

                                    /*
                                    if(roadMat.emissiveMap){
                                        roadMat.emissiveMap.channel = 2;
                                    }
                                    roadMat.envMapIntensity = 0.4;
                                    roadMat.roughness = 1.0;
                                    roadMat.metalness = 0.05;
                                    */
                                    //roadMat.roughnessMap = null;
                                /*}else{
                                    roadMat.driftMapFlags = 7;
                                }*/
    
                                child.material[imat] = roadMat;
                            }
                        }
                    }else{


                       

                        const roadMat = new THREE.MeshRoadPhysicalMaterial();
                        roadMat.copy(child.material);
                            
                        if(this.lightMap){
                            roadMat.lightMap = lightMap;
                            roadMat.lightMapIntensity = 4.0;
                        }

                        roadMat.driftMap = this.bumpTex;
                        roadMat.driftMapScale = new THREE.Vector2(1, 1);
                        
                        if( fxMap){
                            roadMat.fxMap = fxMap;
                            roadMat.fxMap.wrapS = THREE.RepeatWrapping;
                            roadMat.fxMap.wrapT = THREE.RepeatWrapping;
                            roadMat.fxMap.channel = 3;
                            roadMat.fxMap.flipY = false;
                            roadMat.fxMap.repeat.set(1, 1);
                        }

                        roadMat.normalMap2 = nmap2;
                        roadMat.secMap = map2;
                        roadMat.driftMapFlags = 4;

                        //roadMat.envMapIntensity = 0.45;
                        //roadMat.roughness = 0.2;
                        //roadMat.roughnessMap = null;
                       

                        child.material = roadMat;
                    }

                    

                }else{
                    //track.ground.geometry.translate(track.terrain.offset.x, track.terrain.offset.y, track.terrain.offset.z);


                    if(child.material.length > 0){
                        for(let mat of child.material){

                            /*
                            if(mat.emissiveMap){
    
                                mat.lightMap = mat.emissiveMap;
                                mat.lightMap.channel = 2;
                                mat.lightMapIntensity = 2.0;
                                mat.lightMap.wrapS = THREE.RepeatWrapping;
                                mat.lightMap.wrapT = THREE.RepeatWrapping;
                                mat.lightMap.flipY = true;
                                mat.lightMap.repeat.set(1, 1);
                                mat.emissiveMap = null;
                            }
                            */
                        }
                    }
                    
                }

            }else if(child.material.length) {

                for(let n =0;n<child.material.length;n++) {

                    if(child.material[n].name.startsWith('Anchor')){
                        const targetObject = child.material[n].name.substr(7);

                        if(track.movingObjects.hasOwnProperty(targetObject)){
                            const sp = self.getGeoGrpCenter(child.geometry, n);
                            const spC = new THREE.Vector3 (sp.c.x +child.position.x, sp.c.y +child.position.y, sp.c.z +child.position.z);
                            track.movingObjects[targetObject].deltaAnchor = new THREE.Vector3().subVectors(spC, track.movingObjects[targetObject].axis);
                        }
                    }
                }

       
            }
        });

        if(this.rtEngine){
            this.rtEngine.areas.sort(function(a ,b){
                if(a.num < b.num)
                    return -1;
    
                if(a.num > b.num)
                    return 1;
    
                return 0;
            })
        }

        const rc = new THREE.Raycaster();
        const from = new THREE.Vector3();
        const to = new THREE.Vector3(0.0, -1.0, 0.0);

        track.ground.updateMatrixWorld();
        track.ground.geometry.computeBoundsTree();

        for(let gid in grassDef){

            const gdef = grassDef[gid];

            gdef.grassGeo.computeBoundingBox();
            gdef.grassGeo.translate(0, -gdef.grassGeo.boundingBox.min.y, 0);

            const myGrass = new Grass(gdef.grassGeo, gdef.grassMat, gdef.nGrass);

            const min = new THREE.Vector3(Infinity, Infinity, Infinity);
            const max = new THREE.Vector3(-Infinity, -Infinity, -Infinity);

            for(let n=0;n<myGrass.nGrass;n++){

                from.set(   gdef.transforms[n].position.x, 
                            gdef.transforms[n].position.y, 
                            gdef.transforms[n].position.z);
                            
                rc.set(from, to);
                const pts = rc.intersectObject(track.ground, false);

                if(pts.length>0){
                    gdef.transforms[n].position.x = pts[0].point.x - track.terrain.offset.x;
                    gdef.transforms[n].position.y = pts[0].point.y - track.terrain.offset.y;
                    gdef.transforms[n].position.z = pts[0].point.z - track.terrain.offset.z;
                }
                
                min.x = Math.min(min.x, gdef.transforms[n].position.x);
                min.y = Math.min(min.y, gdef.transforms[n].position.y-1.0);
                min.z = Math.min(min.z, gdef.transforms[n].position.z);

                max.x = Math.max(max.x, gdef.transforms[n].position.x);
                max.y = Math.max(max.y, gdef.transforms[n].position.y + 2.0);
                max.z = Math.max(max.z, gdef.transforms[n].position.z);

                myGrass.setTransform(n, gdef.transforms[n]);
            }
            myGrass.buildOcTree();
            myGrass.bbox = new THREE.Box3(min, max);

            track.terrain.grasses.push(myGrass);
        }
    }

    generateGrass(track, nGrass, width, offset, size, tex = "/assets/textures/grass1.webp"){

        const geo = new THREE.PlaneGeometry(size.x, size.y, 1, 1);
        geo.translate(0,size.y/2,0);
        const grassTex = new THREE.TextureLoader().load(this.assetBase+tex)
        const material = new THREE.MeshStandardMaterial({map:grassTex, transparent: true})
        const myGrass = new Grass(geo, material, nGrass);


        const rcObjs = [];

        if(track.roadObj)
            rcObjs.push(track.roadObj)

        if(track.ground)
            rcObjs.push(track.ground)        
         
        const t= new Transformation3();
        const pos = new THREE.Vector3();
        const from = new THREE.Vector3();
        const to = new THREE.Vector3();

        for(let n=0;n<myGrass.nGrass;n++){

            let r;
            let looping = {};
            let p1;
            let p2;
            let insideLooping = true;

          
            while(insideLooping){
                r = Math.random();
                p1 =track.curveL.getPointAt(r);
                p2 =track.curveR.getPointAt(r);

                p1.y -= 0.5;
                p2.y -= 0.5;

                insideLooping = false;
                for(let l of track.loopings){
                    if(l.bbox.containsPoint(p1)){
                        insideLooping = true;
                        break;
                    }
                }
            }

            const r2 = Math.random() * width + offset;
            pos.lerpVectors(p1, p2, r2);

            from.set(pos.x, pos.y + 1.0, pos.z);
            to.set(0,-1,0);

            this.rc.set(from, to);
            const pts = this.rc.intersectObjects(rcObjs);
            if(pts.length>0){
                pos.y = pts[0].point.y;
            }
            t.set(pos)
            myGrass.setTransform(n, t);
        }
        myGrass.buildOcTree();

        return myGrass;
    }

    getRagdollBoneData(bone, array, offset){

        array[offset + 0] = bone.rot.x;
        array[offset + 1] = bone.rot.y;
        array[offset + 2] = bone.rot.z;
        array[offset + 3] = bone.rot.w;
        offset += 4;
    
        for(let child of bone.children){
          offset = this.getRagdollBoneData(child, array, offset);
        }
    
        return offset;
    }

    setRagdollBoneData(bone, array, offset){

        bone.rot.x = array[offset + 0];
        bone.rot.y = array[offset + 1];
        bone.rot.z = array[offset + 2];
        bone.rot.w = array[offset + 3];
        offset+=4;
    
        for(let child of bone.children){
          offset = this.setRagdollBoneData(child, array, offset);
        }

        return offset;
    
      }

    removeDialog(char){

        if(!char.dialogOptions) 
            return;

        if(typeof char.dialog.lines[char.dialogInstance.line].txt === 'string') {
            if(char.dialog.lines[char.dialogInstance.line].txt.txtMesh){
                this.removeMeshChild(char.dialogOptions , char.dialog.lines[char.dialogInstance.line].txt.txtMesh);
            }
        }else{
            for(let l of char.dialog.lines[char.dialogInstance.line].txt){
                this.removeMeshChild(char.dialogOptions , l.txtMesh);
            }
        }

        //this.removeMeshChild(char.dialogOptions , char.backGround);
        this.removeMeshChild(char.dialogOptions , char.bMesh);

        for(let i=0;i<char.dialogAnswers.length;i++){

            const a = char.dialogAnswers[i];
            this.removeMeshChild(char.dialogOptions, a.back);
            this.removeMeshChild(char.dialogOptions , a.txtMesh);
            this.selectScene.remove(a.selectMesh);    
        }

        this.removeMeshChild(char.avatarMesh , char.dialogOptions);
        char.dialogOptions = null;
        char.dialogAnswers = [];
    }
    updateDialog(char){

        this.removeDialog(char);

        const dialogLine = char.dialog.lines[char.dialogInstance.line];
        const txt = (typeof dialogLine.txt === 'string') ? dialogLine.meshs[0] : dialogLine.meshs[char.currentDialogLine];

        char.dialogOptions = new THREE.Group();
        char.dialogOptions.position.set(0, char.avatarDef.height + 3.0, 0)
        char.dialogOptions.lookAt(this.cameraAnchor.position);
        char.dialogOptions.updateMatrixWorld(true);
        char.dialogOptions.renderOrder = 10;

        this.addMeshChild(char.avatarMesh, char.dialogOptions);
        //this.addMeshChild(char.dialogOptions , char.backGround);
        this.addMeshChild(char.dialogOptions , txt.txtMesh);
        this.addMeshChild(char.dialogOptions , char.bMesh);

        let answerPos = -dialogLine.txtSize.y - this.dialogHeaderHeight;

        if(typeof dialogLine.txt !== 'string' && ((char.currentDialogLine+1) < dialogLine.txt.length)){

            const myoffset4 = new THREE.Vector3(0, answerPos, 0);

            const answer = {id : -1};

            if(char.selectedAnswer == 0)
                answer.back = new THREE.Mesh(char.cgeo, char.slotMatSel);
            else
                answer.back = new THREE.Mesh(char.cgeo, char.slotMat);
        
            answer.back.renderOrder = 20;
            answer.back.name = 'next-line';
            answer.back.position.set(myoffset4.x, myoffset4.y, myoffset4.z);

            this.setMeshStatic(answer.back, false);
            this.addMeshChild(char.dialogOptions , answer.back);

            answer.back.updateMatrixWorld(true);

            answer.selectMesh = answer.back.clone();
            answer.selectMesh.name = 'select-next-line';
            answer.selectMesh.matrixAutoUpdate =false;
            this.selectScene.add(answer.selectMesh);               
            
            this.nextLinetxtMesh.position.set(myoffset4.x, myoffset4.y - 0.25, myoffset4.z-0.2);
            this.nextLinetxtMesh.renderOrder = 30;
            this.addMeshChild(char.dialogOptions ,  this.nextLinetxtMesh);
           
            /*
            answer.txt = this.createBitmapText(4, myoffset4.x-0.065, myoffset4.y * 0.8, -1, '#FFF', 0.04, 0.2, 1);
            this.addBitmapText(answer.txt);
            this.setBitmapText(answer.txt, 'next');
            */

            char.dialogAnswers.push(answer);

          
            
        }else{

            for(let i=0;i<dialogLine.answers.length;i++){

                const myoffset4 = new THREE.Vector3(0, answerPos, 0);
                const a = dialogLine.answers[i];
                const answer = {id : a.dialogID};

                if(char.selectedAnswer == i)
                    answer.back = new THREE.Mesh(char.cgeo, char.slotMatSel);
                else
                    answer.back = new THREE.Mesh(char.cgeo, char.slotMat);

                answer.back.renderOrder = 20;
                answer.back.name = a.dialogID >= 0 ?'answer-'+i : 'end-dialog-'+i;
                answer.back.position.set(myoffset4.x, myoffset4.y, myoffset4.z);
              
                this.setMeshStatic(answer.back, false);
                this.addMeshChild(char.dialogOptions, answer.back);
                
                answer.back.updateMatrixWorld(true);

                answer.selectMesh = answer.back.clone();
                answer.selectMesh.name = a.dialogID >= 0 ?'select-answer-'+i : 'select-end-dialog-'+i;
                answer.selectMesh.matrixAutoUpdate =false;
                this.selectScene.add(answer.selectMesh);                

                a.txtMesh.position.set(myoffset4.x, myoffset4.y - 0.25, myoffset4.z-0.2);
                a.txtMesh.renderOrder = 30;
                this.addMeshChild(char.dialogOptions ,  a.txtMesh);
    
                char.dialogAnswers.push(answer);
                answerPos -= this.dialogAnswerHeight;
            }
        }

        char.dialogStart = new Date().getTime();
    }

    
    setDialogLine(char, line, subline = 0){

        if(!char.dialog)
            return;
            
        if((char.dialogInstance.line == line)&&(char.currentDialogLine == subline))
            return;
        
        if( char.dialogInstance.line != line)
            char.currentDialogLine = 0;
        else
            char.currentDialogLine = subline;

        char.dialogInstance.line = line;
    
        if(char.dialogOptions)
            this.updateDialog(char);
        else
            this.OpenDialog(char);
    }
    
    setDialogAnswer(char, answer){

        if(!char.dialog)
            return;

        if(char.selectedAnswer == answer)
            return;

        char.selectedAnswer =  answer;
    
        if(char.dialogOptions)
            this.updateDialog(char);
        else
            this.OpenDialog(char);
    }
    
    OpenDialog(char){

        char.dialogAnswers = [];
        char.currentDialogLine = 0;
        char.selectedAnswer = 0;
        char.dialogInstance.line = 0;

        this.updateDialog(char);
    }

    async initDialog(character){

        if(!this.dialogBack)
            return;
        
        character.dlgMat = new THREE.MeshBasicMaterial({color : 0x880000, depthTest: false, transparent:true, toneMapped: true})
        character.backMat = new THREE.MeshBasicMaterial({name:"dialog background", color :0x000000, side:THREE.DoubleSide, map: null, opacity:1.0, transparent:false, depthTest: false, depthWrite: false, toneMapped: false})
        character.txtMat = new THREE.MeshBasicMaterial({name:"dialog text",color :0x888888, side:THREE.DoubleSide, map: null, opacity:1.0, transparent:false, depthTest: false, depthWrite: false, toneMapped: false})
        character.slotMat = new THREE.MeshBasicMaterial({name : "answer background", color :0x662222, depthTest: false, transparent:false, toneMapped: false});
        character.slotMatSel = new THREE.MeshPhongMaterial({name : "answer sel background", color :0xFFFFFF, depthTest: false, emissiveIntensity:4.0, emissiveMap: this.panelTex, map: this.panelTex, transparent:false, toneMapped: false})
        character.borderMat = new THREE.MeshPhongMaterial({name:"dialog border", color : 0x0000FF, emissive: 0x0000FF, emissiveIntensity : 4.0})

        character.maxTxtHeight = 0;
        character.maxLineWidth = 0;
        character.maxAnswerWidth = 0;

        for(let line of character.dialog.lines) {
            
            line.txtSize = new THREE.Vector3();
            line.meshs  = [];

            if(typeof line.txt === 'string'){
                line.meshs[0] = {};
                line.meshs[0].txtGeo = new TextGeometry(line.txt, this.dialogFont);
                line.meshs[0].txtGeo.computeBoundingBox();
                line.meshs[0].txtGeo.boundingBox.getSize(line.txtSize);
                line.meshs[0].txtGeo.translate(- line.txtSize.x/2, 0, 0);
                line.meshs[0].txtMesh = new THREE.Mesh(line.meshs[0].txtGeo, character.txtMat);                
            }else{

                for(let l =0;l<line.txt.length;l++){
                    line.meshs[l] = {};
                    line.meshs[l].txtGeo = new TextGeometry(line.txt[l], this.dialogFont);
                    line.meshs[l].txtGeo.computeBoundingBox();
                    line.meshs[l].txtGeo.boundingBox.getSize(line.txtSize);
                    line.meshs[l].txtGeo.translate(- line.txtSize.x/2, 0, 0);
                    line.meshs[l].txtMesh = new THREE.Mesh(line.meshs[l].txtGeo, character.txtMat);                

                }
            }

            character.totalTxtHeight = this.dialogBorder + this.dialogHeaderHeight+ line.txtSize.y;
            character.maxLineWidth = Math.max(character.maxLineWidth, line.txtSize.x);

            for(let a of line.answers){
                a.txtSize = new THREE.Vector3();

                a.txtGeo = new TextGeometry(a.txt, this.dialogFont);
                a.txtGeo.computeBoundingBox();
                a.txtGeo.boundingBox.getSize(a.txtSize);
                a.txtGeo.translate(-a.txtSize.x/2, 0, 0);
                a.txtMesh = new THREE.Mesh(a.txtGeo, character.txtMat);

                character.totalTxtHeight += this.dialogAnswerHeight;
                character.maxAnswerWidth = Math.max(character.maxAnswerWidth, a.txtSize.x);
            }

            character.maxTxtHeight = Math.max(character.maxTxtHeight, character.totalTxtHeight);
        }
        
        character.cgeo = new THREE.PlaneGeometry(character.maxAnswerWidth + this.dialogBorder, 0.6);

        character.bMesh = this.dialogBack.clone();
        character.bMesh.name = "dialog background"
        character.bMesh.position.y = -0.8;
        character.bMesh.scale.set((character.maxLineWidth +this.dialogBorder)/2+0.2, (character.maxTxtHeight + this.dialogBorder)/2 + 0.4, 1);
    }

    async loadCharacter(character) {
        const self = this;
        let object;

        if((character.avatarDef.skelFile)&& (character.avatarDef.skelFile.length > 0)){
            character.skeleton = await this.loadSkeleton(character.avatarDef.skelFile);
            character.nBones = this.getNbones(character.skeleton.root);
            character.bindPose = new Float32Array(character.nBones * 7);
            let ofs = 0;
            this.getRagdollBoneData(character.skeleton.root, character.bindPose, ofs );

            const spine = character.skeleton.root.findBone('spine');

            if(spine)
                character.orig_spine = new THREE.Quaternion(spine.rot.x,spine.rot.y,spine.rot.z,spine.rot.w)


            //const bone2 = world.track.npcs[i].tskel.getBoneByName('spine');
            //bone2.quaternion.set(q.x,q.y,q.z,q.z);

        }else
            character.skeleton = null;

        if(character.dialog){
          this.initDialog(character)
        }  

        if(character.instanceID > 0)
            return;
            
        if(character.avatarDef.model.endsWith('glb')){
            const gltf = await this.loadGLTF(character.avatarDef.model);
            object = this.deGroupGLB(gltf);
            this.destroyObj(gltf);

            if(character.avatarDef.model.startsWith('robotdog'))
                console.log(obj.geometry.attributes.position.array);

        }else{
            const mLoader = new MTLLoaderP();
            const oLoader = new OBJLoader(this.manager);
    
            const materials = await mLoader.loadAsync(this.assetBase+"/assets/mesh/" + character.avatarDef.model + ".mtl");
            await materials.preload();
    
            oLoader.setMaterials(materials);
            object = await oLoader.loadAsync(this.assetBase+"/assets/mesh/" + character.avatarDef.model + ".obj");

            await object.traverseAsync(async function (c){

                if(!c.isMesh)
                    return;
    
                if(c.material.length)
                {
                    const lc = c;
                    for(let n=0;n<lc.material.length;n++)
                        lc.material[n] = await lc.material[n];
                }
                else{
                    const lc = c;
                    lc.material = await lc.material;
                }
                    
            });
        }

        object.traverse((child) => {

            let geo = null,mats = null;

            if (child.type == "SkinnedMesh") {

              
                geo = child.geometry;
                delete geo.attributes.skinIndex;
                delete geo.attributes.skinWeight;

                mats = child.material;
            }

            if (child.type == 'Mesh') {

               
                if(child.material.length>0){
                    const m = child;

                    for(let n=0;n<m.material.length;n++){

                        if(m.material[n].name.startsWith('Toon')){
                    
                            if((m.material[n].map) && (m.material[n].map.image)){
                                const oldMat = m.material[n];
                                const color = self.getPixel(m.material[n].map.image, m.material[n].map.image.width - 1 , 0);
                                const toonMat = new THREE.MeshToonMaterial({gradientMap : self.gradientMap});
                                toonMat.color = new THREE.Color().setRGB(color[0]/255, color[1]/255, color[2]/255);
                            
                                m.material[n] = toonMat;
                                m.material[n].name = oldMat.name;
                                m.material[n].needsUpdate = true;
                                m.material[n].toonMap = oldMat.map;
                                m.needsUpdate = true;
        
                                oldMat.dispose();
                            }else{
                                const oldMat = m.material[n];
                                const color = oldMat.color.clone();
    
                                const toonMat = new THREE.MeshToonMaterial({gradientMap : self.gradientMap});
                                toonMat.color = color;//new THREE.Color().setRGB(color[0]/255, color[1]/255, color[2]/255);
                            
                                m.material[n] = toonMat;
                                m.material[n].name = oldMat.name;
                                m.material[n].needsUpdate = true;
                                m.needsUpdate = true;
        
                                oldMat.dispose();
    
                            }
                        }
                    }
                }

                geo = child.geometry;
                mats= child.material;
            }

            if(geo === null)
               return;

            character.avatarMesh = character.nInstances > 0 && character.skeleton === null ? new THREE.InstancedMesh(geo, mats, character.nInstances ) :  new THREE.Mesh(geo, mats);
            character.avatarMesh.geometry.computeBoundingBox();
            
            character.avatarMesh.castShadow = true;
            character.avatarMesh.receiveShadow = true;

            if( character.nInstances > 0 && character.skeleton === null)
                character.avatarMesh.frustumCulled = false;

            const bbox = character.avatarMesh.geometry.boundingBox;
            const center = new THREE.Vector3();
            const size = new THREE.Vector3();
            bbox.getCenter(center);
            bbox.getSize(size);
            character.center = new THREE.Vector3(center.x, center.y, center.z);
            character.size = new THREE.Vector3(size.x / 2.0, size.y / 2.0, size.z / 2.0);
        });
        this.destroyObj(object);
    
    }
   
    findSubsystemsBone(bone, subsystemsDef, subsystems){

        bone.life = -1;
        bone.total_life = -1;

        for(let sub of subsystemsDef){

            if(sub.name == bone.name){
          
                /*
                const geo1 = new THREE.BoxGeometry(6, 0.8, 0.8);
                const geo2 = new THREE.BoxGeometry(6, 0.4, 0.4);
    
                bone.DMGBar1 = new THREE.Mesh(geo1, this.dmgMaterial);
                bone.DMGBar2 = new THREE.Mesh(geo2, this.dmgMaterial2);
                */
                bone.life = sub.life;
                bone.total_life = bone.life;

                bone.dmgBar = this.createDMGBar(new THREE.Vector3(), new THREE.Vector3(0 , 2 , 0), bone.total_life);

                bone.subsystem = {id : bone.id, name : bone.name, pos: new THREE.Vector3(), dmgBar : bone.dmgBar};
                subsystems.push(bone.subsystem);
                break;
            } 
        }


        for(let c of bone.children){
            this.findSubsystemsBone(c, subsystemsDef, subsystems);
        }
    }


    createSelectMesh(char){
        char.tbox = new THREE.Box3(  new THREE.Vector3(char.center.x - char.size.x, char.center.y - char.size.y, char.center.z - char.size.z), 
        new THREE.Vector3(char.center.x + char.size.x, char.center.y + char.size.y, char.center.z + char.size.z));

        const box = new THREE.BoxGeometry(char.size.x * 1.5, char.size.y * 1.5, char.size.z * 1.5);
        const mat = new THREE.MeshBasicMaterial({color : 0xFFFFFF});

        //box.translate(char.center.x, char.center.y, char.center.z)
        char.selectMesh = new THREE.Mesh(box , mat);
        char.selectMesh.position.set(char.pos.x, char.pos.y, char.pos.z);
        char.selectMesh.quaternion.set(char.rot.x, char.rot.y, char.rot.z, char.rot.w);
        char.selectMesh.name = 'select mesh npc-'+char.id;
    }

    async makeCharacter(char) {

        await this.loadCharacter(char);
        
        if(char.nInstances <= 1){
            char.avatarMesh.receiveShadow = false;
            char.avatarMesh.position.set(char.pos.x, char.pos.y, char.pos.z);
            char.avatarMesh.quaternion.set(char.rot.x, char.rot.y, char.rot.z, char.rot.w);
        }

    }

    createBitmapText(numChar, x, y, z, color = '#fff', size = 1.0, maxWidth = 200, type = 0){

        const chars = ['0','1','2', '3', '4', '5', '6', '7', '8', '9' , 'x', '.', '/'];
        //const sizes = [32,32,32,32,32,32,32,32,32,32,32,32,32,32]
        const chars2 = ['a' ,'b' ,'c', 'd', 'e', 'f', 'g', 'h', 'i', 'j', 'k', 'l', 'm', 'n', 'o', 'p', 'q', 'r', 's', 't', 'u', 'v', 'w', 'x', 'y', 'z', '.', ',', '?'];
        //const sizes2 = [ 34 , 34 ,34 , 34 ,  34, 34 , 34 , 34 , 28 , 28 , 37 , 34 , 30 , 34 , 34 , 34 , 34 , 34 , 34 , 34 , 34 , 34 , 34 , 34 , 34 , 34 , 20 , 24, 28 ]
        const fnt = this.speedFont;


        let glyphs = type === 0 ? chars : chars2;
        let sizes = [];
        let gsizes = [];
        let gpos = [];
        let gradv = [];

        let maxAdv = 0;

        for(let ic =0; ic<glyphs.length; ic++){
            const c = glyphs[ic];
            const g = fnt.data.glyphs[c];
            if(g){
                maxAdv = Math.max(maxAdv, g.ha);
                sizes[ic] = g.ha / 20.37;
                gsizes[ic] = (g.x_max-g.x_min) / g.ha;
                gpos[ic] = (g.x_min) / g.ha;
            }
        }
        for(let ic =0; ic<glyphs.length; ic++){
            const c = glyphs[ic];
            const g = fnt.data.glyphs[c];
            if(g){
                gradv[ic] = g.ha / maxAdv;
            }
        }

        let pos = [];
        let cp = 0;
        for(let s of sizes){
            pos.push(cp);
            cp+=s;
        }

        const map  = type === 0 ? this.bitmapFont : this.bitmapFontL.clone();
        const amap  = type === 0 ? this.bitmapFontA : null;

        const txt = {   chars : [], 
                        txtPos : {x:x, y:y , z:z},
                        letterPos : pos, 
                        maxWidth : maxWidth,
                        letterSize : sizes,
                        letterGSize : gsizes,
                        letterGPos : gpos,
                        gradv : gradv,
                        texSize : type === 0 ? 512 : 1024, 
                        planeSize : size * 0.5, 
                        font : map,
                        glyph :glyphs
                    };
        const pgeo = new THREE.PlaneGeometry( size, size);

        let yy = y;
        let xx = x;

        for(let n=0;n<numChar;n++){

            if((xx+txt.planeSize) >= maxWidth){
                xx = x;
                yy -= txt.planeSize;
            }

            const mat = new THREE.MeshBasicMaterial({color : color, alphaTest: 0.3, map: map.clone(), alphaMap: amap ? amap.clone() : null, depthTest:false, transparent : true });
            mat.name = 'letter';
            //mat.map.repeat.x = txt.letterSize/txt.texSize;
            const lmesh = new THREE.Mesh(pgeo, mat);
            lmesh.name = 'ui';
            lmesh.renderOrder = 100;
            const char = {mesh : lmesh, pos :  {x:xx, y:yy, z: z} }
            txt.chars.push(char);

            xx += txt.planeSize;
        }
        return txt;
    }

    /*
    addBitmapText(parent, txt){

        for(let n=0;n<txt.chars.length;n++){
            txt.chars[n].mesh.meshGPU = this.addMeshChild(patxt.chars[n].mesh, txt.chars[n].pos);
            this.setMeshStatic(txt.chars[n].mesh, false);
        }
    }

    removeBitmapText(txt){

        for(let n=0;n<txt.chars.length;n++){
            this.removeMesh(txt.chars[n].mesh);
        }
    }
    */
    getWorldLength(txt, word){

        let sz = 0;

        for(let c of word){
            const idx = txt.glyph.indexOf(c);
            sz += txt.sizes[idx];
        }
        return sz;

    }

    setBitmapText(txt, istr){

        const str = istr.toLowerCase();

        let yy = txt.txtPos.y;
        let xx = txt.txtPos.x;

        const words = str.split(' ');
        let cw = 0;
        let cl = 0;

        let maxHeight = txt.planeSize * 2.0;

        //let wl = this.getWorldLength(words[0]);

        for(let n=0;n<txt.chars.length;n++){

            txt.chars[n].pos.x = xx;
            txt.chars[n].pos.y = yy;

            this.updateCameraSpaceMesh(txt.chars[n].mesh, txt.chars[n].pos);

            if((cw<words.length)&&(cl>=words[cw].length)){
                cl = 0;
                cw ++;
                this.setTextureOffset(txt.chars[n].mesh, 'letter',  (txt.texSize -8) / txt.texSize , 0, 4/ txt.texSize, 1);
                xx += txt.planeSize / 2.0;

                if(cw<words.length){
                    if((xx+ words[cw].length * txt.planeSize) >= txt.maxWidth){
                        xx = txt.txtPos.x;
                        yy -= txt.planeSize * 2.0;
                        maxHeight += txt.planeSize  * 2.0;
                    }
                }

                continue;
            }

            const c = cw<words.length ? txt.glyph.indexOf(words[cw][cl]): -1;
            if(c<0){
                this.setTextureOffset(txt.chars[n].mesh, 'letter',  (txt.texSize -8) / txt.texSize , 0, 4/ txt.texSize, 1);
                xx += txt.planeSize / 2.0;
            }
            else{
                this.setTextureOffset(txt.chars[n].mesh, 'letter',  (txt.letterPos[c] -1) / txt.texSize, 0, (txt.letterSize[c] -1)/ txt.texSize);
                xx += txt.planeSize;
            }
                

            
            txt.chars[n].mesh.scale.x = 1.0; //txt.letterGSize[c];
            /*
            txt.chars[n].pos.x -= txt.letterGPos[c];
            this.updateCameraSpaceMesh(txt.chars[n].mesh, txt.chars[n].pos);
            */

            
            cl++;
        }

        txt.maxHeight = maxHeight;

    }

    async createMiniMap(track, mode = 1){

        if(!track.terrain.minimap)
            return;

        
        const minimap = { pos : {x: 1.2, y : 0.0, z : -4}};

        /*
        const scene = new THREE.Scene();
        for(let o of track.terrain.objs){
            scene.add(o.clone());
        }
        */

        minimap.nMaterial = new THREE.MeshBasicMaterial({color: 0x00FF00});

        minimap.eMaterial = new THREE.MeshBasicMaterial({color: 0xFF0000});
        minimap.eGeo = new THREE.CylinderGeometry(0.04, 0.04, 0.001, 3, 1);
        minimap.eGeo.rotateX(Math.PI /2);
        //minimap.eGeo.rotateZ(Math.PI);


        minimap.targetTex =  new THREE.TextureLoader().load('assets/img/target.png');
        minimap.targetTex.name = 'minimaptarget-tex';

        minimap.backTex =  new THREE.TextureLoader().load(track.terrain.minimap);

        const material = new THREE.MeshBasicMaterial({color: 0xAAAAAAA, map : minimap.backTex});
        const tmaterial = new THREE.MeshPhongMaterial({color: 0xAAAAAAA, transparent: true, depthTest: false, map : minimap.targetTex, emissiveIntensity: 8.0, emissiveMap : minimap.targetTex});
        const carmaterial = new THREE.MeshBasicMaterial({color: 0x00000000});
        const Geo = new THREE.PlaneGeometry(1, 1);
        const mesh = new THREE.Mesh(Geo, material);
        mesh.ox = 0.0;
        mesh.oy = 0.0;
        mesh.meshGPU = this.addCameraSpaceMesh(mesh, minimap.pos );
        minimap.background = { mesh : mesh};

        const tGeo = new THREE.PlaneGeometry(0.1, 0.1);
        //tGeo.rotateX(Math.PI /2);
        //tGeo.translate(-0.05, 0 ,-0.05);
        
        const tmesh = new THREE.Mesh(tGeo, tmaterial);
        const targetPos = {x:0,y:0,z: 0.15}
        tmesh.ox = 0.0;
        tmesh.oy = 0.0;
        minimap.targetMesh = tmesh;
        minimap.targetMesh.meshGPU = this.addCameraSpaceMesh(tmesh, targetPos );
        minimap.background.mesh.add(minimap.targetMesh);
        
        const carGeo = new THREE.CylinderGeometry(0.04, 0.04, 0.001, 3, 1);
        carGeo.rotateX(Math.PI /2);
        carGeo.rotateZ(Math.PI);
        const carMesh = new THREE.Mesh(carGeo, carmaterial);
        const carPos = {x:0,y:0,z: 0.001}
        carMesh.ox = 0.0;
        carMesh.oy = 0.0;
        minimap.carmesh = carMesh;
        minimap.carmesh.meshGPU = this.addCameraSpaceMesh(minimap.carmesh, carPos, false );
        minimap.background.mesh.add(minimap.carmesh);
        
       
        minimap.mode = mode;

       

        minimap.center = new THREE.Vector3();
        minimap.size = new THREE.Vector3();

        track.ground.geometry.computeBoundingBox();
        track.ground.geometry.boundingBox.getSize(minimap.size);
        track.ground.geometry.boundingBox.getCenter(minimap.center);

        minimap.groundMin = track.ground.geometry.boundingBox.min.clone().add(track.ground.position);

        minimap.drones = [];
        minimap.turrets = [];
        minimap.spiders = [];
        minimap.npcs = [];

        track.minimap = minimap;
        track.minimapSize = {x : 200, y: 200}
    }

    worldToMap(pos, carPos, minimapSize, c, s, mode, v){

        const x =  (pos.x - carPos.x) / minimapSize.x;
        const y =  (pos.z - carPos.z) / minimapSize.y;
        
        const px = mode === 1 ? x : c * x + s * y;
        const py = mode === 1 ? y : c * y - s * x;

        v.x = -px;
        v.y =  py;

        //return {x: -px, y : py};
    }

    createParticles(p, pos){
        
        return null;

    }
    updateMiniMap(world){

        if(!world.track.minimap)
                return;

        const car = world.myCar;
        const track = world.track;
        const carPos = car.ChassisMesh.position.clone();
        const minimap = track.minimap;
        const minimapSize = track.minimapSize;
        
        
        const fv = new THREE.Vector3(0,0,1).applyQuaternion(car.ChassisMesh.quaternion);
        const angle = Math.atan2(fv.x, fv.z);
        const c  = Math.cos(-angle);
        const s  = Math.sin(-angle);

        const c2  = Math.cos(angle);
        const s2  = Math.sin(angle);

        if(car.selectedTargetId === 0)
            minimap.targetMesh.visible = false;
        else
            minimap.targetMesh.visible = true;

        if(world.myCar.mapCurveMesh){
            this.removeMeshChild( minimap.background.mesh, world.myCar.mapCurveMesh);
            world.myCar.mapGeometry.dispose();
            world.myCar.mapCurveMesh = null;
        }

        if(world.myCar.curvePts){
            const mapCurve = []

            for(let n =0; n< world.myCar.curvePts.length;n++){
                this.worldToMap(world.myCar.curvePts[n], carPos, minimapSize, c, s, minimap.mode, this.tmpV2 );

                if((Math.abs(this.tmpV2.x)<1.0)&&(Math.abs(this.tmpV2.y)<1.0)){
                    mapCurve.push(new THREE.Vector3(this.tmpV.x, this.tmpV.y, 0.001));
                }
            }

            if(mapCurve.length>=3){
                world.myCar.mapCurve = new THREE.CatmullRomCurve3(mapCurve, false, "catmullrom", 1.0)
                world.myCar.mapGeometry = new THREE.TubeGeometry(world.myCar.mapCurve, 32, 0.01, 4, false );
                world.myCar.mapCurveMesh = new THREE.Mesh(world.myCar.mapGeometry, this.mapCurveMat);
                world.myCar.mapCurveMesh.meshGPU = this.addCameraSpaceMesh(world.myCar.mapCurveMesh, {x : 0, y : 0, z : 0.01}, false );
                minimap.background.mesh.add(world.myCar.mapCurveMesh);
            }
        }
    
        for(let n =0; n< world.track.drones.length;n++){
            if(!minimap.drones[n]){
                minimap.drones[n] = {mesh : null, v : new THREE.Vector3()}
            }

            this.worldToMap(world.track.drones[n].target.pos, carPos, minimapSize, c, s, minimap.mode , this.tmpV2);


            /*
            minimap.drones[n].pos = {   x : -(world.track.drones[n].pos.x - carPos.x) / minimapSize.x,
                                        y :  (world.track.drones[n].pos.z - carPos.z) / minimapSize.y ,
                                        z : 0.1};

            const px = minimap.mode === 1 ? minimap.drones[n].pos.x : c * minimap.drones[n].pos.x + s * minimap.drones[n].pos.y;
            const py = minimap.mode === 1 ? minimap.drones[n].pos.y : c * minimap.drones[n].pos.y - s * minimap.drones[n].pos.x;
            */
                        
            if(!minimap.drones[n].mesh){
                minimap.drones[n].mesh = new THREE.Mesh(minimap.eGeo, minimap.eMaterial);
                minimap.drones[n].mesh.meshGPU = this.addCameraSpaceMesh(minimap.drones[n].mesh, {x : this.tmpV2.x, y : this.tmpV2.y, z : 0.01}, false );
                minimap.background.mesh.add(minimap.drones[n].mesh);
            }else{
                minimap.drones[n].mesh.position.set(this.tmpV2.x, this.tmpV2.y, 0.01);
            }

            if(car.selectedTargetId === world.track.drones[n].target.id){
                minimap.targetMesh.position.set(this.tmpV2.x, this.tmpV2.y, 0.015);
            }
            

            if((Math.abs(this.tmpV2.x)>0.5)||(Math.abs(this.tmpV2.y)>0.5)){
                minimap.drones[n].mesh.visible = false;
            }else{
                minimap.drones[n].mesh.visible = true;
            }


            this.tmpV.set(0,0,1).applyQuaternion(world.track.drones[n].object.quaternion);
            const dangle = Math.atan2(this.tmpV.x, this.tmpV.z);
            minimap.drones[n].mesh.rotation.z = dangle-angle;
        }

        for(let n =0; n< world.track.turrets.length;n++){
            if(!minimap.turrets[n]){
                minimap.turrets[n] = {mesh : null, v : new THREE.Vector3()}
            }

            this.worldToMap(world.track.turrets[n].target.pos, carPos, minimapSize, c, s, minimap.mode , this.tmpV2);


            if(!minimap.turrets[n].mesh){
                minimap.turrets[n].mesh = new THREE.Mesh(minimap.eGeo, minimap.eMaterial);
                minimap.turrets[n].mesh.meshGPU = this.addCameraSpaceMesh(minimap.turrets[n].mesh, {x : this.tmpV2.x, y : this.tmpV2.y, z : 0.01}, false );
                minimap.background.mesh.add(minimap.turrets[n].mesh);
            }else{
                minimap.turrets[n].mesh.position.set(this.tmpV2.x, this.tmpV2.y, 0.01);
            }

            if(car.selectedTargetId === world.track.turrets[n].target.id){
                minimap.targetMesh.position.set(this.tmpV2.x, this.tmpV2.y, 0.015);
            }
            

            if((Math.abs(this.tmpV2.x)>0.5)||(Math.abs(this.tmpV2.y)>0.5)){
                minimap.turrets[n].mesh.visible = false;
            }else{
                minimap.turrets[n].mesh.visible = true;
            }


            this.tmpV.set(0,0,1).applyQuaternion(world.track.turrets[n].head.quaternion);
            const dangle = Math.atan2(this.tmpV.x, this.tmpV.z);
            minimap.turrets[n].mesh.rotation.z = dangle-angle;
        }

        for(let n =0; n< world.track.npcs.length;n++){

            if(!world.track.npcs[n].target)
                continue;
            if(!minimap.npcs[n]){
                minimap.npcs[n] = {mesh : null, v : new THREE.Vector3()}
            }

            this.worldToMap(world.track.npcs[n].pos, carPos, minimapSize, c, s, minimap.mode, this.tmpV2 );


            /*
            minimap.drones[n].pos = {   x : -(world.track.drones[n].pos.x - carPos.x) / minimapSize.x,
                                        y :  (world.track.drones[n].pos.z - carPos.z) / minimapSize.y ,
                                        z : 0.1};

            const px = minimap.mode === 1 ? minimap.drones[n].pos.x : c * minimap.drones[n].pos.x + s * minimap.drones[n].pos.y;
            const py = minimap.mode === 1 ? minimap.drones[n].pos.y : c * minimap.drones[n].pos.y - s * minimap.drones[n].pos.x;
            */
                        
            if(!minimap.npcs[n].mesh){
                minimap.npcs[n].mesh = new THREE.Mesh(minimap.eGeo, minimap.nMaterial);
                minimap.npcs[n].mesh.meshGPU = this.addCameraSpaceMesh(minimap.npcs[n].mesh, {x : this.tmpV2.x, y : this.tmpV2.y, z : 0.01}, false );
                minimap.background.mesh.add(minimap.npcs[n].mesh);
            }else{
                minimap.npcs[n].mesh.position.set(this.tmpV2.x, this.tmpV2.y, 0.01);
            }

            if(car.selectedTargetId === world.track.npcs[n].target.id){
                minimap.targetMesh.position.set(this.tmpV2.x, this.tmpV2.y, 0.015);
            }
            

            if((Math.abs(this.tmpV2.x)>0.5)||(Math.abs(this.tmpV2.y)>0.5)){
                minimap.npcs[n].mesh.visible = false;
            }else{
                minimap.npcs[n].mesh.visible = true;
            }


            this.tmpV.set(0,0,1).applyQuaternion(world.track.npcs[n].avatarMesh.quaternion);
            
            const dangle = Math.atan2(this.tmpV.x, this.tmpV.z);
            minimap.npcs[n].mesh.rotation.z = dangle-angle;
        }

        for(let n =0; n< world.track.spiders.length;n++){
            if(!minimap.spiders[n]){
                minimap.spiders[n] = {mesh : null, v : new THREE.Vector3()}
            }
       
            this.worldToMap(world.track.spiders[n].skel.root.pos, carPos, minimapSize, c, s, minimap.mode, this.tmpV2 );
                        
            if(!minimap.spiders[n].mesh){
                minimap.spiders[n].mesh = new THREE.Mesh(minimap.eGeo, minimap.eMaterial);
                minimap.spiders[n].mesh.meshGPU = this.addCameraSpaceMesh(minimap.spiders[n].mesh, {x : this.tmpV2.x, y : this.tmpV2.y, z : 0.01}, false );
                minimap.background.mesh.add(minimap.spiders[n].mesh);
            }else{
                minimap.spiders[n].mesh.position.set(this.tmpV2.x, this.tmpV2.y, 0.01);
            }

            if(car.selectedTargetId === world.track.spiders[n].target.id){
                minimap.targetMesh.position.set(this.tmpV2.x, this.tmpV2.y, 0.015);
            }


            if((Math.abs(this.tmpV2.x)>0.5)||(Math.abs(this.tmpV2.y)>0.5)){
                minimap.spiders[n].mesh.visible = false;
            }else{
                minimap.spiders[n].mesh.visible = true;
            }

            this.tmpV.set(0,0,1).applyQuaternion(world.track.spiders[n].object.quaternion);
            const sangle = Math.atan2(this.tmpV.x, this.tmpV.z);
            minimap.spiders[n].mesh.rotation.z = sangle-angle;
        }

        const carU = (carPos.x - minimap.groundMin.x) / minimap.size.x;
        const carV = (carPos.z - minimap.groundMin.z) / minimap.size.z;
        const g  = minimap.background.mesh.geometry;

        if(minimap.mode === 1)
            minimap.carmesh.rotation.z = angle;
        
        for(let i =0;i< g.attributes.position.count;i++){
            const x = g.attributes.position.array[i * 3 + 0];
            const y = g.attributes.position.array[i * 3 + 1];
            const z = g.attributes.position.array[i * 3 + 2];

            let u = -x *  minimapSize.x / minimap.size.x;
            let v = y *  minimapSize.y / minimap.size.z;

            if( minimap.mode === 0){
                let nu = c2 * u + s2 * v;
                let nv = c2 * v - s2 * u;

                g.attributes.uv.array[i * 2 + 0] = nu + carU;
                g.attributes.uv.array[i * 2 + 1] = nv + carV;
            } else {
                g.attributes.uv.array[i * 2 + 0] = u + carU;
                g.attributes.uv.array[i * 2 + 1] = v + carV;
            }
        }

        g.attributes.uv.needsUpdate = true;
        this.updateCameraSpaceMesh(minimap.background.mesh, minimap.pos);
        
    }

    async loadWeapon(name){

        const wgltf = await this.loadGLTF(name);
        const scene = this.deGroupGLB(wgltf);
        const weapon = {name : name};

        scene.traverse(function(child){

            if(child.name.startsWith('body')){
                weapon.body = child.clone();
            }
            if(child.name.startsWith('head')){
                weapon.head = child.clone();
            }
            if(child.name.startsWith('canon')){
                weapon.canon = child.clone();
            }
        });

        weapon.canonRel = new THREE.Vector3().subVectors(weapon.canon.position, weapon.head.position);
        weapon.headRel = new THREE.Vector3().subVectors(weapon.head.position, weapon.body.position);
        weapon.bodyRel = new THREE.Vector3().subVectors(weapon.body.position, scene.position);

        return weapon;
    }

    async loadCar(car, mode = 0) {

        let object = null;

        try{
            const gltf = await this.loadGLTF(car.carDef.chassis);
            object = this.deGroupGLB(gltf);
        }
        catch(e){
            object = await this.loadObj(car.carDef.chassis);
        }



        object.traverse((child) => {

            if (child.type == 'Mesh') {

                child.castShadow = true;
                child.receiveShadow = true;

                child.geometry.computeBoundingBox();

                const size = new THREE.Vector3() ;
                child.geometry.boundingBox.getSize(size);
                car.ChassisMesh = child;
                car.ChassisMesh.name = 'Car-'+car.carDef.chassis;
            }
        });

        if (car.carDef.suspension) {
            let tireObj = null;

            try{
                tireObj = await this.loadObj(car.carDef.suspension.tire+'_s');
            }
            catch(e){

                tireObj = await this.loadObj(car.carDef.suspension.tire);

            }
            
            tireObj.traverse((child) => {

                if (child.type == 'Mesh') {

                    child.castShadow = true;
                    child.receiveShadow = true;
                    child.geometry.computeBoundingBox();
                    car.tireMesh = child;
                }
            });

            const s = new THREE.Vector3();
            const c = new THREE.Vector3();
            car.tireMesh.geometry.computeBoundingBox();
            car.tireMesh.geometry.boundingBox.getSize(s);
            car.tireMesh.geometry.boundingBox.getCenter(c);

            car.tireMesh.geometry.translate(-c.x, -c.y, -c.z);
            car.tireMesh.geometry.scale(car.wheelWidth / s.x, car.wheelRadius * 2 / s.y , car.wheelRadius * 2/ s.z);
        }


        

        if (car.ChassisMesh == null) {
            car.chassisWidth = 1.8;
            car.chassisHeight = .6;
            car.chassisLength = 4;

            car.ChassisMesh = this.createChassisMesh(car.chassisWidth, car.chassisHeight, car.chassisLength);
            car.ChassisMesh.castShadow = true;
            car.ChassisMesh.receiveShadow = true;
            car.center = car.pos.clone();
        } else {

            const bbox = car.ChassisMesh.geometry.boundingBox;

            car.chassisWidth = bbox.max.x - bbox.min.x;
            car.chassisHeight = bbox.max.y - bbox.min.y;
            car.chassisLength = bbox.max.z - bbox.min.z;

            car.center = new THREE.Vector3(car.pos.x, car.pos.y, car.pos.z);
        }

        if (car.carDef.weapon) {
            const weapon = await this.loadWeapon(car.carDef.weapon.model);

            const body_pos=  {x: weapon.bodyRel.x, y: weapon.bodyRel.y +  car.chassisHeight/4,  z: weapon.bodyRel.z -car.chassisLength/4};
            const bs = new THREE.Vector3();

            weapon.body.geometry.computeBoundingBox();
            weapon.body.geometry.boundingBox.getSize(bs);
            weapon.body.geometry.scale(car.chassisWidth / bs.x , 1 , 1);


            weapon.body.position.x = body_pos.x;
            weapon.body.position.y = body_pos.y;
            weapon.body.position.z = body_pos.z;

            weapon.BlastImpact = 1;
    
            car.weapon = weapon;
        }

        if (car.carDef.radar) {
            const obj = await this.loadObj(car.carDef.radar.model);
            let radar;

            obj.traverse(function (c){
                if(c.isMesh){
                    radar = c;
                }
            })

            radar.position.x = car.chassisWidth / 4.0;
            radar.position.y = car.chassisHeight/4 + 0.5;
            radar.position.z = -car.chassisLength/4 + 0.1;


            /*
            radar.meshGPU = this.addMesh(radar, RT_DIFF, new Transformation3(radar.position));
            this.setMeshStatic(radar, false);
            */

            car.radar = radar;
        }

        car.updateWheels(mode);

        for (let n = 0; n < 4; n++) {

            car.wheels[n].mesh = (car.tireMesh) ? car.tireMesh.clone() : this.createWheelMesh(car.wheels[n].radius, car.wheels[n].width);

            car.wheels[n].mesh.castShadow = true;
            car.wheels[n].mesh.receiveShadow = true;

            car.wheels[n].mesh.scale.x = car.wheels[n].scale.x;

            car.wheels[n].mesh.position.x = car.wheels[n].pos.x;
            car.wheels[n].mesh.position.y = car.wheels[n].pos.y;
            car.wheels[n].mesh.position.z = car.wheels[n].pos.z;
            car.wheels[n].mesh.smearIntensity = 0;
            car.wheels[n].mesh.expandGeometry = 0;
            car.wheels[n].mesh.interpolateGeometry = 0;
        }

        car.massVehicle = car.carDef.massVehicle;

        car.ChassisMesh.smearIntensity = 0.1;
        car.ChassisMesh.expandGeometry = 0;
        car.ChassisMesh.interpolateGeometry = 0;

    }


    loadPod() {

        const podMat = new THREE.MeshPhysicalMaterial({ color: 0xffFFFF });

        podMat.name ='pod-material';
        podMat.map = this.texLoader.load(this.assetBase+"/assets/textures/pod/color_map.jpg");
        podMat.map.wrapS = THREE.RepeatWrapping;
        podMat.map.wrapT = THREE.RepeatWrapping;
        podMat.map.repeat.set(1, 1);
        podMat.needsUpdate = true;
        
        podMat.normalMap = this.texLoader.load(this.assetBase+"/assets/textures/pod/normal_map_opengl.jpg");
        podMat.normalMap.wrapS = THREE.RepeatWrapping;
        podMat.normalMap.wrapT = THREE.RepeatWrapping;
        podMat.normalMap.repeat.set(1, 1);
        podMat.needsUpdate = true;
        
        podMat.roughnessMap = this.texLoader.load(this.assetBase+"/assets/textures/pod/roughness_map.jpg");
        podMat.roughnessMap.wrapS = THREE.RepeatWrapping;
        podMat.roughnessMap.wrapT = THREE.RepeatWrapping;
        podMat.roughnessMap.repeat.set(1, 1);
        podMat.needsUpdate = true;

        podMat.roughness = 0.2;
        podMat.metalness = 0.7;
        podMat.reflectivity = 0.3;
        podMat.envMapIntensity = 2.0;

        const podGeom = new THREE.CylinderGeometry(5, 5, 0.5, 64, 1);
        const podMesh = new THREE.Mesh(podGeom, podMat);
        podMesh.position.y = -0.8;

        podMesh.name = 'pod';
        podMesh.receiveShadow = true;
        podMesh.castShadow = false;

        this.podMesh = podMesh;

        this.podMesh.geometry.computeBoundingBox();

        if(this.rtEngine)
            this.rtEngine.enableRT(this.podMesh);

        return podMesh;
    }

    addCameraController(c){
        this.sceneUI.add(c.cam);
    }

    addUIButtons(controller){

        if(!controller)
            return;
        
        if(controller.cam)
            this.sceneUI.add(controller.cam);

        //this.sceneUI.add(controller.wheel);
        
        this.sceneUI.add(controller.accel);
        this.sceneUI.add(controller.brake);

        if(controller.nitro)
            this.sceneUI.add(controller.nitro);

        if(controller.left)
            this.sceneUI.add(controller.left);

        if(controller.right)
            this.sceneUI.add(controller.right);
        
    }

    removeUIButtons(controller){
        if(controller.cam)
            this.sceneUI.remove(controller.cam);
        //this.sceneUI.remove(controller.wheel);
        this.sceneUI.remove(controller.accel);
        this.sceneUI.remove(controller.brake);

        if(controller.nitro)
            this.sceneUI.remove(controller.nitro);

        if(controller.left)
            this.sceneUI.remove(controller.left);

        if(controller.right)
            this.sceneUI.remove(controller.right);

    }

    checkSprite(sprite,x,y){

        if(!sprite)
            return;

        if( (x>sprite.position.x - sprite.scale.x / 2.0) && 
            (x<sprite.position.x + sprite.scale.x / 2.0)&& 
            (y>sprite.position.y - sprite.scale.y / 2.0)&&
            (y<sprite.position.y + sprite.scale.y / 2.0))
        return true;

     return false;
    }

    checkUIButtons(controller, x, y) {

        if(this.checkSprite(controller.cam,x,y))
            return controller.cam;
        
        /*
        if(this.checkSprite(controller.wheel,x,y))
            return controller.wheel;
        */

        if(this.checkSprite(controller.accel,x,y))
            return controller.accel;

        if(this.checkSprite(controller.brake,x,y))
            return controller.brake;

        if(this.checkSprite(controller.nitro,x,y))
            return controller.nitro;   

        if(this.checkSprite(controller.left,x,y))
            return controller.left;   
        
        if(this.checkSprite(controller.right,x,y))
            return controller.right;           
        
        return null;
    }

    
    updateCamIcon(gameControls){

        const imgId = this.camMode - 1;
        this.texLoader.loadAsync(this.camImg[imgId]).then((cameraTexture) => {
            gameControls.cam.material.map = cameraTexture;
        })
        
        /*
        const imgId = this.camMode - 1;
        const icon = document.getElementById('cam-icon');
        if(!icon)
            return;

        icon.src = this.camImg[imgId];
        */
    }


    async makeLobbyUIButtons(){

        const AccelTexture = await this.texLoader.loadAsync("assets/img/accel.png");
        const AccelMaterial = new THREE.SpriteMaterial({ map: AccelTexture, color: 0xffffff, rotation: 0, depthTest: false });

        const BrakeTexture = await this.texLoader.loadAsync("assets/img/brake.png");
        const BrakeMaterial = new THREE.SpriteMaterial({ map: BrakeTexture, color: 0xffffff, rotation: 0, depthTest: false });

        const Accel = new THREE.Sprite(AccelMaterial);
        Accel.scale.set(20, 20, 1)
        Accel.position.set(60, -80, 1);
        Accel.name ="accel";

        const Brake = new THREE.Sprite(BrakeMaterial);
        Brake.scale.set(20, 20, 1)
        Brake.position.set(80, -80, 1);
        Brake.name ="brake";    

        return {accel: Accel, brake: Brake};
    }

    async makeUIButtons(){  

        const cameraTexture = await this.texLoader.loadAsync("assets/img/cam1.png");
        const cameraMaterial = new THREE.SpriteMaterial({ map: cameraTexture, color: 0xFFffFF, rotation: 0, depthTest: false });

        /*
        const WheelTexture = await this.texLoader.loadAsync("assets/img/wheel.png");
        const WheelMaterial = new THREE.SpriteMaterial({ map: WheelTexture, color: 0xffffff, rotation: 0, depthTest: false , wrapS : THREE.RepeatWrapping, wrapT : THREE.RepeatWrapping});
        */

        const AccelTexture = await this.texLoader.loadAsync("assets/img/accel.png");
        const AccelMaterial = new THREE.SpriteMaterial({ map: AccelTexture, color: 0xffffff, rotation: 0, depthTest: false });

        const BrakeTexture = await this.texLoader.loadAsync("assets/img/brake.png");
        const BrakeMaterial = new THREE.SpriteMaterial({ map: BrakeTexture, color: 0xffffff, rotation: 0, depthTest: false });

        const NitroTexture = await this.texLoader.loadAsync("assets/img/nitro-icon.png");
        const NitroMaterial = new THREE.SpriteMaterial({ map: NitroTexture, color: 0xffffff, rotation: 0, depthTest: false });

        const LeftSteerTexture = await this.texLoader.loadAsync("assets/img/steer_l.png");
        const LeftSteerMaterial = new THREE.SpriteMaterial({ map: LeftSteerTexture, color: 0xffffff, rotation: 0, depthTest: false });

        const RightSteerTexture = await this.texLoader.loadAsync("assets/img/steer_r.png");
        const RightSteerMaterial = new THREE.SpriteMaterial({ map: RightSteerTexture, color: 0xffffff, rotation: 0, depthTest: false });


        const camera = new THREE.Sprite(cameraMaterial);
        camera.scale.set(20, 20, 1);
        camera.position.set(-90, 90, 1);
        camera.name ="camera";

        /*
        const Wheel = new THREE.Sprite(WheelMaterial);
        Wheel.scale.set(60, 60, 1)
        Wheel.position.set(-70, 10, 1);
        Wheel.name ="wheel";
        */

        const Left = new THREE.Sprite(LeftSteerMaterial);
        Left.scale.set(35, 35, 1)
        Left.position.set(-80, 10, 1);
        Left.name = "left";

        const Right = new THREE.Sprite(RightSteerMaterial);
        Right.scale.set(35, 35, 1)
        Right.position.set(-50, 10, 1);
        Right.name = "right";

        const Accel = new THREE.Sprite(AccelMaterial);
        Accel.scale.set(20, 20, 1)
        Accel.position.set(60, -80, 1);
        Accel.name ="accel";

        const Brake = new THREE.Sprite(BrakeMaterial);
        Brake.scale.set(20, 20, 1)
        Brake.position.set(80, -80, 1);
        Brake.name ="brake";        

        const Nitro = new THREE.Sprite(NitroMaterial);
        Nitro.scale.set(20, 20, 1)
        Nitro.position.set(70, -60, 1);
        Nitro.name = "nitro";

        return {cam: camera, /*wheel: Wheel,*/ accel: Accel, brake: Brake, nitro: Nitro, left: Left, right: Right};
    }

    addTrackWoods(wood, object = null) {
        this.makeTrackWoods(wood, object);

        for (let n = 0; n < wood.objects.length; n++) {
            wood.objects[n].object.meshGPU = this.addMesh(wood.objects[n].object, 0, new Transformation3(wood.objects[n].object.position, wood.objects[n].object.quaternion));
        }

    }

    removeSelectable(oname, matname) {

        for(let s=0;s<this.selectScene.children.length;s++){
            const sc = this.selectScene.children[s];
            if((sc.name =oname) &&(sc.material[grp.materialIndex].name !== matname)){
                this.selectScene.remove(sc);
                return;
            }
        }

    }

    addSelectable(child, m) {

        if(child.material.length > 0){

            const ogeo = child.geometry.toNonIndexed();

            for(let grp of ogeo.groups){

                const mat = child.material[grp.materialIndex];

                if(mat.name === m.name){
                    const newGeo = new THREE.BufferGeometry();

                    for(let a in ogeo.attributes){
                        const attr = ogeo.attributes[a];
                        const buffer = attr.array.subarray(grp.start * attr.itemSize, (grp.start + grp.count) * attr.itemSize)
                        newGeo.setAttribute(a, new THREE.Float32BufferAttribute(buffer, attr.itemSize, attr.normalized));
                    }

                    const nmat = this.selectMat.clone();
                    nmat.name = m.name;

                    const mesh = new THREE.Mesh(newGeo, nmat);
                    mesh.name = 'selectable mesh '+child.name+' '+mat.name;

                    mesh.name = child.name;
                    mesh.position.set(child.position.x, child.position.y, child.position.z);
                    mesh.quaternion.set(child.quaternion.x, child.quaternion.y, child.quaternion.z, child.quaternion.w);
                    mesh.scale.set(child.scale.x, child.scale.y, child.scale.z);
                    mesh.updateMatrixWorld();
                    this.selectScene.add(mesh);

                    ogeo.dispose();
                    return;
                }
            }

        }else{
            const nc =child.clone();
            nc.updateMatrixWorld();
            this.selectScene.add(nc);
        }
    }

    updateCamera(myCar, loop, tube, tmesh, dt, rayCast = []) {

        if (myCar.ChassisMesh === null)
            return;

        const p = myCar.ChassisMesh.position;
        const r = myCar.ChassisMesh.quaternion;



        if (loop !== null) {
            this.camDest = new THREE.Vector3(loop.center.x, loop.center.y, loop.center.z);
            this.targetFov = 55 + this.fovAdd;

            if (loop.hide.length > 0) {
                const objects = loop.hide.split(',');

                for (let obj of objects) {
                    tmesh.traverse((child) => {

                        if (child.name === obj) {
                            child.visible = false;
                            this.hiddenObjects.push(child);
                        }
                    });
                }
            }

            this.cameraAnchor.position.x = this.camDest.x;
            this.cameraAnchor.position.y = this.camDest.y;
            this.cameraAnchor.position.z = this.camDest.z;
            this.cameraAnchor.lookAt(p.x, p.y, p.z);
        }
        else if ((tube !== null) && (p.distanceTo(tube.center) < tube.width)) {
            this.camDest = new THREE.Vector3(tube.center.x, tube.center.y, tube.center.z);
            this.targetFov = 75 + this.fovAdd;

            this.cameraAnchor.position.x = this.camDest.x;
            this.cameraAnchor.position.y = this.camDest.y;
            this.cameraAnchor.position.z = this.camDest.z;
            this.cameraAnchor.lookAt(p.x, p.y, p.z);

        } else {
            

            let obj = null;

            while ((obj = this.hiddenObjects.pop()) != null) {
                obj.visible = true;
            }
            const camOfset = new THREE.Vector3(0, 4.5, -10.5).applyQuaternion(myCar.ChassisMesh.quaternion);
            const camDest = new THREE.Vector3().addVectors(p, camOfset);
            const t = new THREE.Vector3(0, 0, 5).applyQuaternion(myCar.ChassisMesh.quaternion);

            switch (this.camMode) {
                case 1:

                    const frontLook = new THREE.Vector3(0, 0, 10.0).applyQuaternion(myCar.ChassisMesh.quaternion);
                    frontLook.add(p);

                    this.targetFov = ((!this.isVr)&&(myCar.isNitro)) ? 55 :45;

                    this.targetFov += this.fovAdd;

                    this.cameraAnchor.position.x = camDest.x;
                    this.cameraAnchor.position.y = camDest.y;
                    this.cameraAnchor.position.z = camDest.z;
                    this.cameraAnchor.lookAt(frontLook.x, frontLook.y, frontLook.z);
                    this.lastCamAngle = null;
                    break;
                case 2:

                    const frontVec = new THREE.Vector3(0, 0, 1.0).applyQuaternion(myCar.ChassisMesh.quaternion);
                    /*const camDiff = new THREE.Vector3().subVectors(camDest, this.cameraAnchor.position);*/


                    /*const camAngle = Math.atan2(camDiff.x, camDiff.z);*/
                    const carAngle = Math.atan2(frontVec.x, frontVec.z);
                    let curAngle = carAngle;

                    this.targetFov =((!this.isVr)&&(myCar.isNitro)) ? 65 : 75;
                    this.targetFov += this.fovAdd;

                    const dfov = Math.abs(this.targetFov - this.camera.fov) / 30;

                    if (this.lastCamAngle !== null) {
                        if ((carAngle - this.lastCamAngle) < - Math.PI)
                            this.lastCamAngle -= Math.PI * 2;
                        else if ((carAngle - this.lastCamAngle) > Math.PI)
                            this.lastCamAngle += Math.PI * 2;

                        curAngle = this.lastCamAngle + ((carAngle - this.lastCamAngle) * 3 * dt)
                    }

                    //console.log('carAngle:'+carAngle + ' curAngle: '+curAngle+' this.lastCamAngle:'+this.lastCamAngle +' dt:'+dt);

                    const d = 8 - (this.fovAdd / 20);

                    //const camMove = camDiff.clone().multiplyScalar(dt * 3);

                    this.cameraAnchor.position.x = p.x - Math.sin(curAngle) * d;
                    this.cameraAnchor.position.y = p.y + 2.0 + myCar.cameraOffset.y;
                    this.cameraAnchor.position.z = p.z - Math.cos(curAngle) * d;

                    this.cameraAnchor.quaternion.setFromEuler(new THREE.Euler(0,curAngle + Math.PI,0));

                    this.lastCamAngle = curAngle;
                    //this.cameraAnchor.lookAt(p.x, p.y, p.z);
                    break;
                case 3:
                    /*
                    this.targetFov = 75;
                    this.targetFov += this.fovAdd;

                    this.cameraAnchor.position.x = p.x;
                    this.cameraAnchor.position.y = p.y + 50;
                    this.cameraAnchor.position.z = p.z;

                    const tt = new THREE.Vector3(0, 0, 4).applyQuaternion(myCar.ChassisMesh.quaternion);
                    this.cameraAnchor.lookAt(p.x + tt.x, p.y + tt.y, p.z + tt.z);
                    this.lastCamAngle = null;
                    */
                   const ffrontVec = new THREE.Vector3(0, 0, 1.0).applyQuaternion(myCar.ChassisMesh.quaternion);
                    /*const camDiff = new THREE.Vector3().subVectors(camDest, this.cameraAnchor.position);*/


                    /*const camAngle = Math.atan2(camDiff.x, camDiff.z);*/
                    const ccarAngle = Math.atan2(ffrontVec.x, ffrontVec.z);
                    let ccurAngle = ccarAngle;

                    this.targetFov =((!this.isVr)&&(myCar.isNitro)) ? 65 : 75;
                    this.targetFov += this.fovAdd;

                    if (this.lastCamAngle !== null) {
                        if ((ccarAngle - this.lastCamAngle) < - Math.PI)
                            this.lastCamAngle -= Math.PI * 2;
                        else if ((ccarAngle - this.lastCamAngle) > Math.PI)
                            this.lastCamAngle += Math.PI * 2;

                        ccurAngle = this.lastCamAngle + ((ccarAngle - this.lastCamAngle) * 3 * dt)
                    }

                    //console.log('carAngle:'+carAngle + ' curAngle: '+curAngle+' this.lastCamAngle:'+this.lastCamAngle +' dt:'+dt);

                    const dd = 18 - (this.fovAdd / 20);

                    //const camMove = camDiff.clone().multiplyScalar(dt * 3);

                    this.cameraAnchor.position.x = p.x - Math.sin(ccurAngle) * dd;
                    this.cameraAnchor.position.y = p.y + 8.0 + myCar.cameraOffset.y;
                    this.cameraAnchor.position.z = p.z - Math.cos(ccurAngle) * dd;

                    this.cameraAnchor.quaternion.setFromEuler(new THREE.Euler(0,ccurAngle + Math.PI,0));

                    this.lastCamAngle = ccurAngle;
                    break;
                case 4:
                    //const frontOfset = new THREE.Vector3(0, 0.7, 0.2).applyQuaternion(myCar.ChassisMesh.quaternion);
                    //const frontOfset = new THREE.Vector3(0, 1.4, -0.2).applyQuaternion(myCar.ChassisMesh.quaternion);
                    const frontOfset = new THREE.Vector3(0, 1.2, 0.35).applyQuaternion(myCar.ChassisMesh.quaternion);

                    this.targetFov = 45 ;
                    this.targetFov += this.fovAdd;
                    this.cameraAnchor.position.x = p.x + frontOfset.x;
                    this.cameraAnchor.position.y = p.y + frontOfset.y;
                    this.cameraAnchor.position.z = p.z + frontOfset.z;

                    this.cameraAnchor.lookAt(p.x + t.x, p.y + t.y + 0.5, p.z + t.z);
                    this.lastCamAngle = null;


                    break;
            }
        }

        if(myCar.lastCameraMode !== this.camMode){
            this.camera.fov = this.targetFov;
        }

        myCar.lastCameraMode = this.camMode;
/*
        this.cameraAnchor.position.set(this.cameraAnchor.position.x,this.cameraAnchor.position.y,this.cameraAnchor.position.z);
        this.cameraAnchor.quaternion.set(this.cameraAnchor.quaternion.x,this.cameraAnchor.quaternion.y,this.cameraAnchor.quaternion.z,this.camera.quaternion.w);
*/

        /*
        */
        /*
        if(this.lastCamPos)
        {
            this.camera.lookAt( this.lastCamPos.x, this.lastCamPos.y, this.lastCamPos.z );
            const q1 = new THREE.Quaternion().copy( this.camera.quaternion );

            this.camera.lookAt(p.x, p.y, p.z);
            const q2 = new THREE.Quaternion().copy( this.camera.quaternion );

            this.camera.quaternion.slerpQuaternions( q1, q2, dt ); // 0 < time < 
        }
        else
        */


        //this.lastCamAngle = this.camera.quaternion.clone();
        this.lastCamPos = p.clone();

        if(this.isVr){

            myCar.cameraOffset.y = 0;

        }else{
            const dfov = this.targetFov-this.camera.fov;
            const mvfov = 15 * dt;
    
            if(dfov<0){
                
                if(Math.abs(dfov)>mvfov){
                    myCar.cameraOffset.y -= dt;
                    this.camera.fov -= mvfov;
                }
                else{
                    myCar.cameraOffset.y = 0;
                    this.camera.fov = this.targetFov;
                }
                    
            }
            else if(dfov>0){
    
                if(Math.abs(dfov)>mvfov){
                    this.camera.fov += mvfov;
                    myCar.cameraOffset.y += dt;
                } else {
                    myCar.cameraOffset.y = 0;
                    this.camera.fov = this.targetFov;
                }
            }
        }
  
        if(this.camMode == 2){
            if(rayCast.length > 0){
                
                tmpV31.set(this.cameraAnchor.position.x, this.cameraAnchor.position.y , this.cameraAnchor.position.z);
                tmpV32.subVectors(myCar.ChassisMesh.position, tmpV31);
                this.rc.set(tmpV31, tmpV32);
                const pts = this.rc.intersectObjects(rayCast);
                if(pts.length>0){

                    if((this.cameraAnchor.position.y) < pts[0].point.y){
                        this.cameraAnchor.position.set(pts[0].point.x, pts[0].point.y + 2.0, pts[0].point.z);
                        this.cameraAnchor.lookAt(p.x, p.y, p.z);
                    }
                }
            }
        
        }
        

        this.cameraAnchor.updateMatrix();
        this.cameraAnchor.updateWorldMatrix();

        this.camera.updateProjectionMatrix();
        this.camera.updateWorldMatrix();

        if(this.cameraProj)
            this.camera.projectionMatrix.toArray(this.cameraProj);   
        /*
        const fovDiff = this.targetFov - this.camera.fov;
        const fovMov = fovDiff / 20;
        this.camera.fov += fovMov;
        */
    }

    setCameraPos(x, y, z, tx, ty, tz) {
        this.camera.position.set(x, y, z);
        this.camera.lookAt(tx, ty, tz);
    }

 
    NormalToRotation(normal) {
        // Find a vector in the plane
        let tangent0 = new THREE.Vector3().crossVectors(normal,new THREE.Vector3(1, 0, 0));
        if (tangent0.dot(tangent0) < 0.001)
            tangent0 = new THREE.Vector3().crossVectors(normal, new THREE.Vector3(0, 1, 0));
        tangent0 = tangent0.normalize();
        // Find another vector in the plane
        const tangent1 = new THREE.Vector3().crossVectors(normal, tangent0).normalize();
        // Construct a 3x3 matrix by storing three vectors in the columns of the matrix

        const mat = new THREE.Matrix4();

        mat.elements[0] = tangent0.x;
        mat.elements[4] = tangent0.y;
        mat.elements[8] = tangent0.z;

        mat.elements[1] = tangent1.x;
        mat.elements[5] = tangent1.y;
        mat.elements[9] = tangent1.z;

        mat.elements[2] = normal.x;
        mat.elements[6] = normal.y;
        mat.elements[10] = normal.z;

        return mat;
    }


    async makeTrack(track) {
        if (this.creating)
            return;

        this.creating = true;

        if (!track.roadTexScale)
            track.roadTexScale = { x: 2, y: 4 };

       
        this.createTrackGeometry(track);

        const self = this;

        this.wallObject = await this.loadObj(track.wallName);

        this.wallObject.traverse((child) => {

            if (child.type == 'Mesh') {

                self.makeWalls(track, child);
            }
        });

        this.creating = false;
    }

    async createTrack(track, manager = null, loadingController = null) {
        await this.loadStartLine(track, 'finish');
        await this.makeTrack(track);
        await this.loadTerrain(track, manager, loadingController);
    }


    async addTrackDrone(drone) {

                
        await this.makeDrone(drone);


        for(let pale of drone.pales) {
            //drone.object.add(pale);
            pale.mesh.meshGPU = this.addMesh(pale.mesh, RT_COAT, new Transformation3( {   x : drone.object.position.x + pale.mesh.position.x, 
                                                                                          y : drone.object.position.y + pale.mesh.position.y, 
                                                                                          z : drone.object.position.z + pale.mesh.position.z}));

            this.addDMGBar(pale.dmgBar);
            /*
            pale.dmgBar.DMGBar1.meshGPU = this.addMesh(pale.dmgBar.DMGBar1, RT_COAT, new Transformation3(pale.dmgBar.DMGBar1.position));
            pale.dmgBar.DMGBar2.meshGPU = this.addMesh(pale.dmgBar.DMGBar2, RT_COAT, new Transformation3(pale.dmgBar.DMGBar2.position));
            */
        }

        /*
        drone.pales = [];
        drone.wings = [];

        object.traverse((child) => {

            if (!child.isMesh) 
                return;

            if(child.name=='Body') {
                drone.object = child;
                drone.object.material = child.material;
                drone.object.geometry.scale(drone.size.x, drone.size.y, drone.size.z);
            }

            if(child.name.startsWith('Pales')) {
                const size = new THREE.Vector3();
                const pale = child.clone();
                pale.geometry = child.geometry.clone();
                pale.geometry.computeBoundingSphere();
                pale.geometry.computeBoundingBox();
                pale.geometry.boundingBox.getSize(size);

                const gc = { x: pale.geometry.boundingSphere.center.x, y: pale.geometry.boundingSphere.center.y, z: pale.geometry.boundingSphere.center.z };
                pale.geometry.translate(-gc.x, -gc.y, -gc.z);
                pale.geometry.scale(drone.size.x, drone.size.x, drone.size.x);

                pale.pos = { x : gc.x * drone.size.x, y : gc.y * drone.size.x, z : gc.z * drone.size.x}
                pale.rot = new THREE.Quaternion();
                pale.radiusX =  size.x  * drone.size.x / 2;
                pale.height =  size.y  * drone.size.x / 2;
                pale.radiusZ =  size.z  * drone.size.x / 2;

                pale.position.set (pale.pos.x,  pale.pos.y,  pale.pos.z);
                drone.pales.push(pale);
            }

            if(child.name.startsWith('Wing')) {

                const wing = child.clone();
                drone.wings.push(wing);
            }
        });

        const audioLoader = new THREE.AudioLoader();
        drone.positionalAudio = new THREE.PositionalAudio( this.AudioListener );

        audioLoader.load( this.assetBase+'/assets/snd/chopper.wav', function( buffer ) {
            drone.positionalAudio.setBuffer( buffer );
            drone.positionalAudio.setRefDistance( 2 );
            drone.positionalAudio.setMaxDistance ( drone.activation );
            drone.object.add(drone.positionalAudio);
        });
        */

 
    }

    addTrackGrid(grid) {

        this.makeGrid(grid);

        if(grid.hasBorder){
            for(let i=0;i<6;i++){
                this.addMesh(grid.borderMeshes[i]);
            }
        }

        this.addMesh(grid.fallBall.mesh);
        this.addMesh(grid.mesh);
    }
    

    async addTrackTurret(turret) {

        const name = turret.model ? turret.model : "turret";
        const object = await this.loadObj(name);

        object.traverse((child) => {

            if (child.type == 'Mesh') {

                if (child.name === 'body') {
                    turret.object = child.clone();
                    turret.object.material = child.material;

                    
                    turret.object.geometry.scale(turret.size.x, turret.size.y, turret.size.z);
                    //turret.object.geometry.translate(turret.pos.x, turret.pos.y, turret.pos.z);
                    
                    turret.object.position.set( turret.pos.x, turret.pos.y, turret.pos.z);
                    turret.object.updateWorldMatrix(true);


                    /*
                    turret.object.rotation.x = turret.angles.x;
                    turret.object.rotation.y = turret.angles.y;
                    turret.object.rotation.z = turret.angles.z;

                    turret.object.scale.x = turret.size.x;
                    turret.object.scale.y = turret.size.y;
                    turret.object.scale.z = turret.size.z;
                    */

                }
                if (child.name === 'head') {
                    turret.head = child.clone();
                    turret.head.material = child.material;

                    //turret.head.geometry.translate(turret.pos.x, turret.pos.y, turret.pos.z);
                    turret.head.geometry.translate(0, - turret.projPts.y , 0);
                    turret.head.geometry.scale(turret.size.x, turret.size.y, turret.size.z);
                    
                    

                    /*
                    turret.head.castShadow = true;
                    turret.head.receiveShadow = false;
                    turret.head.rotation.y = turret.projAngle;
                    */
                }
                if (child.name === 'canon') {
                    turret.canon = child.clone();
                    turret.canon.material = child.material;

                    //turret.canon.geometry.translate(turret.pos.x, turret.pos.y, turret.pos.z);
                    turret.canon.geometry.translate(0, - ( turret.projPts.y  ), 0);
                    turret.canon.geometry.scale(turret.size.x, turret.size.y, turret.size.z);
                    turret.canon.geometry.rotateZ(turret.projAngle2);


                    /*
                    turret.canon.castShadow = true;
                    turret.canon.receiveShadow = false;
                    turret.canon.geometry.applyMatrix4(new THREE.Matrix4().makeTranslation(0, -turret.projPts.y, 0));
                    turret.canon.position.y = turret.projPts.y;
                    turret.canon.rotation.z = turret.projAngle2;
                    */
                }
            }
        });

        if ((turret.shotModel) && (turret.shotModel.length > 0)) {
            const shotObject = await this.loadObj(turret.shotModel);

            shotObject.traverse(function(child){
                if(!child.isMesh)
                    return;

                turret.shotObject = child;
                turret.shotObject.geometry.computeBoundingBox();
                const gs = new THREE.Vector3();
                const gc = new THREE.Vector3();
                turret.shotObject.geometry.boundingBox.getSize(gs);
                turret.shotObject.geometry.boundingBox.getCenter(gc);
                turret.shotObject.geometry.translate(-gc.x,-gc.y, -gc.z);
                turret.shotObject.geometry.scale(turret.projSize * 2.0/gs.x,turret.projSize * 2.0/gs.y,turret.projSize * 2.0/gs.z);
                turret.shotObject.castShadow = true;
            });
        }else{
            
            turret.shotSphere = {};

            this.addSphere(turret.shotSphere,
                turret.projSize,
                new THREE.Vector3(0, 0, 0),
                new THREE.Vector3(0.8, 0.8, 0.2),
                new THREE.Vector3(0.8, 0.8, 0.2),
                0.2,
                RT_SPEC
             );

             this.setMeshStatic(turret.shotSphere, false);

             

             if(this.rtEngine){
                turret.shotSphere.rtSphere = {};
                this.rtEngine.addSphere(turret.shotSphere.rtSphere, turret.projSize, new THREE.Vector3(0, 0, 0), new THREE.Vector3(0.8, 0.8, 0.2), new THREE.Vector3(0.8, 0.8, 0.2), 0.2, RT_SPEC);
             }
        }
        

        const audioLoader = new THREE.AudioLoader();
        audioLoader.load( this.assetBase+'/assets/snd/'+turret.projSND, function( buffer ) {
            turret.shotSndBuffer = buffer;
        });

        /*
        var grp= new THREE.Group();
        grp.add(turret.canon);
        grp.add(turret.head);
        grp.add(turret.object);
        this.addMesh(grp);
        */

        /*
        turret.head.add(turret.canon);
        turret.object.add(turret.head);
        this.scene.add(turret.object);

        if (turret.shotObject) {
            this.scene.add(turret.shotObject);
        }
        */

    }

    async addTrackJumper(jumper, object = null) {

        var mat = new THREE.Matrix4();

        mat.compose(new THREE.Vector3(jumper.pos.x, jumper.pos.y, jumper.pos.z),
            jumper.rot2,
            new THREE.Vector3(jumper.size.x, jumper.size.y, jumper.size.z));

        if (object === null) {

            const object = await this.loadObj('jumper');

            object.traverse(function (c){ if(c.isMesh) jumper.object = c;});
        }
        else {

            object.traverse((child) => {

                if (child.type == 'Mesh') {
                    const g = child.geometry.clone();
                    jumper.object = new THREE.Mesh(g, child.material);
                }
            });
        }

        jumper.object.name = 'jumper';
        jumper.object.geometry.applyMatrix4(mat);
    }

    
    addTrackRope(rope, object) {

        const a = new THREE.SphereGeometry(0.2);
        rope.anchor.object = new THREE.Mesh(a, this.platformMaterial);
        rope.anchor.object.name = 'ropeanchor-'+rope.id;

        rope.anchor.object.position.x = rope.anchor.pos.x;
        rope.anchor.object.position.y = rope.anchor.pos.y;
        rope.anchor.object.position.z = rope.anchor.pos.z;

        if (object) {
            rope.obj.object = object.clone();

            rope.obj.object.geometry = object.geometry.clone();
            rope.obj.object.geometry.scale(rope.obj.size.x, rope.obj.size.y, rope.obj.size.z);
        } else {
            const t = rope.obj.shape === 0 ? new THREE.BoxGeometry(rope.obj.size.x, rope.obj.size.y, rope.obj.size.z) : new THREE.CylinderGeometry(rope.obj.size.x / 2, rope.obj.size.x / 2, rope.obj.size.y, 12);
            rope.obj.object = new THREE.Mesh(t, this.platformMaterial);
            rope.anchor.object.name = 'rope anchor-'+rope.id;
            rope.obj.object.name = 'rope object-'+rope.id;
        }

        rope.obj.object.position.x = rope.obj.pos.x;
        rope.obj.object.position.y = rope.obj.pos.y;
        rope.obj.object.position.z = rope.obj.pos.z;

        rope.obj.object.rotation.x = rope.obj.angles.x;
        rope.obj.object.rotation.y = rope.obj.angles.y;
        rope.obj.object.rotation.z = rope.obj.angles.z;

        const qo = new THREE.Quaternion().setFromEuler(new THREE.Euler(rope.obj.object.rotation.x, rope.obj.object.rotation.y, rope.obj.object.rotation.z, "XYZ"));
        const type = rope.model.length > 0 ? RT_DIFF : RT_REFR;
        rope.obj.object.meshGPU = this.addMesh(rope.obj.object, type, new Transformation3(rope.obj.object.position, qo));
        this.setMeshStatic(rope.obj.object, false);

        const qa = new THREE.Quaternion().setFromEuler(new THREE.Euler(rope.anchor.object.rotation.x, rope.anchor.object.rotation.y, rope.anchor.object.rotation.z, "XYZ"));
        rope.anchor.object.meshGPU = this.addMesh(rope.anchor.object, RT_SPEC, new Transformation3(rope.anchor.object.position, qa));

    }

    removeBoneCylinders(cyls){

        for(let c of cyls){

            if(c.mesh !== null){

                if(c.leaf)
                    this.removeSphere(c.mesh);
                else
                    this.removeCylinder(c.mesh);
                c.mesh = null;
            }

            if(c.smesh !== null){
                this.removeSphere(c.smesh);
                c.smesh = null;
            }
        }
    }

    addBoneCylinders(cyls, mode, exclude = []){

        for(let c of cyls){

            if(c.mesh == null){
                c.mesh = {}

                if(!c.boneName.startsWith('hand')) {

                    if((mode & 2)&&(exclude.indexOf(c.boneName)<0))
                        this.addCylinder(c.mesh, c.radius, c.center, c.length, c.quat, c.color, 0.6, RT_DIFF);

                    if((mode & 1)&&(exclude.indexOf(c.boneName)<0)){
                        const pos = c.dir.clone().multiplyScalar(c.length).add(c.pos);
                        c.smesh = {};
                        this.addSphere(c.smesh, c.jradius, pos, new THREE.Vector3(0,0,0), c.color, 0.6, RT_DIFF);
                    }
                }
                else if(mode & 2)
                    this.addSphere(c.mesh, c.radius, c.center, new THREE.Vector3(0,0,0), c.color, 0.6, RT_DIFF);
            }
        }
    }

    getSkelSubMeshes(mesh, skel){

        const geo = mesh.geometry.clone().toNonIndexed();
        const map = skel.getBoneMap();

        const primitives= [];

        primitives['root'] = {total : 0,materials : []};

        for(let b in map){
            primitives[b] =  {total : 0,materials : []};
        }

        const nTriangles = geo.attributes.position.count / 3;

        for(let n=0; n<nTriangles; n++) {

            const v1 = new THREE.Vector3(geo.attributes.position.array[n * 9 + 0], geo.attributes.position.array[n * 9 + 1], geo.attributes.position.array[n * 9 + 2]);
            const v2 = new THREE.Vector3(geo.attributes.position.array[n * 9 + 3], geo.attributes.position.array[n * 9 + 4], geo.attributes.position.array[n * 9 + 5]);
            const v3 = new THREE.Vector3(geo.attributes.position.array[n * 9 + 6], geo.attributes.position.array[n * 9 + 7], geo.attributes.position.array[n * 9 + 8]);


            let matId = 0;
            let matName = '';

            if(mesh.material.length) {

                for(let grp of geo.groups) {
                    if(( n * 3 >= grp.start)&&( n * 3 < (grp.start + grp.count))){
                        matId = grp.materialIndex;
                        matName = mesh.material[matId].name;
                        break;
                    }
                }
            } else {
                matId = 0;
                matName = mesh.material.name;
            }

            let done = false;

            for(let mat of skel.matExclude){

                if(mat.startsWith(matName)){

                    if(!primitives['root'].materials[matId]){
                        primitives['root'].materials[matId] = [];
                    }
    
                    primitives['root'].materials[matId].push(n);
                    primitives['root'].total ++;
                    done = true;
                    break;
                }
            }

            if(done)
                continue;

            const v_weights = [];
            
            v_weights[0] = skel.getBoneWeights(v1);
            v_weights[1] = skel.getBoneWeights(v2);
            v_weights[2] = skel.getBoneWeights(v3);

            if( !(v_weights[0].length>0)&&
                !(v_weights[1].length>0)&&
                !(v_weights[2].length>0))
            {

                if(!primitives['root'].materials[matId]){
                    primitives['root'].materials[matId] = [];
                }

                primitives['root'].materials[matId].push(n);
                primitives['root'].total ++;
                continue;
            }

            let weight = null;
            let maxw = -1;

            for(let i=0;i<3;i++){

                if(!v_weights[i])
                    continue;

                for(let wn=0; wn<v_weights[i].length; wn++){

                    if(v_weights[i][wn].weight > maxw){
                        maxw = v_weights[i][wn].weight;
                        weight =v_weights[i][wn];
                    }
                }
            }

            if(!primitives[weight.name].materials[matId]){
                primitives[weight.name].materials[matId] = [];
            }

            primitives[weight.name].materials[matId].push(n);
            primitives[weight.name].total ++;
        }
        
        const geoms = [];

        for(let np in map) {
          
            const newGeom = new THREE.BufferGeometry();
            newGeom.name = np;

            if(primitives[np].total > 0 ){
                const p = primitives[np];
                const nVtx = p.total * 3;
                                
                for(let attr in geo.attributes) {
                    
                    const vSize =  geo.attributes[attr].itemSize;
                    const marray = new Float32Array( nVtx * vSize);
                    let gidx = 0;

                    for(let matid in p.materials) {
    
                        const mat = p.materials[matid];

                        for(let tri =0; tri < mat.length; tri++){
                            
                            let triStart = mat[tri] * 3 * vSize;
    
                            for(let vt =0; vt < 3; vt++){
                                for(let e =0; e < vSize; e++) {
                                    marray[gidx++] = geo.attributes[attr].array[ triStart + e];
                                }
                                triStart += vSize;
                            }
                        }
                    }
                    newGeom.setAttribute(attr, new THREE.Float32BufferAttribute(marray,  vSize) );
                }

                let start = 0;
                for(let matid in p.materials) {
                    const mat = p.materials[matid];
                    newGeom.addGroup(start * 3, mat.length * 3, parseInt(matid));
                    start += mat.length; 
                }

                console.log(newGeom);

                
            } else {
                for(let attr in geo.attributes) {
                    const vSize =  geo.attributes[attr].itemSize;
                    const marray = new Float32Array(0);
                    newGeom.setAttribute(attr, new THREE.Float32BufferAttribute(marray,  vSize) );
                }
            }
            
            geoms[np] = newGeom;
        }
        return geoms;
    }

    doMeshSkinning(igeo, skel){

        const geo = igeo.clone();
        const map = skel.getBoneMap();
        const nVtx = geo.attributes.position.count;
        const nTriangles = geo.index ? geo.index.count / 3 : nVtx / 3;

        if(!geo.attributes.skinIndex){
            const indexes = new Uint16Array(nVtx * 4);
            geo.setAttribute( 'skinIndex', new THREE.Uint16BufferAttribute(indexes, 4 ) );
        }

        if(!geo.attributes.skinWeight){
            const weights = new Float32Array(nVtx * 4);
            for(let t=0; t< weights.length ; t+=4) { weights[t + 0] = 1.0; }
            geo.setAttribute( 'skinWeight', new THREE.Float32BufferAttribute(weights, 4 ) );
        }

        for(let n=0;n<geo.attributes.position.count; n++){
            const v = new THREE.Vector3(geo.attributes.position.array[n * 3 + 0], geo.attributes.position.array[n * 3 + 1], geo.attributes.position.array[n * 3 + 2]);
            const v_weights = skel.getBoneWeights(v);

            if(v_weights.length<=0)
            {
                geo.attributes.skinIndex.array[ n * 4 + 0] = 0;
                geo.attributes.skinIndex.array[ n * 4 + 1] = 0;
                geo.attributes.skinIndex.array[ n * 4 + 2] = 0;
                geo.attributes.skinIndex.array[ n * 4 + 3] = 0;

                geo.attributes.skinWeight.array[ n * 4 + 0] = 1.0;
                geo.attributes.skinWeight.array[ n * 4 + 1] = 0.0;
                geo.attributes.skinWeight.array[ n * 4 + 2] = 0.0;
                geo.attributes.skinWeight.array[ n * 4 + 3] = 0.0;
                continue;
            }

            
            for(let wi =0; wi<v_weights.length; wi++) {
                geo.attributes.skinIndex.array[ n * 4 + wi] = map[v_weights[wi].name];
                geo.attributes.skinWeight.array[ n * 4 + wi ] = v_weights[wi].weight;
            }

            /*
            let tw= 0.0;
            for(let wi =0; wi<v_weights.length; wi++) {
                tw += v_weights[wi].weight;
            }
            
            geo.attributes.skinIndex.array[ n * 4 + 0] = 0;
            geo.attributes.skinWeight.array[ n * 4 + 0] = Math.max(0.0, 1.0 - tw);

            if(v_weights.length > 0){
                geo.attributes.skinIndex.array[ n * 4 + 1] = map[v_weights[0].name];
                geo.attributes.skinWeight.array[ n * 4 + 1] = v_weights[0].weight;
            }

            if(v_weights.length > 1){
                geo.attributes.skinIndex.array[ n * 4 + 2] = map[v_weights[1].name];
                geo.attributes.skinWeight.array[ n * 4 + 2] =v_weights[1].weight;
            }
            */

        }

        return geo;
    }

    addTrackSpring(spring, object) {

        const a = new THREE.SphereGeometry(0.2);
        spring.anchor.object = new THREE.Mesh(a, this.platformMaterial);
        spring.anchor.object.name = 'springplatform-'+spring.id;

        spring.anchor.object.position.x = spring.anchor.pos.x;
        spring.anchor.object.position.y = spring.anchor.pos.y;
        spring.anchor.object.position.z = spring.anchor.pos.z;

        if (object) {
            spring.obj.object = object.clone();
            spring.obj.object.geometry = object.geometry.clone();
            spring.obj.object.geometry.scale(spring.size.x / spring.obj.size.x, spring.size.y / spring.obj.size.y, spring.size.z / spring.obj.size.z);
        }
        else {
            const t = spring.obj.shape === 0 ? new THREE.BoxGeometry(spring.obj.size.x, spring.obj.size.y, spring.obj.size.z) : new THREE.CylinderGeometry(spring.obj.size.x / 2, spring.obj.size.x / 2, spring.obj.size.y, 12);
            spring.obj.object = new THREE.Mesh(t, this.platformMaterial);
            spring.obj.object.name = 'spring object-'+spring.id;
            spring.anchor.object.name = 'spring anchor object-'+spring.id;
        }

        spring.obj.object.position.x = spring.obj.pos.x;
        spring.obj.object.position.y = spring.obj.pos.y;
        spring.obj.object.position.z = spring.obj.pos.z;

        spring.obj.object.rotation.x = spring.obj.angles.x;
        spring.obj.object.rotation.y = spring.obj.angles.y;
        spring.obj.object.rotation.z = spring.obj.angles.z;

        const qo = new THREE.Quaternion().setFromEuler(new THREE.Euler(spring.obj.object.rotation.x, spring.obj.object.rotation.y, spring.obj.object.rotation.z, "XYZ"));
        spring.obj.object.meshGPU = this.addMesh(spring.obj.object, RT_SPEC, new Transformation3(spring.obj.object.position, qo));

        this.setMeshStatic(spring.obj.object, false);

        const qa = new THREE.Quaternion().setFromEuler(new THREE.Euler(spring.anchor.object.rotation.x, spring.anchor.object.rotation.y, spring.anchor.object.rotation.z, "XYZ"));
        spring.anchor.object.meshGPU = this.addMesh(spring.anchor.object, RT_SPEC, new Transformation3(spring.anchor.position, qa));
    }

    
    removeTrack(track) {

        //this.removeMesh(track.terrain.meshGPU);
        //this.removeMesh(track.terrain.groundGPU);
        this.removeMesh(track.bonusMeshGPU);

        this.toonGEO = null;

        if(track.terrain.objs){
            for(let o of track.terrain.objs){
                this.removeMesh(o);
            }
        }

        if (track.roadObj !== null) {
            this.removeMesh(track.roadObj);
        }
        for (let n = 0; n < track.walls.length; n++) {

            if(track.walls[n].meshGPU)
                this.removeMesh(track.walls[n].meshGPU);
        }

        if (this.trackSpehres) {
            for (let n = 0; n < this.trackSpehres.length; n++) {
                this.removeMesh(this.trackSpehres[n]);
            }
        }

        for (let n = 0; n < track.terrain.cyls.length; n ++) {

            if(track.terrain.cyls[n].isCyl)
                this.removeMesh(track.terrain.cyls[n].mesh);
            else
                this.removeMesh(track.terrain.cyls[n]);
        }

        for (let n = 0; n < track.terrain.cubes.length; n ++) {

            if(track.terrain.cubes[n].isCube)
                this.removeMesh(track.terrain.cubes[n].mesh);
            else
                this.removeMesh(track.terrain.cubes[n]);
        }
        
        if (track.npcs) {
            for (let n = 0; n < track.npcs.length; n++) {
                this.removeChar(track.npcs[n]);
            }
        }

        if (track.woods) {
            for (let n = 0; n < track.woods.length; n++) {
                for (let i = 0; i < track.woods[n].objects.length; i++) {
                    this.removeCylinder(track.woods[n].objects[i]);
                }
            }
        }

        if (track.bonuses) {
            for (let n = 0; n < track.bonuses.length; n++) {
                this.removeMesh(track.bonuses[n].object);
                //this.removeMesh(track.bonuses[n].light);
            }
        }


        if (track.interestPoints) {

            for (let n = 0; n < track.interestPoints.length; n++) {
                
                
                this.removeMesh(track.interestPoints[n].object);
                this.removeMesh(track.interestPoints[n].lmesh);

                if(track.interestPoints[n].wireframe) {
                    this.removeMesh(track.interestPoints[n].wireframe);
                }
            }
        }

        if (track.turrets) {
            for (let n = 0; n < track.turrets.length; n++) {
                this.removeMesh(track.turrets[n].head);

                let s =0;
                while(s <track.turrets[n].samples.length){

                    if(!track.turrets[n].samples[s].isPlaying){
                        track.turrets[n].object.remove(track.turrets[n].samples[s]);
                        track.turrets[n].samples[s].pause();
                        track.turrets[n].samples[s].disconnect();
                        track.turrets[n].samples.splice(s, 1);
                    }else
                        s++;
                }
                

                for (let i = 0; i < track.turrets[n].shots.length; i++) {
                    this.removeSphere(track.turrets[n].shots[i]);

                    if(track.turrets[n].shots[i].particles){
                        this.removeParticles(track.turrets[n].shots[i].particles);
                        track.turrets[n].shots[i].particles = null;
                    }
                    
                }

                if(track.turrets[n].shotObject) {
                    this.removeMesh(track.turrets[n].shotObject);
                }
            }
        }

        if (track.drones) {

            for (let n = 0; n < track.drones.length; n++) {
                this.removeMesh(track.drones[n].object);

                for(let pale of track.drones[n].pales) {
                    this.removeMesh(pale.mesh);
                }
                for(let wing of track.drones[n].wings) {
                    this.removeMesh(wing);
                }

                for (let i = 0; i < track.drones[n].shots.length; i++) {
                    this.removeSphere(track.drones[n].shots[i]);
                }
            }
        }

        if (track.springs) {
            for (let n = 0; n < track.springs.length; n++) {
                this.removeMesh(track.springs[n].obj.object);
                this.removeMesh(track.springs[n].anchor.object);
            }
        }

        
        if (track.spiders) {
            for (let n = 0; n < track.spiders.length; n++) {
                this.removeSpiderSkel(track.spiders[n].skel);
                let s = null;
                while((s  = track.spiders[n].samples.pop()) != null){
                    track.spiders[n].object.remove(s);
                    s.getOutput().disconnect();
                }
            }
        }

        if (track.ropes) {
            for (let n = 0; n < track.ropes.length; n++) {
                this.removeMesh(track.ropes[n].obj.object);
                this.removeMesh(track.ropes[n].anchor.object);
            }
        }

        for(let l of track.lights){
            this.removeMesh(l);
        }

        for(let m of track.terrain.movingTerrain){
            this.removeMesh(m);
        }
        for(let m of track.terrain.waterFlows){
            this.removeMesh(m.object);
            m.object.remove(m.positionalAudio);
        }

        if(track.terrainGroup){
            this.removeMesh(track.terrainGroup);
        }
    }
    resetTrack(track) {

        if (track.turrets) {
            for (let n = 0; n < track.turrets.length; n++) {
                track.turrets[n].life = track.turrets[n].total_life;
                let s =0;
                while(s <track.turrets[n].samples.length){

                    if(!track.turrets[n].samples[s].isPlaying){
                        track.turrets[n].object.remove(track.turrets[n].samples[s]);
                        track.turrets[n].samples[s].pause();
                        track.turrets[n].samples[s].disconnect();
                        track.turrets[n].samples.splice(s, 1);
                    }else
                        s++;
                }
                

                for (let i = 0; i < track.turrets[n].shots.length; i++) {
                    this.removeSphere(track.turrets[n].shots[i]);

                    if(track.turrets[n].shots[i].particles){
                        this.removeParticles(track.turrets[n].shots[i].particles);
                        track.turrets[n].shots[i].particles = null;
                    }
                }
                track.turrets[n].shots = [];
            }
        }

        let n=0;
        while(n<this.particles.length){

            if(this.particles[n].pinned === false)
                this.removeParticles(this.particles[n]);
            else
                n++;
        }


        if (track.woods) {
            for (let n = 0; n < track.woods.length; n++) {
                track.computeWoods(track.woods[n]);
            }
        }
        
        if (track.cubes) {
            for (let n = 0; n < track.cubes.length; n++) {
                const mc = track.cubes[n];
                mc.pos = {x: mc.opos.x, y: mc.opos.y, z: mc.opos.z};
                mc.rot = {x: mc.orot.x, y: mc.orot.y, z: mc.orot.z, w: mc.orot.w};

                if((!track.terrain.cubes[n].meshGPU)&&(!track.terrain.cubes[n].instanceOf)){
                   track.terrain.cubes[n].position.set(mc.pos.x, mc.pos.y, mc.pos.z);
                   track.terrain.cubes[n].quaternion.set(mc.rot.x, mc.rot.y, mc.rot.z, mc.rot.w);
                }
            }
        }

        if (track.cyls) {
            for (let n = 0; n < track.cyls.length; n++) {
                const mc = track.cyls[n];
                mc.pos = {x: mc.opos.x, y: mc.opos.y, z: mc.opos.z};
                mc.rot = {x: mc.orot.x, y: mc.orot.y, z: mc.orot.z, w: mc.orot.w};
                if(!track.terrain.cyls[n].meshGPU){
                    track.terrain.cyls[n].position.set(mc.pos.x, mc.pos.y, mc.pos.z);
                    track.terrain.cyls[n].quaternion.set(mc.rot.x, mc.rot.y, mc.rot.z, mc.rot.w);
                }
            }
        }

        if (track.drones) {

            for (let n = 0; n < track.drones.length; n++) {

                track.computeDrone(track.drones[n]);

                for (let i = 0; i < track.drones[n].shots.length; i++) {
                    this.removeSphere(track.drones[n].shots[i]);
                }
            }
        }


        if (track.spiders) {
            for (let n = 0; n < track.spiders.length; n++) {
                let ofs = 0;
                this.setBoneData(track.spiders[n].skel.root, track.spiders[n].bindPose, ofs);
                track.spiders[n].dead = false;
                track.spiders[n].active = false;
                track.spiders[n].samples = [];

                track.spiders[n].lastDist=0;
                track.spiders[n].stuckTime = 0;
                track.spiders[n].stuckMode = false;
                track.spiders[n].backMode = false;
                track.spiders[n].backTime = 0;
                track.spiders[n].dir = 1;

                
                for (let i = 0; i < track.spiders[n].shots.length; i++) {
                    this.removeSphere(track.spiders[n].shots[i]);
                }

                track.spiders[n].shots = [];
            }
        }

        if (track.npcs) {
            for (let n = 0; n < track.npcs.length; n++) {
                let ofs = 0;

                this.removeDialog(track.npcs[n]);
                track.computeNPC(track.npcs[n]);

                track.npcs[n].dialogInstance.line = 0;

                track.npcs[n].script=track.npcs[n].script_o;

                track.npcs[n].scriptInstance.state = null;
                track.npcs[n].scriptInstance.lastStart = 0;
                track.npcs[n].scriptInstance.line = 0;
                track.npcs[n].scriptInstance.nLoops = 0;

                if(track.npcs[n].skeleton)
                    this.setRagdollBoneData(track.npcs[n].skeleton.root, track.npcs[n].bindPose, ofs);
            }
        }
        if (track.xmeshes) {
            let n = 0;
            while ( n < track.xmeshes.length) {

                if(track.xmeshes[n] == null)
                    continue;
           
                if(track.xmeshes[n].splitted === false){
                    track.xmeshes[n].mesh.geometry = track.xmeshes[n].ogeo.clone();
                    track.xmeshes[n].mass = 0;
                    track.computeXMesh(track.xmeshes[n]);
                    n++;
                }else{
                    this.removeMesh(track.xmeshes[n].mesh);
                    this.destroyObj(track.xmeshes[n].mesh);
                    track.xmeshes.splice(n, 1);
                }
            }
        }

        
    }


    createRagDollBoneCyl(bone, ragdollCyls){

        let cgeo = null;
        if(bone.ammoData.shapeType === 1){
            const shape = bone.ammoData.shape;
            const height = shape.getHalfHeight() * 2.0;
            const radius =shape.getRadius();
    
            cgeo = new THREE.CylinderGeometry(radius, radius, height+ radius * 2);
            cgeo.translate(0, height/2 + radius,0);

        }else if(bone.ammoData.shapeType === 0){
            cgeo = new THREE.SphereGeometry(bone.radius);
            cgeo.translate(0, bone.radius,0);
        }
         
        if(cgeo){
            let color = 0xFFFFFFFF;

            if(bone.name.startsWith('spine'))
                color = 0xFF0000FF;
            else if(bone.name.startsWith('shoulder'))
                color = 0x0000FFFF;
            else if(bone.name.startsWith('hip'))
                color = 0x00FF00FF;
    
    
            const mesh = new THREE.Mesh(cgeo, new THREE.MeshBasicMaterial({color: color, depthTest: false, transparent: true, opacity:0.5}));
            mesh.name = bone.name;
            ragdollCyls.push(mesh);
            const p = new Ammo.btVector3();
            const q = new Ammo.btQuaternion();
            const transform = bone.physicsBody.getWorldTransform();
            transform.getOrigin(p);
            transform.getRotation(q);
    
            mesh.position.set(p.x(), p.y(), p.z());
            mesh.quaternion.set(q.x(), q.y(), q.z(), q.w());
    
            Ammo.destroy(p);
            Ammo.destroy(q);
    
            this.addMesh(mesh);
        }
           
        
        

        for(let c of bone.children){
            this.createRagDollBoneCyl(c, ragdollCyls);
        }
    }

    createRagDollCyls(character){

        character.ragdollCyls = [];
        this.createRagDollBoneCyl(character.skeleton.root, character.ragdollCyls);
    }

    updateRagDollCyls(character){

        if(!character.ragdollCyls)
            return;

        const tmpTransformation = new Ammo.btTransform();
        for(let rc of character.ragdollCyls){

            const bone = character.skeleton.root.findBone(rc.name);

            if(!bone)
                continue;

            
            const ms = bone.physicsBody.getMotionState();
            ms.getWorldTransform(tmpTransformation);

            //const transform = bone.physicsBody.getWorldTransform();
            
            const p = tmpTransformation.getOrigin();
            const q = tmpTransformation.getRotation();
    
            rc.position.set(p.x(), p.y(), p.z());
            rc.quaternion.set(q.x(), q.y(), q.z(), q.w());

        
            
            Ammo.destroy(p);
            Ammo.destroy(q);
        }

        Ammo.destroy(tmpTransformation);
        
    }

    async addTrack(track) {

        const grp = new THREE.Group();
        

        const color = new THREE.Color().setRGB(0,1,1);
        const t = new Transformation3();

        if (track.roadObj) {

            //grp.add(track.roadObj);

            //track.roadObj.position.set(track.terrain.offset.x, track.terrain.offset.y, track.terrain.offset.z);            
            track.roadObj.meshGPU = this.addMesh(track.roadObj, RT_METALCOAT);

           
            
            /*
            t.set(track.roadObj.position, track.roadObj.quaternion);
            this.updateTransform(track.roadObj, t);
            track.roadObj.material.name = "rt_metalcoat";
            */
            
            //console.log('walls :', track.walls);

            track.walls[0].meshGPU = this.addMesh(track.walls[0].object, RT_DIFF, new Transformation3(track.walls[0].object.position, track.walls[0].object.quaternion));
            track.walls[1].meshGPU = this.addMeshInstance(track.walls[0].meshGPU, new Transformation3(track.walls[1].object.position, track.walls[1].object.quaternion));
            
            const tubcol = new THREE.Color().setRGB(track.tubecolor.r, track.tubecolor.g, track.tubecolor.b);
            this.tubemat = new THREE.MeshPhongMaterial({ color: tubcol.getHex(), emissive: tubcol.getHex(), toneMapped: false});
            this.tubemat.name = "tubemat";

            //const mat = new THREE.MeshPhongMaterial({ color: tubcol.getHex(), emissive: tubcol.getHex()});
    
            const tubeGeometry1 = new THREE.TubeGeometry( track.curveL, track.NPoints, 0.25, 4, false );
            tubeGeometry1.setAttribute('tangent', new THREE.Float32BufferAttribute(tubeGeometry1.tangents, 4));
            const mesh1 = new THREE.Mesh(tubeGeometry1, this.tubemat);
            mesh1.name ='tube 1';

            const tubeGeometry3 = new THREE.TubeGeometry( track.curveLU, track.NPoints, 0.25, 4, false );
            tubeGeometry3.setAttribute('tangent', new THREE.Float32BufferAttribute(tubeGeometry3.tangents, 4));
            const mesh3 = new THREE.Mesh(tubeGeometry3, this.tubemat);
            mesh3.name ='tube 3';

            const tubeGeometry2 = new THREE.TubeGeometry( track.curveR, track.NPoints, 0.25, 4, false );
            tubeGeometry2.setAttribute('tangent', new THREE.Float32BufferAttribute(tubeGeometry2.tangents, 4));
            const mesh2 = new THREE.Mesh(tubeGeometry2, this.tubemat);

            mesh2.name ='tube 2';
            const tubeGeometry4 = new THREE.TubeGeometry( track.curveRU, track.NPoints, 0.25, 4, false );
            tubeGeometry4.setAttribute('tangent', new THREE.Float32BufferAttribute(tubeGeometry4.tangents, 4));
            const mesh4 = new THREE.Mesh(tubeGeometry4, this.tubemat);
            mesh4.name ='tube 4';

            //mesh1.material.name="rt_coat";

            mesh1.material.disableShadow = true;
            mesh2.material.disableShadow = true;
            mesh3.material.disableShadow = true;
            mesh4.material.disableShadow = true;
            track.roadObj.material.disableShadow = true;

            grp.add(mesh1);
            grp.add(mesh2);
            grp.add(mesh3);
            grp.add(mesh4);

            for (let n = 2; n < track.walls.length; n += 2) {
                
                if (n % 2 == 0) {
                    track.walls[n + 0].meshGPU = this.addMeshInstance(track.walls[0].meshGPU, new Transformation3(track.walls[n + 0].object.position, track.walls[n + 0].object.quaternion));
                    track.walls[n + 1].meshGPU = this.addMeshInstance(track.walls[0].meshGPU, new Transformation3(track.walls[n + 1].object.position, track.walls[n + 1].object.quaternion));
                }
            }
        }

        if (track.roadWall) {
            //track.roadWall.material.opacity = 1.0;
            //track.roadWall.material.transparent = true;
            //track.roadWall.material.disableShadow = true;
            grp.add(track.roadWall);
        }

        let ground = null;

        track.terrain.objs = [];
        const uniqueGeoMats = {};

        track.terrain.mesh.traverse(child => {

            if(!child.isMesh)
                return;



                
            if ( (child.name.startsWith('Wheel')) || 
                 (child.name.startsWith('SWheel')) ||
                 (child.name.startsWith('Crystal')) ||
                 (child.name.startsWith('buddha')) ||
                 (child.name.startsWith('wire')) ||
                 (child.name.startsWith('Star')) ) {

                
                const o = child.clone();

                const ar = o.name.split('-');
                o.position.set(o.position.x + track.terrain.offset.x, o.position.y + track.terrain.offset.y, o.position.z + track.terrain.offset.z);
            
                if(child.name.startsWith('SWheel'))
                    o.oQuaternion = o.quaternion.clone();

                 o.castShadow = true;

                if((ar.length>=2)&&(ar[1].startsWith('Dirigeable')))
                    o.castShadow = false;
                
                //||(ar[1].startsWith('fan'))
                if((ar.length>=2)&&((ar[1].startsWith('donut'))||(ar[1].startsWith('station'))))
                    o.castShadow = false;

                const speed = ar.length>=3 ? parseFloat(ar[2]) /10.0 : 1;

                if(o.material.length > 0 ) {
                
                    for(let imat =0; imat<o.material.length;imat++)
                    {
                        let mat = o.material[imat];

                        if(mat.name.startsWith('Toon')){
                            mat = this.setToon(mat);
                            o.material[imat] = mat;

                            const ng = this.addFlippedGeo(o.geometry, o.geometry.groups[imat]);
                            const toonMesh = new THREE.Mesh(ng, this.toonFlippedMat);
                            o.add(toonMesh);
                        }
                    }
                }

                const lname = this.getInstName(o.name);
                const it = track.findInstance(lname);
                if (it) {
                    if (!it.meshGPU) {
                        it.meshGPU = this.addMesh(o, RT_DIFF, new Transformation3(o.position, o.quaternion), {x:false, y:false, z: false}, true, null, it.nInstances);
                        it.speeds = new Array(it.nInstances);
                        it.name = child.name;
                        track.terrain.movingTerrain.push(it);
                    }

                    it.speeds[it.nFilled] = speed;
                    this.updateTransform(it, new Transformation3(o.position, o.quaternion), it.nFilled++);
                    
                } else {
                    o.nInstances = 1;
                    o.speeds = [speed];
                    track.terrain.movingTerrain.push(o);
                }

                

            }else if ((child.name.startsWith('fire-start'))|| (child.name.startsWith('cyberpart'))|| (child.name.startsWith('sandpart'))){

            }else if(child.name.startsWith('light')){

                const light = {};
                child.geometry.computeBoundingSphere();
                this.addPointLight(light, child.position, child.material.color, child.geometry.boundingSphere.radius);
            }else if (child.name=== 'Plane'){
                ground = child.clone();
                //ground.geometry.translate(track.terrain.offset.x, track.terrain.offset.y, track.terrain.offset.z)

                ground.position.add(track.terrain.offset);
                ground.geometry.computeBoundsTree();
                ground.castShadow = false;

            }else if(child.name.startsWith('path')){
            }else if(child.name.startsWith('Area')){
            }else if(track.movingObjects.hasOwnProperty(child.name)){

                const o = child.clone();
                o.position.set(o.position.x + track.terrain.offset.x, o.position.y + track.terrain.offset.y, o.position.z + track.terrain.offset.z);
                track.terrain.movingTerrain.push(o);

            }else if(child.name.startsWith('Anchor')){
                /*
                const o = child.clone();
                o.position.set(o.position.x + track.terrain.offset.x, o.position.y + track.terrain.offset.y, o.position.z + track.terrain.offset.z);
                track.terrain.movingTerrain.push(o);
                */
                  
            }else if(child.name.startsWith('Axis')){
                /*
                const o = child.clone();
                o.position.set(o.position.x + track.terrain.offset.x, o.position.y + track.terrain.offset.y, o.position.z + track.terrain.offset.z);
                track.terrain.movingTerrain.push(o);
                */
                  
            }else if(child.SimpleSkeleton) {
  
                const o = child;
                o.position.set(o.position.x + track.terrain.offset.x, o.position.y + track.terrain.offset.y, o.position.z + track.terrain.offset.z);
                track.terrain.movingTerrain.push(o);
            }else if (child.name.startsWith('rt-lamp')){

                const q = new THREE.Quaternion();
                const center = new THREE.Vector3();
                const size = new THREE.Vector3();

                child.geometry.computeBoundingBox();
                const box = child.geometry.boundingBox;
                box.getCenter(center);
                box.getSize(size);

                const c = center.add(track.terrain.offset);

                const newcyl = {};
                this.addCylinder(newcyl, size.x / 2.0, c, size.y, q, new THREE.Vector3(child.material.color.r, child.material.color.g, child.material.color.b), 0.6, RT_REFR);

                /*
                const newsphere = {};
                const r = Math.random();
                const col = track.terrain.cyls.length & 1 ? new THREE.Vector3(1, 0.25, 0.25) : new THREE.Vector3(0.25, 0.8, 0.25)

                
                this.addSphere(newsphere,
                    4,
                    new THREE.Vector3(c.x, c.y + size.y / 2.0 + 3, c.z),
                    col.multiplyScalar(0.8),
                    col,
                    0.2,
                    RT_POINT_LIGHT
                );
                */

                track.terrain.cyls.push({radiusX : size.x /2.0, radiusZ : size.z /2.0, b: new THREE.Vector3(c.x, c.y - size.y/2, c.z), height: size.y, isCyl : true,  mesh : newcyl, deltaPhys: new THREE.Vector3()});
            
            }else if (child.name.startsWith('rt-cylinder')){

                child.geometry.computeBoundingBox();
                const box = child.geometry.boundingBox;
                const q = new THREE.Quaternion();
                const center = new THREE.Vector3();
                const size = new THREE.Vector3();
                box.getCenter(center);
                box.getSize(size);

                const c = center.add(track.terrain.offset);

                const newcyl = {};

                const type = child.material.name.startsWith('Water')? RT_REFR :RT_COAT;
                
                this.addCylinder(newcyl, size.x / 2.0, c, size.y, q, new THREE.Vector3(child.material.color.r, child.material.color.g, child.material.color.b), child.material.roughness, type);

                track.terrain.cyls.push({radiusX : size.x /2.0, radiusZ : size.z /2.0, b: new THREE.Vector3(c.x, c.y - size.y/2, c.z), height: size.y, isCyl : true, mesh : newcyl, l : null, deltaPhys : new THREE.Vector3()});
            
            }else if (child.name.startsWith('col-barrel')){

                const mc = child.clone();

                mc.geometry.computeBoundingSphere();

                const c =  mc.geometry.boundingSphere.center.clone()

                mc.pos = {x: c.x+ track.terrain.offset.x, y: c.y+ track.terrain.offset.y, z: c.z + track.terrain.offset.z};

                mc.geometry.translate(-c.x, -c.y, -c.z);
                mc.position.set(mc.pos.x, mc.pos.y, mc.pos.z);
                mc.castShadow = true;
                
                const lname = child.name.substr(11);
                const it = track.findInstance(lname);
                if (it) {
                    if (!it.meshGPU) {
                        it.meshGPU = this.addMesh(mc, RT_DIFF, new Transformation3(mc.position, mc.quaternion), {x:false, y:false, z: false}, true, null, it.nInstances);
                    }
                    this.updateTransform(it, new Transformation3(mc.position, mc.quaternion), it.nFilled++);
                } else {
                    mc.meshGPU = this.addMesh(mc, RT_DIFF, new Transformation3(mc.position, mc.quaternion));
                    this.setMeshStatic(mc, false);
                }

                mc.isCyl = false;
                
                //this.addCylinder(newcyl, size.x / 2.0, c, size.y, q, new THREE.Vector3(child.material.color.r, child.material.color.g, child.material.color.b), 0.6, RT_COAT);
                track.terrain.cyls.push(mc);
            
            }else if (child.name.startsWith('Grass')){
                
            }else if(child.name.startsWith('xmesh')){
            
                const o = child.clone();
                o.name = o.name.substring(6);
                o.geometry.groups[o.geometry.groups.length] = {start : o.geometry.groups[o.geometry.groups.length-1].start + o.geometry.groups[o.geometry.groups.length-1].count, count : 0, materialIndex : o.material.length};
                o.material[o.material.length] = this.splitMaterial;
            
                for(let ms = 0;ms < o.material.length; ms++){
                    o.material[ms].side = THREE.DoubleSide;
                }
                o.castShadow=true;

                const newXMesh = {id : track.lastId++, splitted: false, name : o.name,  mass: 0, size : null, pos: o.position.clone(), mesh: o, rot: child.quaternion.clone(), nVtxAlloc: 128 * 3, nVtx : 0, meshBox: null, boxGeo : null, meshArray : null,  vertices : null, normals : null, uvs : null};
                newXMesh.ogeo = o.geometry.clone();
              
                track.computeXMesh(newXMesh);
                track.xmeshes.push(newXMesh);

                newXMesh.mesh.meshGPU = this.addMesh(newXMesh.mesh, RT_DIFF, new Transformation3(newXMesh.pos, newXMesh.rot));
                this.setMeshStatic(newXMesh.mesh, false);
            
            }/*else if(child.name.startsWith('xmesh')){

                const o = child.clone();
                o.name = o.name.substring(6);

                const center = new THREE.Vector3();
                const size = new THREE.Vector3();
                
                o.geometry.computeBoundingBox();
                o.geometry.boundingBox.getCenter(center);
                o.geometry.boundingBox.getSize(size);
                track.xmeshes.push({name : o.name, size : size, pos: center, mesh: o, meshData : null, rot: child.quaternion.clone()});
            
            }*/else if(child.name.startsWith('lamp')){
                const q = new THREE.Quaternion();
                const center = new THREE.Vector3();
                const size = new THREE.Vector3();
                for(let n=0;n<child.material.length;n++){

                    if(child.material[n].name === 'lampcol') {
                        const c = this.getGeoGrpCenter(child.geometry, n);
                        center.set(c.c.x,c.c.y, c.c.z);
                        size.set(c.s.x,c.s.y, c.s.z);
                    }
                    
                }
                const mc = child.clone();

                mc.pos = {x: center.x+ track.terrain.offset.x, y: center.y+ track.terrain.offset.y, z: center.z + track.terrain.offset.z};

                mc.geometry.translate(-center.x, -center.y, -center.z);
                mc.position.add(mc.pos);
                mc.isCyl = false;
                mc.castShadow = true;
                
                const lname = child.name.substr(5);

                const it = track.findInstance(lname);
                if (it) {
                    if (!it.meshGPU) {
                        it.meshGPU = this.addMesh(mc, RT_DIFF, new Transformation3(mc.position, mc.quaternion), {x:false, y:false, z: false}, true, null, it.nInstances);
                    }
                    this.updateTransform(it, new Transformation3(mc.position, mc.quaternion), it.nFilled++);
                    
                } else {
                    mc.meshGPU = this.addMesh(mc, RT_DIFF, new Transformation3(mc.position, mc.quaternion));
                    this.setMeshStatic(mc, false);
                }

             
                
                //this.addCylinder(newcyl, size.x / 2.0, c, size.y, q, new THREE.Vector3(child.material.color.r, child.material.color.g, child.material.color.b), 0.6, RT_COAT);
                track.terrain.cyls.push(mc);
            }else if (child.name.startsWith('col-dcube')){

               
                const mc = child.clone();

                mc.geometry.computeBoundingSphere();

                const c =  mc.geometry.boundingSphere.center.clone()

                mc.pos = {x: mc.position.x + c.x+ track.terrain.offset.x, y: mc.position.y +c.y+ track.terrain.offset.y, z: mc.position.z +c.z + track.terrain.offset.z};
                mc.geometry.translate(-c.x, -c.y, -c.z);
                mc.position.set(mc.pos.x, mc.pos.y, mc.pos.z);
                mc.castShadow = true;
                 mc.isCube = false;
                

                const lname = child.name.substr(10);
                const it = track.findInstance(lname);
                if (it) {
                    if (!it.meshGPU) {
                        it.meshGPU = this.addMesh(mc, RT_DIFF, new Transformation3(mc.position, mc.quaternion), {x:false, y:false, z: false}, true, null, it.nInstances);
                        this.setMeshStatic(it, false);
                        track.terrain.cubes.push(it);
                    }else{
                        track.terrain.cubes.push({instanceOf : it, instanceID : it.nFilled });

                    }
                    this.updateTransform(it, new Transformation3(mc.position, mc.quaternion), it.nFilled++);
                    
                } else {
                    mc.meshGPU = this.addMesh(mc, RT_DIFF, new Transformation3(mc.position, mc.quaternion));
                    this.setMeshStatic(mc, false);
                     track.terrain.cubes.push(mc);
                }

               
                //this.addCylinder(newcyl, size.x / 2.0, c, size.y, q, new THREE.Vector3(child.material.color.r, child.material.color.g, child.material.color.b), 0.6, RT_COAT);
               
            
            }else if (child.name.startsWith('rt-sphere')){

                /*
                child.geometry.computeBoundingSphere();
                const sphere = child.geometry.boundingSphere;
                const newsphere = {};
                
                const q = new THREE.Quaternion();
                const center = sphere.center.clone().add(track.terrain.offset);
                const size = new THREE.Vector3();
                
             
                this.addSphere(newsphere,
                    sphere.radius,
                    new THREE.Vector3(center.x, center.y, center.z),
                    new THREE.Vector3(child.material.emissive.r, child.material.emissive.g, child.material.emissive.b).multiplyScalar(8),
                    new THREE.Vector3(child.material.color.r, child.material.color.g,child.material.color.b),
                    0.2,
                    RT_POINT_LIGHT
                );

                track.terrain.spheres.push(newsphere);
                */
            }else if(child.name.startsWith('cascade')){
                const t = new  Transformation3();

                const water = child.clone();
                water.position.set(water.position.x + track.terrain.offset.x, water.position.y + track.terrain.offset.y, water.position.z + track.terrain.offset.z);
                water.updateMatrix(true);
                water.updateMatrixWorld(true);
                
                for(let m of water.material){
                    if(m.name == 'waterflow'){
                        m.transmission = 1.0;
                        m.transparent = true;   
                        m.ior = 1.4;
                        m.thickness = 4.8;
                        m.roughness = 0.0;
                        m.reflectivity = 0.2;
                    }
                }
                const audioLoader = new THREE.AudioLoader();
                const positionalAudio = new THREE.PositionalAudio( this.AudioListener );
                const wat = {object : water, positionalAudio: positionalAudio};
        
                audioLoader.load( this.assetBase+'/assets/snd/waterflow.ogg', function( buffer ) {
                    wat.positionalAudio.setBuffer( buffer );
                    wat.positionalAudio.setDistanceModel('exponential');
                    wat.positionalAudio.setRolloffFactor  (1.5);
                    wat.positionalAudio.setRefDistance( 15 );
                    wat.object.add(wat.positionalAudio);

                    wat.positionalAudio.updateMatrix(true);
                    wat.positionalAudio.updateMatrixWorld(true);
                });
        
                water.meshGPU = this.addMesh(water, RT_COAT);

                t.set(water.position);

                this.updateTransform(water, t);
                track.terrain.waterFlows.push(wat);
            }else if (child.name.startsWith('blink')){

                const c = child.clone();
                const arr = c.name.split('-');
                const name = arr[1];
                const timeFac = parseFloat(arr[2]) / 10.0;
                c.position.add(track.terrain.offset);

                let nMats = 0;

                for(let ms = 0;ms < child.material.length; ms++){

                    if(child.material[ms].name.startsWith(name)){
                        const a = child.material[ms].name.split('-');
                        const n = parseInt(a[1]);
                        nMats = Math.max(nMats, n);
                    }
                }

                c.castShadow = true;
                c.meshGPU = this.addMesh(c, RT_DIFF, new Transformation3(c.position, c.quaternion));
                track.terrain.blinks.push({obj : c, mats: name, nMats: nMats, timeFac: timeFac});
                

            }else{
                /*
                const o = child.clone();

                o.geometry.translate(track.terrain.offset.x, track.terrain.offset.y, track.terrain.offset.z);

                t.set(child.position, child.quaternion);
                o.meshGPU = this.addMesh(o, RT_DIFF, t);
                this.updateTransform(o, t);

                track.terrain.objs.push(o);
                */

                const lname = this.getInstName(child.name);
                const it = track.findInstance(lname);
                if (it) {
                    if (!it.meshGPU) {

                        if(child.material.length >0){

                            for(let m of child.material){

                                if(m.name === 'Leaf'){
                                    m.transparent = true;
                                    m.opacity = 0.98;
                                }

                            }

                        }else{
                            if(child.material.name === 'Leaf'){
                                child.material.transparent = true;
                                child.material.opacity = 0.98;
                            }
                        }

                        it.meshGPU = this.addMesh(child, RT_DIFF, new Transformation3(child.position, child.quaternion), {x:false, y:false, z: false}, true, null, it.nInstances);
                    }
                    this.updateTransform(it, new Transformation3(child.position, child.quaternion), it.nFilled++);
                } else {
                    this.addUniqueMaterial(child, uniqueGeoMats);
                }

            }
        });


        if ((track.woodDefs) && (track.woodDefs.length > 0)) {

            for (let n = 0; n < track.woodDefs.length; n++) {
                const wood = track.addWoods(track.woodDefs[n].ptIdx, track.woodDefs[n].num, track.woodDefs[n].width, track.woodDefs[n].radius, track.woodDefs[n].mass, track.woodDefs[n].offset);

                for (let i = 0; i < wood.objects.length; i++) {
                    const q = new THREE.Quaternion().setFromEuler(new THREE.Euler(wood.objects[i].angles.x, wood.objects[i].angles.y, wood.objects[i].angles.z, "XYZ"));
                    this.addCylinder(wood.objects[i], wood.radius, new THREE.Vector3(wood.objects[i].pos.x, wood.objects[i].pos.y, wood.objects[i].pos.z), wood.width, q, new THREE.Vector3(1, 1, 1), 0.6, RT_DARKWOOD, this.woodTexture, this.woodNormal);

                     

                    this.setMeshStatic(wood.objects[i], false);
                }
            }
        }
        
        
        if ((track.springDefs) && (track.springDefs.length > 0)) {
            for (let n = 0; n < track.springDefs.length; n++) {
                const spring = track.addSpring(track.springDefs[n].ptIdx, track.springDefs[n].offset, track.springDefs[n].eqPts, track.springDefs[n].eqA, track.springDefs[n].mass, track.springDefs[n].size, track.springDefs[n].stiffness, track.springDefs[n].damping, track.springDefs[n].initPts, track.springDefs[n].initA, track.springDefs[n].limit, track.springDefs[n].limitA, track.springDefs[n].shape, track.springDefs[n].model);

                let obj = this.plateform;

                if(spring.model){

                    const mod = await this.loadObj(spring.model);

                    mod.traverse(function(child){
                        if(child.isMesh)
                            obj = child;
                    });
                }

                this.addTrackSpring(spring, obj);
                
            }
        }

        if ((track.spiderDefs) && (track.spiderDefs.length > 0)) {

            for (let n = 0; n < track.spiderDefs.length; n++) {
                const spider = track.addSpider(track.spiderDefs[n].ptIdx, track.spiderDefs[n].offset, track.spiderDefs[n].angle, track.spiderDefs[n].size, track.spiderDefs[n].model, track.spiderDefs[n].shotmodel, track.spiderDefs[n].projSize, track.spiderDefs[n].projMass, track.spiderDefs[n].projSpeed, track.spiderDefs[n].projDelay, track.spiderDefs[n].activation);
                await this.addTrackSpider(spider);

                this.setMeshStatic(spider.object, false);
            }

        }
        
        if ((track.ropeDefs) && (track.ropeDefs.length > 0)) {
            for (let n = 0; n < track.ropeDefs.length; n++) {
                const rope = track.addRope(track.ropeDefs[n].ptIdx, track.ropeDefs[n].offset, track.ropeDefs[n].eqPts, track.ropeDefs[n].eqA, track.ropeDefs[n].mass, track.ropeDefs[n].size, track.ropeDefs[n].softness, track.ropeDefs[n].damping, track.ropeDefs[n].motorforce, track.ropeDefs[n].bounce, track.ropeDefs[n].initPts, track.ropeDefs[n].initA, track.ropeDefs[n].limit, track.ropeDefs[n].limitA, track.ropeDefs[n].shape, track.ropeDefs[n].anchorObj, track.ropeDefs[n].model);
                track.computeRope(rope);

                let obj = this.plateform;

                if(rope.model){

                    const mod = await this.loadObj(rope.model);

                    mod.traverse(function(child){
                        if(child.isMesh)
                            obj = child;
                    });
                }
                    

                this.addTrackRope(rope, obj);
            }
        }
        
        
        if (!track.bonusObject) {
            const name = "bonus";
            track.bonusObject = await this.loadObj(name);

            track.bonusObject.traverse(child => {

                if(child.type == 'Mesh')
                {
                    track.bonusMeshGPU = this.addMesh(child, RT_COAT); 
                }
            });
        }

        if (!track.techObject) {
            
            const name = "tech";
            track.techObject = await this.loadObj(name);
        }

        
        if ((track.bonusDefs) && (track.bonusDefs.length > 0)) {

            for (let n = 0; n < track.bonusDefs.length; n++) {
                const bonus = track.addBonus(track.bonusDefs[n].ptIdx, track.bonusDefs[n].type, track.bonusDefs[n].amount, track.bonusDefs[n].offset, track.bonusDefs[n].size);

                let obj;
                switch( track.bonusDefs[n].type){
                    case 0:obj = track.bonusObject;break;
                    case 1:
                        switch( track.bonusDefs[n].amount){
                            case 1: obj = await this.loadObj("battery_bp"); break;
                            case 2: obj = await this.loadObj("radar_bp"); break;
                            case 3: obj = await this.loadObj("weapon_bp"); break;
                        }
                    break;
                    case 2:obj = track.techObject;break;
                }


                bonus.object = obj;
                bonus.object.position.set(bonus.pos.x, bonus.pos.y, bonus.pos.z);
                

                bonus.object.meshGPU = this.addMesh( bonus.object , new Transformation3(bonus.object.position));
                bonus.object.meshGPU.size = track.bonusDefs[n].size;
                this.setMeshStatic(bonus.object, false);
            }
        }
        
        for (let n = 0; n < track.loopings.length; n++) {
            for (let i = 0; i < track.loopings[n].bonuses.length; i++) {
                const bonus = track.addBonus(track.loopings[n].bonuses[i].ptIdx, track.loopings[n].bonuses[i].type, track.loopings[n].bonuses[i].amount, track.loopings[n].bonuses[i].offset, track.loopings[n].bonuses[i].size);

                bonus.object = track.bonusObject.clone();
                bonus.object.position.set(bonus.pos.x,bonus.pos.y, bonus.pos.z);

                bonus.object.meshGPU = this.addMeshInstance(track.bonusMeshGPU, new Transformation3(bonus.object.position));
                this.setMeshStatic(bonus.object, false);
            }
        }
        

        if ((track.jumpersDefs) && (track.jumpersDefs.length > 0)) {
            const name = "jumper";

            const object = await this.loadObj(name);

            for (let n = 0; n < track.jumpersDefs.length; n++) {
                const jumper = await track.addJumper(track.jumpersDefs[n].ptIdx, track.jumpersDefs[n].offset, track.jumpersDefs[n].angle, track.jumpersDefs[n].size);
                await this.addTrackJumper(jumper, object);

                jumper.object.meshGPU = this.addMesh(jumper.object);
                track.terrain.objs.push(jumper.object);

                //grp.add(jumper.object);
            }
        }

        
        if ((track.interestPointsDefs) && (track.interestPointsDefs.length > 0)) {
            const name = "ip";

            const object = await this.loadObj(name);

            for (let n = 0; n < track.interestPointsDefs.length; n++) {

                const ip = await track.addInterestPoint(   track.interestPointsDefs[n].ptIdx, 
                                                           track.interestPointsDefs[n].offset, 
                                                           track.interestPointsDefs[n].angle, 
                                                           track.interestPointsDefs[n].size,
                                                           track.interestPointsDefs[n].lobject,
                                                           track.interestPointsDefs[n].loffset,
                                                           track.interestPointsDefs[n].lsize,
                                                           track.interestPointsDefs[n].lweight,
                                                           track.interestPointsDefs[n].nBonus);

                await this.makeInterestPoint(ip, object);

                //ip.object.geometry.applyMatrix4(ip.object.matrix);

                ip.object.meshGPU = this.addMesh(ip.object);
                track.terrain.objs.push(ip.object);

                //grp.add(ip.object);
                
                const t = new Transformation3(ip.lmesh.position, ip.lmesh.quaternion);
                ip.lmesh.meshGPU = this.addMesh(ip.lmesh, RT_DIFF, t);
                this.setMeshStatic(ip.lmesh, false);
            }
        }


        if (track.charDefs) {
            for (let n = 0; n < track.charDefs.length; n++) {
                await track.addNPC(track.charDefs[n].ptIdx, track.charDefs[n].offset, track.charDefs[n].angle, track.charDefs[n].name, track.charDefs[n].subsystemsDef, track.charDefs[n].skelScale, track.charDefs[n].nInstances, track.charDefs[n].instancesData);
            }

            for (let n = 0; n < track.npcs.length; n++) {
                await this.makeCharacter(track.npcs[n]);

                if(track.npcs[n].instanceID === 0)
                    this.addCharObj(track.npcs[n]);
                else{

                    track.npcs[n].avatarMesh = track.npcs[track.npcs[n].instanceOf].avatarMesh;
                    track.npcs[n].center = track.npcs[track.npcs[n].instanceOf].center.clone();
                    track.npcs[n].size = track.npcs[track.npcs[n].instanceOf].size.clone();
                }
                this.createSelectMesh(track.npcs[n]);
                    

                
            }

        }

        if ((track.droneDefs) && (track.droneDefs.length > 0)) {

            for (let n = 0; n < track.droneDefs.length; n++) {
                const drone = await track.addDrone(track.droneDefs[n].ptIdx, track.droneDefs[n].offset, track.droneDefs[n].caroffset, track.droneDefs[n].angle, track.droneDefs[n].size, track.droneDefs[n].model, track.droneDefs[n].activation,track.droneDefs[n].deactivation,track.droneDefs[n].speed, track.droneDefs[n].weight, track.droneDefs[n].weapons, track.droneDefs[n].speedPale1, track.droneDefs[n].speedPale2, track.droneDefs[n].upLift , track.droneDefs[n].pales);

                //track.droneDefs[n].projPts, track.droneDefs[n].projSize, track.droneDefs[n].projMass, track.droneDefs[n].projSpeed, track.droneDefs[n].projDelay, track.droneDefs[n].projTTL, track.droneDefs[n].projModel
                await this.addTrackDrone(drone);
                drone.object.meshGPU = this.addMesh(drone.object, RT_COAT, new Transformation3(drone.pos, drone.rot), {x:false, y: false, z: false}, true, {skeleton : drone.object.SimpleSkeleton, skale: 1});
                this.setMeshStatic(drone.object, false);


                for (let w of drone.weapons) {
                    const pt = new THREE.Vector3(w.projPts.x, w.projPts.y, w.projPts.z).applyQuaternion(drone.object.quaternion).add(drone.object.position);
                    drone.particles = this.addDronePart(pt, shot_params);
                }

                //drone.meshGPU.size = track.droneDefs[n].size.x;

                for(let pale of drone.pales) {
                    pale.mesh.meshGPU = this.addMesh(pale.mesh, RT_COAT, new Transformation3(pale.position));
                    this.setMeshStatic(pale.mesh, false);
                    //this.addMeshChild(drone.meshGPU, pale.meshGPU);
                }
                for(let wing of drone.wings) {
                    wing.meshGPU = this.addMesh(wing, RT_COAT, new Transformation3());

                    this.setMeshStatic(wing, false);

                    this.addMeshChild(drone.meshGPU, wing.meshGPU);
                }
            }
        }

        if ((track.turretsDefs) && (track.turretsDefs.length > 0)) {

            for (let n = 0; n < track.turretsDefs.length; n++) {
                const turret = await track.addTurret(track.turretsDefs[n].ptIdx, track.turretsDefs[n].model, track.turretsDefs[n].shotModel, track.turretsDefs[n].activation, track.turretsDefs[n].offset, track.turretsDefs[n].angle, track.turretsDefs[n].size, track.turretsDefs[n].projPts, track.turretsDefs[n].projSize, track.turretsDefs[n].projMass, track.turretsDefs[n].projSpeed, track.turretsDefs[n].projAngle, track.turretsDefs[n].projAngle2, track.turretsDefs[n].projDelay, track.turretsDefs[n].projTTL, track.turretsDefs[n].projSND, track.turretsDefs[n].projLL, track.turretsDefs[n].moveAngle, track.turretsDefs[n].projRoll, track.turretsDefs[n].projPart);
                await this.addTrackTurret(turret);

                const turHead = new THREE.Group();
                turHead.add(turret.canon);
                turHead.add(turret.head);

                const headPos = {x:turret.pos.x, y:turret.pos.y, z: turret.pos.z};
                turret.head.meshGPU = this.addMesh(turHead, RT_DIFF, new Transformation3(headPos, turret.rot));
                this.setMeshStatic( turret.head, false);

                turret.object.meshGPU = this.addMesh(turret.object, RT_DIFF, new Transformation3(turret.object.position, turret.rot));

                //grp.add(turret.object);

                if(turret.shotObject)
                {
                    turret.shotObject.meshGPU = this.addMesh(turret.shotObject, RT_DIFF);
                }
                    
            }
        }

        for(let m of  track.terrain.movingTerrain) {
            if(m.nInstances >1)
                continue;

            m.meshGPU = this.addMesh(m, RT_DIFF, new Transformation3(m.position, m.quaternion), {x: false, y: false, z: false}, true, {skeleton:m.SimpleSkeleton, skale: 1.0});
            this.setMeshStatic(m, false);
        }

        for(let grass of track.terrain.grasses){
            this.addGrass(grass);
            this.setMeshStatic(grass.mesh, false);
        }
            

        /*
        if (this.startLine)
            grp.add(this.startLine);
        */
        const newGeos1 = [];
        const newMats1 = [];
        const attrs = [];


        for( let um in uniqueGeoMats){

            uniqueGeoMats[um].geo = mergeGeometries(uniqueGeoMats[um].geos);
            for(let g of uniqueGeoMats[um].geos){
                g.dispose();
            }

            uniqueGeoMats[um].geos = null;

            const g = uniqueGeoMats[um].geo;
            const m = uniqueGeoMats[um].mat;

            newGeos1.push(g);
            newMats1.push(m);
        }

        if(this.toonGEO && this.toonGEO.attributes.position){
            track.terrain.toon_mesh = new THREE.Mesh(this.toonGEO, this.toonFlippedMat);
            track.terrain.toon_mesh.meshGPU = this.addMesh(track.terrain.toon_mesh)
        }

        if(newGeos1.length>0){
            const newgeo1 = mergeGeometries(newGeos1, true);
            const newmesh1 = new THREE.Mesh(newgeo1, newMats1);

            newmesh1.castShadow = true;
              
            track.terrain.mesh = newmesh1;
            track.terrain.mesh.meshGPU = this.addMesh(track.terrain.mesh, RT_DIFF, new Transformation3());
            this.setMeshStatic(track.terrain.mesh, false);
            
            grp.name = 'terrain group';
            track.terrain.base = grp;
            
            track.terrain.base.meshGPU = this.addMesh(track.terrain.base, RT_DIFF, new Transformation3());
            this.setMeshStatic(track.terrain.base, false);
        }

        if(ground.material.length>0){
            for(let m of ground.material){
                m.metalness = 0.1;
            }
        }else{
            ground.material.metalness = 0.1;
        }

        ground.renderOrder = 1000;

        track.terrain.ground = ground;
        track.terrain.ground.meshGPU = this.addMesh(track.terrain.ground, RT_DIFF, new Transformation3(ground.position, ground.quaternion));
        track.terrain.ground.castShadow = false;

        
    }

    
    getLevelOneChild(skel, leg, dir){
        const center = new THREE.Vector3();
        const size = new THREE.Vector3();

        leg.geometry.computeBoundingBox();
        leg.geometry.boundingBox.getSize(size);
        leg.geometry.boundingBox.getCenter(center);

        const p1 = dir === 1 ? leg.geometry.boundingBox.min.clone() : leg.geometry.boundingBox.max.clone();
        const p2 = dir === 1 ? leg.geometry.boundingBox.max.clone() : leg.geometry.boundingBox.min.clone();

        const startPts = p1.clone();
        const startPts2 = leg.name.startsWith('cannon') ? new THREE.Vector3(0, 0, p1.z) : new THREE.Vector3(-p1.x, 0, 0);

        const jointWorldPos1 = startPts2.clone().applyQuaternion(leg.quaternion).add(leg.position);
        const jointRelPos1 = new THREE.Vector3().subVectors(jointWorldPos1, skel.root.position);
        
        const joint1 = new THREE.Group();
        joint1.position.set(jointRelPos1.x, jointRelPos1.y, jointRelPos1.z);
        joint1.name = 'joint-'+leg.name;
    
        const ngeo = leg.geometry.clone();

        if(joint1.name.startsWith('cannon'))
            ngeo.translate(0, 0, leg.geometry.boundingBox.min.z);
        else
            ngeo.translate(startPts.x, 0, 0);

        const childLeg = new THREE.Mesh(ngeo, leg.material);
        childLeg.quaternion.set(leg.quaternion.x, leg.quaternion.y, leg.quaternion.z, leg.quaternion.w);

        childLeg.name = leg.name;
        

        return {joint : joint1, leg : childLeg};
    }

    getLevelTwoChild(leg, fleg, dir){

        const c = new THREE.Vector3();

        leg.geometry.computeBoundingBox();
        fleg.geometry.computeBoundingBox();
        leg.geometry.boundingBox.getCenter(c);

        const p1 = dir === 1 ? leg.geometry.boundingBox.min.clone() : leg.geometry.boundingBox.max.clone();
        const p2 = dir === 1 ? leg.geometry.boundingBox.max.clone() : leg.geometry.boundingBox.min.clone();
        const fp = dir === 1 ? fleg.geometry.boundingBox.min.clone() : fleg.geometry.boundingBox.max.clone();
        
        const startPts = new THREE.Vector3(-p1.x, 0, 0);
        const joinPts =  new THREE.Vector3(-p2.x, 0, 0);
        
        const jointRelPos1 = new THREE.Vector3().subVectors(joinPts, startPts).multiplyScalar(0.5);
        const fstartPts = fp.clone();
        
        const ngeo = fleg.geometry.clone();
        ngeo.translate(fstartPts.x, 0, 0);

        const pi = leg.quaternion.clone().invert();
        const fi = pi.clone().multiply(fleg.quaternion);

        const fLeg = new THREE.Mesh(ngeo, fleg.material);
        fLeg.quaternion.set(fi.x, fi.y, fi.z, fi.w);
        fLeg.position.set(jointRelPos1.x, jointRelPos1.y, jointRelPos1.z);

        fLeg.name = fleg.name;

        return fLeg;
    }
    getNbones(bone, nBones = 0){

        let bones = nBones + 1;

        bone.idx = nBones;

        for(let c of bone.children){
            bones= this.getNbones(c, bones);
        }
        return bones;
    }


    setToon(mat){

        if((mat.map) && (mat.map.image)){
            const oldMat = mat;
            const color = this.getPixel(mat.map.image, mat.map.image.width - 1 , 0);
            const toonMat = new THREE.MeshToonMaterial({gradientMap : this.gradientMap});

            toonMat.name == 'toonMat';


            toonMat.color = new THREE.Color().setRGB(color[0]/255, color[1]/255, color[2]/255);
        
            mat = toonMat;
            mat.name = oldMat.name;
            mat.toonMap = oldMat.map;
            mat.normalMap = oldMat.normalMap;
            mat.lightMap = oldMat.lightMap;
            mat.roughnessMap = oldMat.roughnessMap;

            mat.needsUpdate = true;

            oldMat.dispose();
        
        }else{

            const oldMat = mat;
            const color = oldMat.color.clone();

            const toonMat = new THREE.MeshToonMaterial({gradientMap : this.gradientMap});
            toonMat.color = color;//new THREE.Color().setRGB(color[0]/255, color[1]/255, color[2]/255);
            toonMat.name == 'toonMat';
            mat = toonMat;
            mat.name = oldMat.name;
            mat.toonMap = oldMat.map;
            mat.normalMap = oldMat.normalMap;
            mat.lightMap = oldMat.lightMap;


            mat.needsUpdate = true;
            oldMat.dispose();
        }

        return mat;
    }


    addFlippedGeo(geometry, grp){

        const newGeo = new THREE.BufferGeometry();
        newGeo.name='ToonFlippedGeo';

        if(!grp){
            if(geometry.index){
                grp = {start : 0, count : geometry.index.count};
            }else{
                grp = {start : 0, count : geometry.attributes.position.count / 3};
            }
        }        

        for(let a in geometry.attributes){

            if(a !== 'position')
                continue;

            const attr = geometry.attributes[a];
            const array = new Float32Array(grp.count * attr.itemSize);
            let dvtx = 0;

            if(geometry.index){
                for(let idx = grp.start; idx< (grp.start + grp.count); idx+=3) {

                    const i1 = geometry.index.array[idx + 0];
                    const i2 = geometry.index.array[idx + 1];
                    const i3 = geometry.index.array[idx + 2];

                    for(let itm=0;itm<attr.itemSize;itm++) {
                        array[dvtx++] = attr.array[i3 * attr.itemSize + itm];
                    }

                    for(let itm=0;itm<attr.itemSize;itm++) {
                        array[dvtx++] = attr.array[i2 * attr.itemSize + itm];
                    }

                    for(let itm=0;itm<attr.itemSize;itm++) {
                        array[dvtx++] = attr.array[i1 * attr.itemSize + itm];
                    }

                }
            }else{
                for(let idx = grp.start; idx<grp.start + grp.count; idx+=3) {

                    let idx1 = idx + 0;
                    let idx2 = idx + 1;
                    let idx3 = idx + 2;

                    for(let itm=0;itm<attr.itemSize;itm++) {
                        array[dvtx++] = attr.array[idx3 * attr.itemSize + itm];
                    }

                    for(let itm=0;itm<attr.itemSize;itm++) {
                        array[dvtx++] = attr.array[idx2 * attr.itemSize + itm];
                    }

                    for(let itm=0;itm<attr.itemSize;itm++) {
                        array[dvtx++] = attr.array[idx1 * attr.itemSize + itm];
                    }
                }

            }
            newGeo.setAttribute(a, new THREE.Float32BufferAttribute(array, attr.itemSize));
        }

        //newGeo.scale(1.01, 1.01, 1.01)
        newGeo.computeVertexNormals();

        return newGeo;
    }

    addUniqueMaterial(mesh ,uniqueGeoMats , offset = {x: 0,y:0, z:0}){

        mesh.position.add(offset);

        if(mesh.geometry.attributes.color){
            console.log('color '+mesh.name);
        }


        if(mesh.material.length > 0){
                    
            for(let imat =0;imat <mesh.material.length;imat ++){

                const grp = mesh.geometry.groups[imat];
                let mat = mesh.material[imat];
                
                //const newGeo = mesh.geometry.clone();

                
                const newGeo = new THREE.BufferGeometry();
                const iarray = new Uint16Array(grp.count);
                const imatch = new Uint16Array(mesh.geometry.attributes.position.count);

                for(let a in mesh.geometry.attributes){

                    const attr = mesh.geometry.attributes[a];
                    const array = new Float32Array(grp.count * attr.itemSize);

                    let dvtx = 0;

                    if(mesh.geometry.index){
                        let dindex = 0;

                        imatch.fill(0xFFFF, 0, imatch.length);

                        for(let idx = grp.start; idx<grp.start + grp.count; idx++) {
                            const index = mesh.geometry.index.array[idx];

                            if(imatch[index] === 0xFFFF){
                                imatch[index] = dvtx / attr.itemSize;
                                for(let itm=0;itm<attr.itemSize;itm++) {
                                    array[dvtx++] = attr.array[index * attr.itemSize + itm];
                                }   
                            }
                            iarray[dindex++] = imatch[index];
                        }
                    }else{
                        for(let idx = grp.start; idx<grp.start + grp.count; idx++) {
   
                            for(let itm=0;itm<attr.itemSize;itm++) {
                                array[dvtx++] = attr.array[idx * attr.itemSize + itm];
                            }
                        }

                    }
                    newGeo.setAttribute(a, new THREE.Float32BufferAttribute(array, attr.itemSize));
                }
                
                if(mesh.geometry.index) {
                    newGeo.setIndex(new THREE.Uint16BufferAttribute(iarray, 1));
                }

                if(!newGeo.attributes.tangent)
                {
                    const tangents = new Float32Array(newGeo.attributes.position.count * 4);
                    newGeo.setAttribute("tangent", new THREE.Float32BufferAttribute(tangents,  4) );
                }
                if(!newGeo.attributes.uv)
                {
                    const uvs = new Float32Array(newGeo.attributes.position.count * 2);
                    newGeo.setAttribute("uv", new THREE.Float32BufferAttribute(uvs,  2) );
                }
                
                if(!newGeo.attributes.uv2)
                {
                    const uvs = new Float32Array(newGeo.attributes.position.count * 2);
                    newGeo.setAttribute("uv2", new THREE.Float32BufferAttribute(uvs,  2) );
                }
                
                
                mesh.updateMatrix();
                newGeo.applyMatrix4(mesh.matrix);

                if(mat.name.startsWith('Toon')){
                        
                    mat = this.setToon(mat);

                    const ng = this.addFlippedGeo(newGeo, grp);
                    if(!this.toonGEO){
                        this.toonGEO = ng;        
                    }else{
                        this.toonGEO = mergeGeometries([this.toonGEO, ng]);
                    }
                }

                if(mat.name === 'Leaf'){
                    mat.transparent = true;
                    mat.opacity = 0.98;
                }

                if(!uniqueGeoMats[mat.name]){
                    uniqueGeoMats[mat.name] = {geos : [newGeo], mat : mat};
                } else {
                    uniqueGeoMats[mat.name].geos.push(newGeo);
                }
            }
        }else{


            const newGeo = mesh.geometry.clone();

            if(!newGeo.attributes.tangent)
            {
                const tangents = new Float32Array(newGeo.attributes.position.count * 4);
                newGeo.setAttribute("tangent", new THREE.Float32BufferAttribute(tangents,  4) );
            }
            if(!newGeo.attributes.uv)
            {
                const uvs = new Float32Array(newGeo.attributes.position.count * 2);
                newGeo.setAttribute("uv", new THREE.Float32BufferAttribute(uvs,  2) );
            }
            if(!newGeo.attributes.uv2)
            {
                const uvs = new Float32Array(newGeo.attributes.position.count * 2);
                newGeo.setAttribute("uv2", new THREE.Float32BufferAttribute(uvs,  2) );
            }
            

            let nmat;

            if(mesh.material === 'Leaf'){
                mesh.material.transparent = true;
            }

            //mesh.position.add(offset);
            mesh.updateMatrix();
            newGeo.applyMatrix4(mesh.matrix);

            if(!uniqueGeoMats[mesh.material.name]){

                let mat = mesh.material;


                if(mat.name.startsWith('Toon')){
                      
                    mat = this.setToon(mat);
                    const ng = this.addFlippedGeo(newGeo, newGeo.groups[0]);
                    //geo = mergeGeometries([mesh.geometry, ng], true);

                    if(!this.toonGEO){
                        this.toonGEO = ng;        
                    }else{
                        this.toonGEO = mergeGeometries([this.toonGEO, ng]);
                    }
                }

                uniqueGeoMats[mat.name] = {geos : [newGeo], mat : mat};
            }else{

                if(mesh.material.name.startsWith('Toon')){
                      
                    const ng = this.addFlippedGeo(newGeo, newGeo.groups[0]);

                    if(!this.toonGEO){
                        this.toonGEO = ng;        
                    }else{
                        this.toonGEO = mergeGeometries([this.toonGEO, ng]);
                    }
                }

         

                if(uniqueGeoMats[mesh.material.name].geos)
                    uniqueGeoMats[mesh.material.name].geos.push(newGeo);
                else
                    uniqueGeoMats[mesh.material.name].geos = [newGeo];
            }
        }
    }

    

    async loadSpider(name){

        const gltf = await this.loadGLTF(name);
        const spider = this.deGroupGLB(gltf , false);
        const skel = {legs:{l: [], r:[]}, flegs:{l: [], r:[]}, cannons : []};

        spider.traverse(function(child){

            if(child.name === 'body'){
                skel.root = child.clone();
                skel.root.geometry.computeBoundingSphere();
            }

            if(child.name.startsWith('leg-l')){
                const arr = child.name.split('-');
                const id = parseInt(arr[2]) - 1;
                skel.legs.l[id] = child.clone();
            }

            if(child.name.startsWith('leg-r')){
                const arr = child.name.split('-');
                const id = parseInt(arr[2]) - 1;
                skel.legs.r[id] = child.clone();
            }

            if(child.name.startsWith('fleg-l')){
                const arr = child.name.split('-');
                const id = parseInt(arr[2]) - 1;
                skel.flegs.l[id] = child.clone();
            }

            if(child.name.startsWith('fleg-r')){
                const arr = child.name.split('-');
                const id = parseInt(arr[2]) - 1;
                skel.flegs.r[id] = child.clone();
            }

            if(child.name.startsWith('cannon-')){
                const arr = child.name.split('-');
                const id  = arr[1]== 'l' ? 1 : 0;
                skel.cannons[id] = child.clone();
            }

        });

        for(let i =0; i<skel.legs.l.length;i++){

            const ret = this.getLevelOneChild(skel, skel.legs.l[i], 0);
            const fleg = this.getLevelTwoChild(ret.leg, skel.flegs.l[i], 0);
            
            ret.leg.add(fleg);
            ret.joint.add(ret.leg);
            skel.root.add(ret.joint);
        }

        for(let i =0; i<skel.legs.r.length;i++){

            const ret = this.getLevelOneChild(skel, skel.legs.r[i], 1);
            const fleg = this.getLevelTwoChild(ret.leg, skel.flegs.r[i], 1);
            
            ret.leg.add(fleg);
            ret.joint.add(ret.leg);
            skel.root.add(ret.joint);
        }
        for(let cannon of skel.cannons){

            //cannon.geometry.computeBoundingBox();
            const ret = this.getLevelOneChild(skel, cannon, 1);
            ret.joint.add(ret.leg);
            skel.root.add(ret.joint);
        }

        this.destroyObj(gltf);
        this.destroyObj(spider);

        return skel.root;

    }

    getSpiderBone(spiderObj, bone){

        for(let c of spiderObj.children){

            const size = new THREE.Vector3();
            const boneObj = c.clone();

            boneObj.children = [];
            let boxHalfSize = null;

            if(c.geometry){
                boneObj.geometry = c.geometry.clone();
                boneObj.geometry.boundingBox.getSize(size);
                boxHalfSize = new THREE.Vector3(size.x / 2, size.y / 2, size.z / 2);

                if(c.name.startsWith('leg-l'))
                    boneObj.geometry.translate( -boxHalfSize.x, 0, 0);

                if(c.name.startsWith('fleg-l'))
                    boneObj.geometry.translate( -boxHalfSize.x, 0, 0);

                if(c.name.startsWith('leg-r'))
                    boneObj.geometry.translate( boxHalfSize.x, 0, 0);
                
                if(c.name.startsWith('fleg-r'))
                    boneObj.geometry.translate( boxHalfSize.x, 0, 0);

                if(c.name.startsWith('cannon'))
                    boneObj.geometry.translate( 0,0, -boxHalfSize.z);
             }
            
            
            const newBone = new SimpleBone(c.name, c.quaternion.clone(), 1, 0.4, 0.1, null, boxHalfSize); //{ name : c.name, obj: boneObj,  pos : c.position.clone(), rot : c.quaternion.clone(), size: boxHalfSize, children : []};
            newBone.obj = boneObj;
            newBone.pos = c.position.clone();
            bone.addChild(newBone);
            this.getSpiderBone(c, newBone);
        }
    }
    

    getSpiderSkel(spider){

        spider.geometry.computeBoundingSphere();

        const rootObj = spider.clone();
        rootObj.children = [];

        const skel = new SimpleSkeleton(spider.position.clone(), new THREE.Quaternion(), [], [], 1.6);
        skel.root.obj = rootObj;
        
        //{root: { name : "root", obj: rootObj, pos : spider.position.clone(), size : 1.6, rot : new THREE.Quaternion(), children : []}};
        this.getSpiderBone(spider, skel.root);

        return skel;
    }

    addSpiderSkelBone(bone){

        bone.obj.meshGPU = this.addMesh(bone.obj);
        this.setMeshStatic(bone.obj, false);

        if(this.rtEngine){
            bone.obj.rtMesh = this.rtEngine.addMesh(bone.obj, RT_COAT, new Transformation3(bone.obj.position, bone.obj.quaternion));
            if(bone.obj.rtMesh){
                this.rtEngine.drawMesh(bone.obj.rtMesh);
                this.rtEngine.alwaysRender.push(bone.obj.rtMesh.geoID);
            }
        }

        for(let child of bone.children) {
            this.addSpiderSkelBone(child);
        }

    }

    addSpiderSkel(spiderSkel){

        spiderSkel.root.obj.meshGPU = this.addMesh(spiderSkel.root.obj);
        this.setMeshStatic(spiderSkel.root.obj, false);

        if(this.rtEngine){
            spiderSkel.root.obj.rtMesh = this.rtEngine.addMesh(spiderSkel.root.obj, RT_COAT, new Transformation3(spiderSkel.root.obj.position, spiderSkel.root.obj.quaternion));
            this.rtEngine.drawMesh(spiderSkel.root.obj.rtMesh);
            this.rtEngine.alwaysRender.push(spiderSkel.root.obj.rtMesh.geoID);
        }

        for(let child of spiderSkel.root.children){
            this.addSpiderSkelBone(child);
        }
    }


    async addTrackSpider(spider){
        
        await this.makeSpider(spider);

        this.addSpiderSkel(spider.skel);

        for(let s = 0; s<spider.sounds.length;s++){
            const snd = spider.sounds[s];
            const audioLoader = new THREE.AudioLoader();
            audioLoader.load( this.assetBase+"/assets/snd/"+snd.file+".wav", function( buffer ) {
                snd.sndBuffer = buffer;
            });
        }
    }

    removeSpiderSkelBone(bone){

        this.removeMesh(bone.obj);
        for(let child of bone.children) {
            this.removeSpiderSkelBone(child);
        }

    }

    removeSpiderSkel(spiderSkel){

        this.removeMesh(spiderSkel.root.obj);
        for(let child of spiderSkel.root.children){
            this.removeSpiderSkelBone(child);
        }
    }


    updateSpiderSkelBone(spiderBone){

        if(spiderBone.obj){
            spiderBone.obj.position.set(spiderBone.pos.x, spiderBone.pos.y, spiderBone.pos.z);
            spiderBone.obj.quaternion.set(spiderBone.rot.x, spiderBone.rot.y, spiderBone.rot.z, spiderBone.rot.w);

            if(this.rtEngine){
                this.tmpTransformation.set(spiderBone.obj.position, spiderBone.obj.quaternion);
                this.updateTransform(spiderBone.obj, this.tmpTransformation);
            }
    
        }

        for(let child of spiderBone.children){
            this.updateSpiderSkelBone(child);
        }
    }

    updateSpiderSkel(spiderSkel){

        const root = spiderSkel.root;

        root.obj.position.set(root.pos.x, root.pos.y, root.pos.z);
        root.obj.quaternion.set(root.rot.x, root.rot.y, root.rot.z, root.rot.w);

        if(this.rtEngine){
            this.tmpTransformation.set(root.obj.position, root.obj.quaternion);
            this.updateTransform(root.obj, this.tmpTransformation);
        }

        for(let child of root.children){
            this.updateSpiderSkelBone(child);
        }
    }

   
    interpolateSpiderSkel(spiderBone, lerper, time, log, idx = 0){

        if(spiderBone.obj){
            spiderBone.obj.visible = lerper.getInterpolatedValue(idx, time, spiderBone.obj, log);

            if(this.rtEngine){
                this.tmpTransformation.set(spiderBone.obj.position, spiderBone.obj.quaternion);
                this.updateTransform(spiderBone.obj, this.tmpTransformation);
            }
        }

        idx++;
        for(let n = 0; n<spiderBone.children.length;n++){
            idx = this.interpolateSpiderSkel(spiderBone.children[n], lerper, time, log, idx);
        }
        return idx;
    }

    resetSkel(bone){
        bone.rot.copy(bone.quat);
        for(let c of bone.children){
            this.resetSkel(c);
        }
    }

    
    getIds(bone, ids = []){

        ids.push(bone.id);

        for(let i=0;i<bone.children.length; i++){
            ids = this.getIds(bone.children[i], ids);
        }

        return ids;
    }

    applyBoneQuat(bone, quat){
        bone.quat.multiply(quat);
        bone.rot.copy(bone.quat);
        bone.iq.copy(bone.quat).invert();

        for(let c of bone.children){
            this.applyBoneQuat(c, quat);
        }

    }
    
    splitBone(splitName, bone, tbone, relMatrix = null) {
        
        let newBone = null;
        
        const currentMatrix = (relMatrix === null) ? tbone.matrix.clone() : new THREE.Matrix4().copy(relMatrix).multiply(tbone.matrix);

        for(let i =0; i<bone.children.length; i++) {

            const child = bone.children[i];
            const tchild = tbone.children[i];

            if(child.name === splitName){
                  
                const newBone =  new SimpleBone(child.name, new THREE.Quaternion(), child.length, child.radius, child.jradius, child.color.clone());
                newBone.copy(child);

                newBone.parentName = bone.name;
                
                newBone.pos = new THREE.Vector3();
             
                return newBone;
            }
            
            newBone = this.splitBone(splitName, child, tchild, currentMatrix);
            if(newBone != null)
                break;
        }
        return newBone;
    }


    splitBoneMesh(boneName, char){

        const newRootBone = this.splitBone(boneName, char.skeleton.root, char.tskel.bones[0]);
        this.resetSkel(newRootBone);

        const ids = this.getIds(newRootBone);
        const geo = char.avatarMesh.geometry;

        const newMesh = char.avatarMesh.clone();
        newMesh.geometry = geo.clone();
        newMesh.children = [];

     
        //const tbone = char.tskel.getBoneByName(newRootBone.name);

        for(let n=0;n<char.tskel.bones.length;n++){
            
            if(char.tskel.bones[n].name == newRootBone.name){

                newMesh.splitQuat = new THREE.Quaternion();
                char.tskel.boneInverses[n].decompose(this.v1, newMesh.splitQuat, this.tmpVec3);

                newMesh.geometry.applyMatrix4(char.tskel.boneInverses[n]);
              
                newMesh.geometry.computeBoundingBox();
            }
        }
        

        const newIndexes1 = new Uint16Array(geo.index.count);
        const newIndexes2 = new Uint16Array(geo.index.count);

        let nIdx1 = 0;
        let nIdx2 = 0;

        for(let i =0; i<geo.index.count; i+=3){

            let hasBoneVtx = -1;
                       
            for(let v = 0;v<3;v++) {

                const idx = geo.index.array[i + v];

                for(let b = 0;b<4;b++) { 

                    if(ids.indexOf(geo.attributes.skinIndex.array[ idx * 4 + b])>=0){
                        hasBoneVtx= geo.attributes.skinIndex.array[ idx * 4 + b];
                        break;
                    }
                }

                if(hasBoneVtx >= 0)
                    break;
            }

            if(hasBoneVtx >= 0){
        
                for(let v = 0;v<3;v++) {

                    const vidx = geo.index.array[i + v];

                    for(let n=0;n<4;n++){
                        
                        if(newMesh.geometry.attributes.skinWeight.array[vidx * 4 + n] > 0){
                           newMesh.geometry.attributes.skinIndex.array[vidx * 4 + n] = Math.max(0.0, geo.attributes.skinIndex.array[vidx * 4 + n] - newRootBone.idx);
                        }
                            
                    }
                    
                    newIndexes2[nIdx2++] = vidx;
                }
            }else{
                for(let v = 0;v<3;v++) {
                    newIndexes1[nIdx1++] = geo.index.array[i + v];
                }
            }
        }


        geo.setIndex(new THREE.Uint16BufferAttribute(newIndexes1, 1));
        char.avatarMesh.geometry.drawRange.count = nIdx1;

        newMesh.geometry.setIndex(new THREE.Uint16BufferAttribute(newIndexes2, 1));
        newMesh.geometry.drawRange.count = nIdx2;


        newMesh.position.set(0, 0, 0);
        newMesh.quaternion.set(0, 0, 0, 1.0);
        newMesh.updateMatrixWorld(true);

        const newSkeleton = new SimpleSkeleton(new THREE.Vector3(), newRootBone.quat);
        newSkeleton.root.children = newRootBone.children;
        newSkeleton.root.idx = newRootBone.idx;
        newSkeleton.root.id = newRootBone.id;

        const tskel = new THREE.Skeleton(newSkeleton.getThree(char.skelScale));
        newMesh.add(tskel.bones[0]);
        newMesh.bind(tskel);

        char.splitMesh.push({ skel : newSkeleton, mesh : newMesh});
        this.addMesh(newMesh);

        return newMesh;
    }

    getCharBone(char, name){
        return char.findBone(name);
    }

   



    /*
    updateSpiderSkelBone2(spiderBone, dataBone){

        if(spiderBone.obj){
            spiderBone.obj.position.set(dataBone.pos.x, dataBone.pos.y, dataBone.pos.z);
            spiderBone.obj.quaternion.set(dataBone.rot.x, dataBone.rot.y, dataBone.rot.z, dataBone.rot.w);
        }

        for(let n = 0; n<spiderBone.children.length;n++){
            this.updateSpiderSkelBone2(spiderBone.children[n], dataBone.children[n]);
        }
    }
    updateSpiderSkel2(spiderSkel, dataSkel){

        const root = spiderSkel.root;

        root.obj.position.set(dataSkel.pos.x, dataSkel.pos.y, dataSkel.pos.z);
        root.obj.quaternion.set(dataSkel.rot.x, dataSkel.rot.y, dataSkel.rot.z, dataSkel.rot.w);

        for(let n = 0; n<root.children.length;n++){
            this.updateSpiderSkelBone2(root.children[n], dataSkel.children[n]);
        }
    }
    */


    
}


function createHumanoid(){

    const q_hip_left  = new THREE.Quaternion().setFromEuler(new THREE.Euler(0, 0, -Math.PI / 2));
    const q_hip_right = new THREE.Quaternion().setFromEuler(new THREE.Euler(0, 0,  Math.PI / 2));

    const q_shoulder_left  = new THREE.Quaternion().setFromEuler(new THREE.Euler(0, -0.03, -Math.PI / 2));
    const q_shoulder_right = new THREE.Quaternion().setFromEuler(new THREE.Euler(0, 0,  Math.PI / 2));

    const q_fleg_left  = new THREE.Quaternion().setFromEuler(new THREE.Euler(0, 0, -Math.PI / 2));
    const q_fleg_right = new THREE.Quaternion().setFromEuler(new THREE.Euler(0, 0,  Math.PI / 2));

    const q_leg_left  = new THREE.Quaternion().setFromEuler(new THREE.Euler(0, 0, 0.0));
    const q_leg_right = new THREE.Quaternion().setFromEuler(new THREE.Euler(0, 0, 0.0));

    const q_farm_left  = new THREE.Quaternion().setFromEuler(new THREE.Euler(0, 0, -Math.PI / 4 - 0.05));
    const q_farm_right = new THREE.Quaternion().setFromEuler(new THREE.Euler(0, 0,  Math.PI / 3));

    const q_arm_left  = new THREE.Quaternion().setFromEuler(new THREE.Euler( 0, 0, 0));
    const q_arm_right = new THREE.Quaternion().setFromEuler(new THREE.Euler( 0, 0, 0));

    const q_foot_left  = new THREE.Quaternion().setFromEuler(new THREE.Euler( Math.PI / 2, 0, 0));
    const q_foot_right = new THREE.Quaternion().setFromEuler(new THREE.Euler( Math.PI / 2, 0, 0));

    const q_hand_left  = new THREE.Quaternion().setFromEuler(new THREE.Euler( 0, 0, 0));
    const q_hand_right = new THREE.Quaternion().setFromEuler(new THREE.Euler( 0, 0, 0));

    const q_head =  new THREE.Quaternion();

    const q_spine =  new THREE.Quaternion();


    const spine  = new SimpleBone('spine', q_spine, 1.65, 0.9);

    const shoulder_left  = new SimpleBone('shoulder-left', q_shoulder_left, 0.60, 0.5);
    const shoulder_right = new SimpleBone('shoulder-right', q_shoulder_right, 0.65, 0.5);

    const hip_left  = new SimpleBone('hip-left', q_hip_left, 0.35, 0.4);
    const hip_right = new SimpleBone('hip-right', q_hip_right, 0.4, 0.4);

    const fleg_left  = new SimpleBone('fleg-left', q_fleg_left, 0.625, 0.2);
    const fleg_right = new SimpleBone('fleg-right', q_fleg_right, 0.625, 0.2);

    const leg_left  = new SimpleBone('leg-left', q_leg_left, 0.625, 0.2);
    const leg_right = new SimpleBone('leg-right', q_leg_right, 0.625, 0.2);

    const foot_left  = new SimpleBone('foot-left', q_foot_left, 0.5, 0.3);
    const foot_right = new SimpleBone('foot-right', q_foot_right, 0.5, 0.3);

    const farm_left  = new SimpleBone('farm-left', q_farm_left, 0.85, 0.2);
    const farm_right = new SimpleBone('farm-right', q_farm_right, 0.75, 0.2);

    const arm_left  = new SimpleBone('arm-left', q_arm_left, 0.85, 0.2);
    const arm_right = new SimpleBone('arm-right', q_arm_right, 0.75, 0.2);

    const hand_left  = new SimpleBone('hand-left', q_hand_left, 0.6, 0.3);
    const hand_right = new SimpleBone('hand-right', q_hand_right, 0.6, 0.3);

    const head  = new SimpleBone('head', q_head, 1.2, 0.6);

    leg_left.addChild(foot_left);
    leg_right.addChild(foot_right);

    fleg_left.addChild(leg_left);
    fleg_right.addChild(leg_right);

    hip_left.addChild(fleg_left);
    hip_right.addChild(fleg_right);

    arm_left.addChild(hand_left);
    arm_right.addChild(hand_right);

    farm_left.addChild(arm_left);
    farm_right.addChild(arm_right);

    shoulder_left.addChild(farm_left);
    shoulder_right.addChild(farm_right);

    spine.addChild(head);
    spine.addChild(shoulder_left);
    spine.addChild(shoulder_right);

    const root_pos = new THREE.Vector3(0, 1.4, 0);
    const root_quat = new THREE.Quaternion();

    skeleton = new SimpleSkeleton(root_pos, root_quat, ['spine', 'hip-left', 'hip-right', 'shoulder-left', 'shoulder-right']);
    
    skeleton.addBone(spine);
    skeleton.addBone(hip_left);
    skeleton.addBone(hip_right);
}


export {EngineBase, MaterialPool, RootBone, SimpleBone, SimpleSkeleton, Octree, MetaBallGrid, triTable, carColors}